import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../hooks/useAuth'
import { scanTranscript, formatTimestampedTranscript, generateSermonSummary, generateDisplayTitle, generateBigIdea, generateEncouragementSummary, classifyMainPassage } from '../hooks/useSermons'
import { canScan, recordScans, FREE_SCAN_LIMIT } from '../lib/scanLimits'
import { dedupeRefRows } from '../lib/dedupeRefs'
import UpgradeSheet from './UpgradeSheet'

// Super-admin bulk import tool — desktop-only. Lives at /admin.
//
// Tabs:
//   • Bulk Sermon Upload — drag-drop Excel → configure → process → review → upload.
//   • Needs Review        — items the importer skipped + run history.
//   • Churches / Users    — stubs for future work.

const CLAUDE_MODEL = 'claude-sonnet-4-6'

const C = {
  bg:       '#0a0a0f',
  surface:  '#111111',
  surface2: '#15151c',
  panel:    '#161620',
  border:   '#1f1f2a',
  borderHi: '#2a2a3a',
  text:     '#ffffff',
  muted:    'rgba(255,255,255,0.55)',
  faint:    'rgba(255,255,255,0.35)',
  accent:   '#E8C87A',
  navy:     '#1B2A4A',
  purple:   '#9B6FD4',
  pink:     '#FF6B9D',
  red:      '#DC2626',
  blue:     '#4A90E2',
  green:    '#50C896',
}
const FONT = "'Inter',sans-serif"

// Ref scanning now goes through useSermons.scanTranscript (imported above),
// which owns the system prompt and the supabase.functions.invoke call. The
// previous Pass 1 / Pass 2 sermon-segmentation prompts have been retired.

// ── HELPERS ──────────────────────────────────────────────────────────────
function tsToSeconds(ts) {
  if (ts == null) return null
  if (typeof ts === 'number') return Math.round(ts)
  const s = String(ts).trim()
  if (!s) return null
  const parts = s.split(':').map(Number)
  if (parts.some(isNaN)) return null
  if (parts.length === 3) return parts[0]*3600 + parts[1]*60 + parts[2]
  if (parts.length === 2) return parts[0]*60 + parts[1]
  return parts[0]
}
function secondsToTs(s) {
  if (s == null || isNaN(s)) return null
  s = Math.max(0, Math.round(s))
  const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), ss = s%60
  const pad = n => String(n).padStart(2,'0')
  return h ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
}
function durToSeconds(d) {
  if (d == null || d === '') return null
  if (typeof d === 'number') {
    // xlsx returns Excel time-of-day fraction (0.0 → 1.0) for HH:MM:SS cells.
    if (d > 0 && d < 1) return Math.round(d * 86400)
    return Math.round(d)
  }
  return tsToSeconds(d)
}
function parseExcelDate(v) {
  if (!v && v !== 0) return null
  if (typeof v === 'number') {
    // Excel serial date
    const epoch = new Date(Date.UTC(1899, 11, 30))
    const ms = epoch.getTime() + v * 86400000
    return new Date(ms).toISOString().slice(0,10)
  }
  const d = new Date(v)
  if (!isNaN(d.getTime())) return d.toISOString().slice(0,10)
  return null
}
function extractYoutubeId(url, fallback) {
  if (fallback) return String(fallback).trim()
  if (!url) return null
  const m = String(url).match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : null
}
function delay(ms) { return new Promise(r => setTimeout(r, ms)) }

// Service-y Excel titles like "Welcome to Sunday Morning Service" are useless
// as sermon titles. When they match this pattern we'd rather use Claude's
// suggestion instead.
const GENERIC_TITLE_RX = /(welcome to|morning service|evening service|sunday service|weekly service|online service|live service)/i

function resolveTitle({ detected, suggestion, excel }) {
  const d = (detected || '').trim()
  if (d) return { title: d, source: 'detected' }
  const ex = (excel || '').trim()
  const isGeneric = ex && GENERIC_TITLE_RX.test(ex)
  if (ex && !isGeneric) return { title: ex, source: 'excel' }
  const sug = (suggestion || '').trim()
  if (sug) return { title: sug, source: 'ai_suggested' }
  return { title: ex || 'Untitled sermon', source: 'excel' }
}

// ── ROOT ──────────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const { profile } = useAuth()
  const [tab, setTab] = useState('upload')
  // Videos handed over from the Church Channels screen ("process N new videos")
  // to seed the Bulk Upload flow. { videos, churchId } | null.
  const [channelSeed, setChannelSeed] = useState(null)
  const [needsReviewCount, setNeedsReviewCount] = useState(0)
  const [missingFieldsCount, setMissingFieldsCount] = useState(0)
  const [reportsCount, setReportsCount] = useState(0)
  // Seeds the entity-creation form when "Add new source" is used.
  // { church_name, location, youtube_channel_url, entity_type } | null
  const [sourcePrefill, setSourcePrefill] = useState(null)
  const [tooNarrow, setTooNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < 900
  )

  useEffect(() => {
    const onResize = () => setTooNarrow(window.innerWidth < 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const refreshBadge = useCallback(async () => {
    const { count } = await supabase
      .from('import_queue')
      .select('id', { count: 'exact', head: true })
      .is('dismissed_at', null)
    setNeedsReviewCount(count || 0)
  }, [])

  // Count of sermons + encouragements in the active church missing the
  // equivalent key fields. Drives the Missing Fields sidebar badge.
  const refreshMissingFieldsBadge = useCallback(async () => {
    if (!profile?.church_id) { setMissingFieldsCount(0); return }
    const [sCount, eCount] = await Promise.all([
      supabase.from('sermons')
        .select('id', { count: 'exact', head: true })
        .eq('church_id', profile.church_id)
        .or('preacher.is.null,preacher.eq.,date.is.null'),
      supabase.from('encouragements')
        .select('id', { count: 'exact', head: true })
        .eq('church_id', profile.church_id)
        .eq('removed', false)
        .or('shared_by.is.null,shared_by.eq.,event_date.is.null'),
    ])
    setMissingFieldsCount((sCount.count || 0) + (eCount.count || 0))
  }, [profile?.church_id])

  // Count of unresolved reports (RLS scopes this to the admin's church).
  const refreshReportsBadge = useCallback(async () => {
    const { count } = await supabase
      .from('reports')
      .select('id', { count: 'exact', head: true })
      .eq('resolved', false)
    setReportsCount(count || 0)
  }, [])

  useEffect(() => { refreshBadge() }, [refreshBadge])
  useEffect(() => { refreshMissingFieldsBadge() }, [refreshMissingFieldsBadge])
  useEffect(() => { refreshReportsBadge() }, [refreshReportsBadge])

  if (tooNarrow) return (
    <div style={{
      minHeight:'100vh', background:C.bg, color:C.text, fontFamily:FONT,
      display:'flex', alignItems:'center', justifyContent:'center', padding:24,
    }}>
      <div style={{maxWidth:420, textAlign:'center'}}>
        <div style={{fontSize:42, marginBottom:14}}>🖥️</div>
        <div style={{fontSize:20, fontWeight:800, marginBottom:8}}>Desktop required</div>
        <div style={{fontSize:13, color:C.muted, lineHeight:1.5}}>
          The admin panel needs at least 900px width. Open this on a desktop browser.
        </div>
      </div>
    </div>
  )

  return (
    <div style={{minHeight:'100vh', background:C.bg, color:C.text, fontFamily:FONT, display:'flex'}}>
      <Sidebar tab={tab} setTab={setTab}
        needsReviewCount={needsReviewCount}
        missingFieldsCount={missingFieldsCount}
        reportsCount={reportsCount}
        profile={profile}/>
      <main style={{flex:1, minWidth:0, padding:'32px 40px', overflowY:'auto'}}>
        {tab === 'upload'         && <BulkUploadFlow refreshBadge={refreshBadge} seed={channelSeed} onSeedConsumed={() => setChannelSeed(null)}/>}
        {tab === 'review'         && <NeedsReview refreshBadge={refreshBadge}/>}
        {tab === 'missing-fields' && <MissingFieldsScreen refreshBadge={refreshMissingFieldsBadge}/>}
        {tab === 'reports'        && <ReportsScreen refreshBadge={refreshReportsBadge}/>}
        {tab === 'channels'       && <ChurchChannelsScreen
          onProcess={(videos, churchId) => { setChannelSeed({ videos, churchId }); setTab('upload') }}/>}
        {tab === 'church-settings' && <ChurchSettingsScreen churchId={profile?.church_id}/>}
        {tab === 'create-church'  && <CreateChurchScreen/>}
        {tab === 'bulk-add-churches' && <BulkAddChurches/>}
        {tab === 'church-networks' && <ChurchNetworksScreen/>}
        {tab === 'sources'        && <SourcesScreen
          prefill={sourcePrefill}
          onPrefillConsumed={() => setSourcePrefill(null)}
          setTab={setTab}/>}
        {tab === 'autopilot'      && <AutopilotImport/>}
        {tab === 'users'          && <Stub title="Users" />}
      </main>
    </div>
  )
}

// ── SIDEBAR ──────────────────────────────────────────────────────────────
function Sidebar({ tab, setTab, needsReviewCount, missingFieldsCount, reportsCount, profile }) {
  const items = [
    { id:'upload',           label:'Bulk Sermon Upload', enabled:true  },
    { id:'review',           label:'Needs Review',       enabled:true, badge:needsReviewCount },
    { id:'missing-fields',   label:'Missing Fields',     enabled:true, badge:missingFieldsCount },
    { id:'reports',          label:'Reports',            enabled:true, badge:reportsCount },
    { id:'channels',         label:'Church Channels',    enabled:true  },
    { id:'church-settings',  label:'Church Settings',    enabled:true  },
    // Provisioning sources (churches/events/networks) + managing networks are
    // super-admin only (enforced by RLS too).
    ...(profile?.role === 'super_admin'
      ? [{ id:'sources',         label:'Sources',           enabled:true },
         { id:'create-church',   label:'Create Church',     enabled:true },
         { id:'bulk-add-churches', label:'Bulk Add Churches', enabled:true },
         { id:'church-networks', label:'Church Networks',   enabled:true },
         { id:'autopilot',       label:'Autopilot Import',  enabled:true }]
      : []),
    { id:'users',            label:'Users',              enabled:false },
  ]
  return (
    <aside style={{
      width:240, flexShrink:0, background:C.surface, borderRight:`1px solid ${C.border}`,
      padding:'24px 16px', display:'flex', flexDirection:'column',
    }}>
      <div style={{padding:'4px 8px 22px 8px'}}>
        <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.18em', color:C.accent, marginBottom:4}}>
          BCP ADMIN
        </div>
        <div style={{fontSize:13, color:C.muted}}>
          Signed in as <span style={{color:C.text}}>{profile?.full_name || profile?.email || 'super admin'}</span>
        </div>
      </div>
      {items.map(it => {
        const active = tab === it.id && it.enabled
        return (
          <button key={it.id}
            onClick={() => it.enabled && setTab(it.id)}
            disabled={!it.enabled}
            style={{
              width:'100%', textAlign:'left',
              background: active ? 'rgba(232,200,122,0.10)' : 'transparent',
              border:'none',
              borderLeft: active ? `3px solid ${C.accent}` : '3px solid transparent',
              padding:'11px 13px', borderRadius:6,
              color: it.enabled ? (active ? C.accent : C.text) : C.faint,
              fontSize:14, fontWeight: active ? 700 : 500,
              cursor: it.enabled ? 'pointer' : 'not-allowed',
              fontFamily:FONT, display:'flex', alignItems:'center', justifyContent:'space-between',
              marginBottom:2,
            }}>
            <span>{it.label}</span>
            {it.enabled && it.badge > 0 && (
              <span style={{
                background:C.red, color:'#fff', fontSize:10, fontWeight:800,
                padding:'2px 6px', borderRadius:10, minWidth:18, textAlign:'center',
              }}>{it.badge}</span>
            )}
          </button>
        )
      })}
      <div style={{marginTop:'auto'}}>
        <div style={{padding:'12px 8px', fontSize:11, color:C.faint, lineHeight:1.5}}>
          <a href="/" style={{color:C.muted, textDecoration:'none'}}>← Back to app</a>
        </div>
      </div>
    </aside>
  )
}


function Stub({ title }) {
  return (
    <div style={{maxWidth:640}}>
      <H1>{title}</H1>
      <Note>Coming soon.</Note>
    </div>
  )
}

// ── CHURCH SETTINGS ──────────────────────────────────────────────────────
// Edit every church setting. Super admins can pick any church from the
// dropdown and toggle the privileged flags (paid, self-serve upload, active,
// free-scan counter); other admins are scoped to their own church and see
// only the content fields + invite-only. Also surfaces per-church stats:
// sermons uploaded, Bible references, and who uploaded them (with their role).
function ChurchSettingsScreen({ churchId: ownChurchId }) {
  const { profile } = useAuth()
  const isSuper = profile?.role === 'super_admin'

  const [churchList, setChurchList] = useState([])      // picker options (super only)
  const [selectedId, setSelectedId] = useState(ownChurchId || '')
  const [church,  setChurch]  = useState(null)
  const [form,    setForm]    = useState(null)          // editable copy of the row
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [error,   setError]   = useState('')
  const [stats,   setStats]   = useState(null)          // { sermonCount, refCount, uploaders[] }
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [deletedName, setDeletedName] = useState('')

  // ── Backfill missing preachers ──
  const [bfStatus, setBfStatus] = useState('idle')   // idle | scanning | ready | descChecking | applying | done
  const [bfRows,   setBfRows]   = useState([])        // [{ id, title, youtubeId, suggestion, source, apply }]
  const [bfMsg,    setBfMsg]    = useState('')

  // ── Backfill AI summaries ──
  const [sumStatus, setSumStatus] = useState('idle')  // idle | scanning | ready | running | done
  const [sumRows,   setSumRows]   = useState([])       // [{ id, title, preacher, date }] missing a summary
  const [sumMsg,    setSumMsg]    = useState('')

  // ── Backfill encouragement titles (docs/FIX-ENCOURAGEMENT-UPLOAD.md §6) ──
  const [encTitleStatus, setEncTitleStatus] = useState('idle')  // idle | scanning | ready | applying | done
  const [encTitleRows,   setEncTitleRows]   = useState([])       // [{ id, current, suggested, apply }]
  const [encTitleMsg,    setEncTitleMsg]    = useState('')

  // ── Re-check playlists (multi-playlist backfill) ──
  const [plStatus,   setPlStatus]   = useState('idle')   // idle | scanning | ready | applying
  const [plRows,     setPlRows]     = useState([])        // [{ id, title, current[], suggested[], apply }]
  const [plMsg,      setPlMsg]      = useState('')
  // Extra playlist URLs to include — covers unlisted playlists the channel
  // scan can't see (only public playlists are returned by the channel list).
  const [plExtraUrls, setPlExtraUrls] = useState('')

  // ── Backfill church logos (FIX-CHURCH-LOGOS.md §1e) ──
  // Cross-church (not selectedId-scoped, unlike the other backfills above) —
  // finds every church with a youtube_channel_url but no logo yet.
  const [logoBfStatus, setLogoBfStatus] = useState('idle')  // idle | scanning | ready | applying | done
  const [logoBfRows,   setLogoBfRows]   = useState([])       // [{ id, name, youtube_channel_url, previewDataUrl, channelTitle, previewError, apply }]
  const [logoBfMsg,    setLogoBfMsg]    = useState('')

  // Church list for the picker (super admins only — RLS lets anyone read).
  useEffect(() => {
    if (!isSuper) return
    supabase.from('churches').select('id, name').order('name')
      .then(({ data }) => setChurchList(data || []))
  }, [isSuper])

  // Load the selected church and seed the editable form.
  useEffect(() => {
    let cancelled = false
    if (!selectedId) { setLoading(false); setChurch(null); setForm(null); return }
    setLoading(true)
    supabase.from('churches').select('*').eq('id', selectedId).single()
      .then(({ data }) => {
        if (cancelled) return
        setChurch(data || null)
        setForm(data ? {
          name:                data.name || '',
          location:            data.location || '',
          website:             data.website || '',
          logo_url:            data.logo_url || '',
          network_id:          data.network_id || '',
          youtube_channel_url: data.youtube_channel_url || '',
          vision:              data.vision || '',
          meeting_info:        data.meeting_info || '',
          invite_only:         data.invite_only !== false,
          self_serve_upload:   data.self_serve_upload !== false,
          is_paid:             !!data.is_paid,
          active:              data.active !== false,
          free_scans_used:     data.free_scans_used || 0,
          scan_limit:          Math.max(5, data.scan_limit || 5),
        } : null)
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedId])

  // Per-church stats: sermon count, Bible-ref count, and uploaders + roles,
  // each split by where the sermon was uploaded (admin tool vs in-app).
  const loadStats = useCallback(async (id) => {
    if (!id) { setStats(null); return }
    setStats(null)
    let { data: sermons, error } = await supabase
      .from('sermons')
      .select('id, uploaded_by, source, profiles:uploaded_by(full_name, email, role)')
      .eq('church_id', id)
    // Tolerate the source column not existing yet (migration not run).
    if (error && /source/i.test(error.message || '')) {
      ;({ data: sermons } = await supabase
        .from('sermons')
        .select('id, uploaded_by, profiles:uploaded_by(full_name, email, role)')
        .eq('church_id', id))
    }
    const { count: refCount } = await supabase
      .from('sermon_refs')
      .select('id', { count: 'exact', head: true })
      .eq('church_id', id)
    const byUploader = new Map()
    for (const s of (sermons || [])) {
      const key = s.uploaded_by || 'unknown'
      if (!byUploader.has(key)) {
        byUploader.set(key, {
          id:    key,
          name:  s.profiles?.full_name || (s.uploaded_by ? 'Unknown user' : 'No uploader recorded'),
          email: s.profiles?.email || '',
          role:  s.profiles?.role || '—',
          count: 0, admin: 0, inApp: 0, unknown: 0,
        })
      }
      const u = byUploader.get(key)
      u.count++
      if (s.source === 'admin_tool') u.admin++
      else if (s.source === 'in_app') u.inApp++
      else u.unknown++
    }
    setStats({
      sermonCount: (sermons || []).length,
      refCount:    refCount || 0,
      uploaders:   [...byUploader.values()].sort((a, b) => b.count - a.count),
    })
  }, [])
  useEffect(() => { loadStats(selectedId) }, [selectedId, loadStats])

  // Reset the backfill panels whenever the selected church changes.
  useEffect(() => {
    setBfStatus('idle'); setBfRows([]); setBfMsg('')
    setPlStatus('idle'); setPlRows([]); setPlMsg(''); setPlExtraUrls('')
    setSumStatus('idle'); setSumRows([]); setSumMsg('')
  }, [selectedId])

  // ── Backfill AI summaries + display titles ──────────────────────────────
  // Find this church's sermons missing an ai_summary or a display_title (the
  // latter covers older sermons that already have a summary but predate
  // display_title generation existing), then generate whichever's missing.
  // Best-effort + resumable.
  async function sumScan() {
    if (!selectedId) return
    setSumStatus('scanning'); setSumRows([]); setSumMsg('Finding sermons missing a summary or display title…')
    let data
    try {
      data = await fetchSermonsMissingSummary(selectedId)   // paged — accurate beyond 1000
    } catch (e) {
      setSumStatus('idle')
      setSumMsg(/ai_summary|display_title/i.test(e.message || '')
        ? 'Run the migration first: alter table public.sermons add column if not exists ai_summary text; alter table public.sermons add column if not exists display_title text;'
        : `Could not scan: ${e.message}`)
      return
    }
    setSumRows(data || [])
    setSumStatus('ready')
    setSumMsg(`${(data || []).length} sermon(s) missing a summary or display title. Sermons with no summary yet also need their stored transcript (older ones without it are skipped).`)
  }

  async function sumRun() {
    const rows = sumRows.slice()
    if (rows.length === 0) { setSumMsg('Nothing to generate.'); return }
    if (!window.confirm(`This will generate AI text for ${rows.length} ${rows.length === 1 ? 'sermon' : 'sermons'} — one Claude call each (two for sermons with no summary yet), saved as it goes (a failure never loses completed ones). Proceed?`)) return
    setSumStatus('running')
    let ok = 0, skip = 0, fail = 0
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]
      setSumMsg(`Generating… ${i + 1}/${rows.length} · ${ok} done${skip ? `, ${skip} skipped` : ''}${fail ? `, ${fail} failed` : ''}`)
      try {
        // Already has a summary (just predates display_title existing) —
        // only need the cheaper title-only call, no transcript required.
        if (r.ai_summary) {
          const displayTitle = await generateDisplayTitle(r.ai_summary, { title: r.title })
          if (!displayTitle) { fail++; continue }
          const { error: uErr } = await supabase.from('sermons').update({ display_title: displayTitle }).eq('id', r.id)
          if (uErr) fail++; else ok++
          continue
        }
        const { data: srow } = await supabase.from('sermons').select('transcript').eq('id', r.id).single()
        const transcript = srow?.transcript || ''
        if (!transcript.trim()) { skip++; continue }
        const summary = await generateSermonSummary(transcript, { title: r.title, preacher: r.preacher, date: r.date, church_name: church?.name || '' })
        if (!summary) { fail++; continue }
        const displayTitle = await generateDisplayTitle(summary, { title: r.title })
        const { error: uErr } = await supabase.from('sermons').update({ ai_summary: summary, display_title: displayTitle || null }).eq('id', r.id)
        if (uErr) fail++; else ok++
      } catch { fail++ }
    }
    setSumStatus('done')
    setSumMsg(`Done — ${ok} updated${skip ? `, ${skip} skipped (no transcript)` : ''}${fail ? `, ${fail} failed` : ''}. Already-saved work is kept — re-run to retry the rest.`)
  }

  // ── Backfill encouragement titles ────────────────────────────────────────
  // Finds encouragements whose title is just a joined verse-reference list
  // ("Revelation 12:11 +1 more") rather than a real short title, and
  // suggests a proper one via generateEncouragementSummary. Review-before-
  // write, same pattern as the playlist backfill below.
  function looksLikeVerseRefTitle(title, refs) {
    const t = String(title || '').trim()
    if (!t) return true
    if (/^[1-3]?\s*[A-Za-z ]+\s+\d+:\d+(-\d+)?(\s*\+\s*\d+\s*more)?$/.test(t)) return true
    return (refs || []).some(r => r.verse_ref === t)
  }
  async function encTitleScan() {
    if (!selectedId) return
    setEncTitleStatus('scanning'); setEncTitleRows([]); setEncTitleMsg('Finding encouragements with a verse-reference title…')
    const { data, error: e } = await supabase
      .from('encouragements')
      .select('id, title, text, summary, encouragement_refs(verse_ref)')
      .eq('church_id', selectedId).eq('removed', false)
    if (e) { setEncTitleStatus('idle'); setEncTitleMsg(`Could not load encouragements: ${e.message}`); return }
    const candidates = (data || []).filter(r => looksLikeVerseRefTitle(r.title, r.encouragement_refs))
    setEncTitleMsg(`${candidates.length} encouragement(s) with a verse-reference (or missing) title. Generating suggestions…`)
    const rows = []
    for (const r of candidates) {
      const source = r.summary || r.text || ''
      const ai = source ? await generateEncouragementSummary(source) : null
      rows.push({ id: r.id, current: r.title || '(none)', suggested: ai?.title || '', apply: !!ai?.title })
    }
    setEncTitleRows(rows)
    setEncTitleStatus('ready')
    setEncTitleMsg(`${rows.length} encouragement(s) found · ${rows.filter(r => r.suggested).length} with a suggestion. Review below, then Apply.`)
  }
  async function encTitleApply() {
    const targets = encTitleRows.filter(r => r.apply && r.suggested)
    if (targets.length === 0) { setEncTitleMsg('Nothing selected to apply.'); return }
    setEncTitleStatus('applying')
    let ok = 0, fail = 0
    for (const r of targets) {
      const { error: uErr } = await supabase.from('encouragements').update({ title: r.suggested }).eq('id', r.id)
      if (uErr) fail++; else ok++
    }
    setEncTitleStatus('done')
    setEncTitleMsg(`Done — ${ok} updated${fail ? `, ${fail} failed` : ''}.`)
  }

  // ── Backfill church logos ────────────────────────────────────────────────
  // Preview-every-row-before-writing, same shape as encTitleScan/Apply above,
  // except scoped across every church rather than one selected church —
  // fetch-church-logo's no-church_id mode does the preview fetch, its
  // church_id mode does the actual upload+save once a row is confirmed.
  async function logoBfScan() {
    setLogoBfStatus('scanning'); setLogoBfRows([]); setLogoBfMsg('Finding churches with a YouTube channel but no logo…')
    const { data, error } = await supabase.from('churches')
      .select('id, name, youtube_channel_url, logo_url')
      .not('youtube_channel_url', 'is', null).neq('youtube_channel_url', '')
      .is('logo_url', null)
    if (error) { setLogoBfStatus('idle'); setLogoBfMsg(`Could not load churches: ${error.message}`); return }
    const candidates = data || []
    setLogoBfMsg(`${candidates.length} church(es) found. Fetching channel icons…`)
    const rows = []
    for (const c of candidates) {
      try {
        const { data: res, error: fnErr } = await supabase.functions.invoke('fetch-church-logo', { body: { channel_url: c.youtube_channel_url } })
        if (fnErr || res?.error) throw new Error(res?.error || fnErr?.message || 'Lookup failed')
        rows.push({ id: c.id, name: c.name, youtube_channel_url: c.youtube_channel_url, previewDataUrl: res.image_data_url, channelTitle: res.channel_title || '', previewError: '', apply: true })
      } catch (e) {
        rows.push({ id: c.id, name: c.name, youtube_channel_url: c.youtube_channel_url, previewDataUrl: '', channelTitle: '', previewError: e.message || 'Could not fetch icon', apply: false })
      }
    }
    setLogoBfRows(rows)
    setLogoBfStatus('ready')
    const okCount = rows.filter(r => r.previewDataUrl).length
    setLogoBfMsg(`${rows.length} church(es) checked · ${okCount} icon(s) found. Review below, then Apply.`)
  }
  async function logoBfApply() {
    const targets = logoBfRows.filter(r => r.apply && r.previewDataUrl)
    if (targets.length === 0) { setLogoBfMsg('Nothing selected to apply.'); return }
    setLogoBfStatus('applying')
    let ok = 0, fail = 0
    for (const r of targets) {
      try {
        const { error } = await supabase.functions.invoke('fetch-church-logo', { body: { channel_url: r.youtube_channel_url, church_id: r.id } })
        if (error) throw error
        ok++
      } catch { fail++ }
    }
    setLogoBfStatus('done')
    setLogoBfMsg(`Done — ${ok} updated${fail ? `, ${fail} failed` : ''}.`)
  }

  // ── Backfill missing preachers ───────────────────────────────────────────
  // Step 1: find this church's sermons with no preacher and re-derive one from
  // the title (free, instant). Step 2 (optional): for the ones still missing,
  // fetch the YouTube description and try that. The admin reviews + edits every
  // suggestion before applying.
  async function bfScanTitles() {
    if (!selectedId) return
    setBfStatus('scanning'); setBfRows([]); setBfMsg('Loading sermons with no preacher…')
    const { data, error: e } = await supabase
      .from('sermons')
      .select('id, title, youtube_id, preacher')
      .eq('church_id', selectedId)
      .or('preacher.is.null,preacher.eq.')
      .order('date', { ascending: false })
    if (e) { setBfStatus('idle'); setBfMsg(`Could not load sermons: ${e.message}`); return }
    const rows = (data || []).map(s => {
      const suggestion = extractPreacherFromTitleStr(s.title || '')
      return { id: s.id, title: s.title || '', youtubeId: s.youtube_id || '', suggestion: suggestion || '', source: suggestion ? 'title' : '', apply: !!suggestion }
    })
    setBfRows(rows)
    const found = rows.filter(r => r.suggestion).length
    setBfStatus('ready')
    setBfMsg(`${rows.length} sermon(s) missing a preacher · ${found} guessed from the title. Review below, then Apply. Use "Check descriptions" for the rest.`)
  }

  // Fetch the YouTube description for rows still without a suggestion and try to
  // pull a preacher from it. Sequential with a small gap to be gentle on the API.
  async function bfCheckDescriptions() {
    const targets = bfRows.filter(r => !r.suggestion && r.youtubeId)
    if (targets.length === 0) { setBfMsg('No remaining sermons with a YouTube id to check.'); return }
    setBfStatus('descChecking')
    let done = 0, found = 0
    for (const r of targets) {
      setBfMsg(`Checking descriptions… ${done}/${targets.length}`)
      try {
        const { data } = await supabase.functions.invoke('claude-proxy', {
          body: { action: 'fetch_video_info', youtube_url: `https://www.youtube.com/watch?v=${r.youtubeId}` },
        })
        const desc = getDescriptionFromResponse(data)
        const name = extractPreacherFromDescription(desc)
        if (name) {
          found++
          setBfRows(rows => rows.map(x => x.id === r.id ? { ...x, suggestion: name, source: 'description', apply: true } : x))
        }
      } catch (_e) { /* skip this one */ }
      done++
      await delay(400)
    }
    setBfStatus('ready')
    setBfMsg(`Checked ${targets.length} description(s) · found ${found} more. Review, then Apply.`)
  }

  async function bfApply() {
    const toApply = bfRows.filter(r => r.apply && r.suggestion.trim())
    if (toApply.length === 0) { setBfMsg('Nothing ticked to apply.'); return }
    setBfStatus('applying')
    let ok = 0, fail = 0
    for (const r of toApply) {
      const { error: e } = await supabase.from('sermons').update({ preacher: r.suggestion.trim() }).eq('id', r.id)
      if (e) fail++; else ok++
      setBfMsg(`Applying… ${ok + fail}/${toApply.length}`)
    }
    // Drop the applied rows from the list.
    setBfRows(rows => rows.filter(r => !(r.apply && r.suggestion.trim())))
    setBfStatus('ready')
    setBfMsg(`Updated ${ok} sermon(s)${fail ? ` · ${fail} failed` : ''}.`)
  }

  // ── Re-check playlists ───────────────────────────────────────────────────
  // Re-scan the church's YouTube channel to find EVERY playlist each uploaded
  // video belongs to, and propose updating sermons that are missing a playlist
  // (e.g. uploaded before multi-playlist support, so they only carry the first
  // one). Non-destructive: suggested = current ∪ found, so it only ever adds.
  async function plRecheck() {
    const handle = (form?.youtube_channel_url || church?.youtube_channel_url || '').trim()
    setPlStatus('scanning'); setPlRows([]); setPlMsg('Loading playlists…')
    try {
      // Channel's public playlists.
      let playlists = []
      if (handle) {
        const { data: plData, error: plErr } = await supabase.functions.invoke('claude-proxy', {
          body: { action: 'fetch_youtube_playlists', channel_url: handle, strict: true },
        })
        if (plErr) throw new Error(plData?.error || plErr.message || 'Could not load playlists')
        playlists = (Array.isArray(plData?.playlists) ? plData.playlists : []).map(p => ({ id: p.id, title: p.title }))
      }
      // Plus any pasted playlist URLs — covers UNLISTED playlists the channel
      // list can't return (their title is resolved from the videos response).
      const seen = new Set(playlists.map(p => p.id))
      for (const line of plExtraUrls.split(/\r?\n/)) {
        const pid = extractPlaylistId(line)
        if (pid && !seen.has(pid)) { seen.add(pid); playlists.push({ id: pid, title: null }) }
      }
      if (playlists.length === 0) {
        setPlStatus('idle')
        setPlMsg(handle ? 'No playlists found for that channel. Paste the playlist URL(s) below to include them.'
                        : 'No channel URL saved — paste the playlist URL(s) below to check them.')
        return
      }

      // Build videoId → set of playlist titles it appears in.
      const byVideo = new Map()
      for (let i = 0; i < playlists.length; i++) {
        const pl = playlists[i]
        let title = pl.title && pl.title !== 'No series' ? pl.title : null
        setPlMsg(`Scanning playlist ${i + 1}/${playlists.length}${title ? ` — ${title}` : ''}…`)
        let token = null, page = 1
        while (true) {
          const { data, error } = await supabase.functions.invoke('claude-proxy', {
            body: { action: 'fetch_playlist_videos', playlist_id: pl.id, continuationToken: token },
          })
          if (error) break
          // Resolve a missing title (pasted/unlisted playlist) from the response.
          if (!title && page === 1) {
            title = data?.title || data?.playlistTitle || data?.name || data?.metadata?.title || data?.playlist?.title || `Playlist ${pl.id}`
          }
          const items = data?.videos || data?.items || data?.results || data?.playlistVideos || []
          for (const v of items) {
            const shaped = shapeVideo(v, title || 'No series')
            if (!shaped?.id) continue
            if (!byVideo.has(shaped.id)) byVideo.set(shaped.id, new Set())
            if (title) byVideo.get(shaped.id).add(title)
          }
          token = data?.continuationToken || data?.cursor || null
          if (!token) break
          page += 1
          await delay(250)
        }
        await delay(250)
      }

      // Load this church's sermons and diff their playlists.
      const { data: sermons, error: sErr } = await supabase
        .from('sermons').select('id, title, youtube_id, playlists').eq('church_id', selectedId)
      if (sErr) throw new Error(sErr.message)
      const rows = []
      for (const s of (sermons || [])) {
        if (!s.youtube_id) continue
        const found = byVideo.get(s.youtube_id)
        if (!found || found.size === 0) continue
        const current = Array.isArray(s.playlists) ? s.playlists.filter(Boolean) : []
        const suggested = [...new Set([...current, ...found])]
        // Only surface sermons that would GAIN a playlist.
        if (suggested.length > current.length) {
          rows.push({ id: s.id, title: s.title || '', current, suggested, apply: true })
        }
      }
      setPlRows(rows)
      setPlStatus('ready')
      setPlMsg(rows.length
        ? `${rows.length} sermon(s) are missing a playlist. Review, then Apply.`
        : 'All sermons already have their full playlist membership. Nothing to update.')
    } catch (e) {
      setPlStatus('idle')
      setPlMsg(`Re-check failed: ${e.message || e}`)
    }
  }

  async function plApply() {
    const toApply = plRows.filter(r => r.apply && r.suggested.length)
    if (toApply.length === 0) { setPlMsg('Nothing ticked to apply.'); return }
    setPlStatus('applying')
    let ok = 0, fail = 0, missingCol = false
    for (const r of toApply) {
      const { error: e } = await supabase.from('sermons').update({ playlists: r.suggested }).eq('id', r.id)
      if (e) { fail++; if (/playlists.*does not exist/i.test(e.message || '')) missingCol = true } else ok++
      setPlMsg(`Applying… ${ok + fail}/${toApply.length}`)
    }
    setPlRows(rows => rows.filter(r => !(r.apply && r.suggested.length)))
    setPlStatus('ready')
    setPlMsg(missingCol
      ? 'The playlists column is missing — run supabase-schema-sermon-playlists.sql first.'
      : `Updated ${ok} sermon(s)${fail ? ` · ${fail} failed` : ''}.`)
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function save() {
    if (!form || !selectedId) return
    setSaving(true); setError(''); setSaved(false)
    const updates = {
      name:                form.name.trim(),
      location:            form.location.trim()            || null,
      website:             form.website.trim()             || null,
      logo_url:            form.logo_url.trim()             || null,
      network_id:          form.network_id                 || null,
      youtube_channel_url: form.youtube_channel_url.trim()  || null,
      vision:              form.vision.trim()               || null,
      meeting_info:        form.meeting_info.trim()         || null,
      invite_only:         !!form.invite_only,
    }
    // Privileged flags are super-admin only.
    if (isSuper) {
      updates.self_serve_upload = !!form.self_serve_upload
      updates.is_paid           = !!form.is_paid   // true = unlimited scans
      updates.active            = !!form.active
      // Scan allowance (ignored while unlimited). Floored at 5.
      updates.scan_limit        = Math.max(5, Number(form.scan_limit) || 5)
    }
    // Retry, stripping any column that doesn't exist yet (migrations not run),
    // until the update succeeds or fails for another reason.
    let error = null
    for (let attempt = 0; attempt < 3; attempt++) {
      ;({ error } = await supabase.from('churches').update(updates).eq('id', selectedId))
      if (!error) break
      let stripped = false
      for (const col of ['self_serve_upload', 'scan_limit', 'network_id']) {
        if (col in updates && new RegExp(`\\b${col}\\b`, 'i').test(error.message || '')) {
          delete updates[col]; stripped = true
        }
      }
      if (!stripped) break
    }
    setSaving(false)
    if (error) { setError(error.message); return }
    setChurch(c => ({ ...c, ...updates }))
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  // Permanently delete the selected church (super admin only) via the
  // delete_church RPC, which cascades sermons, refs and all church data.
  const isOwnChurch = !!selectedId && selectedId === profile?.church_id
  async function doDelete() {
    if (!selectedId || isOwnChurch) return
    setDeleting(true); setDeleteError('')
    const name = church?.name || 'Church'
    const { error } = await supabase.rpc('delete_church', { p_church_id: selectedId })
    setDeleting(false)
    if (error) {
      setDeleteError(/delete_church.*does not exist|could not find/i.test(error.message || '')
        ? 'Delete isn’t enabled yet — run the delete-church migration (supabase-schema-delete-church.sql).'
        : error.message)
      return
    }
    setConfirmDelete(false)
    setChurchList(list => list.filter(c => c.id !== selectedId))
    setSelectedId(''); setChurch(null); setForm(null); setStats(null)
    setDeletedName(name)
  }

  const ta = { ...inputStyle, minHeight:80, resize:'vertical', lineHeight:1.5 }
  const roleLabels = { member:'Member', elder:'Elder', admin:'Admin', super_admin:'Super admin' }

  return (
    <div style={{maxWidth:600}}>
      <H1>Church Settings</H1>

      {/* Church picker — super admins choose any church; others are locked. */}
      {isSuper ? (
        <Field label="Church">
          <select style={inputStyle} value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            <option value="">— Select a church —</option>
            {churchList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      ) : !ownChurchId ? (
        <Note>No church is attached to your account yet.</Note>
      ) : null}

      {isSuper && (
        <div style={{marginTop:20, marginBottom:20, paddingTop:20, borderTop:`1px solid ${C.borderHi}`}}>
          <H2>Backfill church logos</H2>
          <Sub>Finds churches with a YouTube channel but no logo yet, fetches each channel's icon, and lets you review before saving anything.</Sub>
          <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:10}}>
            <PrimaryBtn kind="ghost" onClick={logoBfScan} disabled={logoBfStatus === 'scanning' || logoBfStatus === 'applying'}>
              {logoBfStatus === 'scanning' ? 'Scanning…' : 'Find churches needing a logo'}
            </PrimaryBtn>
            {logoBfRows.some(r => r.apply) && logoBfStatus !== 'applying' && (
              <PrimaryBtn onClick={logoBfApply}>Apply {logoBfRows.filter(r => r.apply).length} logo(s)</PrimaryBtn>
            )}
            {logoBfStatus === 'applying' && <PrimaryBtn kind="ghost" disabled>Applying…</PrimaryBtn>}
          </div>
          {logoBfMsg && <Note>{logoBfMsg}</Note>}
          {logoBfRows.length > 0 && (
            <div style={{display:'flex', flexDirection:'column', gap:8, marginTop:10}}>
              {logoBfRows.map(r => (
                <div key={r.id} style={{display:'flex', alignItems:'center', gap:12, background:C.surface2, border:`1px solid ${C.borderHi}`, borderRadius:10, padding:'10px 12px'}}>
                  {r.previewDataUrl
                    ? <img src={r.previewDataUrl} alt="" style={{width:40, height:40, borderRadius:9, objectFit:'cover', flexShrink:0}}/>
                    : <div style={{width:40, height:40, borderRadius:9, background:C.surface2, border:`1px solid ${C.borderHi}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:16}}>⛪</div>}
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:700, color:C.text}}>{r.name}</div>
                    <div style={{fontSize:11.5, color: r.previewError ? '#FF8888' : C.faint, marginTop:2}}>
                      {r.previewError || r.channelTitle || r.youtube_channel_url}
                    </div>
                  </div>
                  {r.previewDataUrl && (
                    <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.muted, cursor:'pointer', flexShrink:0}}>
                      <input type="checkbox" checked={r.apply}
                        onChange={e => setLogoBfRows(rows => rows.map(x => (x.id === r.id ? { ...x, apply: e.target.checked } : x)))}/>
                      Apply
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {deletedName && !selectedId && (
        <Note kind="ok">✓ Deleted <strong>{deletedName}</strong> and all its sermons, references and data.</Note>
      )}

      {!selectedId ? (
        isSuper ? <Sub>Pick a church above to edit its settings.</Sub> : null
      ) : loading || !form ? (
        <Note>Loading…</Note>
      ) : (
        <>
          <H2>Details</H2>
          <Field label="Church name">
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Church name"/>
          </Field>
          <Field label="Location">
            <input style={inputStyle} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Tunbridge Wells, Kent"/>
          </Field>
          <Field label="Website">
            <input style={inputStyle} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://…"/>
          </Field>
          <Field label="Church logo">
            <LogoUploadField value={form.logo_url} onChange={v => set('logo_url', v)} idHint={form.slug || form.name}
              churchId={selectedId} youtubeUrl={form.youtube_channel_url}/>
          </Field>
          <Field label="Network — group of connected churches">
            <NetworkSelectField value={form.network_id} onChange={v => set('network_id', v)}/>
          </Field>
          <Field label="YouTube channel URL — for auto-scan">
            <input style={inputStyle} value={form.youtube_channel_url} onChange={e => set('youtube_channel_url', e.target.value)} placeholder="https://www.youtube.com/@yourchurch"/>
          </Field>

          <H2>Church page</H2>
          <Field label="Vision">
            <textarea style={ta} value={form.vision} onChange={e => set('vision', e.target.value)} placeholder="The church's vision statement."/>
          </Field>
          <Field label="Meeting info">
            <textarea style={ta} value={form.meeting_info} onChange={e => set('meeting_info', e.target.value)} placeholder="When & where the church meets."/>
          </Field>

          <H2>Access &amp; uploads</H2>
          <ToggleRow label="Invite-only"
            hint="On = hidden from public join lists; people join via invite / access request only."
            value={form.invite_only} onChange={v => set('invite_only', v)}/>
          {isSuper && (
            <>
              <ToggleRow label="Self-serve upload"
                hint="On = this church's own users can upload sermons in-app. Off = only super admins upload (from this admin tool). Super-admin uploads are always unlimited regardless."
                value={form.self_serve_upload} onChange={v => set('self_serve_upload', v)}/>
              <ToggleRow label="Unlimited in-app scans"
                hint="On = this church's in-app uploads are unlimited. Off = capped at the scan limit below."
                value={form.is_paid} onChange={v => set('is_paid', v)}/>
              {!form.is_paid && (
                <Field label={`Scan limit — how many in-app sermon scans this church gets (min ${FREE_SCAN_LIMIT})`}>
                  <input style={{...inputStyle, maxWidth:140}} type="number" min={FREE_SCAN_LIMIT}
                    value={form.scan_limit}
                    onChange={e => set('scan_limit', e.target.value)}
                    onBlur={e => set('scan_limit', Math.max(FREE_SCAN_LIMIT, Number(e.target.value) || FREE_SCAN_LIMIT))}/>
                  <div style={{fontSize:12, color:C.faint, marginTop:6}}>
                    {form.free_scans_used || 0} used so far.
                  </div>
                </Field>
              )}
              <ToggleRow label="Active"
                hint="Off = soft-disables the church."
                value={form.active} onChange={v => set('active', v)}/>
            </>
          )}

          <div style={{marginTop:18, display:'flex', alignItems:'center', gap:14}}>
            <PrimaryBtn onClick={save} disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : 'Save changes'}
            </PrimaryBtn>
            {saved && <span style={{color:C.green, fontSize:13, fontWeight:700}}>✓ Saved</span>}
            {error && <span style={{color:C.red, fontSize:13}}>{error}</span>}
          </div>

          {/* Stats */}
          <H2>Uploads</H2>
          <div style={{display:'flex', gap:12, marginBottom:16, flexWrap:'wrap'}}>
            <div style={{flex:'1 1 160px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 18px'}}>
              <div style={{fontSize:26, fontWeight:800, color:C.text}}>{stats ? stats.sermonCount : '…'}</div>
              <div style={{fontSize:12, color:C.faint, marginTop:2}}>Sermons uploaded</div>
            </div>
            <div style={{flex:'1 1 160px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 18px'}}>
              <div style={{fontSize:26, fontWeight:800, color:C.text}}>{stats ? stats.refCount : '…'}</div>
              <div style={{fontSize:12, color:C.faint, marginTop:2}}>Bible references</div>
            </div>
          </div>

          <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.faint, marginBottom:8}}>Uploaded by</div>
          {!stats ? (
            <Note>Loading…</Note>
          ) : stats.uploaders.length === 0 ? (
            <Note>No sermons uploaded yet.</Note>
          ) : (
            <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden'}}>
              {stats.uploaders.map((u, i) => (
                <div key={u.id} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
                  padding:'12px 16px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                }}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13.5, fontWeight:700, color:C.text}}>{u.name}</div>
                    {u.email && (
                      <div style={{fontSize:12, color:C.muted, marginTop:1, wordBreak:'break-all'}}>{u.email}</div>
                    )}
                    <div style={{fontSize:12, color:C.faint, marginTop:1}}>{roleLabels[u.role] || u.role}</div>
                  </div>
                  <div style={{textAlign:'right', whiteSpace:'nowrap'}}>
                    <div style={{fontSize:13, color:C.muted}}>{u.count} sermon{u.count === 1 ? '' : 's'}</div>
                    <div style={{fontSize:11, color:C.faint, marginTop:1}}>
                      {[
                        u.admin   ? `${u.admin} admin tool` : null,
                        u.inApp   ? `${u.inApp} in-app`      : null,
                        u.unknown ? `${u.unknown} source n/a` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Backfill missing preachers ── */}
          <div style={{marginTop:28}}>
            <H2>Backfill missing preachers</H2>
            <Sub>Find this church's sermons with no preacher and re-derive one from the video title (and optionally the YouTube description). Review each suggestion, then apply.</Sub>
            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12}}>
              <PrimaryBtn kind="gold" onClick={bfScanTitles}
                disabled={!selectedId || bfStatus === 'scanning' || bfStatus === 'descChecking' || bfStatus === 'applying'}>
                {bfStatus === 'scanning' ? 'Scanning…' : 'Find missing preachers'}
              </PrimaryBtn>
              {bfRows.some(r => !r.suggestion && r.youtubeId) && (
                <PrimaryBtn kind="ghost" onClick={bfCheckDescriptions}
                  disabled={bfStatus === 'descChecking' || bfStatus === 'applying'}>
                  {bfStatus === 'descChecking' ? 'Checking…' : `Check descriptions for the rest (${bfRows.filter(r => !r.suggestion && r.youtubeId).length})`}
                </PrimaryBtn>
              )}
              {bfRows.some(r => r.apply && r.suggestion.trim()) && (
                <PrimaryBtn kind="gold" onClick={bfApply} disabled={bfStatus === 'applying'}>
                  {bfStatus === 'applying' ? 'Applying…' : `Apply ${bfRows.filter(r => r.apply && r.suggestion.trim()).length} preacher(s)`}
                </PrimaryBtn>
              )}
            </div>
            {bfMsg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{bfMsg}</div>}
            {bfRows.length > 0 && (
              <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:420, overflowY:'auto'}}>
                {bfRows.map((r, i) => (
                  <div key={r.id} style={{
                    display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                    borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                  }}>
                    <input type="checkbox" checked={r.apply}
                      onChange={e => setBfRows(rows => rows.map(x => x.id === r.id ? { ...x, apply: e.target.checked } : x))}
                      style={{width:16, height:16, flexShrink:0, accentColor:C.accent}}/>
                    <div style={{flex:'1 1 220px', minWidth:0}}>
                      <div style={{fontSize:13, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.title || '(untitled)'}</div>
                      {r.source && <div style={{fontSize:11, color:C.faint, marginTop:1}}>from {r.source}</div>}
                    </div>
                    <input
                      value={r.suggestion}
                      onChange={e => setBfRows(rows => rows.map(x => x.id === r.id ? { ...x, suggestion: e.target.value, apply: !!e.target.value.trim(), source: x.source || 'manual' } : x))}
                      placeholder="No preacher found — type one"
                      style={{...inputStyle, flex:'0 0 200px', padding:'8px 10px', fontSize:13}}/>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Backfill AI summaries ── */}
          <div style={{marginTop:28}}>
            <H2>Backfill AI summaries</H2>
            <Sub>Generate a warm, congregation-facing summary for this church's sermons that don't have one yet. Needs each sermon's stored transcript (older imports without a transcript are skipped). Runs one at a time; safe to re-run to pick up the rest.</Sub>
            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12}}>
              <PrimaryBtn kind="gold" onClick={sumScan}
                disabled={!selectedId || sumStatus === 'scanning' || sumStatus === 'running'}>
                {sumStatus === 'scanning' ? 'Scanning…' : 'Find sermons without a summary'}
              </PrimaryBtn>
              {sumRows.length > 0 && sumStatus !== 'running' && (
                <PrimaryBtn kind="gold" onClick={sumRun}>
                  {`Generate ${sumRows.length} summary${sumRows.length === 1 ? '' : 's'}`}
                </PrimaryBtn>
              )}
              {sumStatus === 'running' && <PrimaryBtn kind="ghost" disabled>Generating…</PrimaryBtn>}
            </div>
            {sumMsg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{sumMsg}</div>}
            {sumRows.length > 0 && sumStatus !== 'done' && (
              <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:320, overflowY:'auto'}}>
                {sumRows.map((r, i) => (
                  <div key={r.id} style={{padding:'9px 14px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`, fontSize:13, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {r.title || '(untitled)'}{r.preacher ? <span style={{color:C.faint}}> · {r.preacher}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Backfill encouragement titles ── */}
          <div style={{marginTop:28}}>
            <H2>Backfill encouragement titles</H2>
            <Sub>Finds encouragements whose title is just a list of verse references and suggests a proper short title instead. Nothing is written until you apply.</Sub>
            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12}}>
              <PrimaryBtn kind="gold" onClick={encTitleScan}
                disabled={!selectedId || encTitleStatus === 'scanning' || encTitleStatus === 'applying'}>
                {encTitleStatus === 'scanning' ? 'Scanning…' : 'Find encouragements needing a title'}
              </PrimaryBtn>
              {encTitleRows.some(r => r.apply) && encTitleStatus !== 'applying' && (
                <PrimaryBtn kind="gold" onClick={encTitleApply}>
                  {`Apply to ${encTitleRows.filter(r => r.apply).length} encouragement(s)`}
                </PrimaryBtn>
              )}
              {encTitleStatus === 'applying' && <PrimaryBtn kind="ghost" disabled>Applying…</PrimaryBtn>}
            </div>
            {encTitleMsg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{encTitleMsg}</div>}
            {encTitleRows.length > 0 && (
              <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:320, overflowY:'auto'}}>
                {encTitleRows.map((r, i) => (
                  <div key={r.id} style={{
                    display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px',
                    borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                  }}>
                    <input type="checkbox" checked={r.apply} disabled={!r.suggested}
                      onChange={e => setEncTitleRows(rows => rows.map(x => x.id === r.id ? { ...x, apply: e.target.checked } : x))}
                      style={{width:16, height:16, flexShrink:0, marginTop:3, accentColor:C.accent}}/>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:11.5, color:C.faint}}>Now: {r.current}</div>
                      <div style={{fontSize:13, color:C.text, marginTop:2}}>{r.suggested ? `→ ${r.suggested}` : '(no suggestion)'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Re-check playlists ── */}
          <div style={{marginTop:28}}>
            <H2>Re-check playlists</H2>
            <Sub>Re-scan this church's YouTube channel and add any playlists an uploaded sermon is missing (e.g. videos uploaded before a sermon could be in more than one playlist). Only adds — never removes.</Sub>
            <Field label="Also include these playlist URLs (optional — for unlisted playlists the channel scan can't see, one per line)">
              <textarea
                style={{...inputStyle, minHeight:64, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, resize:'vertical'}}
                placeholder={'https://www.youtube.com/playlist?list=PL...'}
                value={plExtraUrls}
                onChange={e => setPlExtraUrls(e.target.value)}
                disabled={plStatus === 'scanning' || plStatus === 'applying'}/>
            </Field>
            <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12, marginTop:12}}>
              <PrimaryBtn kind="gold" onClick={plRecheck}
                disabled={!selectedId || plStatus === 'scanning' || plStatus === 'applying'}>
                {plStatus === 'scanning' ? 'Scanning…' : 'Re-check playlists'}
              </PrimaryBtn>
              {plRows.some(r => r.apply) && (
                <PrimaryBtn kind="gold" onClick={plApply} disabled={plStatus === 'applying'}>
                  {plStatus === 'applying' ? 'Applying…' : `Apply to ${plRows.filter(r => r.apply).length} sermon(s)`}
                </PrimaryBtn>
              )}
            </div>
            {plMsg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{plMsg}</div>}
            {plRows.length > 0 && (
              <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:420, overflowY:'auto'}}>
                {plRows.map((r, i) => (
                  <div key={r.id} style={{
                    display:'flex', alignItems:'flex-start', gap:12, padding:'10px 14px',
                    borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                  }}>
                    <input type="checkbox" checked={r.apply}
                      onChange={e => setPlRows(rows => rows.map(x => x.id === r.id ? { ...x, apply: e.target.checked } : x))}
                      style={{width:16, height:16, flexShrink:0, marginTop:3, accentColor:C.accent}}/>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:13, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.title || '(untitled)'}</div>
                      <div style={{fontSize:11.5, color:C.faint, marginTop:2}}>
                        {r.current.length ? `Now: ${r.current.join(', ')}` : 'Now: (none)'}
                      </div>
                      <div style={{fontSize:11.5, color:C.accent, marginTop:1}}>
                        → {r.suggested.join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Danger zone — super admins only */}
          {isSuper && (
            <>
              <H2>Danger zone</H2>
              <Note kind="err">
                Deleting a church permanently removes it and <strong>all</strong> of its data — every
                sermon, Bible reference, member profile and community post. This cannot be undone.
              </Note>
              {isOwnChurch ? (
                <Sub>You can’t delete the church your own account belongs to.</Sub>
              ) : (
                <PrimaryBtn kind="red" onClick={() => { setDeleteError(''); setConfirmDelete(true) }}>
                  Delete this church
                </PrimaryBtn>
              )}
            </>
          )}
        </>
      )}

      {/* Confirmation modal */}
      {confirmDelete && church && (
        <div
          onClick={() => { if (!deleting) setConfirmDelete(false) }}
          style={{
            position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
          }}>
          <div onClick={e => e.stopPropagation()} style={{
            maxWidth:440, width:'100%', background:C.panel, border:`1px solid ${C.borderHi}`,
            borderRadius:14, padding:'24px 24px 20px', fontFamily:FONT,
          }}>
            <div style={{fontSize:18, fontWeight:800, color:C.text, marginBottom:10}}>
              Delete {church.name}?
            </div>
            <div style={{fontSize:13.5, color:C.muted, lineHeight:1.6, marginBottom:18}}>
              This permanently deletes the church along with{' '}
              <strong style={{color:C.text}}>{stats ? stats.sermonCount : '…'} sermon{stats && stats.sermonCount === 1 ? '' : 's'}</strong>,{' '}
              <strong style={{color:C.text}}>{stats ? stats.refCount : '…'} Bible reference{stats && stats.refCount === 1 ? '' : 's'}</strong>,
              and every member profile and community post for this church.
              <br/><br/>This <strong style={{color:'#FF8888'}}>cannot be undone</strong>.
            </div>
            {deleteError && <Note kind="err">{deleteError}</Note>}
            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <PrimaryBtn kind="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </PrimaryBtn>
              <PrimaryBtn kind="red" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── CREATE CHURCH ─────────────────────────────────────────────────────────
// Super-admin-only screen to provision a brand-new church with every field
// exposed (incl. the invite_only flag). Inserts directly into `churches` —
// the "Super admins can create churches" RLS policy permits this, so no RPC
// is needed. Differs from the self-serve ChurchOnboarding flow, which is for
// church admins creating their own church and only collects name + location.
function churchNameToSlug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Compact on/off pill used for the boolean church fields.
function ToggleRow({ label, hint, value, onChange }) {
  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:16,
      padding:'12px 0', borderBottom:`1px solid ${C.border}`}}>
      <div>
        <div style={{fontSize:13.5, fontWeight:700, color:C.text}}>{label}</div>
        {hint && <div style={{fontSize:12, color:C.faint, marginTop:2, lineHeight:1.4}}>{hint}</div>}
      </div>
      <button type="button" onClick={() => onChange(!value)} style={{
        flexShrink:0, width:46, height:26, borderRadius:13, border:'none', cursor:'pointer',
        background: value ? C.accent : 'rgba(255,255,255,0.15)', position:'relative',
        transition:'background 0.15s',
      }}>
        <span style={{
          position:'absolute', top:3, left: value ? 23 : 3, width:20, height:20, borderRadius:10,
          background:'#fff', transition:'left 0.15s',
        }}/>
      </button>
    </div>
  )
}

function CreateChurchScreen() {
  const { profile } = useAuth()
  const [form, setForm] = useState({
    name: '', slug: '', location: '', website: '', logo_url: '', network_id: '',
    youtube_channel_url: '', vision: '', meeting_info: '',
    invite_only: true, is_paid: false, setup_complete: true, active: true,
    free_scans_used: 0,
  })
  // Track whether the user has hand-edited the slug so we stop auto-deriving it
  // from the name once they take control.
  const [slugTouched, setSlugTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')
  const [created, setCreated] = useState(null) // the inserted church row

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const onName = (v) => setForm(f => ({
    ...f, name: v, slug: slugTouched ? f.slug : churchNameToSlug(v),
  }))

  async function create() {
    setSaving(true); setError(''); setCreated(null)
    const slug = (form.slug.trim() || churchNameToSlug(form.name))
    const payload = {
      name:                form.name.trim(),
      slug,
      location:            form.location.trim()            || null,
      website:             form.website.trim()             || null,
      logo_url:            form.logo_url.trim()             || null,
      youtube_channel_url: form.youtube_channel_url.trim()  || null,
      vision:              form.vision.trim()               || null,
      meeting_info:        form.meeting_info.trim()         || null,
      invite_only:         !!form.invite_only,
      is_paid:             !!form.is_paid,
      setup_complete:      !!form.setup_complete,
      active:              !!form.active,
      free_scans_used:     Number(form.free_scans_used) || 0,
      created_by:          profile?.id || null,
      // Only sent when chosen, so creates still work before the migration runs.
      ...(form.network_id ? { network_id: form.network_id } : {}),
    }
    const { data, error: insErr } = await supabase
      .from('churches').insert(payload).select().single()
    setSaving(false)
    if (insErr) {
      // Most likely causes: duplicate slug (unique constraint) or RLS (caller
      // isn't a super_admin). Surface the raw message either way.
      setError(/duplicate key|unique/i.test(insErr.message || '')
        ? `That slug ("${slug}") is already taken — choose a different one.`
        : insErr.message)
      return
    }
    setCreated(data)
    // Reset the form for the next church.
    setForm({
      name: '', slug: '', location: '', website: '', logo_url: '', network_id: '',
      youtube_channel_url: '', vision: '', meeting_info: '',
      invite_only: true, is_paid: false, setup_complete: true, active: true,
      free_scans_used: 0,
    })
    setSlugTouched(false)
  }

  if (profile?.role !== 'super_admin') return (
    <div style={{maxWidth:640}}>
      <H1>Create Church</H1>
      <Note kind="warn">Only super admins can create churches.</Note>
    </div>
  )

  const ta = { ...inputStyle, minHeight:80, resize:'vertical', lineHeight:1.5 }

  return (
    <div style={{maxWidth:560}}>
      <H1>Create Church</H1>
      <Sub>Provision a new church. All fields are editable; only a name and a unique slug are required.</Sub>

      {created && (
        <Note kind="ok">
          ✓ Created <strong>{created.name}</strong> (slug <code>{created.slug}</code>).
          {' '}It’s {created.invite_only ? 'invite-only (hidden from join lists)' : 'publicly listed'}.
        </Note>
      )}
      {error && <Note kind="err">{error}</Note>}

      <H2>Basics</H2>
      <Field label="Church name *">
        <input style={inputStyle} value={form.name} onChange={e => onName(e.target.value)} placeholder="e.g. New Life Church – Tunbridge Wells"/>
      </Field>
      <Field label="Slug * — unique URL id">
        <input style={inputStyle} value={form.slug}
          onChange={e => { setSlugTouched(true); set('slug', e.target.value) }}
          placeholder="e.g. nlc-tunbridge-wells"/>
      </Field>
      <Field label="Location">
        <input style={inputStyle} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Tunbridge Wells, Kent"/>
      </Field>
      <Field label="Website">
        <input style={inputStyle} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://…"/>
      </Field>
      <Field label="Church logo">
        <LogoUploadField value={form.logo_url} onChange={v => set('logo_url', v)} idHint={form.slug || form.name}/>
      </Field>
      <Field label="Network — group of connected churches">
        <NetworkSelectField value={form.network_id} onChange={v => set('network_id', v)}/>
      </Field>
      <Field label="YouTube channel URL — for auto-scan">
        <input style={inputStyle} value={form.youtube_channel_url} onChange={e => set('youtube_channel_url', e.target.value)} placeholder="https://www.youtube.com/@yourchurch"/>
      </Field>

      <H2>Church page</H2>
      <Field label="Vision">
        <textarea style={ta} value={form.vision} onChange={e => set('vision', e.target.value)} placeholder="The church’s vision statement, shown on its info page."/>
      </Field>
      <Field label="Meeting info">
        <textarea style={ta} value={form.meeting_info} onChange={e => set('meeting_info', e.target.value)} placeholder="When & where the church meets."/>
      </Field>

      <H2>Settings</H2>
      <ToggleRow label="Invite-only"
        hint="On = hidden from public join lists; people can only join via an invite/access request."
        value={form.invite_only} onChange={v => set('invite_only', v)}/>
      <ToggleRow label="Paid (unlimited scans)"
        hint="On = bypasses the free-scan limit."
        value={form.is_paid} onChange={v => set('is_paid', v)}/>
      <ToggleRow label="Setup complete"
        hint="On = skips the first-run onboarding scan prompt for this church’s admin."
        value={form.setup_complete} onChange={v => set('setup_complete', v)}/>
      <ToggleRow label="Active"
        hint="Off = soft-disables the church."
        value={form.active} onChange={v => set('active', v)}/>
      <Field label="Free scans used">
        <input style={{...inputStyle, maxWidth:120}} type="number" min={0}
          value={form.free_scans_used} onChange={e => set('free_scans_used', e.target.value)}/>
      </Field>

      <div style={{marginTop:22}}>
        <PrimaryBtn onClick={create} disabled={saving || !form.name.trim()}>
          {saving ? 'Creating…' : 'Create church'}
        </PrimaryBtn>
      </div>
    </div>
  )
}

// ── SOURCES / ENTITY MANAGEMENT (super-admin) ──────────────────────────────
// A "source" is any row in the churches table, distinguished by entity_type:
// church | event | event_series | network | ministry. Events sit under a
// series via parent_id; churches/ministries can sit under a network via
// parent_id. Sermons always attach to a concrete row's id, so events/series
// are churches rows too. The legacy Create Church + Church Networks screens
// still work alongside this.
const ENTITY_META = {
  church:       { label:'Church',       badge:'CHURCH',       color:C.blue },
  event:        { label:'Event',        badge:'EVENT',        color:'#fbbf24' },
  event_series: { label:'Event Series', badge:'EVENT SERIES', color:'#f59e0b' },
  network:      { label:'Network',      badge:'NETWORK',      color:C.purple },
  ministry:     { label:'Ministry',     badge:'MINISTRY',     color:C.green },
}
function TypeBadge({ type }) {
  const m = ENTITY_META[type] || ENTITY_META.church
  return (
    <span style={{
      display:'inline-block', fontSize:10, fontWeight:800, letterSpacing:'0.06em',
      color:m.color, background:`${m.color}1a`, border:`1px solid ${m.color}55`,
      padding:'2px 7px', borderRadius:6, whiteSpace:'nowrap',
    }}>{m.badge}</span>
  )
}

// Insert into churches, tolerating new columns that may not exist yet
// (pre-migration). On a "column does not exist / not found" error we strip the
// named column and retry, up to a few times.
async function resilientChurchInsert(payload) {
  let body = { ...payload }
  for (let i = 0; i < 8; i++) {
    const { data, error } = await supabase.from('churches').insert(body).select().single()
    if (!error) return { data, error: null }
    const m = /'?([a-z_]+)'? column|column "([a-z_]+)"|Could not find the '([a-z_]+)'/i.exec(error.message || '')
    const col = m && (m[1] || m[2] || m[3])
    if (col && col in body && !['name','slug'].includes(col)) { delete body[col]; continue }
    return { data: null, error }
  }
  return { data: null, error: { message: 'Too many missing columns.' } }
}

// Update a churches row, tolerating new columns missing (same strategy as
// resilientChurchInsert). Never strips name/slug.
async function resilientChurchUpdate(id, payload) {
  let body = { ...payload }
  for (let i = 0; i < 8; i++) {
    const { data, error } = await supabase.from('churches').update(body).eq('id', id).select().single()
    if (!error) return { data, error: null }
    const m = /'?([a-z_]+)'? column|column "([a-z_]+)"|Could not find the '([a-z_]+)'/i.exec(error.message || '')
    const col = m && (m[1] || m[2] || m[3])
    if (col && col in body && !['name','slug'].includes(col)) { delete body[col]; continue }
    return { data: null, error }
  }
  return { data: null, error: { message: 'Too many missing columns.' } }
}

function SourcesScreen({ prefill, onPrefillConsumed, setTab }) {
  const { profile } = useAuth()
  const [rows, setRows]       = useState([])
  const [counts, setCounts]   = useState({ sermons:{}, refs:{} })
  const [loading, setLoading] = useState(true)
  const [q, setQ]             = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [mode, setMode]       = useState('list')   // 'list' | 'create' | 'detail'
  const [seed, setSeed]       = useState(null)
  const [editRow, setEditRow] = useState(null)     // row being edited (null = creating)
  const [detailId, setDetailId] = useState(null)   // row id shown in the detail page
  const [menuId, setMenuId]   = useState(null)     // row id whose ⋮ menu is open
  const [busyId, setBusyId]   = useState(null)
  const [delErr, setDelErr]   = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('churches').select('*').order('name')
    setRows(data || [])
    setLoading(false)
    // Counts: one lightweight column-only pull each, tallied client-side.
    const tally = (arr) => {
      const map = {}
      for (const r of (arr || [])) { const k = r.church_id; if (k) map[k] = (map[k] || 0) + 1 }
      return map
    }
    const [sRes, rRes] = await Promise.all([
      supabase.from('sermons').select('church_id'),
      supabase.from('sermon_refs').select('church_id'),
    ])
    setCounts({ sermons: tally(sRes.data), refs: tally(rRes.data) })
  }, [])
  useEffect(() => { load() }, [load])

  // "Create from request" (Part 3F/6G): open the form pre-filled.
  useEffect(() => {
    if (!prefill) return
    setSeed({
      name:     prefill.church_name || '',
      location: prefill.location || '',
      youtube_channel_url: prefill.youtube_channel_url || '',
      entity_type: prefill.entity_type || 'church',
    })
    setMode('create')
    onPrefillConsumed?.()
  }, [prefill, onPrefillConsumed])

  if (profile?.role !== 'super_admin') return (
    <div style={{maxWidth:640}}><H1>Sources</H1><Note kind="warn">Only super admins can manage sources.</Note></div>
  )

  if (mode === 'create') return (
    <EntityCreateForm seed={seed} editRow={editRow} allRows={rows}
      onCancel={() => { setSeed(null); setEditRow(null); setMode('list') }}
      onCreated={() => { setSeed(null); setEditRow(null); setMode('list'); load() }}/>
  )

  if (mode === 'detail') {
    const detailRow = rows.find(r => r.id === detailId)
    if (!detailRow) { setMode('list'); return null }
    return (
      <EntityDetailScreen
        row={detailRow} allRows={rows} setTab={setTab}
        onOpenEntity={(r) => { setDetailId(r.id); setMode('detail') }}
        onBack={() => { setDetailId(null); setMode('list'); load() }}
        onChanged={load}/>
    )
  }

  async function del(r) {
    if (!window.confirm(`Delete "${r.name}"? This removes the source row (sermons are not deleted).`)) return
    setBusyId(r.id); setDelErr(''); setMenuId(null)
    const { error } = await supabase.from('churches').delete().eq('id', r.id)
    setBusyId(null)
    if (error) setDelErr(error.message || 'Could not delete source.')
    else load()
  }
  function openDetail(r) { setMenuId(null); setDetailId(r.id); setMode('detail') }
  function openEdit(r)   { setMenuId(null); setSeed(null); setEditRow(r); setMode('create') }
  function viewInApp(r)  { setMenuId(null); window.open('/' + (r.slug || ''), '_blank') }

  const nameById = Object.fromEntries(rows.map(r => [r.id, r.name]))
  const typeOf = (r) => r.entity_type || 'church'
  const matchType = (r) => {
    if (typeFilter === 'all') return true
    if (typeFilter === 'events') return ['event','event_series'].includes(typeOf(r))
    return typeOf(r) === typeFilter
  }
  const filtered = rows.filter(r => {
    if (!matchType(r)) return false
    if (q.trim() && !`${r.name} ${r.location || ''}`.toLowerCase().includes(q.trim().toLowerCase())) return false
    return true
  })
  const nChurch  = rows.filter(r => typeOf(r) === 'church').length
  const nEvent   = rows.filter(r => ['event','event_series'].includes(typeOf(r))).length
  const nNetwork = rows.filter(r => typeOf(r) === 'network').length
  const totalSermons = Object.values(counts.sermons).reduce((a,b)=>a+b,0)
  const totalRefs    = Object.values(counts.refs).reduce((a,b)=>a+b,0)

  const th = { textAlign:'left', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:C.faint, padding:'8px 10px', borderBottom:`1px solid ${C.border}` }
  const td = { fontSize:13, color:C.text, padding:'10px', borderBottom:`1px solid ${C.border}`, verticalAlign:'middle' }

  return (
    <div style={{maxWidth:1100}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16}}>
        <div><H1>Sources</H1><Sub>Churches, events, series, networks and ministries — everything that owns sermons.</Sub></div>
        <PrimaryBtn onClick={() => { setSeed(null); setEditRow(null); setMode('create') }}>+ Add new source</PrimaryBtn>
      </div>

      {delErr && <Note kind="err">{delErr}</Note>}

      <div style={{fontSize:13, color:C.muted, marginBottom:14}}>
        {nChurch} churches · {nEvent} events · {nNetwork} networks · Total: {totalSermons} sermons · {totalRefs} refs
      </div>

      {/* Sub-tabs (Part 12) — filter by entity type. "Events & Series" folds
          both event and event_series into one pill. */}
      <div style={{display:'flex', gap:8, marginBottom:12, flexWrap:'wrap'}}>
        {[
          { key:'all',          label:'All Sources' },
          { key:'church',       label:'Churches' },
          { key:'events',       label:'Events & Series' },
          { key:'network',      label:'Networks' },
          { key:'ministry',     label:'Ministries' },
        ].map(t => {
          const active = typeFilter === t.key
          return (
            <button key={t.key} type="button" onClick={()=>setTypeFilter(t.key)}
              style={{
                background: active ? 'rgba(232,200,122,0.10)' : C.surface2,
                border:`1px solid ${active ? C.accent : C.borderHi}`, borderRadius:999,
                padding:'7px 14px', cursor:'pointer', color: active ? C.accent : C.muted,
                fontFamily:FONT, fontSize:12, fontWeight:700,
              }}>{t.label}</button>
          )
        })}
      </div>

      <div style={{display:'flex', gap:10, marginBottom:14, flexWrap:'wrap'}}>
        <input style={{...inputStyle, maxWidth:280}} placeholder="Search sources…" value={q} onChange={e=>setQ(e.target.value)}/>
      </div>

      {loading ? <Note>Loading…</Note> : (
        <div style={{overflowX:'auto', border:`1px solid ${C.border}`, borderRadius:10}}>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead><tr>
              <th style={th}>Name</th><th style={th}>Type</th><th style={th}>Parent</th>
              <th style={th}>Sermons</th><th style={th}>Refs</th><th style={th}>Dates</th>
              <th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td style={td}>
                    <button type="button" onClick={()=>openDetail(r)}
                      style={{background:'none', border:'none', padding:0, cursor:'pointer', color:C.text, fontSize:13, fontWeight:600, fontFamily:FONT, textAlign:'left'}}>
                      {r.name}
                    </button>
                    {r.location ? <span style={{color:C.faint}}> · {r.location}</span> : null}
                  </td>
                  <td style={td}><TypeBadge type={typeOf(r)}/></td>
                  <td style={{...td, color:C.muted}}>{r.parent_id ? (nameById[r.parent_id] || '—') : '—'}</td>
                  <td style={td}>{counts.sermons[r.id] || 0}</td>
                  <td style={td}>{counts.refs[r.id] || 0}</td>
                  <td style={{...td, color:C.muted, whiteSpace:'nowrap'}}>
                    {r.event_start_date ? new Date(r.event_start_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                    {r.event_end_date ? ` – ${new Date(r.event_end_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}` : ''}
                  </td>
                  <td style={{...td, position:'relative', overflow:'visible'}}>
                    <button type="button" disabled={busyId===r.id}
                      onClick={()=>setMenuId(menuId===r.id ? null : r.id)}
                      style={{background:'none', border:'none', padding:'2px 8px', cursor:'pointer', color:C.muted, fontSize:18, fontWeight:800, fontFamily:FONT, lineHeight:1, borderRadius:6}}>
                      {busyId===r.id ? '…' : '⋮'}
                    </button>
                    {menuId===r.id && (() => {
                      const t = typeOf(r)
                      const item = (label, onClick, danger) => (
                        <button type="button" key={label} onClick={onClick}
                          style={{display:'block', width:'100%', textAlign:'left', background:'none', border:'none', padding:'8px 12px', cursor:'pointer', color: danger ? C.red : C.text, fontSize:12, fontWeight:600, fontFamily:FONT, whiteSpace:'nowrap'}}>{label}</button>
                      )
                      const items = [ item('Edit', ()=>openEdit(r)), item('View in app', ()=>viewInApp(r)) ]
                      if (t === 'church' || t === 'event') {
                        items.push(item('Scan YouTube channel', ()=>{ setMenuId(null); setTab?.('channels') }))
                        items.push(item('Bulk import', ()=>{ setMenuId(null); setTab?.('upload') }))
                      } else if (t === 'event_series') {
                        items.push(item('Add new year', ()=>openDetail(r)))
                        items.push(item('View all years', ()=>openDetail(r)))
                      } else if (t === 'network') {
                        items.push(item('Add member', ()=>openDetail(r)))
                        items.push(item('View members', ()=>openDetail(r)))
                      }
                      items.push(item('Delete', ()=>del(r), true))
                      return (
                        <>
                          <div onClick={()=>setMenuId(null)} style={{position:'fixed', inset:0, zIndex:40}}/>
                          <div style={{position:'absolute', right:10, top:'100%', zIndex:41, minWidth:180, background:C.panel, border:`1px solid ${C.borderHi}`, borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.5)', padding:'4px 0'}}>
                            {items}
                          </div>
                        </>
                      )
                    })()}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td style={{...td, color:C.faint}} colSpan={7}>No sources match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Entity detail / edit page (Part 12). Opened from SourcesScreen when a source
// row is clicked. Shows editable info (reusing EntityCreateForm in edit mode),
// parent/child relationships with quick-add controls, and the entity's sermons
// — grouped by year for an event series, filterable by member for a network.
function EntityDetailScreen({ row, allRows, setTab, onOpenEntity, onBack, onChanged }) {
  const type = row.entity_type || 'church'
  const [editing, setEditing]           = useState(false)
  const [creatingChild, setCreatingChild] = useState(false)
  const [sermons, setSermons]           = useState([])
  const [loadingSermons, setLoadingSermons] = useState(true)
  const [memberFilter, setMemberFilter] = useState('all')

  // Add-year (event_series) quick form.
  const [addYear, setAddYear] = useState(false)
  const [yr, setYr] = useState(''); const [yrStart, setYrStart] = useState(''); const [yrEnd, setYrEnd] = useState('')
  const [yrBusy, setYrBusy] = useState(false); const [yrErr, setYrErr] = useState('')

  // Add-member (network) search.
  const [memberQ, setMemberQ]       = useState('')
  const [memberBusy, setMemberBusy] = useState('')
  const [memberErr, setMemberErr]   = useState('')

  const parent   = row.parent_id ? allRows.find(r => r.id === row.parent_id) : null
  const children = allRows.filter(r => r.parent_id === row.id)
  const childIds = children.map(c => c.id)
  const nameById = Object.fromEntries(allRows.map(r => [r.id, r.name]))

  // Which church ids own the sermons shown here.
  const targetIds = (type === 'event_series' || type === 'network')
    ? (childIds.length ? childIds : [row.id])
    : [row.id]
  const targetKey = targetIds.join(',')

  const loadSermons = useCallback(async () => {
    setLoadingSermons(true)
    const ids = targetKey ? targetKey.split(',') : []
    if (!ids.length) { setSermons([]); setLoadingSermons(false); return }
    let { data, error } = await supabase.from('sermons').select('id,title,date,church_id').in('church_id', ids)
    if (error) { ({ data } = await supabase.from('sermons').select('id,church_id').in('church_id', ids)) }
    setSermons((data || []).sort((a,b) => String(b.date||'').localeCompare(String(a.date||''))))
    setLoadingSermons(false)
  }, [targetKey])
  useEffect(() => { loadSermons() }, [loadSermons])

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) }
    catch { return String(iso) }
  }
  const linkBtn = (label, onClick) => (
    <button type="button" onClick={onClick}
      style={{background:'none', border:'none', padding:0, cursor:'pointer', color:C.accent, fontSize:13, fontWeight:700, fontFamily:FONT}}>{label}</button>
  )

  async function createYear() {
    const y = yr.trim(); if (!y) { setYrErr('Enter a year.'); return }
    setYrBusy(true); setYrErr('')
    const { error } = await resilientChurchInsert({
      name: `${row.name} ${y}`,
      slug: `${churchNameToSlug(`${row.name} ${y}`)}-${Math.random().toString(36).slice(2,6)}`,
      entity_type: 'event', parent_id: row.id,
      event_start_date: yrStart || (/^\d{4}$/.test(y) ? `${y}-01-01` : null),
      event_end_date: yrEnd || null,
      setup_complete: true, invite_only: false, active: true,
    })
    setYrBusy(false)
    if (error) { setYrErr(error.message || 'Could not add year.'); return }
    setAddYear(false); setYr(''); setYrStart(''); setYrEnd('')
    onChanged?.()
  }

  async function addMember(c) {
    setMemberBusy(c.id); setMemberErr('')
    const { error } = await resilientChurchUpdate(c.id, { parent_id: row.id })
    setMemberBusy('')
    if (error) { setMemberErr(error.message || 'Could not add member.'); return }
    setMemberQ('')
    onChanged?.()
  }

  // Editing / creating-a-child re-use the create form.
  if (editing) return (
    <EntityCreateForm editRow={row} allRows={allRows}
      onCancel={() => setEditing(false)}
      onCreated={() => { setEditing(false); onChanged?.() }}/>
  )
  if (creatingChild) return (
    <EntityCreateForm allRows={allRows} seed={{ entity_type:'church', parent_id: row.id }}
      onCancel={() => setCreatingChild(false)}
      onCreated={() => { setCreatingChild(false); onChanged?.() }}/>
  )

  // Candidate members for a network: churches/ministries not already attached.
  const memberCandidates = type !== 'network' ? [] : allRows.filter(r => {
    const t = r.entity_type || 'church'
    if (r.id === row.id) return false
    if (!['church','ministry'].includes(t)) return false
    if (r.parent_id === row.id) return false
    if (!memberQ.trim()) return true
    return `${r.name} ${r.location||''}`.toLowerCase().includes(memberQ.trim().toLowerCase())
  }).slice(0, 12)

  const shownSermons = (type === 'network' && memberFilter !== 'all')
    ? sermons.filter(s => s.church_id === memberFilter)
    : sermons

  const sectionCard = { background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 18px', marginBottom:16 }

  return (
    <div style={{maxWidth:900}}>
      <button type="button" onClick={onBack}
        style={{background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:13, fontFamily:FONT, marginBottom:12}}>← Back to sources</button>

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, marginBottom:8}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <H1>{row.name}</H1>
          <TypeBadge type={type}/>
        </div>
        <PrimaryBtn onClick={() => setEditing(true)}>Edit details</PrimaryBtn>
      </div>
      {row.location && <Sub>{row.location}</Sub>}

      {/* Info summary */}
      <div style={sectionCard}>
        <H2>Details</H2>
        <div style={{fontSize:13, color:C.muted, lineHeight:1.7}}>
          {row.description && <div style={{color:C.text, marginBottom:6}}>{row.description}</div>}
          {(row.website_url || row.website) && <div>Website: <a href={row.website_url || row.website} target="_blank" rel="noreferrer" style={{color:C.accent}}>{row.website_url || row.website}</a></div>}
          {row.youtube_channel_url && <div>YouTube: <a href={row.youtube_channel_url} target="_blank" rel="noreferrer" style={{color:C.accent}}>{row.youtube_channel_url}</a></div>}
          {(row.event_start_date || row.event_end_date) && <div>Dates: {fmtDate(row.event_start_date)}{row.event_end_date ? ` – ${fmtDate(row.event_end_date)}` : ''}</div>}
          {parent && <div>Part of: {linkBtn(parent.name, () => onOpenEntity(parent))}</div>}
        </div>
      </div>

      {/* Relationships */}
      {(type === 'event_series' || type === 'network' || children.length > 0) && (
        <div style={sectionCard}>
          <H2>{type === 'event_series' ? 'Years' : type === 'network' ? 'Members' : 'Children'}</H2>
          {children.length === 0 ? (
            <div style={{fontSize:13, color:C.faint, marginBottom:10}}>None yet.</div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:6, marginBottom:10}}>
              {children.map(c => (
                <div key={c.id} style={{display:'flex', alignItems:'center', gap:10, fontSize:13}}>
                  <TypeBadge type={c.entity_type || 'church'}/>
                  {linkBtn(c.name, () => onOpenEntity(c))}
                  {(c.event_start_date) && <span style={{color:C.faint}}>· {fmtDate(c.event_start_date)}</span>}
                </div>
              ))}
            </div>
          )}

          {type === 'event_series' && (
            addYear ? (
              <div style={{display:'flex', flexDirection:'column', gap:6, maxWidth:320, background:C.surface2, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px'}}>
                <input style={{...inputStyle, padding:'8px 10px', fontSize:12}} placeholder="Year e.g. 2025" value={yr} onChange={e=>setYr(e.target.value)}/>
                <div style={{display:'flex', gap:6}}>
                  <input type="date" style={{...inputStyle, padding:'8px 10px', fontSize:12}} value={yrStart} onChange={e=>setYrStart(e.target.value)}/>
                  <input type="date" style={{...inputStyle, padding:'8px 10px', fontSize:12}} value={yrEnd} onChange={e=>setYrEnd(e.target.value)}/>
                </div>
                <div style={{fontSize:10, color:C.faint}}>Names to “{row.name} {yr || '{year}'}”. Start/end optional (defaults to Jan 1).</div>
                {yrErr && <div style={{fontSize:11, color:'#FF8888'}}>{yrErr}</div>}
                <div style={{display:'flex', gap:8}}>
                  <PrimaryBtn onClick={createYear} disabled={yrBusy}>{yrBusy ? 'Adding…' : 'Add year'}</PrimaryBtn>
                  <PrimaryBtn kind="ghost" onClick={()=>{ setAddYear(false); setYrErr('') }}>Cancel</PrimaryBtn>
                </div>
              </div>
            ) : (
              <PrimaryBtn kind="ghost" onClick={()=>setAddYear(true)}>+ Add event year</PrimaryBtn>
            )
          )}

          {type === 'network' && (
            <div>
              <div style={{display:'flex', gap:10, marginBottom:8, flexWrap:'wrap'}}>
                <input style={{...inputStyle, maxWidth:280}} placeholder="Search churches to add…" value={memberQ} onChange={e=>setMemberQ(e.target.value)}/>
                <PrimaryBtn kind="ghost" onClick={()=>setCreatingChild(true)}>+ Create new member</PrimaryBtn>
              </div>
              {memberErr && <div style={{fontSize:11, color:'#FF8888', marginBottom:6}}>{memberErr}</div>}
              {memberQ.trim() && (
                memberCandidates.length ? (
                  <div style={{display:'flex', flexDirection:'column', gap:4, maxWidth:420}}>
                    {memberCandidates.map(c => (
                      <button key={c.id} type="button" disabled={memberBusy===c.id} onClick={()=>addMember(c)}
                        style={{display:'flex', justifyContent:'space-between', alignItems:'center', background:C.surface2, border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', cursor:'pointer', color:C.text, fontSize:13, fontFamily:FONT, textAlign:'left'}}>
                        <span>{c.name}{c.location ? <span style={{color:C.faint}}> · {c.location}</span> : null}</span>
                        <span style={{color:C.accent, fontWeight:700, fontSize:12}}>{memberBusy===c.id ? 'Adding…' : '+ Add'}</span>
                      </button>
                    ))}
                  </div>
                ) : <div style={{fontSize:12, color:C.faint}}>No matching churches.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sermons */}
      <div style={sectionCard}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap'}}>
          <H2>Sermons {loadingSermons ? '' : `(${sermons.length})`}</H2>
          <div style={{display:'flex', gap:8}}>
            <PrimaryBtn kind="ghost" onClick={()=>setTab?.('channels')}>Scan YouTube channel</PrimaryBtn>
            <PrimaryBtn kind="ghost" onClick={()=>setTab?.('upload')}>Bulk import</PrimaryBtn>
          </div>
        </div>

        {type === 'network' && children.length > 0 && (
          <div style={{margin:'8px 0 12px'}}>
            <select style={{...inputStyle, maxWidth:260, cursor:'pointer'}} value={memberFilter} onChange={e=>setMemberFilter(e.target.value)}>
              <option value="all">All members</option>
              {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {loadingSermons ? <Note>Loading…</Note> : (
          type === 'event_series' && children.length > 0 ? (
            children.map(yearRow => {
              const rows = sermons.filter(s => s.church_id === yearRow.id)
              return (
                <div key={yearRow.id} style={{marginBottom:12}}>
                  <div style={{fontSize:13, fontWeight:700, color:C.text, marginBottom:4}}>{yearRow.name} <span style={{color:C.faint, fontWeight:500}}>({rows.length})</span></div>
                  {rows.length === 0 ? <div style={{fontSize:12, color:C.faint}}>No sermons.</div> : rows.map(s => (
                    <div key={s.id} style={{display:'flex', justifyContent:'space-between', fontSize:13, color:C.muted, padding:'4px 0', borderBottom:`1px solid ${C.border}`}}>
                      <span style={{color:C.text}}>{s.title || 'Untitled'}</span><span>{fmtDate(s.date)}</span>
                    </div>
                  ))}
                </div>
              )
            })
          ) : shownSermons.length === 0 ? (
            <div style={{fontSize:13, color:C.faint}}>No sermons attached yet.</div>
          ) : (
            <div>
              {shownSermons.map(s => (
                <div key={s.id} style={{display:'flex', justifyContent:'space-between', gap:12, fontSize:13, color:C.muted, padding:'6px 0', borderBottom:`1px solid ${C.border}`}}>
                  <span style={{color:C.text}}>{s.title || 'Untitled'}{type === 'network' && s.church_id !== row.id && nameById[s.church_id] ? <span style={{color:C.faint}}> · {nameById[s.church_id]}</span> : null}</span>
                  <span style={{whiteSpace:'nowrap'}}>{fmtDate(s.date)}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

// Type-aware creation form (Part 6C). Creates a churches row with the right
// entity_type + relationships. Networks are modelled as churches rows; children
// (events under a series, churches/ministries under a network) link via
// parent_id — which is what the Bible reader's follow-inclusion logic reads.
function EntityCreateForm({ seed, editRow, allRows, onCancel, onCreated }) {
  const { profile } = useAuth()
  const isEdit = !!editRow
  // Both event and event_series map to the "Event" type card; the kind toggle
  // distinguishes them.
  const normType = (et) => (et === 'event' || et === 'event_series' ? 'event' : (et || 'church'))
  const [type, setType] = useState(normType(editRow?.entity_type || seed?.entity_type))
  const [eventKind, setEventKind] = useState(editRow?.entity_type === 'event_series' ? 'series' : 'event')
  const [form, setForm] = useState({
    name: editRow?.name || seed?.name || '',
    location: editRow?.location || seed?.location || '',
    description: editRow?.description || '',
    website_url: editRow?.website_url || editRow?.website || '',
    youtube_channel_url: editRow?.youtube_channel_url || seed?.youtube_channel_url || '',
    logo_url: editRow?.logo_url || '',
    parent_id: editRow?.parent_id || seed?.parent_id || '',
    event_start_date: (editRow?.event_start_date || '').slice(0,10),
    event_end_date: (editRow?.event_end_date || '').slice(0,10),
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')
  const [created, setCreated] = useState(null)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const networks = (allRows || []).filter(r => (r.entity_type) === 'network')
  const seriesList = (allRows || []).filter(r => (r.entity_type) === 'event_series')

  async function create() {
    setSaving(true); setError(''); setCreated(null)
    const resolvedType = type === 'event' ? (eventKind === 'series' ? 'event_series' : 'event') : type
    const name = form.name.trim()
    if (!name) { setSaving(false); setError('Name is required.'); return }
    // Fields common to create + edit.
    const payload = {
      name,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
      website_url: form.website_url.trim() || null,
      website: form.website_url.trim() || null,   // legacy column the app already reads
      youtube_channel_url: form.youtube_channel_url.trim() || null,
      logo_url: form.logo_url.trim() || null,
      entity_type: resolvedType,
    }
    // Relationships — always set (null clears) so editing can detach a parent.
    if (resolvedType === 'event') {
      payload.event_start_date = form.event_start_date || null
      payload.event_end_date   = form.event_end_date || null
      payload.parent_id = form.parent_id || null   // parent series
    } else if (resolvedType === 'event_series') {
      payload.is_event_series = true
      payload.parent_id = form.parent_id || null   // optional associated network
    } else if (resolvedType === 'church' || resolvedType === 'ministry') {
      payload.parent_id = form.parent_id || null   // parent network
    } else {
      payload.parent_id = null   // network
    }

    let data, opErr
    if (isEdit) {
      ;({ data, error: opErr } = await resilientChurchUpdate(editRow.id, payload))
    } else {
      ;({ data, error: opErr } = await resilientChurchInsert({
        ...payload,
        slug: `${churchNameToSlug(name)}-${Math.random().toString(36).slice(2,6)}`,
        created_by: profile?.id || null,
        setup_complete: true,
        invite_only: false,
        active: true,
      }))
    }
    setSaving(false)
    if (opErr) { setError(opErr.message || `Could not ${isEdit ? 'update' : 'create'} source.`); return }
    setCreated(data)
  }

  const ta = { ...inputStyle, minHeight:80, resize:'vertical', lineHeight:1.5 }
  const typeCards = [
    { key:'church',   label:'Church',   emoji:'⛪' },
    { key:'event',    label:'Event',    emoji:'📅' },
    { key:'network',  label:'Network',  emoji:'🕸️' },
    { key:'ministry', label:'Ministry', emoji:'🙌' },
  ]

  if (created) return (
    <div style={{maxWidth:560}}>
      <H1>{isEdit ? 'Edit source' : 'Add source'}</H1>
      <Note kind="ok">✓ {isEdit ? 'Updated' : 'Created'} <strong>{created.name}</strong> as a {ENTITY_META[created.entity_type || 'church']?.label}.</Note>
      <div style={{display:'flex', gap:10}}>
        <PrimaryBtn onClick={onCreated}>Back to sources</PrimaryBtn>
        {!isEdit && <PrimaryBtn kind="ghost" onClick={() => { setCreated(null); setForm(f => ({ ...f, name:'' })) }}>Add another</PrimaryBtn>}
      </div>
    </div>
  )

  return (
    <div style={{maxWidth:560}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <H1>{isEdit ? 'Edit source' : 'Add source'}</H1>
        <button onClick={onCancel} style={{background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:13, fontFamily:FONT}}>← Back to sources</button>
      </div>
      <Sub>Pick a type, then fill in the details. Sermons can be attached later via Bulk Upload.</Sub>
      {error && <Note kind="err">{error}</Note>}

      <H2>1. Type</H2>
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:6}}>
        {typeCards.map(tc => {
          const active = type === tc.key
          return (
            <button key={tc.key} type="button" onClick={() => setType(tc.key)}
              style={{
                background: active ? 'rgba(232,200,122,0.10)' : C.surface2,
                border:`1px solid ${active ? C.accent : C.borderHi}`, borderRadius:10,
                padding:'14px 8px', cursor:'pointer', color: active ? C.accent : C.text,
                fontFamily:FONT, fontSize:13, fontWeight:700, textAlign:'center',
              }}>
              <div style={{fontSize:22, marginBottom:6}}>{tc.emoji}</div>{tc.label}
            </button>
          )
        })}
      </div>

      <H2>2. Details</H2>
      <Field label="Name *"><input style={inputStyle} value={form.name} onChange={e=>set('name', e.target.value)} placeholder="e.g. New Wine, or Spring Harvest 2025"/></Field>
      <Field label="Location"><input style={inputStyle} value={form.location} onChange={e=>set('location', e.target.value)} placeholder="e.g. Somerset"/></Field>
      <Field label="Description"><textarea style={ta} value={form.description} onChange={e=>set('description', e.target.value)} placeholder="Short description shown on the source page."/></Field>
      <Field label="Website URL"><input style={inputStyle} value={form.website_url} onChange={e=>set('website_url', e.target.value)} placeholder="https://…"/></Field>
      <Field label="YouTube channel URL"><input style={inputStyle} value={form.youtube_channel_url} onChange={e=>set('youtube_channel_url', e.target.value)} placeholder="https://www.youtube.com/@…"/></Field>
      <Field label="Logo"><LogoUploadField value={form.logo_url} onChange={v=>set('logo_url', v)} idHint={form.name}/></Field>

      <H2>3. {ENTITY_META[type === 'event' ? (eventKind === 'series' ? 'event_series' : 'event') : type]?.label} settings</H2>
      {type === 'church' && (
        <Field label="Parent network (optional)">
          <select style={inputStyle} value={form.parent_id} onChange={e=>set('parent_id', e.target.value)}>
            <option value="">No network</option>
            {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </Field>
      )}
      {type === 'ministry' && (
        <Field label="Parent network (optional)">
          <select style={inputStyle} value={form.parent_id} onChange={e=>set('parent_id', e.target.value)}>
            <option value="">No network</option>
            {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </Field>
      )}
      {type === 'event' && (
        <>
          <Field label="Event kind">
            <div style={{display:'flex', gap:8}}>
              {[{k:'event',l:'Single event'},{k:'series',l:'Event series'}].map(o => (
                <button key={o.k} type="button" onClick={()=>setEventKind(o.k)}
                  style={{flex:1, background: eventKind===o.k?'rgba(232,200,122,0.10)':C.surface2, border:`1px solid ${eventKind===o.k?C.accent:C.borderHi}`, borderRadius:8, padding:'10px', color:eventKind===o.k?C.accent:C.text, fontFamily:FONT, fontWeight:700, fontSize:13, cursor:'pointer'}}>{o.l}</button>
              ))}
            </div>
          </Field>
          {eventKind === 'event' && (
            <>
              <Field label="Start date *"><input type="date" style={inputStyle} value={form.event_start_date} onChange={e=>set('event_start_date', e.target.value)}/></Field>
              <Field label="End date"><input type="date" style={inputStyle} value={form.event_end_date} onChange={e=>set('event_end_date', e.target.value)}/></Field>
              <Field label="Belongs to (optional)">
                <select style={inputStyle} value={form.parent_id} onChange={e=>set('parent_id', e.target.value)}>
                  <option value="">Nothing</option>
                  {seriesList.length > 0 && (
                    <optgroup label="Event series">
                      {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </optgroup>
                  )}
                  {networks.length > 0 && (
                    <optgroup label="Networks">
                      {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                    </optgroup>
                  )}
                </select>
              </Field>
              <Note kind="info">Pick the event series this year belongs to (usual), or a network directly. It inherits followers of whatever it&apos;s under.</Note>
            </>
          )}
          {eventKind === 'series' && (
            <>
              <Field label="Associated network (optional)">
                <select style={inputStyle} value={form.parent_id} onChange={e=>set('parent_id', e.target.value)}>
                  <option value="">No network</option>
                  {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              </Field>
              <Note kind="info">An event series has no dates. Add year events under it afterwards (each with this series as its parent) — they inherit its network automatically.</Note>
            </>
          )}
        </>
      )}
      {type === 'network' && <Note kind="info">A network groups churches and event series. After creating it, set members&apos; Parent/Associated network to this network.</Note>}

      <div style={{marginTop:22, display:'flex', gap:10}}>
        <PrimaryBtn onClick={create} disabled={saving || !form.name.trim() || (type==='event' && eventKind==='event' && !form.event_start_date)}>
          {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create source')}
        </PrimaryBtn>
        <PrimaryBtn kind="ghost" onClick={onCancel}>Cancel</PrimaryBtn>
      </div>
    </div>
  )
}

// ── REPORTS ──────────────────────────────────────────────────────────────
const REPORT_CONTENT = {
  post:          { table:'community_posts', label:'Community post' },
  comment:       { table:'verse_comments',  label:'Verse comment' },
  prayer:        { table:'prayers',          label:'Prayer request' },
  encouragement: { table:'encouragements',   label:'Encouragement' },
  general:       { table:null,               label:'App / general' },
}

function ReportsScreen({ refreshBadge }) {
  const [reports, setReports]   = useState([])
  const [snippets, setSnippets] = useState({})   // content_id -> { text, removed }
  const [loading, setLoading]   = useState(true)
  const [busyId, setBusyId]     = useState(null)
  const [expanded, setExpanded] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('reports')
      .select('id, content_type, content_id, reason, notes, created_at, reporter:profiles!reporter_id(full_name, email)')
      .eq('resolved', false)
      .order('created_at', { ascending: false })
    setLoading(false)
    if (error) { console.warn('load reports:', error.message); setReports([]); return }
    const rows = data || []
    setReports(rows)
    // Fetch a snippet of each reported item, one query per content table.
    const byTable = {}
    for (const r of rows) {
      const t = REPORT_CONTENT[r.content_type]?.table
      if (t) (byTable[t] = byTable[t] || []).push(r.content_id)
    }
    const map = {}
    await Promise.all(Object.entries(byTable).map(async ([table, ids]) => {
      const { data: cData } = await supabase.from(table).select('id, text, removed').in('id', ids)
      for (const c of (cData || [])) map[c.id] = { text: c.text, removed: c.removed }
    }))
    setSnippets(map)
  }, [])
  useEffect(() => { load() }, [load])

  async function removeContent(r) {
    const table = REPORT_CONTENT[r.content_type]?.table
    if (!table) return
    setBusyId(r.id)
    const { error } = await supabase.from(table).update({ removed: true }).eq('id', r.content_id)
    if (!error) {
      // Resolve every report pointing at this same content.
      await supabase.from('reports').update({ resolved: true }).eq('content_id', r.content_id)
    } else {
      console.warn('remove content:', error.message)
    }
    setBusyId(null)
    await load(); refreshBadge?.()
  }
  async function dismiss(r) {
    setBusyId(r.id)
    await supabase.from('reports').update({ resolved: true }).eq('id', r.id)
    setBusyId(null)
    await load(); refreshBadge?.()
  }

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) }
    catch { return '' }
  }
  const btn = (label, onClick, kind) => (
    <button type="button" onClick={onClick} disabled={!!busyId}
      style={{
        background: kind === 'danger' ? C.red : kind === 'primary' ? C.accent : 'transparent',
        color: kind === 'danger' ? '#fff' : kind === 'primary' ? C.navy : C.muted,
        border: kind === 'ghost' ? `1px solid ${C.border}` : 'none',
        borderRadius:8, padding:'7px 12px', fontSize:12, fontWeight:700,
        cursor: busyId ? 'default' : 'pointer', opacity: busyId ? 0.6 : 1, fontFamily:FONT,
      }}>{label}</button>
  )

  return (
    <div style={{maxWidth:760}}>
      <H1>Reports</H1>
      <Note>Content flagged by members of your church. Removing content soft-deletes it (restorable from Removed Posts); dismissing keeps it.</Note>

      {loading ? (
        <div style={{color:C.muted, fontSize:13, marginTop:20}}>Loading…</div>
      ) : reports.length === 0 ? (
        <div style={{color:C.muted, fontSize:13, marginTop:20}}>No unresolved reports. 🎉</div>
      ) : (
        <div style={{marginTop:18, display:'flex', flexDirection:'column', gap:12}}>
          {reports.map(r => {
            const meta = REPORT_CONTENT[r.content_type] || { label: r.content_type }
            const snip = snippets[r.content_id]
            const text = r.content_type === 'general' ? (r.notes || '') : (snip?.text || '')
            const isExpanded = expanded === r.id
            const preview = text.length > 160 && !isExpanded ? text.slice(0, 160).trim() + '…' : text
            const reporter = r.reporter?.full_name || r.reporter?.email?.split('@')[0] || 'A member'
            return (
              <div key={r.id} style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 16px'}}>
                <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap'}}>
                  <span style={{background:'rgba(232,200,122,0.12)', color:C.accent, fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6}}>{meta.label}</span>
                  <span style={{background:'rgba(220,38,38,0.12)', color:'#F87171', fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6}}>{r.reason}</span>
                  {snip?.removed && <span style={{color:C.faint, fontSize:11}}>· already removed</span>}
                  <span style={{marginLeft:'auto', color:C.faint, fontSize:11}}>{fmtDate(r.created_at)}</span>
                </div>
                {text ? (
                  <div style={{fontSize:13, color:C.text, lineHeight:1.5, whiteSpace:'pre-wrap', background:C.surface2, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px'}}>
                    {preview || <span style={{color:C.faint}}>(no text)</span>}
                  </div>
                ) : (
                  <div style={{fontSize:12, color:C.faint}}>Content not found — it may have been deleted.</div>
                )}
                {r.content_type !== 'general' && r.notes && (
                  <div style={{fontSize:12, color:C.muted, marginTop:6}}>Reporter note: {r.notes}</div>
                )}
                <div style={{display:'flex', alignItems:'center', gap:8, marginTop:12, flexWrap:'wrap'}}>
                  <span style={{fontSize:12, color:C.muted, marginRight:'auto'}}>Reported by {reporter}</span>
                  {text.length > 160 && btn(isExpanded ? 'Hide' : 'View content', () => setExpanded(isExpanded ? null : r.id), 'ghost')}
                  {meta.table && !snip?.removed && btn('Remove content', () => removeContent(r), 'danger')}
                  {btn('Dismiss', () => dismiss(r), 'primary')}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── CHURCH CHANNELS ──────────────────────────────────────────────────────
// Per-church YouTube channel URL + a "new videos" picker. Scans a church's
// channel, hides any video already imported as a sermon, and hands the
// selected (un-uploaded) videos to BulkUploadFlow for the normal
// transcript → scan → upload flow. Super-admin only (the whole panel is).
function ChurchChannelsScreen({ onProcess }) {
  const [churches, setChurches] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [colMissing, setColMissing] = useState(false)

  // Editable channel-URL text per church id (mirrors the saved value).
  const [urls,     setUrls]     = useState({})
  const [savingId, setSavingId] = useState(null)
  const [savedId,  setSavedId]  = useState(null)
  const [saveErr,  setSaveErr]  = useState('')

  // Scan state — scoped to whichever church is currently expanded.
  const [activeId,  setActiveId]  = useState(null)
  const [scanning,  setScanning]  = useState(false)
  const [scanError, setScanError] = useState('')
  const [scanLog,   setScanLog]   = useState('')            // live progress line
  const [videos,    setVideos]    = useState([])           // un-uploaded only
  const [selected,  setSelected]  = useState(() => new Set())
  const [stats,     setStats]     = useState(null)          // { total, inRange, uploaded, fresh, fromPlaylists }
  // Only surface videos uploaded on/after this date. Defaults to a week ago so
  // the list shows recent uploads rather than the whole back catalogue.
  const [sinceDate, setSinceDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return _toIsoDay(d)
  })

  const load = useCallback(async () => {
    setLoading(true)
    let { data, error } = await supabase
      .from('churches').select('id,name,youtube_channel_url').order('name')
    if (error && /youtube_channel_url/i.test(error.message || '')) {
      // Column not migrated yet — fall back so the screen still renders.
      setColMissing(true)
      ;({ data } = await supabase.from('churches').select('id,name').order('name'))
    }
    const rows = data || []
    setChurches(rows)
    const seedUrls = {}
    for (const c of rows) seedUrls[c.id] = c.youtube_channel_url || ''
    setUrls(seedUrls)
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])

  async function saveUrl(c) {
    setSavingId(c.id); setSavedId(null); setSaveErr('')
    const value = (urls[c.id] || '').trim() || null
    const { error } = await supabase.from('churches').update({ youtube_channel_url: value }).eq('id', c.id)
    setSavingId(null)
    if (error) {
      setSaveErr(/youtube_channel_url/i.test(error.message || '')
        ? 'Run supabase-schema-church-youtube-channel.sql first — the youtube_channel_url column is missing.'
        : (error.message || 'Could not save.'))
      return
    }
    setSavedId(c.id); setTimeout(() => setSavedId(null), 2200)
    setChurches(cs => cs.map(x => x.id === c.id ? { ...x, youtube_channel_url: value || '' } : x))
  }

  async function findNewVideos(c) {
    const handle = (urls[c.id] ?? c.youtube_channel_url ?? '').trim()
    if (!handle) { setActiveId(c.id); setScanError('Add and save a YouTube handle first.'); setVideos([]); setStats(null); return }
    setActiveId(c.id); setScanning(true); setScanError(''); setVideos([]); setStats(null); setSelected(new Set())
    setScanLog('Scanning channel videos…')
    try {
      const byId = new Map()   // video id -> shaped video (first occurrence wins)
      const playlistOnlyIds = new Set()   // ids surfaced only by the playlist sweep

      // 1. Channel "Videos" tab — regular uploads. Pass the handle directly (not
      // a URL) so the backend targets the exact channel.
      const { data: chData, error: chErr } = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_channel_all_videos', handle },
      })
      // eslint-disable-next-line no-console
      console.log('[church-channels scan] channel response:', chData, 'error:', chErr)
      if (chErr && !Array.isArray(chData?.videos)) {
        throw new Error(chData?.error || chData?.message || chErr.message || 'Channel scan failed')
      }
      for (const v of (Array.isArray(chData?.videos) ? chData.videos : [])) {
        const sv = shapeVideo(v, 'No series')
        if (sv && !byId.has(sv.id)) byId.set(sv.id, sv)
      }
      const channelCount = byId.size

      // 2. Playlists — live streams are normally filed under a playlist and the
      // channel "Videos" tab often omits them, so sweep every playlist too and
      // merge by video id. Non-fatal: if this fails we still return the channel
      // results.
      let fromPlaylists = 0
      setScanLog(`Found ${channelCount} channel video${channelCount === 1 ? '' : 's'} · loading playlists…`)
      try {
        // strict: true → the resolver only returns THIS channel's playlists and
        // never falls back to a fuzzy text search (which could match a
        // different channel's playlists and pull in the wrong videos).
        const { data: plData, error: plErr } = await supabase.functions.invoke('claude-proxy', {
          body: { action: 'fetch_youtube_playlists', handle, strict: true },
        })
        // eslint-disable-next-line no-console
        console.log('[church-channels scan] playlists response:', plData, 'error:', plErr)
        const playlists = Array.isArray(plData?.playlists) ? plData.playlists : []
        for (let i = 0; i < playlists.length; i++) {
          const pl = playlists[i]
          if (!pl?.id) continue
          setScanLog(`Scanning playlist ${i + 1}/${playlists.length}${pl.title ? ` — ${pl.title}` : ''}…`)
          let continuationToken = null
          let page = 0
          while (true) {
            page += 1
            const { data, error } = await supabase.functions.invoke('claude-proxy', {
              body: { action: 'fetch_playlist_videos', playlist_id: pl.id, continuationToken },
            })
            if (error) { console.warn('[church-channels scan] playlist error', pl.id, data, error); break }
            const items = data?.videos || data?.items || data?.results || data?.playlistVideos || []
            for (const v of items) {
              const sv = shapeVideo(v, pl.title || 'No series')
              if (!sv) continue
              if (!byId.has(sv.id)) { byId.set(sv.id, sv); fromPlaylists += 1; playlistOnlyIds.add(sv.id) }
              else if (byId.get(sv.id).series === 'No series' && sv.series && sv.series !== 'No series') {
                // Channel scan had no series for this video — adopt the
                // playlist name so the import flow gets a sensible series.
                byId.get(sv.id).series = sv.series
              }
            }
            continuationToken = data?.continuationToken || data?.cursor || null
            if (!continuationToken || page >= 25) break
            await delay(250)
          }
          await delay(200)
        }
      } catch (plEx) {
        console.warn('[church-channels scan] playlist sweep failed (keeping channel results):', plEx)
      }

      const shaped = [...byId.values()]
      // Keep only videos uploaded on/after the chosen date (ISO YYYY-MM-DD
      // strings compare correctly with >=). A missing/unparseable date is kept
      // so we never silently drop a genuinely-new upload whose date didn't read.
      const inRange = shaped.filter(v => !sinceDate || !v.date || v.date >= sinceDate)
      // Diff against sermons already imported for this church (by youtube_id).
      setScanLog('Checking against imported sermons…')
      const { data: existing } = await supabase
        .from('sermons').select('youtube_id').eq('church_id', c.id)
      const uploadedIds = new Set((existing || []).map(s => s.youtube_id).filter(Boolean))
      const fresh = inRange.filter(v => !uploadedIds.has(v.id))
      fresh.sort((a, b) => String(b.date).localeCompare(String(a.date)))   // newest first

      setVideos(fresh)
      setSelected(new Set(fresh.map(v => v.id)))   // default: all new videos ticked
      setStats({
        total:        shaped.length,
        inRange:      inRange.length,
        uploaded:     inRange.length - fresh.length,
        fresh:        fresh.length,
        fromPlaylists: fresh.filter(v => playlistOnlyIds.has(v.id)).length,
        since:        sinceDate,
      })
      setScanLog('')
      if (shaped.length === 0) {
        setScanError('Channel scan returned no videos. Check the URL/handle, or open the console for the raw response.')
      }
    } catch (e) {
      setScanError(e.message || String(e))
    } finally {
      setScanning(false)
      setScanLog('')
    }
  }

  function toggle(id) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  const allSelected = videos.length > 0 && selected.size === videos.length
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(videos.map(v => v.id)))
  }
  function process() {
    const chosen = videos.filter(v => selected.has(v.id))
    if (chosen.length === 0 || !activeId) return
    onProcess(chosen, activeId)
  }

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) }
    catch { return iso }
  }

  return (
    <div style={{maxWidth:900}}>
      <style>{`@keyframes sermon-spin { to { transform: rotate(360deg); } }`}</style>
      <H1>Church Channels</H1>
      <Sub>
        Save each church's YouTube <strong>handle</strong> (e.g. <code>@yourchurch</code>),
        then scan it for sermons that haven't been imported yet. Pick the ones you
        want and process them through the usual transcript → scripture-scan →
        upload flow.
      </Sub>

      {colMissing && (
        <Note kind="err">
          The <code>youtube_channel_url</code> column doesn't exist yet. Run{' '}
          <strong>supabase-schema-church-youtube-channel.sql</strong> against the
          database, then reload this page.
        </Note>
      )}
      {saveErr && <Note kind="err">{saveErr}</Note>}

      {!loading && churches.length > 0 && (
        <div style={{display:'flex', alignItems:'center', gap:10, margin:'4px 0 18px', flexWrap:'wrap'}}>
          <span style={{fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.faint}}>
            Uploaded since
          </span>
          <input type="date" value={sinceDate} max={_toIsoDay(new Date())}
            onChange={e => setSinceDate(e.target.value)}
            style={{...inputStyle, width:'auto', colorScheme:'dark'}}/>
          <span style={{fontSize:12, color:C.faint}}>
            Only fetches videos published on or after this date (default: a week ago). Re-scan after changing it.
          </span>
        </div>
      )}

      {loading ? (
        <div style={{color:C.muted, fontSize:13, marginTop:20}}>Loading churches…</div>
      ) : churches.length === 0 ? (
        <div style={{color:C.muted, fontSize:13, marginTop:20}}>No churches found.</div>
      ) : (
        <div style={{display:'flex', flexDirection:'column', gap:14, marginTop:8}}>
          {churches.map(c => {
            const isActive = activeId === c.id
            const dirty = (urls[c.id] || '') !== (c.youtube_channel_url || '')
            return (
              <div key={c.id} style={{background:C.surface, border:`1px solid ${isActive ? C.borderHi : C.border}`, borderRadius:12, padding:'16px 18px'}}>
                <div style={{fontSize:15, fontWeight:700, marginBottom:10}}>{c.name}</div>

                {/* Channel handle row */}
                <div style={{display:'flex', gap:10, alignItems:'center', flexWrap:'wrap'}}>
                  <input
                    style={{...inputStyle, flex:1, minWidth:280}}
                    placeholder="@yourchurch  (the YouTube handle, not the URL)"
                    value={urls[c.id] ?? ''}
                    onChange={e => setUrls(u => ({ ...u, [c.id]: e.target.value }))}
                  />
                  <PrimaryBtn kind={dirty ? 'gold' : 'ghost'} disabled={savingId === c.id || !dirty}
                    onClick={() => saveUrl(c)}>
                    {savingId === c.id ? 'Saving…' : savedId === c.id ? 'Saved ✓' : 'Save handle'}
                  </PrimaryBtn>
                  <PrimaryBtn kind="purple"
                    disabled={scanning || !(urls[c.id] || c.youtube_channel_url)}
                    onClick={() => findNewVideos(c)}>
                    {scanning && isActive ? 'Scanning…' : 'Find new videos'}
                  </PrimaryBtn>
                </div>

                {/* Scan results for the active church */}
                {isActive && (
                  <div style={{marginTop:16}}>
                    {scanError && <Note kind="err">{scanError}</Note>}
                    {scanning && (
                      <div style={{color:C.muted, fontSize:13, display:'flex', alignItems:'center', gap:8}}>
                        <span style={{display:'inline-block', width:12, height:12, border:`2px solid ${C.accent}`, borderTopColor:'transparent', borderRadius:'50%', animation:'sermon-spin 0.8s linear infinite'}}/>
                        {scanLog || 'Scanning channel + playlists…'}
                      </div>
                    )}

                    {!scanning && stats && (
                      <div style={{fontSize:12, color:C.muted, marginBottom:12}}>
                        {stats.total} found (channel + playlists) ·{' '}
                        <span style={{color:C.text}}>{stats.inRange} since {fmtDate(stats.since)}</span> ·{' '}
                        <span style={{color:C.green}}>{stats.uploaded} already imported</span> ·{' '}
                        <span style={{color:C.accent, fontWeight:700}}>{stats.fresh} new</span>
                        {stats.fromPlaylists > 0 && (
                          <span style={{color:C.faint}}> · {stats.fromPlaylists} only found via playlists/streams</span>
                        )}
                      </div>
                    )}

                    {!scanning && videos.length > 0 && (
                      <>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, flexWrap:'wrap', gap:10}}>
                          <button type="button" onClick={toggleAll}
                            style={{background:'transparent', border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONT}}>
                            {allSelected ? 'Deselect all' : 'Select all'}
                          </button>
                          <PrimaryBtn kind="gold" disabled={selected.size === 0} onClick={process}>
                            Process {selected.size} video{selected.size === 1 ? '' : 's'} →
                          </PrimaryBtn>
                        </div>

                        <div style={{display:'flex', flexDirection:'column', gap:6, maxHeight:520, overflowY:'auto'}}>
                          {videos.map(v => {
                            const on = selected.has(v.id)
                            return (
                              <label key={v.id} style={{
                                display:'flex', alignItems:'center', gap:12, cursor:'pointer',
                                background: on ? 'rgba(232,200,122,0.06)' : C.surface2,
                                border:`1px solid ${on ? 'rgba(232,200,122,0.30)' : C.border}`,
                                borderRadius:10, padding:'8px 12px',
                              }}>
                                <input type="checkbox" checked={on} onChange={() => toggle(v.id)}
                                  style={{width:16, height:16, accentColor:C.accent, flexShrink:0, cursor:'pointer'}}/>
                                <img src={v.thumbnail} alt="" width={88} height={50}
                                  style={{borderRadius:6, objectFit:'cover', flexShrink:0, background:C.surface}}
                                  onError={e => { e.target.style.visibility = 'hidden' }}/>
                                <div style={{minWidth:0, flex:1}}>
                                  <div style={{fontSize:13, fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                    {cleanSermonTitle(v.title) || v.title || v.id}
                                  </div>
                                  <div style={{fontSize:11, color:C.faint, marginTop:2}}>
                                    {fmtDate(v.date)}{v.durationS ? ` · ${secondsToTs(v.durationS)}` : ''}
                                  </div>
                                </div>
                                <a href={v.youtubeUrl} target="_blank" rel="noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  style={{fontSize:11, color:C.muted, textDecoration:'none', flexShrink:0}}>▶ open</a>
                              </label>
                            )
                          })}
                        </div>
                      </>
                    )}

                    {!scanning && stats && videos.length === 0 && !scanError && (
                      <Note kind="ok">
                        {stats.inRange === 0
                          ? `No videos uploaded since ${fmtDate(stats.since)} (${stats.total} found overall). Pick an earlier date to widen the search.`
                          : `All ${stats.inRange} video${stats.inRange === 1 ? '' : 's'} since ${fmtDate(stats.since)} are already imported. Nothing new. 🎉`}
                      </Note>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── SHARED PRIMITIVES ────────────────────────────────────────────────────
function H1({ children }) {
  return <h1 style={{fontSize:24, fontWeight:800, margin:'0 0 6px 0'}}>{children}</h1>
}
function H2({ children }) {
  return <h2 style={{fontSize:16, fontWeight:700, margin:'24px 0 10px 0'}}>{children}</h2>
}
function Sub({ children }) {
  return <div style={{fontSize:13, color:C.muted, marginBottom:18, lineHeight:1.5}}>{children}</div>
}
function Note({ children, kind='info' }) {
  const palette = {
    info: { bg:'rgba(74,144,226,0.08)', br:'rgba(74,144,226,0.25)', fg:C.blue },
    ok:   { bg:'rgba(80,200,150,0.08)', br:'rgba(80,200,150,0.25)', fg:C.green },
    warn: { bg:'rgba(232,200,122,0.08)', br:'rgba(232,200,122,0.25)', fg:C.accent },
    err:  { bg:'rgba(220,38,38,0.10)',  br:'rgba(220,38,38,0.30)',  fg:'#FF8888' },
  }[kind]
  return (
    <div style={{
      background: palette.bg, border:`1px solid ${palette.br}`, color:palette.fg,
      padding:'10px 14px', borderRadius:8, fontSize:13, lineHeight:1.5,
      marginBottom:14,
    }}>{children}</div>
  )
}
function PrimaryBtn({ children, onClick, disabled, kind='gold', style }) {
  const palette = {
    gold:   { bg:C.accent, fg:C.navy },
    purple: { bg:C.purple, fg:'#fff' },
    red:    { bg:C.red,    fg:'#fff' },
    ghost:  { bg:'transparent', fg:C.text },
  }[kind]
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        background: palette.bg,
        color: palette.fg,
        border: kind === 'ghost' ? `1px solid ${C.borderHi}` : 'none',
        borderRadius:8, padding:'11px 18px', fontSize:13, fontWeight:700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1, fontFamily:FONT,
        ...style,
      }}>{children}</button>
  )
}
function Field({ label, children }) {
  return (
    <label style={{display:'block', marginBottom:14}}>
      <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.12em',
        textTransform:'uppercase', color:C.faint, marginBottom:6}}>{label}</div>
      {children}
    </label>
  )
}
const inputStyle = {
  width:'100%', background:C.surface2, border:`1px solid ${C.borderHi}`,
  borderRadius:8, padding:'10px 12px', color:C.text, fontSize:13, fontFamily:FONT,
  boxSizing:'border-box', outline:'none',
  // Render native control popups (e.g. <select> option lists) in dark mode so
  // the options aren't white-on-white on desktop.
  colorScheme:'dark',
}

// Church-logo picker: upload an image (stored in the dedicated, role-gated
// `church-logos` bucket — see supabase-schema-church-logos.sql) which sets
// the logo_url, with the raw URL still editable as a fallback. The chosen
// logo shows wherever the church is listed. When churchId/youtubeUrl are
// given (the real Church Settings panel, editing an existing church), also
// offers "Refresh from YouTube" (re-fetches the channel icon server-side via
// fetch-church-logo) and persists logo_source/logo_updated_at on the church
// row directly, not just the local form field — the "create new" call sites
// don't have a church id yet, so they only get the plain upload+URL fields.
function LogoUploadField({ value, onChange, idHint, churchId, youtubeUrl }) {
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [err, setErr]   = useState('')

  async function handleFile(file) {
    if (!file) return
    setBusy(true); setErr('')
    try {
      const ext  = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg'
      const base = (idHint || 'new').toString().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40) || 'new'
      const path = churchId ? `${churchId}.${ext}` : `${base}-${Math.random().toString(36).slice(2, 8)}.${ext}`
      const { error: upErr } = await supabase.storage.from('church-logos')
        .upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' })
      if (upErr) throw upErr
      const { data: pub } = supabase.storage.from('church-logos').getPublicUrl(path)
      const url = `${pub.publicUrl}?v=${Date.now()}`
      onChange(url)
      if (churchId) {
        await supabase.from('churches').update({ logo_url: url, logo_source: 'upload', logo_updated_at: new Date().toISOString() }).eq('id', churchId)
      }
    } catch (e) {
      setErr(e?.message || String(e))
    } finally { setBusy(false) }
  }

  async function refreshFromYoutube() {
    if (!youtubeUrl || !churchId) return
    setRefreshing(true); setErr('')
    try {
      const { data, error } = await supabase.functions.invoke('fetch-church-logo', { body: { channel_url: youtubeUrl, church_id: churchId } })
      if (error || data?.error) throw new Error(data?.error || error?.message || 'Could not fetch the channel logo.')
      onChange(data.logo_url)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally { setRefreshing(false) }
  }

  async function useInitials() {
    onChange('')
    if (churchId) {
      try { await supabase.from('churches').update({ logo_url: null, logo_source: 'initials', logo_updated_at: new Date().toISOString() }).eq('id', churchId) } catch {}
    }
  }

  return (
    <div>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8, flexWrap:'wrap'}}>
        <div style={{
          width:48, height:48, borderRadius:10, overflow:'hidden', flexShrink:0,
          background:C.surface2, border:`1px solid ${C.borderHi}`,
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          {value
            ? <img src={value} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} onError={e=>{e.currentTarget.style.display='none'}}/>
            : <span style={{color:C.faint, fontSize:18}}>⛪</span>}
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{display:'none'}}
          onChange={e=>handleFile(e.target.files?.[0])}/>
        <PrimaryBtn kind="ghost" onClick={()=>inputRef.current?.click()} disabled={busy || refreshing}>
          {busy ? 'Uploading…' : (value ? 'Replace logo' : 'Upload logo')}
        </PrimaryBtn>
        {churchId && youtubeUrl && (
          <PrimaryBtn kind="ghost" onClick={refreshFromYoutube} disabled={busy || refreshing}>
            {refreshing ? 'Fetching…' : 'Refresh from YouTube'}
          </PrimaryBtn>
        )}
        {value && (
          <button type="button" onClick={useInitials}
            style={{background:'none', border:'none', color:C.faint, cursor:'pointer', fontSize:13, fontFamily:FONT}}>
            Use initials
          </button>
        )}
      </div>
      <input style={inputStyle} value={value} onChange={e=>onChange(e.target.value)}
        placeholder="Upload above, or paste an image URL"/>
      {err && <div style={{color:'#FF8888', fontSize:12, marginTop:6}}>{err}</div>}
    </div>
  )
}

// Network picker for the Create / Edit church forms. Lists existing networks
// and lets you add a new one inline. Degrades gracefully (disabled note) if the
// church_networks table doesn't exist yet.
function NetworkSelectField({ value, onChange }) {
  const [networks, setNetworks] = useState([])
  const [missing, setMissing]   = useState(false)
  const [adding, setAdding]     = useState(false)
  const [newName, setNewName]   = useState('')
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState('')
  const load = useCallback(async () => {
    const { data, error } = await supabase.from('church_networks').select('id, name').order('name')
    if (error) { setMissing(true); return }
    setNetworks(data || [])
  }, [])
  useEffect(() => { load() }, [load])
  async function createNetwork() {
    const name = newName.trim()
    if (!name) return
    setBusy(true); setErr('')
    const { data, error } = await supabase.from('church_networks')
      .insert({ name, slug: churchNameToSlug(name) }).select().single()
    setBusy(false)
    if (error) { setErr(error.message); return }
    setNetworks(n => [...n, { id:data.id, name:data.name }].sort((a, b) => a.name.localeCompare(b.name)))
    onChange(data.id)
    setAdding(false); setNewName('')
  }
  if (missing) return <Note kind="info">Run the church-networks migration to enable networks.</Note>
  return (
    <div>
      {adding ? (
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <input style={{...inputStyle, flex:1, minWidth:180}} value={newName} autoFocus
            onChange={e=>setNewName(e.target.value)} placeholder="New network name"/>
          <PrimaryBtn kind="gold" onClick={createNetwork} disabled={busy || !newName.trim()}>{busy ? 'Adding…' : 'Add'}</PrimaryBtn>
          <PrimaryBtn kind="ghost" onClick={()=>{ setAdding(false); setNewName('') }}>Cancel</PrimaryBtn>
        </div>
      ) : (
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <select style={{...inputStyle, flex:1, minWidth:180, cursor:'pointer'}} value={value || ''} onChange={e=>onChange(e.target.value)}>
            <option value="">No network</option>
            {networks.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
          <PrimaryBtn kind="ghost" onClick={()=>setAdding(true)}>+ New network</PrimaryBtn>
        </div>
      )}
      {err && <div style={{color:'#FF8888', fontSize:12, marginTop:6}}>{err}</div>}
    </div>
  )
}

// ── CHURCH NETWORKS (super-admin) ──────────────────────────────────────────
// Manage networks: create / rename / delete, and add or remove member churches.
function ChurchNetworksScreen() {
  const { profile } = useAuth()
  const [networks, setNetworks] = useState([])
  const [churches, setChurches] = useState([])
  const [loading, setLoading]   = useState(true)
  const [newName, setNewName]   = useState('')
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState('')

  const load = useCallback(async () => {
    setLoading(true); setErr('')
    const [{ data: nets, error: nErr }, { data: chs }] = await Promise.all([
      supabase.from('church_networks').select('id, name').order('name'),
      supabase.from('churches').select('id, name, location, network_id').order('name'),
    ])
    if (nErr) setErr(/relation|does not exist/i.test(nErr.message || '')
      ? 'The church_networks table is missing — run supabase-schema-church-networks.sql first.'
      : nErr.message)
    setNetworks(nets || [])
    setChurches(chs || [])
    setLoading(false)
  }, [])
  useEffect(() => { load() }, [load])

  if (profile?.role !== 'super_admin') return (
    <div style={{maxWidth:640}}><H1>Church Networks</H1><Note kind="warn">Only super admins can manage networks.</Note></div>
  )

  async function createNetwork() {
    const name = newName.trim(); if (!name) return
    setBusy(true)
    const { error } = await supabase.from('church_networks').insert({ name, slug: churchNameToSlug(name) })
    setBusy(false)
    if (error) { setErr(error.message); return }
    setNewName(''); load()
  }
  async function renameNetwork(id, name) {
    const v = (name || '').trim(); if (!v) return
    const { error } = await supabase.from('church_networks').update({ name: v }).eq('id', id)
    if (error) setErr(error.message); else load()
  }
  async function deleteNetwork(id) {
    // churches.network_id is ON DELETE SET NULL, so members just become unassigned.
    const { error } = await supabase.from('church_networks').delete().eq('id', id)
    if (error) setErr(error.message); else load()
  }
  async function setChurchNetwork(churchId, networkId) {
    const { error } = await supabase.from('churches').update({ network_id: networkId || null }).eq('id', churchId)
    if (error) setErr(error.message); else load()
  }

  if (loading) return <div style={{maxWidth:760}}><H1>Church Networks</H1><Sub>Loading…</Sub></div>

  const unassigned = churches.filter(c => !c.network_id)

  return (
    <div style={{maxWidth:760}}>
      <H1>Church Networks</H1>
      <Sub>Group connected churches into a network (e.g. a movement, denomination, or multi-site family). A church belongs to one network.</Sub>
      {err && <Note kind="err">{err}</Note>}

      <div style={{display:'flex', gap:8, marginBottom:24, maxWidth:480}}>
        <input style={inputStyle} value={newName} onChange={e=>setNewName(e.target.value)} placeholder="New network name"/>
        <PrimaryBtn kind="gold" onClick={createNetwork} disabled={busy || !newName.trim()}>{busy ? 'Adding…' : 'Add network'}</PrimaryBtn>
      </div>

      {networks.length === 0 ? (
        <Note kind="info">No networks yet — add one above, then assign churches to it.</Note>
      ) : networks.map(n => {
        const members = churches.filter(c => c.network_id === n.id)
        return (
          <div key={n.id} style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 16px', marginBottom:14}}>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:members.length ? 12 : 8, flexWrap:'wrap'}}>
              <input defaultValue={n.name}
                onBlur={e=>{ if (e.target.value.trim() && e.target.value.trim() !== n.name) renameNetwork(n.id, e.target.value) }}
                style={{...inputStyle, fontWeight:700, maxWidth:300}}/>
              <span style={{fontSize:12, color:C.muted}}>{members.length} church{members.length === 1 ? '' : 'es'}</span>
              <button onClick={()=>deleteNetwork(n.id)}
                style={{marginLeft:'auto', background:'none', border:'none', color:C.faint, cursor:'pointer', fontSize:12, fontFamily:FONT}}>
                Delete network
              </button>
            </div>
            {members.length > 0 && (
              <div style={{display:'flex', flexWrap:'wrap', gap:8, marginBottom:12}}>
                {members.map(c => (
                  <span key={c.id} style={{display:'inline-flex', alignItems:'center', gap:8, background:C.surface2, border:`1px solid ${C.borderHi}`, borderRadius:999, padding:'5px 6px 5px 12px', fontSize:13, color:C.text}}>
                    {c.name}
                    <button onClick={()=>setChurchNetwork(c.id, null)} title="Remove from network"
                      style={{background:'none', border:'none', color:C.faint, cursor:'pointer', fontSize:16, lineHeight:1}}>×</button>
                  </span>
                ))}
              </div>
            )}
            <select value="" onChange={e=>{ if (e.target.value) setChurchNetwork(e.target.value, n.id) }}
              style={{...inputStyle, cursor:'pointer', maxWidth:340}}>
              <option value="">+ Add a church…</option>
              {churches.filter(c => c.network_id !== n.id).map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.network_id ? ' (move here)' : ''}</option>
              ))}
            </select>
          </div>
        )
      })}

      {unassigned.length > 0 && (
        <div style={{marginTop:16, fontSize:12.5, color:C.faint}}>
          {unassigned.length} church{unassigned.length === 1 ? '' : 'es'} not in any network.
        </div>
      )}
    </div>
  )
}

// ── AUTOPILOT IMPORT (super-admin) ─────────────────────────────────────────
// Unattended, sequential import for many churches at once. For each selected
// church (matched to an existing church via its saved youtube_channel_url) it
// scans all playlists + the Live tab, dedupes against already-uploaded videos,
// then for each new video: fetch transcript → Claude scripture scan → insert
// sermon + refs. Reuses the same shapeVideo / scanTranscript / insert shape as
// the manual bulk flow so the data is identical. Runs as the logged-in admin
// (no service key). Resumable: re-running skips anything already uploaded.
function AutopilotImport() {
  const { user, profile } = useAuth()
  const [list, setList]         = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [running, setRunning]   = useState(false)
  const [logLines, setLogLines] = useState([])
  const [prog, setProg]         = useState(null)
  const stopRef = useRef(false)

  const log = (text, kind = 'info') =>
    setLogLines(l => [...l.slice(-500), { id: `${Date.now()}-${Math.random()}`, text, kind }])

  const load = useCallback(async () => {
    const { data } = await supabase.from('churches')
      .select('id, name, location, youtube_channel_url').order('name')
    const withCh = (data || []).filter(c => (c.youtube_channel_url || '').trim())
    setList(withCh)
    setSelected(new Set(withCh.map(c => c.id)))
  }, [])
  useEffect(() => { load() }, [load])

  if (profile?.role !== 'super_admin') return (
    <div style={{maxWidth:640}}><H1>Autopilot import</H1><Note kind="warn">Only super admins can run autopilot import.</Note></div>
  )

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  // Collect every video for a channel (playlists + channel videos + lives).
  async function collectVideos(handle) {
    const byId = new Map()
    try {
      const { data: plData } = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_youtube_playlists', channel_url: handle, strict: true },
      })
      const playlists = Array.isArray(plData?.playlists) ? plData.playlists : []
      for (const pl of playlists) {
        if (stopRef.current || !pl?.id) continue
        let token = null, page = 0
        while (true) {
          if (stopRef.current) break
          page++
          const { data, error } = await supabase.functions.invoke('claude-proxy', {
            body: { action: 'fetch_playlist_videos', playlist_id: pl.id, continuationToken: token },
          })
          if (error) break
          const items = data?.videos || data?.items || data?.results || []
          for (const v of items) {
            const sv = shapeVideo(v, pl.title || 'No series')
            if (sv?.id && !byId.has(sv.id)) byId.set(sv.id, sv)
          }
          token = data?.continuationToken || data?.cursor || null
          if (!token || page >= 25) break
          await delay(250)
        }
        await delay(200)
      }
    } catch (e) { log(`  playlist scan error: ${e?.message || e}`, 'warn') }
    try {
      const { data: chData } = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_channel_all_videos', channel_url: handle, includeLives: true },
      })
      for (const v of (Array.isArray(chData?.videos) ? chData.videos : [])) {
        const sv = shapeVideo(v, 'No series')
        if (sv?.id && !byId.has(sv.id)) byId.set(sv.id, sv)
      }
    } catch (e) { log(`  channel scan error: ${e?.message || e}`, 'warn') }
    return [...byId.values()]
  }

  // Fetch transcript → scan → insert one sermon + refs. Returns a status string.
  async function importOne(church, shaped) {
    // Reuse a cached transcript if a prior run already fetched it (saves
    // ScrapeCreators credits, and means a transcript fetched before a failed
    // scan — e.g. Anthropic credits out — isn't lost and re-paid for).
    let data = null
    try { data = await getTranscript(shaped.id) } catch (_e) { /* cache miss */ }
    if (!data) {
      const res = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_transcript', youtube_url: shaped.youtubeUrl },
      })
      if (res.error || !res.data) return 'notranscript'   // transient — not cached, retried next run
      data = res.data
      // Cache real responses only (incl. genuine "no captions"), never error bodies.
      if (data.success || data.transcript_only_text) { try { await saveTranscript(shaped.id, data) } catch (_e) {} }
    }
    const text = data.transcript_only_text || ''
    if (!text.trim()) return 'notranscript'
    // shaped.date was already resolved from the channel/playlist listing
    // (API field -> title -> relative phrase -> today). Verified live against
    // the real ScrapeCreators responses: fetch_transcript NEVER carries a
    // usable date (publishedAt/uploadDate/publishDate/date/videoDetails all
    // come back null), so resolveVideoDateInfo(data) always fell through to
    // "today" and that silently overwrote a perfectly good shaped.date on
    // every single video. The one endpoint that DOES carry the real date is
    // fetch_video_info's `publishDateText` field (e.g. "Streamed live on Oct
    // 25, 2026") - mirror BulkUploadFlow's fetchVideoInfoFor() fallback here,
    // only spending the extra ScrapeCreators credit when the channel-level
    // resolution wasn't confident (a bare "today" guess, or a year merely
    // inferred from a title with no year in it).
    let date = shaped.date || null
    const apiFromTranscript = extractApiDateFromMeta(data)
    if (apiFromTranscript.date) {
      date = apiFromTranscript.date
    } else if (shaped.dateSource === 'today' || shaped.dateSource === 'title+inferred') {
      try {
        const { data: viData } = await supabase.functions.invoke('claude-proxy', {
          body: { action: 'fetch_video_info', youtube_url: shaped.youtubeUrl },
        })
        const apiFromVideoInfo = extractApiDateFromMeta(viData)
        if (apiFromVideoInfo.date) date = apiFromVideoInfo.date
      } catch (_e) { /* keep shaped.date on failure */ }
    }
    const segs = Array.isArray(data.transcript) ? data.transcript : null

    // Long full-service / podcast transcripts can exceed the scanner's limits
    // (claude-proxy then returns a non-2xx). Cap the text we send and retry with
    // a smaller slice before giving up. Scripture refs in a sermon are well
    // within the first chunk, so this keeps coverage while staying in-bounds.
    const meta = { title: shaped.title, series: shaped.series, date }
    let refs = null, scanErr = null
    for (const cap of [90000, 45000, 20000]) {
      let t = text, s = segs
      if (text.length > cap) {
        t = text.slice(0, cap)
        if (segs) { const c = []; let tot = 0; for (const seg of segs) { tot += (seg.text || '').length + 1; if (tot > cap) break; c.push(seg) } s = c }
      }
      try { refs = await scanTranscript(t, meta, s, { onLog: () => {} }); scanErr = null; break }
      catch (e) { scanErr = e; if (cap === 20000) break; await delay(1500) }
    }
    if (refs === null) { log(`  ⚠ scan error: ${scanErr?.message || scanErr}`, 'err'); return 'scanfail' }
    refs = (refs || []).filter(r => (r.kind || 'sermon') === 'sermon')

    const sermonInsert = {
      uploaded_by: user?.id || null,
      church_id:   church.id,
      title:       shaped.title,
      series:      (shaped.series && shaped.series !== 'No series') ? shaped.series : null,
      preacher:    shaped.preacher || null,
      date,
      youtube_id:  shaped.id,
      youtube_url: shaped.youtubeUrl,
      duration:    shaped.durationS || null,
      thumbnail:   shaped.thumbnail || null,
      transcript:  text,
      transcript_json: Array.isArray(data.transcript) ? data.transcript : null,
      needs_review:false,
      is_full_service:false,
      source:      'autopilot',
    }
    let { data: sRows, error: sErr } = await supabase.from('sermons').insert(sermonInsert).select()
    if (sErr && /column.*(is_full_service|thumbnail|source|transcript_json).*does not exist/i.test(sErr.message || '')) {
      // eslint-disable-next-line no-unused-vars
      const { is_full_service, thumbnail, source, transcript_json, ...fb } = sermonInsert
      ;({ data: sRows, error: sErr } = await supabase.from('sermons').insert(fb).select())
    }
    if (sErr) { log(`  ✗ ${shaped.title}: ${sErr.message}`, 'err'); return 'fail' }
    const row = Array.isArray(sRows) ? sRows[0] : sRows
    if (!row?.id) return 'fail'

    if (refs.length) {
      const baseRows = dedupeRefRows(refs.map(ref => ({
        sermon_id:   row.id,
        verse_ref:   ref.verseRef ?? ref.verse_ref,
        book:        ref.book,
        chapter:     ref.chapter,
        verse:       ref.verse,
        end_verse:   ref.end_verse ?? ref.endVerse ?? ref.verse ?? null,
        timestamp_s: ref.timestamp_s ?? ref.timestampS ?? null,
        timestamp:   ref.timestamp || null,
        notes:       ref.notes || null,
        // main_passage | moment | cross_reference — defaults to 'moment'
        // (the DB column's own default) for any ref not classified.
        kind:        ref.kind || 'moment',
      })))
      let rErr
      ;({ error: rErr } = await supabase.from('sermon_refs').insert(baseRows.map(r => ({ ...r, church_id: church.id }))))
      if (rErr && /column.*does not exist/i.test(rErr.message || '')) {
        const msg = rErr.message || ''
        const keepChurch = !/church_id/i.test(msg)
        const keepEnd    = !/end_verse/i.test(msg)
        const keepKind   = !/kind/i.test(msg)
        const retry = baseRows.map(r => {
          let b = r
          if (!keepEnd) { const { end_verse, ...rest } = b; b = rest }
          if (!keepKind) { const { kind, ...rest } = b; b = rest }
          return keepChurch ? { ...b, church_id: church.id } : b
        })
        ;({ error: rErr } = await supabase.from('sermon_refs').insert(retry))
      }
      if (rErr) log(`  ⚠ refs failed for ${shaped.title}: ${rErr.message}`, 'warn')
    }
    return 'uploaded'
  }

  // Record a video that couldn't be imported into the Needs Review queue
  // (the "Skipped Services" tab reads import_queue), so failures are visible.
  // Deduped per (church, video) so re-runs don't pile up duplicate rows.
  async function queueForReview(church, shaped, reason) {
    try {
      const { data: existing } = await supabase.from('import_queue')
        .select('id').eq('church_id', church.id).eq('video_id', shaped.id).is('dismissed_at', null).limit(1)
      if (existing && existing.length) return
      await supabase.from('import_queue').insert({
        church_id:   church.id,
        type:        'service',
        video_id:    shaped.id || null,
        title:       shaped.title || null,
        youtube_url: shaped.youtubeUrl || null,
        reason,
        data_json:   { autopilot: true, date: shaped.date, series: shaped.series,
          row: { youtubeId: shaped.id, youtubeUrl: shaped.youtubeUrl, title: shaped.title, date: shaped.date, series: shaped.series, durationS: shaped.durationS, thumbnail: shaped.thumbnail } },
        created_by:  user?.id || null,
      })
    } catch (e) { log(`  ⚠ couldn't queue ${shaped.title} for review: ${e?.message || e}`, 'warn') }
  }

  async function run() {
    stopRef.current = false
    setRunning(true); setLogLines([])
    const targets = list.filter(c => selected.has(c.id))
    if (!targets.length) { setRunning(false); return }
    const t = { found:0, scanned:0, uploaded:0, skipped:0, failed:0, notranscript:0 }
    const setP = (extra) => setProg({ ct: targets.length, ...t, ...extra })
    try {
      for (let ci = 0; ci < targets.length; ci++) {
        if (stopRef.current) { log('■ Stopped.', 'warn'); break }
        const church = targets[ci]
        const handle = (church.youtube_channel_url || '').trim()
        log(`▶ [${ci + 1}/${targets.length}] ${church.name}`, 'info')
        setP({ ci: ci + 1, name: church.name, phase: 'Scanning playlists + Live tab…', found: 0, vi: 0, vt: 0 })

        const vids = await collectVideos(handle)
        t.found += vids.length
        // Dedupe against already-uploaded videos for this church.
        const ids = vids.map(v => v.id)
        const seen = new Set()
        for (let i = 0; i < ids.length; i += 200) {
          const { data } = await supabase.from('sermons').select('youtube_id')
            .eq('church_id', church.id).in('youtube_id', ids.slice(i, i + 200))
          for (const d of (data || [])) if (d.youtube_id) seen.add(d.youtube_id)
        }
        const todo = vids.filter(v => !seen.has(v.id))
        t.skipped += (vids.length - todo.length)
        log(`  ${vids.length} videos found · ${todo.length} new · ${vids.length - todo.length} already imported`, 'info')

        for (let vi = 0; vi < todo.length; vi++) {
          if (stopRef.current) { log('■ Stopped.', 'warn'); break }
          const v = todo[vi]
          setP({ ci: ci + 1, name: church.name, phase: `Importing "${(v.title || '').slice(0, 48)}"`, found: vids.length, vi: vi + 1, vt: todo.length })
          let res
          try { res = await importOne(church, v) } catch (e) { res = 'fail'; log(`  ✗ ${v.title}: ${e?.message || e}`, 'err') }
          if (res === 'uploaded') {
            t.uploaded++; t.scanned++; log(`  ✓ ${v.title}`, 'ok')
          } else if (res !== 'skip') {
            // Couldn't load/scan/insert — record it in Needs Review so it's not lost.
            const reason = res === 'notranscript' ? 'No captions on YouTube — can’t auto-scan'
              : res === 'scanfail' ? 'Scripture scan failed (transcript too long or API error)'
              : 'Import failed'
            await queueForReview(church, v, reason)
            if (res === 'notranscript') { t.notranscript++; log(`  – No captions → queued for review: ${v.title}`, 'warn') }
            else { if (res === 'scanfail') t.scanned++; t.failed++; log(`  ✗ Failed → queued for review: ${v.title}`, 'err') }
          }
          setP({ ci: ci + 1, name: church.name, phase: `Importing "${(v.title || '').slice(0, 48)}"`, found: vids.length, vi: vi + 1, vt: todo.length })
          await delay(5000)   // be kind to the transcript + Claude APIs
        }
        log(`  ✓ ${church.name} done — ${t.uploaded} uploaded so far`, 'ok')
      }
      log(`✅ Finished. Found ${t.found} · scanned ${t.scanned} · uploaded ${t.uploaded} · already-there ${t.skipped} · no-captions ${t.notranscript} · failed ${t.failed}`, 'ok')
      if (t.notranscript + t.failed > 0) log(`   ${t.notranscript + t.failed} video(s) couldn't be imported — see Needs Review → Skipped Services.`, 'warn')
    } catch (e) {
      log(`✗ Autopilot error: ${e?.message || e}`, 'err')
    } finally {
      setProg(null); setRunning(false)
    }
  }

  return (
    <div style={{maxWidth:760}}>
      <H1>Autopilot import</H1>
      <Sub>Pick churches and let it run unattended: for each church it scans every playlist + the Live tab, skips anything already uploaded, then fetches transcripts, extracts Bible references, and uploads the sermons — one church after another.</Sub>
      <Note kind="warn">Long-running. Keep this tab open and your computer awake. Don't run it at the same time as a manual bulk upload (it doubles the load on the transcript + AI APIs). It's resumable — re-running skips anything already imported.</Note>

      <div style={{display:'flex', alignItems:'center', gap:12, margin:'14px 0'}}>
        <PrimaryBtn kind="gold" onClick={run} disabled={running || selected.size === 0}>
          {running ? 'Running…' : `Start autopilot (${selected.size})`}
        </PrimaryBtn>
        {running && <PrimaryBtn kind="red" onClick={()=>{ stopRef.current = true }}>Stop</PrimaryBtn>}
        {!running && (
          <button type="button" onClick={()=>setSelected(s => s.size === list.length ? new Set() : new Set(list.map(c=>c.id)))}
            style={{background:'none', border:'none', color:C.accent, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:FONT}}>
            {selected.size === list.length ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {prog && (
        <div style={{background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:12, padding:'14px 16px', marginBottom:16}}>
          <div style={{fontSize:13, color:C.muted, marginBottom:4}}>
            Church <strong style={{color:C.text}}>{prog.ci}/{prog.ct}</strong> — <strong style={{color:C.accent}}>{prog.name}</strong>
          </div>
          <div style={{fontSize:13, color:C.text, marginBottom:10}}>
            {prog.phase}{prog.vt ? `  ·  video ${prog.vi}/${prog.vt}` : ''}
          </div>
          {/* per-church video progress bar */}
          {prog.vt > 0 && (
            <div style={{height:6, borderRadius:3, background:C.surface2, overflow:'hidden', marginBottom:12}}>
              <div style={{height:'100%', width:`${Math.round((prog.vi / prog.vt) * 100)}%`, background:C.accent, transition:'width 200ms'}}/>
            </div>
          )}
          <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
            {[
              ['Videos found', prog.found ?? 0, C.text],
              ['Transcripts scanned', prog.scanned ?? 0, C.blue],
              ['Uploaded', prog.uploaded ?? 0, C.green],
              ['Already there', prog.skipped ?? 0, C.muted],
              ['No captions', prog.notranscript ?? 0, C.accent],
              ['Failed', prog.failed ?? 0, '#FF8888'],
            ].map(([label, val, col]) => (
              <div key={label} style={{flex:'1 1 90px', minWidth:90, background:C.surface2, border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 10px'}}>
                <div style={{fontSize:18, fontWeight:800, color:col, lineHeight:1.1}}>{Number(val).toLocaleString()}</div>
                <div style={{fontSize:10.5, color:C.faint, textTransform:'uppercase', letterSpacing:'0.05em', marginTop:2}}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <Note kind="info">No churches have a YouTube channel URL saved. Add one in Church Settings first.</Note>
      ) : (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:8, marginBottom:18}}>
          {list.map(c => {
            const on = selected.has(c.id)
            return (
              <button key={c.id} type="button" onClick={()=>!running && toggle(c.id)} disabled={running}
                style={{textAlign:'left', cursor:running?'default':'pointer', fontFamily:FONT,
                  background:on?'rgba(232,200,122,0.06)':C.surface2, border:`1px solid ${on?C.accent:C.borderHi}`,
                  borderRadius:8, padding:'10px 12px', display:'flex', gap:10, alignItems:'center', opacity:running?0.7:1}}>
                <span style={{flexShrink:0, width:16, height:16, borderRadius:4, border:`1.5px solid ${on?C.accent:C.borderHi}`, background:on?C.accent:'transparent', color:C.navy, fontSize:12, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center'}}>{on?'✓':''}</span>
                <span style={{minWidth:0}}>
                  <span style={{display:'block', fontSize:13, fontWeight:700, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.name}</span>
                  <span style={{display:'block', fontSize:11, color:C.faint, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.youtube_channel_url}</span>
                </span>
              </button>
            )
          })}
        </div>
      )}

      {logLines.length > 0 && (
        <div style={{background:'#08080d', border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px', maxHeight:340, overflowY:'auto', fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, lineHeight:1.7}}>
          {logLines.map(l => (
            <div key={l.id} style={{color: l.kind==='err'?'#FF8888':l.kind==='warn'?C.accent:l.kind==='ok'?C.green:C.muted, whiteSpace:'pre-wrap', wordBreak:'break-word'}}>{l.text}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── BULK ADD CHURCHES (super-admin) ────────────────────────────────────────
// A small editable grid to provision several churches at once: name, location,
// YouTube URL, invite-only (default No). Slug is derived from the name.
function BulkAddChurches() {
  const { profile } = useAuth()
  const blank = () => ({ name:'', location:'', youtube_channel_url:'', invite_only:false })
  const [rows, setRows] = useState(() => Array.from({ length:5 }, blank))
  const [busy, setBusy] = useState(false)
  const [results, setResults] = useState(null)

  if (profile?.role !== 'super_admin') return (
    <div style={{maxWidth:640}}><H1>Bulk add churches</H1><Note kind="warn">Only super admins can add churches.</Note></div>
  )

  const setCell  = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  const addRow   = () => setRows(rs => [...rs, blank()])
  const removeRow = (i) => setRows(rs => { const n = rs.filter((_, idx) => idx !== i); return n.length ? n : [blank()] })
  const validCount = rows.filter(r => r.name.trim()).length

  async function createAll() {
    const valid = rows.filter(r => r.name.trim())
    if (!valid.length) return
    setBusy(true); setResults(null)
    const out = []
    for (const r of valid) {
      const payload = {
        name:                r.name.trim(),
        slug:                churchNameToSlug(r.name),
        location:            r.location.trim() || null,
        youtube_channel_url: r.youtube_channel_url.trim() || null,
        invite_only:         !!r.invite_only,
        is_paid:             false,
        setup_complete:      true,
        active:              true,
        created_by:          profile?.id || null,
      }
      const { error } = await supabase.from('churches').insert(payload)
      out.push({ name: r.name.trim(), ok: !error, error: error ? (/(duplicate|unique)/i.test(error.message || '') ? `slug "${payload.slug}" already exists` : error.message) : null })
    }
    setBusy(false); setResults(out)
    const okNames = new Set(out.filter(o => o.ok).map(o => o.name))
    setRows(rs => { const left = rs.filter(r => !(r.name.trim() && okNames.has(r.name.trim()))); return left.length ? left : [blank()] })
  }

  const cols = '1.7fr 1.2fr 1.9fr 84px 30px'
  return (
    <div style={{maxWidth:960}}>
      <H1>Bulk add churches</H1>
      <Sub>Add several churches at once. Only a name is required — the URL slug is generated from it. Invite-only churches are hidden from public discovery + onboarding (default: No).</Sub>
      {results && (
        <Note kind={results.every(o => o.ok) ? 'ok' : 'warn'}>
          Created {results.filter(o => o.ok).length}/{results.length}.
          {results.some(o => !o.ok) && ' Failed: ' + results.filter(o => !o.ok).map(o => `${o.name} (${o.error})`).join(' · ')}
        </Note>
      )}

      <div style={{display:'grid', gridTemplateColumns:cols, gap:8, alignItems:'center', marginBottom:8, fontSize:11, fontWeight:700, color:C.faint, textTransform:'uppercase', letterSpacing:'0.06em'}}>
        <div>Name *</div><div>Location</div><div>YouTube URL</div><div style={{textAlign:'center'}}>Invite only</div><div/>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{display:'grid', gridTemplateColumns:cols, gap:8, alignItems:'center', marginBottom:8}}>
          <input style={inputStyle} value={r.name} onChange={e => setCell(i, 'name', e.target.value)} placeholder="Church name"/>
          <input style={inputStyle} value={r.location} onChange={e => setCell(i, 'location', e.target.value)} placeholder="Town / city"/>
          <input style={inputStyle} value={r.youtube_channel_url} onChange={e => setCell(i, 'youtube_channel_url', e.target.value)} placeholder="https://youtube.com/@…"/>
          <button type="button" onClick={() => setCell(i, 'invite_only', !r.invite_only)}
            style={{padding:'9px 0', borderRadius:8, cursor:'pointer', fontFamily:FONT, fontSize:13, fontWeight:700,
              background: r.invite_only ? C.accent : 'transparent', color: r.invite_only ? C.navy : C.muted,
              border:`1px solid ${r.invite_only ? C.accent : C.borderHi}`}}>
            {r.invite_only ? 'Yes' : 'No'}
          </button>
          <button type="button" onClick={() => removeRow(i)} title="Remove row"
            style={{background:'none', border:'none', color:C.faint, cursor:'pointer', fontSize:18, lineHeight:1}}>×</button>
        </div>
      ))}

      <div style={{display:'flex', gap:10, marginTop:16, alignItems:'center'}}>
        <PrimaryBtn kind="ghost" onClick={addRow} disabled={busy}>+ Add row</PrimaryBtn>
        <PrimaryBtn kind="gold" onClick={createAll} disabled={busy || validCount === 0}>
          {busy ? 'Creating…' : `Create ${validCount} church${validCount === 1 ? '' : 'es'}`}
        </PrimaryBtn>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
//                       BULK UPLOAD FLOW
// ─────────────────────────────────────────────────────────────────────────
const STEPS = ['Add videos', 'Fetch transcripts', 'Review videos', 'Configure scan', 'Process', 'Done']

function BulkUploadFlow({ refreshBadge, seed, onSeedConsumed }) {
  const { user } = useAuth()
  // When seeded from the Church Channels screen we skip the "Add videos" step
  // and drop straight into transcript fetching (step 1) with the selected
  // videos + church preloaded.
  const [step, setStep] = useState(seed ? 1 : 0)

  // STEP 1 — Step1AddVideos collects videos from one of three sources
  // (paste video URLs / paste playlist URLs / scan whole channel) and hands
  // back a list of { id, title, date, durationS, youtubeUrl, series }.
  // No transcripts yet — Step 2 fetches those.
  const [videos, setVideos] = useState(seed?.videos || [])

  // STEP 4 — scan config
  const [config, setConfig] = useState(() => {
    // Scan-mode preference persists across sessions so admins don't have
    // to re-enable NLC estimation every time they open the importer.
    let scanMode = 'whole'
    let manualStartTs = ''
    try {
      const saved = localStorage.getItem('adminScanMode')
      if (saved === 'whole' || saved === 'estimate' || saved === 'manual') scanMode = saved
      const startSaved = localStorage.getItem('adminManualStartTs')
      if (typeof startSaved === 'string') manualStartTs = startSaved
    } catch (_e) { /* SSR / no localStorage — leave default */ }
    return {
      churchId: seed?.churchId || '',
      defaultPreacher: '',
      testMode: false,
      scanMode,         // 'whole' (default) | 'estimate' (NLC) | 'manual' (start time)
      manualStartTs,    // MM:SS string applied to every video when scanMode === 'manual'
    }
  })
  // Persist any scanMode change.
  useEffect(() => {
    try { localStorage.setItem('adminScanMode', config.scanMode) }
    catch (_e) { /* ignore */ }
  }, [config.scanMode])
  useEffect(() => {
    try { localStorage.setItem('adminManualStartTs', config.manualStartTs || '') }
    catch (_e) { /* ignore */ }
  }, [config.manualStartTs])
  const [churches, setChurches] = useState([])

  // Load (and reload) the churches list. Exposed to Step5ScanConfig so a newly
  // created event year / network member appears in the destination selector
  // without leaving the flow. Tolerates the newer columns not existing yet.
  const loadChurches = useCallback(async () => {
    let { data: rows, error } = await supabase
      .from('churches').select('id,name,youtube_channel_url,entity_type,parent_id').order('name')
    if (error && /entity_type|parent_id/i.test(error.message || '')) {
      ;({ data: rows, error } = await supabase.from('churches').select('id,name,youtube_channel_url').order('name'))
    }
    if (error && /youtube_channel_url/i.test(error.message || '')) {
      ;({ data: rows } = await supabase.from('churches').select('id,name').order('name'))
    }
    setChurches(rows || [])
    return rows || []
  }, [])

  // STEP 5/6 — processed sermons + final results
  const [processed, setProcessed] = useState([])
  const [results,   setResults]   = useState(null)
  // When resuming an interrupted scan, the already-scanned results seed the
  // scan step so it continues rather than restarts.
  const [seedResults, setSeedResults] = useState([])
  // When resuming an interrupted playlist discovery: { videos, pending }.
  const [resumeDiscovery, setResumeDiscovery] = useState(null)

  // Resume — `savedProgress` is the union of the three localStorage keys
  // when something restorable is found on mount.
  const [savedProgress, setSavedProgress] = useState(null)

  useEffect(() => {
    // Pull youtube_channel_url too so Step 1's channel mode can pre-fill from
    // the chosen church's settings. Tolerate the column not existing yet.
    loadChurches().then(rows => {
      if (rows?.length === 1 && !config.churchId) {
        setConfig(c => ({ ...c, churchId: rows[0].id }))
      }
    })
    // Seeded from Church Channels — the videos + church are already loaded and
    // we've jumped to step 1, so don't offer to resume an old saved import.
    // Consume the seed so navigating back here later starts clean.
    if (seed) { onSeedConsumed?.(); return }
    // Bulk stores are async (IndexedDB) now, so load them off the main thread.
    ;(async () => {
      try {
        const savedMeta = loadProgress()
        // Stage 1 (playlist discovery) was interrupted — offer to continue it
        // BEFORE looking at the videos store (which may still hold the previous
        // import's set, not cleared until discovery hands off).
        if (savedMeta?.phase === 'discovery' && savedMeta?.pendingPlaylists?.length) {
          const disc = await loadDiscovery()
          setSavedProgress({
            discovery: { videos: disc?.videos || [], pending: savedMeta.pendingPlaylists },
            churchId:  savedMeta.churchId || null,
            savedAt:   savedMeta.savedAt  || null,
          })
          return
        }
        const [savedVideos, savedResults] = await Promise.all([loadVideos(), loadResults()])
        const hasVideos    = Array.isArray(savedVideos)  && savedVideos.length  > 0
        const hasResults   = Array.isArray(savedResults) && savedResults.length > 0
        if (hasVideos || hasResults) {
          setSavedProgress({
            videos:  hasVideos  ? savedVideos  : [],
            results: hasResults ? savedResults : [],
            step:             savedMeta?.step ?? null,
            churchId:         savedMeta?.churchId         || null,
            defaultPreacher:  savedMeta?.defaultPreacher  || '',
            testMode:        !!savedMeta?.testMode,
            savedAt:          savedMeta?.savedAt          || null,
          })
        }
      } catch (e) { /* corrupt entry — ignore */ }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function clearSavedProgress() {
    clearAllProgress()
    setSavedProgress(null)
    setResumeDiscovery(null)
  }
  function resumeSavedProgress() {
    if (!savedProgress) return
    // Stage 1 (playlist discovery) → re-enter Step 1 and continue discovery.
    if (savedProgress.discovery) {
      if (savedProgress.churchId) setConfig(c => ({ ...c, churchId: savedProgress.churchId }))
      setResumeDiscovery(savedProgress.discovery)
      setStep(0)
      setSavedProgress(null)
      return
    }
    // Videos come back exactly as they were saved (transcripts intact).
    if (savedProgress.videos?.length) setVideos(savedProgress.videos)
    if (savedProgress.churchId || savedProgress.defaultPreacher !== '' || savedProgress.testMode) {
      setConfig(c => ({
        ...c,
        churchId:        savedProgress.churchId        || c.churchId,
        defaultPreacher: savedProgress.defaultPreacher || c.defaultPreacher,
        testMode:        !!savedProgress.testMode,
      }))
    }
    if (savedProgress.step === 4) {
      // Interrupted mid-ref-scan → re-enter the scan step seeded with the
      // already-scanned results, so it skips those and continues the rest.
      setSeedResults(rehydrateResults(savedProgress.results || [], savedProgress.videos))
      setStep(4)
    } else if (savedProgress.results?.length) {
      // Scan finished (step 5) — re-attach transcripts to processed sermons
      // from the videos store so the review UI + Step 7 upload have everything.
      setProcessed(rehydrateResults(savedProgress.results, savedProgress.videos))
      setStep(5)   // → Step 7 (Review & upload)
    } else if (savedProgress.videos?.length) {
      // If any video still lacks a transcript, drop back into the fetch step —
      // it cache-hits the ones already done (from IDB) and fetches the rest, so
      // an import interrupted mid-transcript-fetch picks up where it left off.
      const allFetched = savedProgress.videos.every(v => v.hasTranscript && (v.transcript || v.transcriptJson))
      setStep(allFetched ? 2 : 1)
    }
    setSavedProgress(null)
  }

  function reset() {
    setStep(0)
    setVideos([]); setProcessed([]); setResults(null); setSeedResults([]); setResumeDiscovery(null)
    clearSavedProgress()
  }

  return (
    <div style={{maxWidth:1100}}>
      <H1>Bulk Sermon Import</H1>
      <Sub>Pull a church's sermons from YouTube — paste video URLs, paste playlist URLs, or scan a whole channel. Then fetch transcripts and run Claude over each one.</Sub>

      {step === 0 && savedProgress && (
        <div style={{
          background:'rgba(232,200,122,0.08)', border:`1px solid rgba(232,200,122,0.30)`,
          borderRadius:10, padding:'14px 16px', marginBottom:18,
          display:'flex', alignItems:'center', gap:14, flexWrap:'wrap',
        }}>
          <div style={{flex:1, minWidth:220, fontSize:13, color:C.accent}}>
            Unfinished import found —{' '}
            {savedProgress.discovery ? (
              <><strong>{savedProgress.discovery.pending.length}</strong> playlist(s) still to load · <strong>{savedProgress.discovery.videos.length}</strong> video(s) found so far</>
            ) : savedProgress.results?.length > 0 ? (
              <><strong>{savedProgress.results.length}</strong> video(s) processed</>
            ) : (
              <><strong>{savedProgress.videos?.length || 0}</strong> video(s) fetched with transcripts</>
            )}
            {savedProgress.savedAt && <span style={{color:C.muted}}> · saved {new Date(savedProgress.savedAt).toLocaleString()}</span>}
          </div>
          <PrimaryBtn kind="gold" onClick={resumeSavedProgress}>Resume previous import?</PrimaryBtn>
          <PrimaryBtn kind="ghost" onClick={clearSavedProgress}>Start fresh</PrimaryBtn>
        </div>
      )}

      <StepIndicator step={step}/>

      {step === 0 && <Step1AddVideos
        churches={churches}
        churchId={config.churchId}
        resumeDiscovery={resumeDiscovery}
        onChurchChange={(id) => setConfig(c => ({ ...c, churchId: id }))}
        onLoaded={async (vids) => {
          // Persist the freshly-collected list (no transcripts yet) BEFORE
          // entering the fetch step, so a reload mid-fetch resumes the full set
          // and Step 2's per-video saves can't be overwritten by a late initial
          // write. Clear any leftover videos from a prior import first.
          await clearVideos()
          await saveVideos(vids)
          saveProgress({
            step: 1,
            churchId:        config.churchId,
            defaultPreacher: config.defaultPreacher,
            testMode:        config.testMode,
          })
          setResumeDiscovery(null)   // discovery handed off — don't re-trigger
          setVideos(vids); setStep(1)
        }}
        onBackupLoaded={async (vids) => {
          // Backup includes transcripts already — write the full array
          // straight into the videos store and jump to the review screen.
          await clearVideos()
          await saveVideos(vids)
          saveProgress({
            step: 2,
            churchId:        config.churchId,
            defaultPreacher: config.defaultPreacher,
            testMode:        config.testMode,
          })
          setVideos(vids); setStep(2)
        }}/>}

      {step === 1 && <Step2FetchTranscripts
        videos={videos}
        onBack={() => setStep(0)}
        onDone={(enriched) => {
          setVideos(enriched)
          // Snapshot the full videos array (Step 2 already saved each
          // entry incrementally; this is the final sync after the loop).
          saveVideos(enriched)
          saveProgress({
            step: 2,
            churchId:        config.churchId,
            defaultPreacher: config.defaultPreacher,
            testMode:        config.testMode,
          })
          setStep(2)
        }}/>}

      {step === 2 && <Step4VideoList
        videos={videos} setVideos={setVideos}
        onBack={() => setStep(1)}
        onNext={() => setStep(3)}/>}

      {step === 3 && <Step5ScanConfig
        videos={videos} churches={churches} reloadChurches={loadChurches}
        config={config} setConfig={setConfig}
        onBack={() => setStep(2)}
        onNext={() => setStep(4)}/>}

      {step === 4 && <Step6Process
        videos={videos} config={config} user={user}
        onBack={() => setStep(3)}
        seedResults={seedResults}
        onDone={(out) => { setProcessed(out); setSeedResults([]); setStep(5) }}/>}

      {step === 5 && <Step7ReviewUpload
        processed={processed} setProcessed={setProcessed}
        config={config} user={user}
        refreshBadge={refreshBadge}
        results={results} setResults={setResults}
        onReset={reset}/>}
    </div>
  )
}

function StepIndicator({ step }) {
  return (
    <div style={{display:'flex', alignItems:'center', gap:0, marginBottom:28}}>
      {STEPS.map((label, i) => {
        const done   = i < step
        const active = i === step
        return (
          <div key={label} style={{display:'flex', alignItems:'center', flex: i === STEPS.length-1 ? 0 : 1}}>
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <div style={{
                width:24, height:24, borderRadius:'50%',
                background: done ? C.accent : (active ? 'rgba(232,200,122,0.18)' : C.surface),
                border: active ? `1.5px solid ${C.accent}` : `1.5px solid ${C.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:11, fontWeight:800,
                color: done ? C.navy : (active ? C.accent : C.faint),
              }}>{done ? '✓' : i+1}</div>
              <div style={{
                fontSize:12, fontWeight: active ? 700 : 500,
                color: active ? C.accent : (done ? C.muted : C.faint),
              }}>{label}</div>
            </div>
            {i < STEPS.length-1 && (
              <div style={{
                flex:1, height:1, background: done ? C.accent : C.border, margin:'0 12px',
              }}/>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── STEP 1 — Add videos (3 modes: paste URLs / paste playlists / scan channel) ──

// Pull a YouTube video ID out of a URL or raw 11-char id.
function extractVideoId(raw) {
  const s = String(raw || '').trim()
  if (!s) return null
  const m = s.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|\/live\/)([A-Za-z0-9_-]{11})/)
  if (m) return m[1]
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s
  return null
}

// Pull a playlist ID out of a URL or raw string.
function extractPlaylistId(raw) {
  const s = String(raw || '').trim()
  if (!s) return null
  const m = s.match(/[?&]list=([A-Za-z0-9_-]+)/)
  if (m) return m[1]
  if (/^(PL|UU|LL|FL|OL|RD)[A-Za-z0-9_-]+$/.test(s)) return s
  return null
}

// Parse an ISO-8601 duration ("PT1H2M3S") to seconds. Used when the upstream
// response is shaped like the YouTube Data API rather than ScrapeCreators.
function isoDurationToSeconds(s) {
  if (typeof s !== 'string') return null
  const m = s.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/)
  if (!m) return null
  const h = parseInt(m[1] || '0', 10)
  const min = parseInt(m[2] || '0', 10)
  const sec = parseInt(m[3] || '0', 10)
  return h * 3600 + min * 60 + sec
}

// ── Date resolution helpers ─────────────────────────────────────────────
// ScrapeCreators sometimes returns relative dates ("3 weeks ago") in the
// only date-shaped field on a video, which `new Date(…)` can't parse. We
// resolve to a YYYY-MM-DD string by trying, in order:
//   1. absolute candidates (ISO 8601, unix ms, "DD/MM/YYYY", etc.)
//   2. relative candidates ("X weeks ago") → today minus the offset
//   3. NLC-style title regex ("Sunday 6th December 2024")
const _MONTH_NUM = {
  jan:1, january:1, feb:2, february:2, mar:3, march:3, apr:4, april:4,
  may:5, jun:6, june:6, jul:7, july:7, aug:8, august:8,
  sep:9, sept:9, september:9, oct:10, october:10, nov:11, november:11, dec:12, december:12,
}
const _MONTH_NAMES = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?"

function _toIsoDay(d) {
  if (!d || isNaN(d.getTime())) return null
  // Use local date components — these are calendar dates, not wall-clock
  // moments, so we shouldn't shift them through UTC.
  const yr  = d.getFullYear()
  const mon = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${yr}-${mon}-${day}`
}

// Strip the "Streamed " / "Premiered " / "Streamed live on " prefixes
// YouTube uses on past livestreams — without this, "Streamed 3 months ago"
// and "Streamed live on 14 May 2023" both silently fail every date check
// below them and every livestreamed sermon falls through to today's date.
function _stripStreamPrefix(s) {
  return String(s || '').replace(/^\s*(streamed|premiered)\s*(live)?\s*(on)?\s*/i, '').trim()
}

function parseRelativeDate(raw) {
  const s = _stripStreamPrefix(raw).toLowerCase()
  if (!s) return null
  if (/^just now$/.test(s) || /^moments? ago$/.test(s)) return _toIsoDay(new Date())
  const m = s.match(/^(\d+)\s+(year|month|week|day|hour|minute|second)s?\s+ago$/)
  if (!m) return null
  const n = parseInt(m[1], 10)
  const unit = m[2]
  const ms = unit === 'year'   ? n * 365 * 86400000
           : unit === 'month'  ? n * 30  * 86400000
           : unit === 'week'   ? n * 7   * 86400000
           : unit === 'day'    ? n       * 86400000
           : unit === 'hour'   ? n       * 3600000
           : unit === 'minute' ? n       * 60000
           : unit === 'second' ? n       * 1000
           : null
  if (ms == null) return null
  return _toIsoDay(new Date(Date.now() - ms))
}

// Returns { year: number|null, month, day } when a date pattern is found
// in the title, else null. `year` is null when the title omitted the year —
// callers decide how to estimate it (see estimateYearForTitleDate).
function parseDateFromTitleParts(title) {
  const t = String(title || '')
  if (!t) return null
  // DD/MM/YYYY (UK) — must come first so the slash form wins over later
  // month-name patterns picking up stray numbers. Year is always present
  // in this shape so we never need estimation.
  let m = t.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/)
  if (m) {
    const day = parseInt(m[1], 10)
    const mon = parseInt(m[2], 10)
    const yr  = parseInt(m[3], 10)
    if (day >= 1 && day <= 31 && mon >= 1 && mon <= 12) {
      return { year: yr, month: mon, day }
    }
  }
  // "6th December 2024" / "20 April 2025" / "Sunday 6th December 2024" — year optional.
  m = t.match(new RegExp(
    `\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${_MONTH_NAMES})(?:\\s+(\\d{4}))?\\b`, 'i'))
  if (m) {
    const day = parseInt(m[1], 10)
    const mon = _MONTH_NUM[m[2].toLowerCase()]
    const yr  = m[3] ? parseInt(m[3], 10) : null
    if (mon && day >= 1 && day <= 31) {
      return { year: yr, month: mon, day }
    }
  }
  // "December 6th 2024" / "April 20, 2025" / "April 20" — year optional.
  m = t.match(new RegExp(
    `\\b(${_MONTH_NAMES})\\s+(\\d{1,2})(?:st|nd|rd|th)?,?(?:\\s+(\\d{4}))?\\b`, 'i'))
  if (m) {
    const mon = _MONTH_NUM[m[1].toLowerCase()]
    const day = parseInt(m[2], 10)
    const yr  = m[3] ? parseInt(m[3], 10) : null
    if (mon && day >= 1 && day <= 31) {
      return { year: yr, month: mon, day }
    }
  }
  return null
}

// Legacy string form — used purely as a date-detector in
// extractPreacherFromTitleStr. Falls back to the current year when the
// title omits one; resolveVideoDateInfo does smarter estimation.
function parseDateFromTitleStr(title) {
  const p = parseDateFromTitleParts(title)
  if (!p) return null
  const yr = p.year ?? new Date().getFullYear()
  return `${yr}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

// Try to derive the year from ScrapeCreators "X years/months/weeks ago" text.
// Returns the year of the resulting estimate, or null when no relative
// phrase is present.
function inferYearFromRelativeFields(v) {
  const candidates = [v?.publishedTimeText, v?.publishedTime, v?.date, v?.publishedAt]
  for (const c of candidates) {
    if (typeof c !== 'string') continue
    const iso = parseRelativeDate(c)
    if (iso) {
      const yr = parseInt(iso.slice(0, 4), 10)
      if (Number.isFinite(yr)) return yr
    }
  }
  return null
}

// Pick the best year for a title-extracted month/day combo. Spec priority:
//   1. Year derived from the video's relative-date phrase
//   2. Most-recent past occurrence of that month/day
// (Playlist-context consistency is applied as a separate post-pass over
// the full video list — see applyPlaylistYearConsistency.)
function estimateYearForTitleDate(month, day, v) {
  const fromRelative = inferYearFromRelativeFields(v)
  if (fromRelative) return { year: fromRelative, source: 'title+relative' }
  const today = new Date()
  let year = today.getFullYear()
  const candidate = new Date(year, month - 1, day)
  if (candidate > today) year -= 1
  return { year, source: 'title+inferred' }
}

function _ymd(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Walk every known absolute-date field the ScrapeCreators transcript
// endpoint might populate, return the first one that parses to a valid
// calendar day. Relative phrases ("2 years ago") are skipped — we want
// the *exact* upload date here. Returns { date, field, raw } where
// date is YYYY-MM-DD or null.
export function extractApiDateFromMeta(meta) {
  if (!meta || typeof meta !== 'object') return { date: null, field: null, raw: null }
  const candidates = [
    ['publishedAt',                meta.publishedAt],
    ['uploadDate',                 meta.uploadDate],
    ['publishDate',                meta.publishDate],
    ['date',                       meta.date],
    // The ONLY field ScrapeCreators' fetch_video_info actually populates in
    // practice (verified live) — e.g. "Streamed live on Oct 25, 2026" for a
    // past livestream, or "Oct 25, 2026" for a regular upload. Every other
    // candidate in this list has been observed to always come back null.
    ['publishDateText',            meta.publishDateText],
    ['videoDetails.publishDate',   meta.videoDetails?.publishDate],
    ['videoDetails.uploadDate',    meta.videoDetails?.uploadDate],
  ]
  for (const [field, raw] of candidates) {
    if (raw == null || raw === '') continue
    if (typeof raw === 'number') {
      const ms = raw > 1e12 ? raw : raw * 1000
      const iso = _toIsoDay(new Date(ms))
      if (iso) return { date: iso, field, raw }
      continue
    }
    if (typeof raw !== 'string') continue
    let s = raw.trim()
    if (!s) continue
    // Relative phrases ("2 years ago", "yesterday", "2w ago") are not
    // absolute dates — let the relative-phrase path (resolveVideoDateInfo)
    // handle those instead.
    if (/\bago\b|^yesterday$|^today$|^just now$/i.test(s)) continue
    // Strip "Streamed live on " / "Premiered " — the prefix YouTube puts on
    // publishDateText for past livestreams — before the year check.
    s = _stripStreamPrefix(s)
    // Absolute strings always carry a 4-digit year somewhere.
    if (!/\d{4}/.test(s)) continue
    // Already an ISO calendar date (e.g. "2026-10-25T18:17:33-07:00")? Take
    // those digits directly - routing through `new Date()` + local getters
    // can shift the day by one depending on the reader's own timezone vs.
    // the string's embedded UTC offset (verified: that exact string reads
    // back as the 26th, not the 25th, for a UTC-based reader).
    const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/)
    if (isoPrefix) return { date: isoPrefix[1], field, raw }
    // DD/MM/YYYY (common UK export shape) — convert before Date parsing.
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (dmy) {
      const d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10))
      const iso = _toIsoDay(d)
      if (iso) return { date: iso, field, raw }
      continue
    }
    const iso = _toIsoDay(new Date(s))
    if (iso) return { date: iso, field, raw }
  }
  return { date: null, field: null, raw: null }
}

// Words that disqualify a title segment from being a preacher name — these
// tend to appear in church/service descriptors, not person names.
const _NON_PREACHER_WORDS = /\b(church|service|welcome|sunday|morning|evening|online|live|stream|worship|prayer|meeting|conference|guest|special|kids?|youth)\b/i
// Religious nouns/titles that are capitalised like a name but aren't a person.
const _NON_NAME_WORDS = /\b(jesus|christ|god|lord|holy|spirit|gospel|bible|scripture|ministries|ministry|fellowship|chapel|tabernacle|cathedral|assembly|series|part)\b/i
// Title-case English function words that show up in sermon TITLES but never in
// a real person's name — used to stop a title masquerading as a name (e.g.
// "Walking In The Spirit") when we only have a weak positional signal.
const _TITLE_STOPWORDS = /\b(the|in|of|and|to|for|on|at|with|from|by|your|our|my|his|her|is|are|be|a|an)\b/i

// Validate a captured name. `strict` adds the title-stopword guard, used for
// the lower-confidence patterns (a verb cue, or the name merely leading the
// description) where a sermon title could otherwise slip through.
function _cleanPreacherName(n, { strict = false } = {}) {
  const s = String(n || '').trim().replace(/\s+/g, ' ')
  if (!s) return null
  if (_NON_PREACHER_WORDS.test(s)) return null
  if (_NON_NAME_WORDS.test(s)) return null
  if (/\d/.test(s)) return null
  if (strict && _TITLE_STOPWORDS.test(s)) return null
  return s
}

// Try to derive the preacher from a video description. Church YouTube
// descriptions carry the preacher in many forms; we try them strongest-first:
//   "Speaker: Dave Smith" / "Preacher - Rich Young" / "Message by Stef Liston"
//   "Sarah Mitchell preaches…"
//   "Dave Smith" or "Dave Smith | Sunday Service"  (description STARTS with it)
// Returns the matched 2-3 word name, or null when nothing plausible is found.
// Used by Step 2 after fetching transcript / video info — see the "preacher
// from description" log line.
function extractPreacherFromDescription(desc) {
  if (!desc || typeof desc !== 'string') return null
  const raw  = desc.replace(/\r\n|\r/g, '\n')
  // Collapse newlines so labels on their own line still match.
  const text = raw.replace(/\n+/g, ' ').trim()
  if (!text) return null
  // Capitalised name token — 2 or 3 words total ("Dave Smith" / "Mary J Blige").
  const NAME = "[A-Z][A-Za-z'’\\-.]+(?:\\s+[A-Z][A-Za-z'’\\-.]+){1,2}"

  // Pattern A: explicit label — "<label>: Name" / "<label> - Name" / "<label> Name".
  const labelRe = new RegExp(
    `\\b(?:speaker|preacher|pastor|minister|preaching|preached\\s+by|spoken\\s+by|message\\s+by|word\\s+by|talk\\s+by|sermon\\s+by|given\\s+by|ministered\\s+by|brought\\s+(?:to\\s+us\\s+)?by|guest\\s+speaker)\\s*[:\\-—–]?\\s*(${NAME})`, 'i'
  )
  const a = text.match(labelRe)
  if (a) { const n = _cleanPreacherName(a[1]); if (n) return n }

  // Pattern B: "Name preaches/speaks/teaches/brings…".
  const verbRe = new RegExp(
    `(${NAME})\\s+(?:preaches|preaching|speaks|teaches|brings|delivers|shares|ministers)`
  )
  const b = text.match(verbRe)
  if (b) { const n = _cleanPreacherName(b[1], { strict: true }); if (n) return n }

  // Pattern C: the description STARTS with the preacher's name — the first
  // non-empty line begins with a plausible "First Last" name, on its own or
  // followed by a separator ("Dave Smith | Sunday Service", "Dave Smith // …").
  // Requiring a trailing separator or end-of-line (plus the strict stopword
  // guard) keeps a sermon title that happens to lead the description from
  // being read as a name. Lowest confidence, so it runs last.
  const firstLine = (raw.split('\n').find(l => l.trim()) || '').trim()
  const startRe = new RegExp(`^(${NAME})\\s*(?:[|/]{1,2}|[\\-–—·•]|$)`)
  const c = firstLine.match(startRe)
  if (c) { const n = _cleanPreacherName(c[1], { strict: true }); if (n) return n }

  return null
}

// Pluck a description string from the various shapes ScrapeCreators or the
// YouTube Data API might return.
function getDescriptionFromResponse(data) {
  if (!data || typeof data !== 'object') return ''
  return (typeof data.description === 'string' && data.description)
      || (typeof data.videoDetails?.shortDescription === 'string' && data.videoDetails.shortDescription)
      || (typeof data.videoDetails?.description === 'string' && data.videoDetails.description)
      || (typeof data.snippet?.description === 'string' && data.snippet.description)
      || ''
}

// Split the title by //, ||, or | and pick the first segment AFTER the
// title (segment 0) that looks like a "First Last" person name. The first
// segment is almost always the sermon title itself — even when it happens
// to be exactly two words like "Sermon Title" — so we skip it. Within the
// remaining segments we skip ones that contain church/service words, parse
// as a date, or start with a digit. Returns '' when nothing matches.
function extractPreacherFromTitleStr(title) {
  const t = String(title || '').trim()
  if (!t) return ''
  // (0) Name LEADING the title — the common case the segment loop below misses
  // because it always skips segment 0 as "the title". Matches:
  //   "Pastor John Smith - Romans 8", "Ps Dave Smith | Sunday Service",
  //   "John Smith: The Power of Faith".
  // The honorific OR the trailing separator is what marks the leading words as
  // a speaker label rather than the sermon title. Honorific → unambiguous;
  // without one we require exactly a "First Last" pair to avoid treating a
  // 2-word title as a name. (The import review step lets the admin fix any
  // mistake before upload.)
  const lead = t.match(/^(?:(pastor|ps|rev|dr|reverend|bishop|elder|minister)\.?\s+)?([A-Z][A-Za-z'’\-.]+(?:\s+[A-Z][A-Za-z'’\-.]+){1,2})\s*[-:–—|/]/)
  if (lead) {
    const hadHonorific = !!lead[1]
    const n = _cleanPreacherName(lead[2], { strict: !hadHonorific })
    if (n && (hadHonorific || n.split(/\s+/).length === 2)) return n
  }
  const parts = t.split(/\s*(?:\/\/|\|\||\|)\s*/).map(s => s.trim()).filter(Boolean)
  if (parts.length < 2) return ''  // no separator → just a plain title
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i]
    if (_NON_PREACHER_WORDS.test(part)) continue
    if (parseDateFromTitleStr(part)) continue
    if (/^\d/.test(part)) continue
    // Strip honorifics so "Pastor John Smith" → "John Smith"
    const cleaned = part.replace(/^(pastor|ps\.?|rev\.?|dr\.?|reverend|bishop|elder|minister)\s+/i, '').trim()
    const words = cleaned.split(/\s+/).filter(Boolean)
    if (words.length === 2 && /^[A-Za-z][A-Za-z'’\-.]*$/.test(words[0]) && /^[A-Za-z][A-Za-z'’\-.]*$/.test(words[1])) {
      return cleaned
    }
  }
  return ''
}

// Returns { value, source } where value is a YYYY-MM-DD string (never empty —
// today is the last-resort fallback) and source is one of 'api' | 'title' |
// 'relative' | 'today'. Priority order per spec: absolute API field first
// (most reliable), then a date parsed from the title, then a "X weeks ago"
// phrase, then today. Title beats relative because relative arithmetic is
// approximate and titles usually carry the exact service date.
function resolveVideoDateInfo(v) {
  const today = _toIsoDay(new Date())
  if (!v) return { value: today, source: 'today' }

  const tryAbsolute = (val) => {
    if (val == null || val === '') return null
    if (typeof val === 'number') {
      const ms = val > 1e12 ? val : val * 1000
      return _toIsoDay(new Date(ms))
    }
    if (typeof val !== 'string') return null
    // Relative phrases ("3 months ago") carry no 4-digit year and must not
    // reach here as an "absolute" candidate — they're handled in step 3.
    if (/\bago\b/i.test(val)) return null
    // Strip "Streamed live on " / "Premiered " before the year check, since
    // that prefix is what YouTube puts on past livestreams' absolute dates.
    const cleaned = _stripStreamPrefix(val)
    if (!/\d{4}/.test(cleaned)) return null
    // Already an ISO calendar date? Take the digits directly - see the
    // matching comment in extractApiDateFromMeta for why routing this
    // through `new Date()` + local getters can shift the day by one.
    const isoPrefix = cleaned.match(/^(\d{4}-\d{2}-\d{2})/)
    if (isoPrefix) return isoPrefix[1]
    const dmy = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (dmy) {
      const d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10))
      const iso = _toIsoDay(d)
      if (iso) return iso
    }
    return _toIsoDay(new Date(cleaned))
  }

  // 1. Absolute from API
  const absoluteCandidates = [
    v.publishedAt, v.published, v.uploadDate, v.publishedDate,
    v.snippet?.publishedAt, v.date, v.publishedTime,
  ]
  for (const c of absoluteCandidates) {
    const abs = tryAbsolute(c)
    if (abs) return { value: abs, source: 'api' }
  }

  // 2. From title — if the title carries the year explicitly we use it
  // directly; otherwise we estimate via relative-date → most-recent-past.
  const title = v.title || v.name || v.videoTitle || v.snippet?.title || ''
  const parts = parseDateFromTitleParts(title)
  if (parts) {
    if (parts.year) {
      return { value: _ymd(parts.year, parts.month, parts.day), source: 'title' }
    }
    const est = estimateYearForTitleDate(parts.month, parts.day, v)
    return { value: _ymd(est.year, parts.month, parts.day), source: est.source }
  }

  // 3. Relative phrase
  const relativeCandidates = [
    v.publishedTimeText, v.publishedTime, v.date, v.publishedAt,
  ]
  for (const c of relativeCandidates) {
    if (typeof c !== 'string') continue
    const rel = parseRelativeDate(c)
    if (rel) return { value: rel, source: 'relative' }
  }

  // 4. Last resort
  return { value: today, source: 'today' }
}

// Legacy thin wrapper for any old call sites — kept for safety. Exported so the
// onboarding / add-sermon scan flow resolves dates identically to the importer.
export function resolveVideoDate(v) {
  return resolveVideoDateInfo(v).value
}

// Shape a raw video object (ScrapeCreators / YouTube Data API / variant) into
// the import-flow shape. ScrapeCreators field naming has shifted over time, so
// every reasonable key is probed before giving up.
function shapeVideo(v, fallbackSeries) {
  if (!v || typeof v !== 'object') return null
  // ID — videoId is the canonical ScrapeCreators field; id is a common alias.
  const id = v.id || v.videoId || v.videoID || v.video_id
  if (!id) return null
  // Title — fall through ScrapeCreators / YouTube Data API / generic shapes.
  const title =
       (typeof v.title         === 'string' && v.title)
    || (typeof v.name          === 'string' && v.name)
    || (typeof v.videoTitle    === 'string' && v.videoTitle)
    || (typeof v.snippet?.title === 'string' && v.snippet.title)
    || ''
  // Date — priority order: API → title → relative → today (see helper).
  // dateSource is preserved on the row so the review UI can show how it
  // was determined ("from API", "from title", …).
  const dateInfo = resolveVideoDateInfo(v)
  // Preacher — try to pull a "First Last" name out of the title using
  // "//", "||" or "|" segment splitting. Empty string when nothing matches;
  // the config-form default preacher is applied at processing time.
  const preacherFromTitle = extractPreacherFromTitleStr(title)
  // Duration — accept numeric seconds, text like "47:11", or ISO 8601 (PT47M11S).
  let durationS = null
  if (typeof v.lengthSeconds === 'number') durationS = v.lengthSeconds
  else if (typeof v.durationSeconds === 'number') durationS = v.durationSeconds
  else {
    const cand = v.lengthText || v.duration || v.length || v.durationText
              || v.contentDetails?.duration
    if (typeof cand === 'string' && cand) {
      durationS = isoDurationToSeconds(cand) ?? durToSeconds(cand)
    } else if (typeof cand === 'number') {
      durationS = cand
    }
  }
  // Thumbnail — playlist + channel endpoints already include one, so prefer
  // that. Fall through the common shapes (string URL, array of {url}, YouTube
  // Data API's snippet.thumbnails), then default to YouTube's mqdefault image
  // built from the video id.
  const thumbnail =
       (typeof v.thumbnail    === 'string' && v.thumbnail)
    || (typeof v.thumbnailUrl === 'string' && v.thumbnailUrl)
    || (Array.isArray(v.thumbnails) && v.thumbnails[0]?.url)
    || v.snippet?.thumbnails?.medium?.url
    || v.snippet?.thumbnails?.high?.url
    || v.snippet?.thumbnails?.default?.url
    || `https://img.youtube.com/vi/${id}/mqdefault.jpg`
  return {
    id,
    title,
    date:           dateInfo.value,
    dateSource:     dateInfo.source,
    durationS,
    thumbnail,
    youtubeUrl:     `https://www.youtube.com/watch?v=${id}`,
    series:         fallbackSeries || 'No series',
    // Every playlist this video belongs to (a video can be in several). `series`
    // stays the primary/first one for back-compat; `playlists` is the full set
    // so the sermon can appear under each playlist in Church → Sermons.
    playlists:      (fallbackSeries && fallbackSeries !== 'No series') ? [fallbackSeries] : [],
    preacher:       preacherFromTitle,
    preacherSource: preacherFromTitle ? 'title' : 'none',
  }
}

// Friendly label for a *Source value — shown under the date/preacher inputs
// in the review tables. Returns '' for sources we don't want to surface
// (e.g. 'none' when there's nothing to report).
// Format a one-line summary of how classifyRefForUpload defaulted a
// batch of refs — e.g. "5 sermon, 2 encouragement, 1 communion
// (unticked)". Returns null when everything defaulted to sermon+picked
// (i.e. there's nothing surprising worth logging).
function describeAutoClassification(refs) {
  if (!Array.isArray(refs) || refs.length === 0) return null
  let sermon = 0, enc = 0, communion = 0
  for (const r of refs) {
    if (r.picked === false) communion++
    else if (r.kind === 'encouragement') enc++
    else sermon++
  }
  if (enc === 0 && communion === 0) return null
  const parts = []
  if (sermon)    parts.push(`${sermon} sermon`)
  if (enc)       parts.push(`${enc} encouragement`)
  if (communion) parts.push(`${communion} communion (unticked)`)
  return `Auto-defaults: ${parts.join(', ')}`
}

// Heuristically default each Claude-scanned ref's `picked` state based on
// the notes Claude wrote. Every ref defaults to kind 'sermon' now —
// auto-routing "congregation-brought" refs to the encouragements table
// used to happen here, but there's no real speaker identification behind
// it (just Claude's own inference, pattern-matched a second time), and the
// saved encouragement text/timestamp frequently ended up capturing the
// preacher rather than whoever actually spoke. Encouragements are now
// added separately, by a person who was actually there (SermonUpload's
// Encouragement mode / EncouragementUpload.jsx).
//   • Communion / Lord's-Supper context → default `picked` to false so
//     the ref is shown but not uploaded by default. The admin can tick
//     it back on if they actually want it stored. Still a conservative
//     heuristic — false positives are user-correctable in the review
//     screen, false negatives are not.
function classifyRefForUpload(ref) {
  const notes = String(ref?.notes || '').toLowerCase()

  // Communion — only flagged when the notes explicitly frame the
  // verse in a communion / Lord's-supper context. Sitting on a
  // common communion passage (1 Cor 11, Luke 22, etc.) by itself
  // isn't enough — those passages get preached on plenty outside
  // communion services.
  const isCommunion = /\b(communion|the\s+lord'?s\s+supper|breaking\s+of\s+bread|breaking\s+bread\s+together|the\s+(bread|cup)\s+(of|that\s+represents?|symboli[sz](?:e|ing)|represent)|eucharist|remembrance\s+(of|table)|cup\s+of\s+remembrance)\b/i.test(notes)
  if (isCommunion) {
    return { kind: 'sermon', picked: false }
  }

  return { kind: 'sermon', picked: true }
}

// Strip common YouTube title metadata patterns for *display only*. Mirrors
// the helper of the same name in BibleApp.jsx so admin review cards show
// the same cleaned title members will see. The raw title is still stored
// in the DB.
function cleanSermonTitle(title) {
  if (!title) return ''
  const parts = String(title).split(/\s+(?:\/\/|\|\|)\s+/)
  const first = (parts[0] || '').trim().replace(/\s{2,}/g, ' ')
  return first || String(title)
}

function sourceLabel(src) {
  switch (src) {
    case 'api':             return 'from API'
    case 'title':           return 'from title'
    case 'title+relative':  return 'from title (year from upload)'
    case 'title+inferred':  return 'from title (year inferred)'
    case 'title+playlist':  return 'from title (playlist year)'
    case 'relative':        return 'from relative date'
    case 'today':           return 'today (fallback)'
    case 'transcript':      return 'from transcript'
    case 'config':          return 'from config default'
    case 'manual':          return 'manual'
    default:                return ''
  }
}

// Post-pass over the shaped video list: for any video whose year was
// guessed via "most-recent past" (dateSource === 'title+inferred'), prefer
// the majority year of definitive siblings in the same `series` bucket.
// Only applied where a playlist boundary genuinely exists — see the call
// site in handleLoadPlaylists. Mutates videos in place and returns them.
function applyPlaylistYearConsistency(videos) {
  if (!Array.isArray(videos) || videos.length < 2) return videos
  const bySeries = new Map()
  for (const v of videos) {
    const key = v.series || ''
    if (!bySeries.has(key)) bySeries.set(key, [])
    bySeries.get(key).push(v)
  }
  for (const group of bySeries.values()) {
    if (group.length < 2) continue
    // "Definitive" = anything not guessed (today/most-recent-past). API,
    // explicit-year-in-title, relative-derived, and title+relative all count.
    const definitiveYears = group
      .filter(v => v.dateSource && v.dateSource !== 'today' && v.dateSource !== 'title+inferred')
      .map(v => parseInt(String(v.date || '').slice(0, 4), 10))
      .filter(y => Number.isFinite(y))
    if (definitiveYears.length === 0) continue
    // Modal year (ties: latest year wins, since most playlists trend forward).
    const counts = new Map()
    for (const y of definitiveYears) counts.set(y, (counts.get(y) || 0) + 1)
    let bestYear = null, bestCount = 0
    for (const [y, c] of counts) {
      if (c > bestCount || (c === bestCount && bestYear != null && y > bestYear)) {
        bestCount = c; bestYear = y
      }
    }
    if (bestYear == null) continue
    for (const v of group) {
      if (v.dateSource !== 'title+inferred') continue
      const mmdd = String(v.date || '').slice(5)
      if (mmdd) {
        v.date = `${bestYear}-${mmdd}`
        v.dateSource = 'title+playlist'
      }
    }
  }
  return videos
}

function Step1AddVideos({ churches = [], churchId = '', onChurchChange, onLoaded, onBackupLoaded, resumeDiscovery = null }) {
  const [mode, setMode] = useState('playlists')   // 'videos' | 'playlists' | 'channel'

  // Shared input state per mode.
  const [videoUrlsInput,   setVideoUrlsInput]   = useState('')
  const [videosSeriesName, setVideosSeriesName] = useState('')
  const [playlistsInput,   setPlaylistsInput]   = useState('')
  const [channelUrl,       setChannelUrl]       = useState('')

  // Channel → playlist picker. After "Load playlists" we list every playlist
  // on the channel (with thumbnails) and let the admin tick the ones to import;
  // the selected playlists then run through the same playlist→videos pipeline
  // as the paste-playlist-URLs path.
  const [channelPlaylists,  setChannelPlaylists]  = useState([])   // [{id,title,videoCount,thumbnail}]
  const [selectedPlaylists, setSelectedPlaylists] = useState(() => new Set())
  const [loadingPlaylists,  setLoadingPlaylists]  = useState(false)
  // Sentinel "playlist" id for the channel's Live tab. YouTube "podcasts" /
  // streamed-service catalogues never appear in the playlists list and the
  // playlist endpoint caps at 200, so we surface the Live tab as its own
  // pickable card and import it via the fully-paginated lives sweep instead.
  const LIVES_PSEUDO_ID = '__LIVE_TAB__'
  const livesCacheRef = useRef(null)   // lives fetched at load time, reused on import

  const selectedChurch = churches.find(c => c.id === churchId) || null

  // When the church changes, pre-fill the channel box from its saved settings
  // and reset any previously-loaded playlist list.
  useEffect(() => {
    setChannelUrl(selectedChurch?.youtube_channel_url || '')
    setChannelPlaylists([])
    setSelectedPlaylists(new Set())
    livesCacheRef.current = null
  // Re-runs when the church changes, or once its saved channel URL arrives
  // (the church list loads a tick after mount).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId, selectedChurch?.youtube_channel_url])

  // Resume an interrupted playlist discovery: continue fetching the playlists
  // that were still pending, seeded with the videos already found.
  useEffect(() => {
    if (resumeDiscovery?.pending?.length) {
      setMode('channel')
      fetchAndLoadPlaylists(resumeDiscovery.pending, { seedVideos: resumeDiscovery.videos || [] })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Run state.
  const [loading,  setLoading]  = useState(false)
  const [err,      setErr]      = useState('')
  const [log,      setLog]      = useState([])    // [{text, kind}]
  const [foundCount, setFoundCount] = useState(0)
  const [backupBusy, setBackupBusy] = useState(false)

  const cancelRef    = useRef(false)
  const backupInputRef = useRef(null)

  async function handleBackupFile(file) {
    if (!file) return
    setErr(''); setBackupBusy(true)
    try {
      const restored = await parseTranscriptsBackup(file)
      onBackupLoaded?.(restored)
    } catch (e) {
      setErr(`Couldn't restore backup: ${e.message || e}`)
    } finally {
      setBackupBusy(false)
      if (backupInputRef.current) backupInputRef.current.value = ''
    }
  }

  function pushLog(text, kind = 'info') {
    setLog(l => [...l, { id: Date.now() + Math.random(), text, kind }])
  }

  // ── OPTION 1: paste video URLs ────────────────────────────────────
  function handleLoadVideoUrls() {
    setErr(''); setLog([])
    const lines = videoUrlsInput.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
    if (lines.length === 0) { setErr('Paste at least one YouTube URL.'); return }
    const seen = new Set()
    const out = []
    let skipped = 0
    for (const line of lines) {
      const id = extractVideoId(line)
      if (!id) { skipped += 1; continue }
      if (seen.has(id)) continue
      seen.add(id)
      out.push(shapeVideo({ id }, videosSeriesName.trim() || 'No series'))
    }
    if (out.length === 0) { setErr(`Couldn't find any video IDs in those ${lines.length} line(s).`); return }
    pushLog(`✓ Extracted ${out.length} video URL${out.length === 1 ? '' : 's'}${skipped ? ` (${skipped} unparseable)` : ''}`, 'ok')
    onLoaded(out)
  }

  // ── OPTION 2: paste playlist URLs ─────────────────────────────────
  async function handleLoadPlaylists() {
    setErr(''); setLog([])
    const lines = playlistsInput.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
    const playlistIds = []
    const seenPid = new Set()
    for (const line of lines) {
      const pid = extractPlaylistId(line)
      if (!pid || seenPid.has(pid)) continue
      seenPid.add(pid)
      playlistIds.push(pid)
    }
    if (playlistIds.length === 0) { setErr('Paste at least one playlist URL containing ?list=…'); return }
    await fetchAndLoadPlaylists(playlistIds)
  }

  // Shared playlist→videos pipeline used by both the paste-URLs path and the
  // channel playlist picker. Fetches each playlist's videos (paginated),
  // dedupes across playlists, and hands the shaped videos to the next step.
  async function fetchAndLoadPlaylists(playlistIds, { seedVideos = [] } = {}) {
    setErr(''); setLog([]); setFoundCount(0); cancelRef.current = false
    if (!playlistIds || playlistIds.length === 0) return
    setLoading(true)
    pushLog(`Fetching ${playlistIds.length} playlist${playlistIds.length === 1 ? '' : 's'}…`, 'info')
    // Seed with any videos found in a prior (interrupted) discovery so we
    // continue rather than restart. First occurrence wins for series naming.
    const combinedById = new Map((seedVideos || []).filter(v => v && v.id).map(v => [v.id, v]))
    if (seedVideos?.length) { pushLog(`Resuming — ${seedVideos.length} video(s) already found.`, 'info'); setFoundCount(combinedById.size) }
    try {
      for (let pi = 0; pi < playlistIds.length; pi++) {
        const pid = playlistIds[pi]
        if (cancelRef.current) break

        // The Live-tab pseudo-playlist: import the channel's streamed services
        // via the fully-paginated lives sweep instead of the 200-capped playlist
        // endpoint. Reuse the lives fetched at load time when available.
        if (pid === LIVES_PSEUDO_ID) {
          pushLog('  Loading streamed services (Live tab)…', 'info')
          let lives = livesCacheRef.current
          if (!Array.isArray(lives)) {
            const { data, error } = await supabase.functions.invoke('claude-proxy', {
              body: { action: 'fetch_channel_lives', channel_url: channelUrl.trim() },
            })
            if (error) throw new Error(`Live tab — ${data?.error || data?.message || error.message || 'unknown error'}`)
            lives = Array.isArray(data?.videos) ? data.videos : []
          }
          let added = 0
          for (const v of lives) {
            if (cancelRef.current) break
            const shaped = shapeVideo(v, 'No series')
            if (!shaped) continue
            if (!combinedById.has(shaped.id)) { shaped.playlists = []; combinedById.set(shaped.id, shaped); added += 1 }
          }
          pushLog(`  ✓ Streamed services — ${lives.length} videos (${added} new)`, 'ok')
          setFoundCount(combinedById.size)
          if (!cancelRef.current) {
            const remaining = playlistIds.slice(pi + 1)
            await saveDiscovery([...combinedById.values()], remaining)
            saveProgress({ step: 0, phase: 'discovery', pendingPlaylists: remaining, churchId })
          }
          await delay(200)
          continue
        }

        pushLog(`  Loading playlist ${pid}…`, 'info')
        let continuationToken = null
        let title = ''
        let page = 1
        let videosInThisPlaylist = 0
        let totalVideos = null   // upstream-reported size; lets us detect the 200-cap
        while (true) {
          if (cancelRef.current) break
          const { data, error } = await supabase.functions.invoke('claude-proxy', {
            body: { action: 'fetch_playlist_videos', playlist_id: pid, continuationToken },
          })
          if (error) {
            const upstreamMsg = data?.error || data?.message || ''
            throw new Error(`Playlist ${pid} — ${upstreamMsg || error.message || 'unknown error'}`)
          }
          if (page === 1) {
            title = data?.title || data?.playlistTitle || data?.name || data?.metadata?.title || data?.playlist?.title || `Playlist ${pid}`
            const tv = data?.totalVideos ?? data?.videoCount ?? data?.total
            totalVideos = typeof tv === 'number' ? tv : null
          }
          const items = data?.videos || data?.items || data?.results || data?.playlistVideos || []
          // First-page diagnostic: surface the raw video shape so any field
          // rename from ScrapeCreators is visible without guessing. Logs the
          // top-level keys of the first item + the item itself.
          if (page === 1 && items.length > 0) {
            const v0 = items[0]
            // eslint-disable-next-line no-console
            console.log(`[fetch_playlist_videos] playlist ${pid} — raw item[0] keys:`,
              Object.keys(v0 || {}),
              '\n  full item[0]:', v0)
            // eslint-disable-next-line no-console
            console.log('Video date fields:', {
              publishedAt:       v0?.publishedAt,
              publishedTime:     v0?.publishedTime,
              publishedTimeText: v0?.publishedTimeText,
              publishedDate:     v0?.publishedDate,
              date:              v0?.date,
              uploadDate:        v0?.uploadDate,
              resolved:          resolveVideoDate(v0),
            })
          }
          for (const v of items) {
            const shaped = shapeVideo(v, title)
            if (!shaped) continue
            // First playlist that includes a given video wins for `series`
            // (the primary label). A video can be in several playlists though,
            // so we ACCUMULATE every playlist it appears in into `playlists` —
            // the sermon is still uploaded once but shows under each playlist.
            const plName = (title && title !== 'No series') ? title : null
            if (!combinedById.has(shaped.id)) {
              shaped.playlists = plName ? [plName] : []
              combinedById.set(shaped.id, shaped)
            } else if (plName) {
              const existing = combinedById.get(shaped.id)
              if (!Array.isArray(existing.playlists)) existing.playlists = existing.series && existing.series !== 'No series' ? [existing.series] : []
              if (!existing.playlists.includes(plName)) existing.playlists.push(plName)
            }
          }
          videosInThisPlaylist += items.length
          continuationToken = data?.continuationToken || data?.cursor || null
          if (!continuationToken) break
          page += 1
          await delay(300)
        }
        pushLog(`  ✓ ${title} — ${videosInThisPlaylist} videos`, 'ok')
        // YouTube "podcasts" and very large playlists are capped at 200 by the
        // upstream playlist endpoint (no pagination past that). Warn loudly so a
        // partial import isn't mistaken for the whole catalogue — for these,
        // "Scan all videos" (which also sweeps the Live tab) gets everything.
        if (totalVideos && videosInThisPlaylist < totalVideos) {
          pushLog(`  ⚠ Only ${videosInThisPlaylist} of ${totalVideos} fetched — this playlist exceeds YouTube's 200-per-playlist export limit. Use "Scan all videos" for the channel to get them all.`, 'warn')
        }
        setFoundCount(combinedById.size)
        // Persist after each playlist so a crash/close mid-discovery resumes:
        // keep the videos found so far + the playlists still to do.
        if (!cancelRef.current) {
          const remaining = playlistIds.slice(pi + 1)
          await saveDiscovery([...combinedById.values()], remaining)
          saveProgress({ step: 0, phase: 'discovery', pendingPlaylists: remaining, churchId })
        }
        await delay(400)
      }
      const out = [...combinedById.values()]
      // For videos whose year had to be guessed from "most-recent past",
      // promote the modal year of definitive siblings in the same playlist
      // (series) — keeps a 30-video sermon series from drifting across years.
      applyPlaylistYearConsistency(out)
      pushLog(`Done — ${playlistIds.length} playlist${playlistIds.length === 1 ? '' : 's'} · ${out.length} videos found (after dedup)`, 'ok')
      setFoundCount(out.length)
      // Discovery finished (or the user is taking the partial set forward) —
      // drop the discovery marker so resume doesn't re-enter this stage.
      await clearDiscovery()
      if (cancelRef.current) {
        setErr('Cancelled before all playlists finished. Click Continue to use what was fetched, or paste again to restart.')
        // Keep the partial result accessible via Continue button.
        onLoaded(out)
        return
      }
      onLoaded(out)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // ── OPTION 3: scan whole channel ──────────────────────────────────
  async function handleScanChannel() {
    setErr(''); setLog([]); setFoundCount(0); cancelRef.current = false
    if (!channelUrl.trim()) { setErr('Paste a YouTube channel URL or handle.'); return }
    setLoading(true)
    try {
      pushLog(`Scanning channel ${channelUrl.trim()}…`, 'info')
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { action: 'fetch_channel_all_videos', channel_url: channelUrl.trim() },
      })
      // eslint-disable-next-line no-console
      console.log('[fetch_channel_all_videos] response:', data, 'error:', error)
      if (error) {
        // Body usually still populated on non-2xx — prefer it for the message.
        const msg = data?.error || data?.message || error.message || 'unknown error'
        throw new Error(msg)
      }
      const items = Array.isArray(data?.videos) ? data.videos : []
      // Same diagnostic as the playlist path — show the raw shape once.
      if (items.length > 0) {
        const v0 = items[0]
        // eslint-disable-next-line no-console
        console.log('[fetch_channel_all_videos] raw item[0] keys:',
          Object.keys(v0 || {}),
          '\n  full item[0]:', v0)
        // eslint-disable-next-line no-console
        console.log('Video date fields:', {
          publishedAt:       v0?.publishedAt,
          publishedTime:     v0?.publishedTime,
          publishedTimeText: v0?.publishedTimeText,
          publishedDate:     v0?.publishedDate,
          date:              v0?.date,
          uploadDate:        v0?.uploadDate,
          resolved:          resolveVideoDate(v0),
        })
      }
      const out = []
      const seen = new Set()
      for (const v of items) {
        const shaped = shapeVideo(v, 'No series')
        if (!shaped || seen.has(shaped.id)) continue
        seen.add(shaped.id)
        out.push(shaped)
      }
      pushLog(`✓ ${out.length} videos found across ${data?.pages || 1} page(s)${data?.usedFallback ? ' (via fallback endpoint)' : ''}`, 'ok')
      setFoundCount(out.length)
      if (out.length === 0) {
        setErr('Channel scan returned no videos. Open the browser console for the raw response, or try pasting playlist URLs instead.')
        return
      }
      onLoaded(out)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // ── OPTION 3b: list a channel's playlists to pick from ────────────
  async function handleLoadChannelPlaylists() {
    setErr(''); setLog([]); setChannelPlaylists([]); setSelectedPlaylists(new Set())
    const handle = channelUrl.trim()
    if (!handle) { setErr('Enter a YouTube channel URL or handle (or pick a church with one saved).'); return }
    setLoadingPlaylists(true)
    try {
      pushLog(`Loading playlists for ${handle}…`, 'info')
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        // strict → only THIS channel's playlists (don't pull a similarly-named
        // channel's playlists via the search fallback).
        body: { action: 'fetch_youtube_playlists', channel_url: handle, strict: true },
      })
      // eslint-disable-next-line no-console
      console.log('[fetch_youtube_playlists] response:', data, 'error:', error)
      if (error) {
        const msg = data?.error || data?.message || error.message || 'unknown error'
        throw new Error(msg)
      }
      const pls = Array.isArray(data?.playlists) ? data.playlists : []

      // Also surface the channel's Live tab as a pickable card. Streamed
      // services (and the YouTube "podcast" that bundles them) never appear in
      // the playlists list, and the playlist endpoint caps at 200 — the Live
      // tab paginates fully, so this is the only way to pick up the whole
      // streamed-service back catalogue. Non-fatal if it fails.
      let liveCard = null
      livesCacheRef.current = null
      try {
        pushLog('Checking the Live tab for streamed services…', 'info')
        const { data: lv } = await supabase.functions.invoke('claude-proxy', {
          body: { action: 'fetch_channel_lives', channel_url: handle },
        })
        const lives = Array.isArray(lv?.videos) ? lv.videos : []
        if (lives.length > 0) {
          livesCacheRef.current = lives
          liveCard = {
            id: LIVES_PSEUDO_ID,
            title: 'Streamed services (Live tab)',
            videoCount: lives.length,
            thumbnail: lives[0]?.thumbnail || null,
          }
          pushLog(`✓ Live tab — ${lives.length} streamed service${lives.length === 1 ? '' : 's'}`, 'ok')
        }
      } catch (lvEx) {
        console.warn('[fetch_channel_lives] failed (non-fatal):', lvEx)
      }

      const entries = liveCard ? [liveCard, ...pls] : pls
      if (entries.length === 0) {
        setErr('No playlists or streamed services found for that channel. You can still "Scan all videos" below, or paste playlist URLs.')
        pushLog('Nothing returned.', 'warn')
        return
      }
      setChannelPlaylists(entries)
      // Pre-select all by default — most imports want the whole catalogue.
      setSelectedPlaylists(new Set(entries.map(p => p.id)))
      pushLog(`✓ Found ${pls.length} playlist${pls.length === 1 ? '' : 's'}${liveCard ? ' + Live tab' : ''}`, 'ok')
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setLoadingPlaylists(false)
    }
  }

  function togglePlaylist(id) {
    setSelectedPlaylists(s => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id); else n.add(id)
      return n
    })
  }
  function toggleSelectAllPlaylists() {
    setSelectedPlaylists(s =>
      s.size === channelPlaylists.length ? new Set() : new Set(channelPlaylists.map(p => p.id)))
  }

  async function handleAddSelectedPlaylists() {
    const ids = channelPlaylists.filter(p => selectedPlaylists.has(p.id)).map(p => p.id)
    if (ids.length === 0) { setErr('Select at least one playlist.'); return }
    await fetchAndLoadPlaylists(ids)
  }

  const MODES = [
    { id: 'videos',    label: 'Paste video URLs',
      desc: 'Paste individual YouTube video URLs, one per line.' },
    { id: 'playlists', label: 'Paste playlist URLs',
      desc: 'Paste YouTube playlist URLs. The tool will find all videos within each playlist and use the playlist name as the series.' },
    { id: 'channel',   label: 'Choose from a channel’s playlists',
      desc: 'Load every playlist on the channel (pulled from the church’s settings) and tick which to import — or scan all the channel’s videos.' },
  ]

  return (
    <div>
      <H2>1. Which church?</H2>
      <Sub>Pick the church these sermons belong to. If it has a YouTube channel saved in its settings, the channel option below is pre-filled for you.</Sub>
      <div style={{maxWidth:420, marginBottom:22}}>
        <select
          value={churchId}
          onChange={e => onChurchChange?.(e.target.value)}
          style={{...inputStyle, cursor:'pointer'}}>
          <option value="">Select a church…</option>
          {churches.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <H2>2. How do you want to add videos?</H2>
      <Sub>Pick a source. You can switch — the import flow is the same after this step.</Sub>

      {/* ── Restore-from-backup row — appears at the top so it's an
          obvious alternative to picking a fresh source. */}
      <div style={{
        display:'flex', alignItems:'center', gap:10, marginBottom:14,
        padding:'10px 14px',
        background:'rgba(232,200,122,0.04)',
        border:`1px dashed ${C.borderHi}`, borderRadius:10,
      }}>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight:700, color:C.text}}>Already have a backup?</div>
          <div style={{fontSize:12, color:C.muted, marginTop:2}}>
            Upload a transcripts-backup-*.json file to skip the fetch step and jump straight to review.
          </div>
        </div>
        <input ref={backupInputRef} type="file" accept="application/json,.json"
          style={{display:'none'}}
          onChange={e => handleBackupFile(e.target.files?.[0])}/>
        <PrimaryBtn kind="ghost"
          disabled={loading || backupBusy}
          onClick={() => backupInputRef.current?.click()}>
          {backupBusy ? 'Restoring…' : 'Upload backup'}
        </PrimaryBtn>
      </div>

      <div style={{display:'flex', flexDirection:'column', gap:8, maxWidth:680, marginBottom:18}}>
        {MODES.map(opt => {
          const active = mode === opt.id
          return (
            <button key={opt.id} type="button"
              onClick={() => { setMode(opt.id); setErr(''); setLog([]); }}
              disabled={loading}
              style={{
                textAlign:'left', cursor: loading ? 'default' : 'pointer', fontFamily:FONT,
                background: active ? 'rgba(232,200,122,0.06)' : C.surface2,
                border: `1px solid ${active ? C.accent : C.borderHi}`,
                borderRadius:8, padding:'12px 14px', color:C.text,
                display:'flex', gap:12, alignItems:'flex-start',
                opacity: loading ? 0.6 : 1,
              }}>
              <span style={{
                flexShrink:0, width:16, height:16, borderRadius:'50%', marginTop:2,
                border:`2px solid ${active ? C.accent : C.borderHi}`,
                position:'relative',
              }}>
                {active && <span style={{
                  position:'absolute', top:2, left:2, right:2, bottom:2,
                  borderRadius:'50%', background:C.accent,
                }}/>}
              </span>
              <span style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:700}}>{opt.label}{opt.id === 'playlists' && <span style={{color:C.faint, fontWeight:500, marginLeft:6}}>(default)</span>}</div>
                <div style={{fontSize:12, color:C.muted, marginTop:2}}>{opt.desc}</div>
              </span>
            </button>
          )
        })}
      </div>

      {mode === 'videos' && (
        <div>
          <Field label="YouTube video URLs (one per line)">
            <textarea
              style={{...inputStyle, minHeight:140, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, resize:'vertical'}}
              placeholder={"https://www.youtube.com/watch?v=x0xeGyfBbwM\nhttps://www.youtube.com/watch?v=TOSUxVryYs8\nhttps://youtu.be/another_video_id"}
              value={videoUrlsInput}
              onChange={e => setVideoUrlsInput(e.target.value)}/>
          </Field>
          <Field label="Series name for all these videos (optional)">
            <input style={inputStyle}
              placeholder='Leave blank for "No series"'
              value={videosSeriesName}
              onChange={e => setVideosSeriesName(e.target.value)}/>
          </Field>
          {err && <Note kind="err">{err}</Note>}
          <div style={{display:'flex', gap:10, marginTop:18}}>
            <PrimaryBtn kind="gold" onClick={handleLoadVideoUrls} disabled={!videoUrlsInput.trim()}>
              Load videos
            </PrimaryBtn>
          </div>
        </div>
      )}

      {mode === 'playlists' && (
        <div>
          <Field label="YouTube playlist URLs (one per line)">
            <textarea
              style={{...inputStyle, minHeight:140, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, resize:'vertical'}}
              placeholder={"https://www.youtube.com/playlist?list=PLDwF-YJjXxXQfHB8lu1RgnwnlQSp3UJ21\nhttps://www.youtube.com/playlist?list=PLDwF-YJjXxXSold47UfiGQHdg1nfUR_Ri"}
              value={playlistsInput}
              onChange={e => setPlaylistsInput(e.target.value)}
              disabled={loading}/>
          </Field>
          {err && <Note kind="err">{err}</Note>}
          <div style={{display:'flex', gap:10, marginTop:18, alignItems:'center'}}>
            <PrimaryBtn kind="gold" onClick={handleLoadPlaylists}
              disabled={loading || !playlistsInput.trim()}>
              {loading ? 'Loading playlists…' : 'Load playlists'}
            </PrimaryBtn>
            {loading && (
              <PrimaryBtn kind="red" onClick={() => { cancelRef.current = true }}>Stop</PrimaryBtn>
            )}
            {foundCount > 0 && <div style={{fontSize:13, color:C.muted}}>{foundCount} videos so far</div>}
          </div>
        </div>
      )}

      {mode === 'channel' && (
        <div>
          <Field label={`YouTube channel URL${selectedChurch?.youtube_channel_url ? ' (from church settings)' : ''}`}>
            <input style={inputStyle}
              placeholder="https://www.youtube.com/@newlifechurchyoutube"
              value={channelUrl}
              onChange={e => setChannelUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !loading && !loadingPlaylists) handleLoadChannelPlaylists() }}
              disabled={loading || loadingPlaylists}/>
          </Field>
          {err && <Note kind="err">{err}</Note>}

          <div style={{display:'flex', gap:10, marginTop:18, alignItems:'center', flexWrap:'wrap'}}>
            <PrimaryBtn kind="gold" onClick={handleLoadChannelPlaylists}
              disabled={loading || loadingPlaylists || !channelUrl.trim()}>
              {loadingPlaylists ? 'Loading playlists…' : 'Load playlists'}
            </PrimaryBtn>
            {/* Fallback for channels that don't organise sermons into playlists. */}
            <PrimaryBtn kind="ghost" onClick={handleScanChannel}
              disabled={loading || loadingPlaylists || !channelUrl.trim()}>
              {loading ? 'Scanning…' : 'Scan all videos instead'}
            </PrimaryBtn>
            {loading && (
              <PrimaryBtn kind="red" onClick={() => { cancelRef.current = true }}>Stop</PrimaryBtn>
            )}
            {foundCount > 0 && <div style={{fontSize:13, color:C.muted}}>{foundCount} videos found so far</div>}
          </div>

          {/* Playlist picker — appears once playlists are loaded. */}
          {channelPlaylists.length > 0 && (
            <div style={{marginTop:22}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:12, flexWrap:'wrap'}}>
                <div style={{fontSize:13, fontWeight:700, color:C.text}}>
                  {channelPlaylists.length} playlist{channelPlaylists.length === 1 ? '' : 's'} ·{' '}
                  <span style={{color:C.accent}}>{selectedPlaylists.size} selected</span>
                </div>
                <button type="button" onClick={toggleSelectAllPlaylists} disabled={loading}
                  style={{background:'none', border:'none', color:C.accent, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONT}}>
                  {selectedPlaylists.size === channelPlaylists.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              {channelPlaylists.some(p => p.id === LIVES_PSEUDO_ID) && (
                <Note kind="info">
                  YouTube “podcasts” can’t be listed here, but their episodes are live streams and/or uploads. Picking <b>Streamed services (Live tab)</b> covers the streamed ones; for a guaranteed-complete import of a podcast’s sermons use <b>Scan all videos instead</b> above.
                </Note>
              )}
              <div style={{
                display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12,
              }}>
                {channelPlaylists.map(pl => {
                  const on = selectedPlaylists.has(pl.id)
                  return (
                    <button key={pl.id} type="button" onClick={() => togglePlaylist(pl.id)} disabled={loading}
                      style={{
                        textAlign:'left', cursor: loading ? 'default' : 'pointer', fontFamily:FONT, padding:0,
                        background: C.surface2, color:C.text, overflow:'hidden',
                        border:`1px solid ${on ? C.accent : C.borderHi}`, borderRadius:10,
                        boxShadow: on ? `0 0 0 1px ${C.accent}` : 'none',
                      }}>
                      <div style={{position:'relative', width:'100%', aspectRatio:'16 / 9', background:'#000'}}>
                        {pl.thumbnail
                          ? <img src={pl.thumbnail} alt="" loading="lazy" referrerPolicy="no-referrer"
                              style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}}
                              onError={e => { e.currentTarget.style.display = 'none' }}/>
                          : <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:C.faint, fontSize:22}}>🎬</div>}
                        <span style={{
                          position:'absolute', top:8, right:8, width:22, height:22, borderRadius:'50%',
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800,
                          background: on ? C.accent : 'rgba(0,0,0,0.55)', color: on ? C.navy : '#fff',
                          border:`1.5px solid ${on ? C.accent : 'rgba(255,255,255,0.6)'}`,
                        }}>{on ? '✓' : ''}</span>
                      </div>
                      <div style={{padding:'10px 12px'}}>
                        <div style={{fontSize:13, fontWeight:700, lineHeight:1.3, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{pl.title || 'Untitled playlist'}</div>
                        {pl.videoCount != null && (
                          <div style={{fontSize:11, color:C.muted, marginTop:3}}>{pl.videoCount} video{pl.videoCount === 1 ? '' : 's'}</div>
                        )}
                        {pl.id === LIVES_PSEUDO_ID && (
                          <div style={{fontSize:11, color:C.faint, marginTop:5, lineHeight:1.4}}>
                            All live-streamed services. If sermons here are part of a YouTube “podcast”, use <b style={{color:C.muted}}>Scan all videos</b> for a guaranteed-complete import.
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
              <div style={{display:'flex', gap:10, marginTop:18, alignItems:'center'}}>
                <PrimaryBtn kind="gold" onClick={handleAddSelectedPlaylists}
                  disabled={loading || selectedPlaylists.size === 0}>
                  {loading ? 'Loading videos…' : `Import ${selectedPlaylists.size} playlist${selectedPlaylists.size === 1 ? '' : 's'}`}
                </PrimaryBtn>
                {loading && (
                  <PrimaryBtn kind="red" onClick={() => { cancelRef.current = true }}>Stop</PrimaryBtn>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {log.length > 0 && (
        <div style={{
          marginTop:18,
          background:'#08080d', border:`1px solid ${C.border}`, borderRadius:10,
          padding:'12px 14px', maxHeight:200, overflowY:'auto',
          fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, lineHeight:1.7,
        }}>
          {log.map(l => (
            <div key={l.id} style={{
              color: l.kind === 'err' ? '#FF8888' : l.kind === 'warn' ? C.accent : l.kind === 'ok' ? C.green : C.muted,
              whiteSpace:'pre-wrap', wordBreak:'break-word',
            }}>{l.text}</div>
          ))}
        </div>
      )}
    </div>
  )
}


// ── STEP 2 — Fetch transcripts for the videos collected in Step 1 ────────
// Transcripts are expensive to fetch (ScrapeCreators credits + network) so
// every successful fetch is written to `adminImportVideos` (the single
// canonical store for video metadata + transcripts), so restarting picks
// the work up without re-calling fetch_transcript.

function Step2FetchTranscripts({ videos, onBack, onDone }) {
  const [status, setStatus] = useState('idle')    // idle | fetching | paused | cancelled | done
  const [logs,   setLogs]   = useState([])
  const [progress, setProgress] = useState({ done:0, total: videos.length })

  const pausedRef = useRef(false)
  const cancelRef = useRef(false)

  function log(text, kind='info') {
    setLogs(l => [...l, { id: Date.now() + Math.random(), text, kind }])
  }
  async function waitPaused() {
    while (pausedRef.current && !cancelRef.current) await delay(150)
  }

  async function fetchTranscriptFor(video) {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: { action: 'fetch_transcript', youtube_url: video.youtubeUrl },
    })
    // supabase-js typically still populates `data` with the upstream JSON
    // even when `error` is set (e.g. 502 with success:false body), so check
    // success first before falling back to the error message.
    if (!data?.success) {
      const reason = data?.message || data?.error || error?.message || 'no transcript available'
      return { ok: false, reason }
    }
    // Full raw response — JSON-stringified so every nested field is visible
    // in the browser console (object refs would otherwise collapse). Lets us
    // see every property ScrapeCreators returns, not just the ones we know about.
    // eslint-disable-next-line no-console
    console.log(`Full transcript response: ${video.id || video.youtubeUrl}`, JSON.stringify(data))
    // Log every date-related field returned alongside the transcript so we
    // can confirm exactly which keys ScrapeCreators populates per video.
    // eslint-disable-next-line no-console
    console.log(`[fetch_transcript date fields] ${video.id || video.youtubeUrl}`, {
      publishedAt:                data.publishedAt,
      uploadDate:                 data.uploadDate,
      publishDate:                data.publishDate,
      date:                       data.date,
      publishedTimeText:          data.publishedTimeText,
      'videoDetails.publishDate': data.videoDetails?.publishDate,
      'videoDetails.uploadDate':  data.videoDetails?.uploadDate,
      videoDetails:               data.videoDetails,
    })
    const dateInfo = extractApiDateFromMeta(data)
    if (dateInfo.date) {
      // eslint-disable-next-line no-console
      console.log(`[fetch_transcript] using API date ${dateInfo.date} from "${dateInfo.field}" (raw: ${JSON.stringify(dateInfo.raw)})`)
    }
    const descPreacher = extractPreacherFromDescription(getDescriptionFromResponse(data))
    return {
      ok: true,
      transcript:     data.transcript_only_text || '',
      transcriptJson: Array.isArray(data.transcript) ? data.transcript : null,
      apiDate:        dateInfo.date,
      apiDateField:   dateInfo.field,
      descPreacher,
    }
  }

  // Date-recovery fallback. Only called when fetch_transcript didn't carry
  // an absolute date. Costs an extra ScrapeCreators credit per video.
  async function fetchVideoInfoFor(video) {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: { action: 'fetch_video_info', youtube_url: video.youtubeUrl },
    })
    if (error || !data || data.error) {
      const reason = data?.error || data?.message || error?.message || 'fetch_video_info failed'
      return { ok: false, reason }
    }
    // eslint-disable-next-line no-console
    console.log(`Full fetch_video_info response: ${video.id || video.youtubeUrl}`, JSON.stringify(data))
    const dateInfo = extractApiDateFromMeta(data)
    const descPreacher = extractPreacherFromDescription(getDescriptionFromResponse(data))
    return {
      ok: true, data,
      apiDate: dateInfo.date, apiDateField: dateInfo.field,
      descPreacher,
    }
  }

  async function run() {
    pausedRef.current = false
    cancelRef.current = false
    setLogs([]); setProgress({ done:0, total: videos.length })

    if (videos.length === 0) {
      setStatus('done')
      onDone([])
      return
    }
    setStatus('fetching')
    log(`Fetching transcripts for ${videos.length} video${videos.length === 1 ? '' : 's'}…`, 'info')
    // Build a by-id map from the persisted videos store — any video already
    // captured there counts as a fast-path hit.
    const stored = await loadVideos()
    const cached = new Map((stored || []).map(v => [v.id, v]))
    const cacheHits = videos.filter(v => v.id && (cached.get(v.id)?.transcript || cached.get(v.id)?.transcriptJson)).length
    if (cacheHits > 0) log(`Using ${cacheHits} cached transcript${cacheHits === 1 ? '' : 's'} from previous runs.`, 'info')

    const enriched = []
    for (let i = 0; i < videos.length; i++) {
      if (cancelRef.current) break
      await keepSessionAlive()   // keep the token fresh across a long fetch
      await waitPaused()
      const v = videos[i]

      // ── 1. Already fetched in this session? Keep as-is. ──
      if (v.hasTranscript && (v.transcript || v.transcriptJson)) {
        log(`✓ ${v.title || v.id} — already fetched`, 'ok')
        enriched.push({ ...v, selected: v.selected !== false })
        setProgress({ done: i+1, total: videos.length })
        continue
      }

      // ── 2. Cache hit? No network call. Check the current-import store, then
      // the PERSISTENT transcript cache (so a video fetched in any prior import
      // is skipped, not re-downloaded). ──
      let hit = v.id ? cached.get(v.id) : null
      if (!(hit && (hit.transcript || hit.transcriptJson)) && v.id) {
        const cachedT = await getTranscript(v.id)
        if (cachedT && (cachedT.transcript || cachedT.transcriptJson)) hit = cachedT
      }
      if (hit && (hit.transcript || hit.transcriptJson)) {
        const segCount = Array.isArray(hit.transcriptJson) ? hit.transcriptJson.length : null
        log(`✓ ${v.title || v.id} — cached${segCount != null ? ` (${segCount} segments)` : ''}`, 'ok')
        const fromCache = {
          ...v,
          transcript:     hit.transcript     || '',
          transcriptJson: hit.transcriptJson || null,
          hasTranscript:  true,
          selected:       true,
        }
        if (hit.date && !fromCache.date) { fromCache.date = hit.date; fromCache.dateSource = hit.dateSource || 'api' }
        if (hit.preacher && (!fromCache.preacher || fromCache.preacherSource === 'title')) {
          fromCache.preacher = hit.preacher; fromCache.preacherSource = hit.preacherSource || 'description'
        }
        enriched.push(fromCache)
        // Mirror into the current-import store so resume + processing see it.
        if (v.id) await saveVideo(fromCache)
        setProgress({ done: i+1, total: videos.length })
        continue
      }

      // ── 3. Cold path — fetch via the edge function. ──
      const res = await fetchTranscriptFor(v)
      if (res.ok) {
        const segCount = Array.isArray(res.transcriptJson) ? res.transcriptJson.length : null
        log(`✓ ${v.title || v.id} — transcript fetched${segCount != null ? ` (${segCount} segments)` : ''}`, 'ok')
        const fetched = { ...v, transcript: res.transcript, transcriptJson: res.transcriptJson, hasTranscript: true, selected: true }
        // Absolute upload date from the transcript response wins over every
        // other source (title extraction, "X weeks ago" estimates, today
        // fallback). Spec: "this should be prioritised over everything else
        // including title extraction".
        if (res.apiDate) {
          if (fetched.date !== res.apiDate) {
            log(`  ↳ date corrected to ${res.apiDate} (was ${fetched.date || 'unset'}, from "${res.apiDateField}")`, 'info')
          }
          fetched.date       = res.apiDate
          fetched.dateSource = 'api'
        } else {
          // Transcript response had no absolute date — try the dedicated
          // video-info endpoint as a second call.
          log(`  ↳ no date in transcript response — calling fetch_video_info`, 'info')
          const info = await fetchVideoInfoFor(v)
          if (info.ok && info.apiDate) {
            if (fetched.date !== info.apiDate) {
              log(`  ↳ date recovered: ${info.apiDate} (was ${fetched.date || 'unset'}, from "${info.apiDateField}")`, 'ok')
            }
            fetched.date       = info.apiDate
            fetched.dateSource = 'api'
          } else {
            log(`  ↳ fetch_video_info returned no absolute date${info.ok ? '' : `: ${info.reason}`}`, 'warn')
          }
          // Even when video_info doesn't carry a date, the description it
          // returns can still surface a preacher.
          if (info.ok && info.descPreacher && !res.descPreacher) {
            res.descPreacher = info.descPreacher
          }
        }
        // Preacher priority: description > title > transcript > null.
        // Description-derived value overrides whatever shapeVideo extracted
        // from the title at Step 1.
        if (res.descPreacher) {
          fetched.preacher       = res.descPreacher
          fetched.preacherSource = 'description'
          log(`  ↳ preacher from description: ${res.descPreacher}`, 'ok')
        } else if (fetched.preacher && fetched.preacherSource === 'title') {
          log(`  ↳ preacher from title: ${fetched.preacher}`, 'info')
        } else if (!fetched.preacher) {
          log(`  ↳ preacher not found`, 'warn')
        }
        enriched.push(fetched)
        // Incrementally persist this ONE video (IndexedDB, keyed by id) so a
        // refresh mid-fetch keeps every video that got through — no rewriting
        // the whole array, so it scales to hundreds of videos.
        if (v.id) {
          cached.set(v.id, fetched)
          await saveVideo(fetched)
          // Also write to the persistent transcript cache so a FUTURE import of
          // this same video skips the network fetch entirely.
          await saveTranscript(v.id, {
            transcript:     fetched.transcript     || '',
            transcriptJson: fetched.transcriptJson || null,
            date:           fetched.dateSource === 'api' ? fetched.date : null,
            dateSource:     fetched.dateSource === 'api' ? 'api' : null,
            preacher:       fetched.preacherSource === 'description' ? fetched.preacher : null,
            preacherSource: fetched.preacherSource === 'description' ? 'description' : null,
          })
        }
      } else {
        log(`✗ ${v.title || v.id} — ${res.reason}`, 'warn')
        enriched.push({ ...v, transcript:'', transcriptJson:null, hasTranscript:false, selected:false })
      }
      setProgress({ done: i+1, total: videos.length })
      if (i < videos.length - 1) await delay(800)
    }

    setStatus(cancelRef.current ? 'cancelled' : 'done')
    onDone(enriched)
  }

  useEffect(() => { run() /* once on mount */ // eslint-disable-next-line
  }, [])

  return (
    <div>
      <H2>2. Fetching transcripts</H2>
      <Sub>Pulling the transcript for each video. Paused or cancelled runs hand back whatever was fetched so far.</Sub>

      <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', marginBottom:14}}>
        <div style={{fontSize:13, color:C.muted, marginBottom:8}}>
          Fetching transcripts… {progress.done}/{progress.total} complete
        </div>
        <div style={{height:6, background:C.surface2, borderRadius:3, overflow:'hidden'}}>
          <div style={{
            height:'100%',
            width: progress.total ? `${Math.round(progress.done/progress.total*100)}%` : '0%',
            background:C.accent, transition:'width 200ms',
          }}/>
        </div>
      </div>

      <div style={{display:'flex', gap:8, marginBottom:14}}>
        {status === 'fetching' ? (
          <>
            <PrimaryBtn kind="ghost" onClick={() => { pausedRef.current = true; setStatus('paused') }}>Pause</PrimaryBtn>
            <PrimaryBtn kind="red"   onClick={() => { cancelRef.current = true; pausedRef.current = false }}>Cancel</PrimaryBtn>
          </>
        ) : status === 'paused' ? (
          <>
            <PrimaryBtn onClick={() => { pausedRef.current = false; setStatus('fetching') }}>Resume</PrimaryBtn>
            <PrimaryBtn kind="red" onClick={() => { cancelRef.current = true; pausedRef.current = false }}>Cancel</PrimaryBtn>
          </>
        ) : (
          <PrimaryBtn kind="ghost" onClick={onBack}>← Back</PrimaryBtn>
        )}
      </div>

      <div style={{
        background:'#08080d', border:`1px solid ${C.border}`, borderRadius:10,
        padding:'14px 16px', maxHeight:340, overflowY:'auto',
        fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, lineHeight:1.7,
      }}>
        {logs.length === 0 && <div style={{color:C.faint}}>Starting…</div>}
        {logs.map(l => (
          <div key={l.id} style={{
            color: l.kind === 'err' ? '#FF8888' : l.kind === 'warn' ? C.accent : l.kind === 'ok' ? C.green : C.muted,
            whiteSpace:'pre-wrap', wordBreak:'break-word',
          }}>{l.text}</div>
        ))}
      </div>
    </div>
  )
}

// ── STEP 4 — Review fetched video list ───────────────────────────────────
function Step4VideoList({ videos, setVideos, onBack, onNext }) {
  const [search, setSearch] = useState('')

  function patchVideo(id, patch) {
    setVideos(prev => prev.map(v => v.id === id ? { ...v, ...patch } : v))
  }
  function setAll(filterFn, selected) {
    setVideos(prev => prev.map(v => (filterFn(v) ? { ...v, selected } : v)))
  }

  const filtered = videos.filter(v => {
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return (v.title || '').toLowerCase().includes(q) || (v.series || '').toLowerCase().includes(q)
  })

  const withTranscript = videos.filter(v => v.hasTranscript).length
  const withoutTranscript = videos.length - withTranscript
  const selectedCount = videos.filter(v => v.selected).length
  const ok = selectedCount > 0

  return (
    <div>
      <H2>3. Review video list</H2>
      <Sub>{videos.length} videos · {withTranscript} with transcripts · {withoutTranscript} without transcripts</Sub>

      <div style={{display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center'}}>
        <PrimaryBtn kind="ghost" onClick={() => setAll(v => v.hasTranscript, true)}>Select all with transcripts</PrimaryBtn>
        <PrimaryBtn kind="ghost" onClick={() => setAll(v => !v.hasTranscript, false)}>Deselect all without transcripts</PrimaryBtn>
        <PrimaryBtn kind="ghost"
          disabled={withTranscript === 0}
          onClick={() => downloadTranscriptsBackup(videos)}
          title="Download a JSON backup of every video + transcript so you can restore without re-fetching from ScrapeCreators.">
          ⬇ Download transcripts backup
        </PrimaryBtn>
        <div style={{flex:1, minWidth:200}}>
          <input style={inputStyle} placeholder="Search title or series…"
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <div style={{fontSize:12, color:C.muted}}>{selectedCount} selected</div>
      </div>

      <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden'}}>
        <div style={{
          display:'grid', gridTemplateColumns:'30px 1.6fr 130px 1fr 1fr 80px 40px',
          gap:10, padding:'10px 14px', fontSize:11, fontWeight:700,
          letterSpacing:'0.08em', textTransform:'uppercase', color:C.faint,
          borderBottom:`1px solid ${C.border}`, background:C.surface2,
        }}>
          <div></div>
          <div>Title</div>
          <div>Date</div>
          <div>Series</div>
          <div>Preacher</div>
          <div>Duration</div>
          <div style={{textAlign:'center'}}>T</div>
        </div>
        <div style={{maxHeight:480, overflowY:'auto'}}>
          {filtered.map(v => {
            const dateSrc = sourceLabel(v.dateSource)
            const preacherSrc = sourceLabel(v.preacherSource)
            return (
            <div key={v.id} style={{
              borderBottom:`1px solid ${C.border}`,
            }}>
              <div style={{
                display:'grid', gridTemplateColumns:'30px 1.6fr 130px 1fr 1fr 80px 40px',
                gap:10, padding:'10px 14px', alignItems:'center', fontSize:12,
              }}>
                <input type="checkbox" checked={!!v.selected}
                  onChange={e => patchVideo(v.id, { selected: e.target.checked })}
                  style={{accentColor:C.accent, width:14, height:14}}/>
                <input style={{...inputStyle, padding:'6px 8px', fontSize:12}}
                  value={v.title || ''} onChange={e => patchVideo(v.id, { title: e.target.value })}/>
                <div style={{display:'flex', flexDirection:'column', gap:2}}>
                  <input type="date"
                    style={{...inputStyle, padding:'6px 8px', fontSize:12}}
                    value={v.date || ''}
                    onChange={e => patchVideo(v.id, { date: e.target.value, dateSource: 'manual' })}/>
                  {dateSrc && <span style={{fontSize:10, color:C.faint}}>{dateSrc}</span>}
                </div>
                <input style={{...inputStyle, padding:'6px 8px', fontSize:12}}
                  value={v.series || ''} onChange={e => patchVideo(v.id, { series: e.target.value })}/>
                <div style={{display:'flex', flexDirection:'column', gap:2}}>
                  <input style={{...inputStyle, padding:'6px 8px', fontSize:12}}
                    placeholder="(unset)"
                    value={v.preacher || ''}
                    onChange={e => patchVideo(v.id, { preacher: e.target.value, preacherSource: 'manual' })}/>
                  {preacherSrc && <span style={{fontSize:10, color:C.faint}}>{preacherSrc}</span>}
                </div>
                <div style={{color:C.muted}}>{v.durationS ? secondsToTs(v.durationS) : '—'}</div>
                <div style={{textAlign:'center', fontSize:14, color: v.hasTranscript ? C.green : '#FF8888'}}>
                  {v.hasTranscript ? '✓' : '✕'}
                </div>
              </div>
            </div>
          )})}
          {filtered.length === 0 && (
            <div style={{padding:'24px', color:C.faint, textAlign:'center', fontSize:13}}>No videos match.</div>
          )}
        </div>
      </div>

      <div style={{display:'flex', gap:10, marginTop:24}}>
        <PrimaryBtn kind="ghost" onClick={onBack}>← Back</PrimaryBtn>
        <PrimaryBtn disabled={!ok} onClick={onNext}>Configure scan →</PrimaryBtn>
      </div>
    </div>
  )
}

// ── STEP 5 — Configure scan ──────────────────────────────────────────────
function Step5ScanConfig({ videos, churches, reloadChurches, config, setConfig, onBack, onNext }) {
  const selected = videos.filter(v => v.selected)
  const effectiveCount = config.testMode ? Math.min(5, selected.length) : selected.length
  const estMin = Math.ceil(effectiveCount * 45 / 60)
  const ok = config.churchId && selected.length > 0

  // Inline "create new year" (event child) state for the event-series
  // destination sub-selector.
  const [addingYear, setAddingYear] = useState(false)
  const [newYear, setNewYear]       = useState('')
  const [newYearStart, setNewYearStart] = useState('')
  const [newYearEnd, setNewYearEnd] = useState('')
  const [yearBusy, setYearBusy]     = useState(false)
  const [yearErr, setYearErr]       = useState('')

  // Resolve the event series in play: the destination row itself when a series
  // is picked, or the parent series when one of its year events is picked (so
  // the "Attach to year" selector stays visible after choosing a year).
  const selRow = churches.find(c => c.id === config.churchId) || null
  const seriesRow = selRow?.entity_type === 'event_series'
    ? selRow
    : (selRow?.parent_id
        ? churches.find(c => c.id === selRow.parent_id && c.entity_type === 'event_series') || null
        : null)
  const networkRow = selRow?.entity_type === 'network'
    ? selRow
    : (selRow?.parent_id
        ? churches.find(c => c.id === selRow.parent_id && c.entity_type === 'network') || null
        : null)

  async function createYear() {
    if (!seriesRow) return
    const yr = newYear.trim()
    if (!yr) { setYearErr('Enter a year.'); return }
    setYearBusy(true); setYearErr('')
    const { data, error } = await resilientChurchInsert({
      name: `${seriesRow.name} ${yr}`,
      slug: `${churchNameToSlug(`${seriesRow.name} ${yr}`)}-${Math.random().toString(36).slice(2,6)}`,
      entity_type: 'event',
      parent_id: seriesRow.id,
      event_start_date: newYearStart || (/^\d{4}$/.test(yr) ? `${yr}-01-01` : null),
      event_end_date: newYearEnd || null,
      setup_complete: true, invite_only: false, active: true,
    })
    setYearBusy(false)
    if (error) { setYearErr(error.message || 'Could not create year.'); return }
    await reloadChurches?.()
    if (data?.id) setConfig(c => ({ ...c, churchId: data.id, childChurchId: data.id }))
    setAddingYear(false); setNewYear(''); setNewYearStart(''); setNewYearEnd('')
  }

  return (
    <div>
      <H2>4. Configure scan</H2>
      <Sub>{selected.length} videos selected. Claude scans the whole transcript of each video for Bible references.</Sub>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, maxWidth:680, marginBottom:14}}>
        <Field label="Destination source">
          <select style={inputStyle} value={config.churchId}
            onChange={e => setConfig(c => ({...c, churchId: e.target.value}))}>
            <option value="">— Select a source —</option>
            {(() => {
              // Group by entity_type (Part 6E). Sermons still attach to a real
              // church_id row (events/series are churches rows too). Falls back
              // to a flat list when entity_type isn't present (pre-migration).
              const hasTypes = churches.some(c => c.entity_type)
              if (!hasTypes) return churches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
              const groups = [
                { key:'church',       label:'CHURCHES' },
                { key:'event',        label:'EVENTS' },
                { key:'event_series', label:'EVENT SERIES' },
                { key:'network',      label:'NETWORKS' },
                { key:'ministry',     label:'MINISTRIES' },
              ]
              const byType = t => churches.filter(c => (c.entity_type || 'church') === t)
              return groups.map(g => {
                const rows = byType(g.key)
                if (!rows.length) return null
                return (
                  <optgroup key={g.key} label={g.label}>
                    {rows.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                )
              })
            })()}
          </select>
          {/* When an event series is the destination, offer its year children
              plus a "Create new year…" inline form that adds an event child. */}
          {seriesRow && (
            <div style={{marginTop:8}}>
              <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.faint, marginBottom:6}}>Attach to year</div>
              <select style={inputStyle}
                value={addingYear ? '__new__' : (config.childChurchId || '')}
                onChange={e => {
                  const v = e.target.value
                  if (v === '__new__') { setAddingYear(true); return }
                  setAddingYear(false)
                  setConfig(c => ({...c, churchId: v || seriesRow.id, childChurchId: v}))
                }}>
                <option value="">Series (all years)</option>
                {churches.filter(c => c.parent_id === seriesRow.id).map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                <option value="__new__">+ Create new year…</option>
              </select>
              {addingYear && (
                <div style={{marginTop:8, display:'flex', flexDirection:'column', gap:6, background:C.surface2, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px'}}>
                  <input style={{...inputStyle, padding:'8px 10px', fontSize:12}} placeholder="Year e.g. 2025" value={newYear} onChange={e=>setNewYear(e.target.value)}/>
                  <div style={{display:'flex', gap:6}}>
                    <input type="date" style={{...inputStyle, padding:'8px 10px', fontSize:12}} value={newYearStart} onChange={e=>setNewYearStart(e.target.value)}/>
                    <input type="date" style={{...inputStyle, padding:'8px 10px', fontSize:12}} value={newYearEnd} onChange={e=>setNewYearEnd(e.target.value)}/>
                  </div>
                  <div style={{fontSize:10, color:C.faint}}>Names to “{seriesRow.name} {newYear || '{year}'}”. Start/end optional (defaults to Jan 1 of the year).</div>
                  {yearErr && <div style={{fontSize:11, color:'#FF8888'}}>{yearErr}</div>}
                  <div style={{display:'flex', gap:8}}>
                    <button type="button" disabled={yearBusy} onClick={createYear}
                      style={{background:C.accent, color:C.navy, border:'none', borderRadius:6, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONT, opacity:yearBusy?0.5:1}}>{yearBusy ? 'Creating…' : 'Create year'}</button>
                    <button type="button" onClick={()=>{ setAddingYear(false); setYearErr('') }}
                      style={{background:'none', border:'none', color:C.muted, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONT}}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* When a network is the destination, attach to a specific member. */}
          {networkRow && !seriesRow && (
            <div style={{marginTop:8}}>
              <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.faint, marginBottom:6}}>Attach to member</div>
              {churches.filter(c => c.parent_id === networkRow.id).length === 0 ? (
                <div style={{fontSize:12, color:C.muted}}>No members yet — add churches to this network in Sources, or attach to the network directly.</div>
              ) : (
                <select style={inputStyle} value={config.childChurchId || ''}
                  onChange={e => setConfig(c => ({...c, churchId: e.target.value || networkRow.id, childChurchId: e.target.value}))}>
                  <option value="">Network (no specific member)</option>
                  {churches.filter(c => c.parent_id === networkRow.id).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              )}
            </div>
          )}
        </Field>
        <Field label="Default preacher (optional)">
          <input style={inputStyle} placeholder="Used when Claude can't suggest one"
            value={config.defaultPreacher}
            onChange={e => setConfig(c => ({...c, defaultPreacher: e.target.value}))}/>
        </Field>
      </div>

      {/* Scan mode — three options:
          • whole  (default, all churches)
          • estimate (NLC two-pass gap detection)
          • manual (user enters Sermon start / end on each video)
          Stored in config.scanMode and persisted to localStorage so the
          choice carries between sessions. */}
      <Field label="Scan mode">
        <div style={{
          background:C.surface2, border:`1px solid ${C.border}`,
          borderRadius:10, padding:'4px', maxWidth:560,
        }}>
          {[
            {
              id: 'whole',
              title: 'Scan whole video',
              desc: 'Scans the full transcript for Bible references. Works for all churches.',
              extra: null,
            },
            {
              id: 'estimate',
              title: 'Predict sermon start/end',
              desc: 'Uses two-pass gap detection to find the sermon segment before scanning. Only recommended for New Life Church Tunbridge Wells:',
              extra: (
                <ul style={{margin:'6px 0 0 18px', padding:0, color:C.muted, lineHeight:1.5}}>
                  <li>Long broadcast pre-roll (ignored)</li>
                  <li>Children dismissed with 3 minute gap after</li>
                  <li>Sermon starts after the gap</li>
                  <li>Sermon ends when worship resumes</li>
                </ul>
              ),
            },
            {
              id: 'manual',
              title: 'Choose start time',
              desc: 'Enter one sermon start timestamp below — it’s applied to every selected video. Claude scans from that point to the end of each video.',
              extra: null,
            },
          ].map(opt => {
            const checked = config.scanMode === opt.id
            return (
              <button key={opt.id} type="button"
                onClick={() => setConfig(c => ({ ...c, scanMode: opt.id }))}
                aria-pressed={checked}
                style={{
                  display:'flex', alignItems:'flex-start', gap:10,
                  width:'100%', textAlign:'left',
                  padding:'12px 14px', borderRadius:8,
                  background: checked ? 'rgba(232,200,122,0.08)' : 'transparent',
                  border:'none', cursor:'pointer', fontFamily:FONT,
                  color:C.text,
                  marginBottom: opt.id !== 'manual' ? 2 : 0,
                }}>
                <div style={{
                  flexShrink:0, width:14, height:14, borderRadius:'50%',
                  border:`2px solid ${checked ? C.accent : C.borderHi}`,
                  background: checked ? C.accent : 'transparent',
                  marginTop:3,
                }}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:700, color:C.text, marginBottom:2}}>{opt.title}</div>
                  <div style={{fontSize:12, color:C.muted, lineHeight:1.5}}>
                    {opt.desc}
                    {opt.extra}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </Field>

      {config.scanMode === 'estimate' && (
        <Note kind="warn">
          Best results for NLC full service recordings. May not work for other churches.
        </Note>
      )}
      {config.scanMode === 'manual' && (
        <div style={{
          marginTop:10, marginBottom:14, maxWidth:560,
          padding:'12px 14px', background:C.surface2,
          border:`1px solid ${C.border}`, borderRadius:10,
          display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
        }}>
          <label style={{display:'flex', alignItems:'center', gap:10}}>
            <span style={{fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:C.faint}}>Sermon start time</span>
            <input
              placeholder="MM:SS"
              value={config.manualStartTs || ''}
              onChange={e => setConfig(c => ({ ...c, manualStartTs: e.target.value }))}
              style={{...inputStyle, padding:'8px 10px', fontSize:13, width:110, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace'}}/>
          </label>
          <span style={{fontSize:12, color:C.muted, lineHeight:1.5, flex:1, minWidth:200}}>
            {config.manualStartTs?.trim()
              ? `Claude scans every selected video from ${config.manualStartTs} to the end.`
              : 'Applied to every selected video. Leave blank to scan the whole video.'}
          </span>
        </div>
      )}

      <Field label="Test mode">
        <button type="button"
          onClick={() => setConfig(c => ({...c, testMode: !c.testMode}))}
          style={{
            display:'flex', alignItems:'center', gap:10,
            width:'100%', maxWidth:340, padding:'10px 12px', borderRadius:8,
            background: C.surface2,
            border: `1px solid ${config.testMode ? C.accent : C.borderHi}`,
            cursor:'pointer', fontFamily:FONT, color:C.text,
          }}>
          <span style={{
            width:34, height:20, borderRadius:10, flexShrink:0,
            background: config.testMode ? C.accent : C.borderHi,
            position:'relative',
          }}>
            <span style={{
              position:'absolute', top:2, left: config.testMode ? 16 : 2,
              width:16, height:16, borderRadius:'50%', background:'#fff',
              transition:'left 120ms',
            }}/>
          </span>
          <span style={{fontSize:13, fontWeight:600}}>
            {config.testMode ? 'On — first 5 only' : 'Off — process all'}
          </span>
        </button>
      </Field>

      {config.testMode && (
        <Note kind="warn">Test mode — processing first 5 videos only.</Note>
      )}

      <div style={{display:'flex', gap:10, marginTop:24, alignItems:'center'}}>
        <PrimaryBtn kind="ghost" onClick={onBack}>← Back</PrimaryBtn>
        <PrimaryBtn disabled={!ok} onClick={onNext}>Start processing →</PrimaryBtn>
        <div style={{flex:1}}/>
        <div style={{fontSize:12, color:C.muted}}>Estimated time: ~{estMin} min for {effectiveCount} video{effectiveCount===1?'':'s'}</div>
      </div>
    </div>
  )
}

// ── NLC SERMON SEGMENTATION ─────────────────────────────────────────────
// Pass 1 (this helper): pure-JS gap detection over the ScrapeCreators
// transcript segments. Designed for New Life Church Tunbridge Wells
// services where:
//   • The first ~15 min is broadcast pre-roll / worship
//   • Children get dismissed to their groups before the sermon
//   • There's a 1-3 minute pause while kids leave and people greet
//   • The preacher then walks up and starts
// Returns:
//   { sermon_start_ms, dismissal_ms, kid_marker_ms, gap_type, debug }
// `sermon_start_ms` is null when no recognisable gap is found — caller
// should mark the video as full-service.
function detectSermonStartFromSegments(segments) {
  if (!Array.isArray(segments) || segments.length === 0) {
    return { sermon_start_ms: null, debug: 'No segments to analyse' }
  }
  // Normalise to { ms, text } pairs. ScrapeCreators uses startMs (number
  // or numeric string); fall through to start_ms and seconds-based start.
  const norm = []
  for (const seg of segments) {
    let ms = null
    if (seg?.startMs != null) {
      const n = typeof seg.startMs === 'number' ? seg.startMs : parseInt(seg.startMs, 10)
      if (Number.isFinite(n)) ms = n
    }
    if (ms == null && seg?.start_ms != null) {
      const n = typeof seg.start_ms === 'number' ? seg.start_ms : parseInt(seg.start_ms, 10)
      if (Number.isFinite(n)) ms = n
    }
    if (ms == null && seg?.start != null) {
      const n = typeof seg.start === 'number' ? seg.start : parseFloat(seg.start)
      if (Number.isFinite(n)) ms = Math.round(n * 1000)
    }
    if (ms == null) continue
    norm.push({ ms, text: String(seg?.text || '').toLowerCase() })
  }
  if (norm.length === 0) {
    return { sermon_start_ms: null, debug: 'No usable segment timestamps' }
  }
  norm.sort((a, b) => a.ms - b.ms)

  const PRE_ROLL_MS  = 15 * 60 * 1000     // ignore first 15 min
  const FIVE_MIN_MS  = 5  * 60 * 1000
  const GAP_3MIN_MS  = 3  * 60 * 1000
  const GAP_90SEC_MS = 90 * 1000

  // Step 2 — LAST occurrence of any children-dismissal word past 15 min.
  // `\bkids?\b` matches "kid"/"kids"; `cre[csç]he` covers UK/AU spellings.
  const KID_RE = /\b(?:kids?|children|youth|cre[csç]he|juniors?)\b/i
  let lastKidMs = null
  for (const seg of norm) {
    if (seg.ms < PRE_ROLL_MS) continue
    if (KID_RE.test(seg.text)) lastKidMs = seg.ms
  }
  if (lastKidMs == null) {
    return { sermon_start_ms: null, debug: 'No children-dismissal marker found after 15 min' }
  }

  // Within 5 min after the kid marker, look for greeting markers — these
  // come right before the sermon starts in NLC services.
  const GREET_RE = /\b(?:greet|turn to|say hello|introduce yourself|say hi|person next to you|someone near you)\b/i
  let greetMs = null
  for (const seg of norm) {
    if (seg.ms < lastKidMs) continue
    if (seg.ms > lastKidMs + FIVE_MIN_MS) break
    if (GREET_RE.test(seg.text)) { greetMs = seg.ms; break }
  }
  const dismissalMs = greetMs ?? lastKidMs

  // Step 3 — first inter-segment gap ≥3 min after dismissal_ms.
  let sermonStartMs = null
  let gapAtMs       = null
  let gapType       = null
  for (let i = 0; i < norm.length - 1; i++) {
    if (norm[i].ms < dismissalMs) continue
    const gap = norm[i + 1].ms - norm[i].ms
    if (gap >= GAP_3MIN_MS) {
      gapAtMs       = norm[i].ms
      sermonStartMs = norm[i + 1].ms
      gapType       = '3min'
      break
    }
  }
  // Fallback: 90-sec gap if nothing ≥3 min was found.
  if (sermonStartMs == null) {
    for (let i = 0; i < norm.length - 1; i++) {
      if (norm[i].ms < dismissalMs) continue
      const gap = norm[i + 1].ms - norm[i].ms
      if (gap >= GAP_90SEC_MS) {
        gapAtMs       = norm[i].ms
        sermonStartMs = norm[i + 1].ms
        gapType       = '90sec-fallback'
        break
      }
    }
  }

  const fmt = ms => secondsToTs(ms / 1000)
  if (sermonStartMs == null) {
    return {
      sermon_start_ms: null,
      dismissal_ms:    dismissalMs,
      kid_marker_ms:   lastKidMs,
      debug: `Dismissal at ${fmt(dismissalMs)} but no gap found after — needs manual review`,
    }
  }
  return {
    sermon_start_ms: sermonStartMs,
    dismissal_ms:    dismissalMs,
    kid_marker_ms:   lastKidMs,
    gap_at_ms:       gapAtMs,
    gap_type:        gapType,
    debug: `Dismissal at ${fmt(dismissalMs)} · ${gapType} gap at ${fmt(gapAtMs)} · sermon starts at ${fmt(sermonStartMs)}`,
  }
}

// Pass 2: send the segments from sermon_start_ms onwards to Claude with an
// NLC-tailored prompt that asks where the sermon ends + extracts title /
// preacher / passage / confidence. Returns the parsed JSON object directly,
// or throws on hard errors. Caller applies the 45-min fallback when
// sermon_end_ms comes back null.
const SERMON_END_SYSTEM_PROMPT = `You are analysing the end portion of a church sermon transcript from New Life Church Tunbridge Wells.

The sermon has already started at the beginning of this transcript.
Find exactly where the sermon ENDS and worship music resumes.

The sermon ends when you see ANY of these signals:
- 'can the band come up' / 'as the band comes' / 'band coming up'
- A closing prayer starting with 'Father God' / 'Lord Jesus' / 'Heavenly Father' / 'Let us pray' / 'Let me pray'
- Worship song lyrics beginning to repeat (same short phrases repeated: 'hallelujah', 'worthy', 'holy')
- 'amen' followed immediately by song-like repeated text
- An invitation to respond: 'come forward' / 'hands up' / 'ministry time' / 'if you want to give your life'

The sermon typically lasts 30-45 minutes.
Do NOT return the end of the video as the sermon end time unless the sermon genuinely runs to the very end with no worship after it.

Also detect:
- Preacher name: look for introduction in first 5 minutes with phrases like 'please welcome', 'hand over to', 'will be preaching', or someone introducing themselves
- Main Bible passage: primary scripture stated in first 10 minutes
- Sermon title: explicit title stated by preacher, or short 4-word max title derived from passage and opening theme
- Confidence: high / medium / low

Return ONLY this JSON with no markdown:
{
  "sermon_end_ms": 0,
  "sermon_end_time": "00:00",
  "title_suggestion": "",
  "main_passage": "",
  "preacher": "",
  "confidence": "high"
}

If sermon end cannot be identified return:
{"sermon_end_ms": null, "title_suggestion": "", "main_passage": "", "preacher": "", "confidence": "low"}`

async function detectSermonEndWithClaude(sermonStartMs, segments) {
  // Slice + shift segment timestamps so Claude sees [MM:SS] relative to
  // SERMON start — that's what the prompt promises. We re-add
  // sermonStartMs when interpreting the response back to absolute ms.
  const slice = []
  for (const seg of (segments || [])) {
    const raw = seg?.startMs ?? seg?.start_ms ?? (seg?.start != null ? seg.start * 1000 : null)
    const ms  = typeof raw === 'number' ? raw : parseInt(raw, 10)
    if (!Number.isFinite(ms) || ms < sermonStartMs) continue
    slice.push({ startMs: ms - sermonStartMs, text: seg.text })
  }
  if (slice.length === 0) {
    return { sermon_end_ms: null, title_suggestion: '', main_passage: '', preacher: '', confidence: 'low' }
  }
  const transcript = formatTimestampedTranscript(slice)
  const { data, error } = await supabase.functions.invoke('claude-proxy', {
    body: {
      model:      'claude-sonnet-4-6',
      max_tokens: 1500,
      system:     SERMON_END_SYSTEM_PROMPT,
      messages: [{ role: 'user', content: `TRANSCRIPT FROM SERMON START:\n${transcript}` }],
    },
  })
  if (error) {
    const upstream = data?.error?.message || data?.message || error.message
    throw new Error(upstream || 'claude-proxy failed')
  }
  if (data?.error) throw new Error(data.error.message || JSON.stringify(data.error))
  const text = data?.content?.[0]?.text || ''
  if (!text) throw new Error('Empty response from Claude')
  const clean = text.replace(/^```[a-z]*\n?/i, '').replace(/\n?```$/i, '').trim()
  let parsed
  try { parsed = JSON.parse(clean) }
  catch (_e) { throw new Error(`Sermon-end response was not JSON: ${clean.slice(0, 200)}`) }

  // Translate the relative-to-sermon-start ms back to absolute video ms.
  if (typeof parsed.sermon_end_ms === 'number' && parsed.sermon_end_ms > 0) {
    parsed.sermon_end_ms = sermonStartMs + parsed.sermon_end_ms
  } else {
    parsed.sermon_end_ms = null
  }
  return {
    sermon_end_ms:    parsed.sermon_end_ms,
    title_suggestion: String(parsed.title_suggestion || '').trim(),
    main_passage:     String(parsed.main_passage     || '').trim(),
    preacher:         String(parsed.preacher         || '').trim(),
    confidence:       ['high','medium','low'].includes(parsed.confidence) ? parsed.confidence : 'low',
  }
}

// ── STEP 6 — process ─────────────────────────────────────────────────────
// ── localStorage layout (split to dodge the per-key 5 MB quota) ──────────
// Three keys, written and read independently:
//   adminImportVideos   — full videos array with metadata + transcriptJson.
//                         The single source of truth for transcripts.
//   adminImportResults  — processed[] with transcripts stripped. Sermon
//                         records reference videos by youtube_id; transcripts
//                         get re-attached from adminImportVideos at load.
//   adminImportProgress — small: step, churchId, settings, savedAt.
const PROGRESS_KEY  = 'adminImportProgress'
const VIDEOS_KEY    = 'adminImportVideos'
const RESULTS_KEY   = 'adminImportResults'

const PER_VIDEO_SEC   = 10      // UI time estimate — one Claude scan + 5s gap
const DELAY_VIDEO_MS  = 5000    // between videos — 5s is plenty for Anthropic rate limits

// ── IndexedDB store for bulk-import data ─────────────────────────────────
// localStorage (~5 MB total) is SHARED with the Supabase auth session, so a big
// import (hundreds of transcripts) filled it and silently signed the admin out.
// IndexedDB holds hundreds of MB, is separate from the session store, and lets
// us write ONE video per transcript fetch instead of rewriting the whole array.
// Videos are keyed by id in their own store; results live in a small kv store.
// Progress (tiny: step/churchId/flags) stays in localStorage.
const IDB_NAME = 'nlc_admin_import'
const IDB_VERSION = 1
let _idbPromise = null
function openIdb() {
  if (_idbPromise) return _idbPromise
  _idbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no indexedDB')); return }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('videos')) db.createObjectStore('videos', { keyPath: 'id' })
      if (!db.objectStoreNames.contains('kv'))     db.createObjectStore('kv')
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror   = () => reject(req.error)
  })
  return _idbPromise
}
// Run a transaction body and resolve once it commits. Errors are swallowed to a
// safe default so a storage hiccup never breaks the import (it just isn't
// persisted that tick).
function _idbRun(store, mode, body, fallback) {
  return openIdb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode)
    const result = body(tx.objectStore(store))
    tx.oncomplete = () => resolve(result && result.__req ? result.__req.result : result)
    tx.onerror    = () => reject(tx.error)
    tx.onabort    = () => reject(tx.error)
  })).catch((e) => {
    // eslint-disable-next-line no-console
    console.warn(`[admin] idb ${store}/${mode} failed:`, e?.message)
    return fallback
  })
}

// ── Videos store (one record per video, keyed by id) ─────────────────────
function loadVideos() {
  return _idbRun('videos', 'readonly', (os) => ({ __req: os.getAll() }), [])
    .then(async (vids) => {
      if (Array.isArray(vids) && vids.length) return vids
      // One-time migration from the old localStorage blob, if present.
      try {
        const old = JSON.parse(localStorage.getItem(VIDEOS_KEY) || '[]')
        if (Array.isArray(old) && old.length) {
          await saveVideos(old)
          try { localStorage.removeItem(VIDEOS_KEY) } catch (_e) { /* ignore */ }
          return old
        }
      } catch (_e) { /* ignore */ }
      return Array.isArray(vids) ? vids : []
    })
}
// Persist a single video (incremental — used after each transcript fetch).
function saveVideo(video) {
  if (!video || !video.id) return Promise.resolve()
  return _idbRun('videos', 'readwrite', (os) => { os.put(video) })
}
// Persist many videos at once (initial list from Step 1, final sync, backup).
function saveVideos(videos) {
  const arr = (videos || []).filter(v => v && v.id)
  if (!arr.length) return Promise.resolve()
  return _idbRun('videos', 'readwrite', (os) => { for (const v of arr) os.put(v) })
}
function clearVideos() {
  return _idbRun('videos', 'readwrite', (os) => { os.clear() })
}

// ── Discovery store (stage 1: listing videos from playlists) ─────────────
// Persisted per-playlist so closing mid-discovery keeps what's been found and
// the list of playlists still to do, letting discovery resume.
function saveDiscovery(videos, pending) {
  return _idbRun('kv', 'readwrite', (os) => { os.put({ videos: videos || [], pending: pending || [] }, 'discovery') })
}
function loadDiscovery() {
  return _idbRun('kv', 'readonly', (os) => ({ __req: os.get('discovery') }), null)
}
function clearDiscovery() {
  return _idbRun('kv', 'readwrite', (os) => { os.delete('discovery') })
}

// ── Persistent transcript cache (survives across imports) ────────────────
// Keyed by video id under `t:<id>` in the kv store. NOT cleared on a fresh
// import or "Start fresh", so a video whose transcript was fetched in any prior
// import is skipped rather than re-downloaded (saves ScrapeCreators credits +
// time on a re-run or overlapping playlists).
function saveTranscript(id, data) {
  if (!id) return Promise.resolve()
  return _idbRun('kv', 'readwrite', (os) => { os.put(data, `t:${id}`) })
}
function getTranscript(id) {
  if (!id) return Promise.resolve(null)
  return _idbRun('kv', 'readonly', (os) => ({ __req: os.get(`t:${id}`) }), null)
}

// ── Keep the auth session alive across a long import ─────────────────────
// A 300+ video transcript-fetch or scan runs well past Supabase's ~1h
// access-token lifetime. If the background auto-refresh timer misses (e.g. a
// backgrounded tab) the token expires and the user is signed out mid-run —
// exactly the "logged out at 304/326" symptom. Proactively refresh the session
// every ~10 minutes of wall-clock as belt-and-braces. Best-effort: a transient
// failure must never abort the import (the resume flow covers a real sign-out).
let _lastAuthKeepAlive = 0
async function keepSessionAlive() {
  try {
    const now = Date.now()
    if (now - _lastAuthKeepAlive < 10 * 60 * 1000) return
    _lastAuthKeepAlive = now
    await supabase.auth.refreshSession()
  } catch (_e) { /* ignore — don't let a refresh hiccup stop the run */ }
}

// ── Results store (transcripts stripped) ─────────────────────────────────
// Each processed entry's `row.transcript`, `row.transcriptJson`, and every
// sermon's `transcriptSlice` are dropped before write. They're reattached
// on load via rehydrateResults (joined by row.youtubeId against the videos
// store). Keeps the results blob small even after 200 videos.
function slimResults(results) {
  return (results || []).map(p => {
    const row = p.row || {}
    // eslint-disable-next-line no-unused-vars
    const { transcript: _t, transcriptJson: _tj, ...slimRow } = row
    const sermons = (p.sermons || []).map(s => {
      // eslint-disable-next-line no-unused-vars
      const { transcriptSlice: _ts, ...slimS } = s
      return slimS
    })
    return { ...p, row: slimRow, sermons }
  })
}
function loadResults() {
  return _idbRun('kv', 'readonly', (os) => ({ __req: os.get('results') }), null)
    .then((val) => {
      if (Array.isArray(val)) return val
      // One-time migration from the old localStorage blob.
      try {
        const old = JSON.parse(localStorage.getItem(RESULTS_KEY) || '[]')
        if (Array.isArray(old) && old.length) {
          saveResults(old)
          try { localStorage.removeItem(RESULTS_KEY) } catch (_e) { /* ignore */ }
          return old
        }
      } catch (_e) { /* ignore */ }
      return []
    })
}
function saveResults(results) {
  return _idbRun('kv', 'readwrite', (os) => { os.put(slimResults(results), 'results') })
}
// Re-attach transcripts to slim processed[] using the videos store.
// Sermons inserted via Supabase (Step 7) need transcriptSlice populated;
// the review UI also reads transcripts indirectly via row fields.
function rehydrateResults(slimResults, videos) {
  if (!Array.isArray(slimResults)) return []
  const byId = new Map((videos || []).map(v => [v.id, v]))
  return slimResults.map(p => {
    const id = p.row?.youtubeId
    const v  = id ? byId.get(id) : null
    const transcript     = v?.transcript     || ''
    const transcriptJson = v?.transcriptJson || null
    const sermons = (p.sermons || []).map(s => ({ ...s, transcriptSlice: transcript }))
    return {
      ...p,
      row:     { ...p.row, transcript, transcriptJson },
      sermons,
    }
  })
}

// ── Progress store (tiny) ────────────────────────────────────────────────
function loadProgress() {
  try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || 'null') }
  catch { return null }
}
function saveProgress(state) {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify({
      savedAt: new Date().toISOString(),
      ...state,
    }))
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[admin] could not persist progress:', e.message)
  }
}

function clearAllProgress() {
  // Tiny progress marker lives in localStorage; the bulk data lives in IDB.
  try { localStorage.removeItem(PROGRESS_KEY) } catch (e) { /* ignore */ }
  // Legacy localStorage keys from before the IDB migration.
  try { localStorage.removeItem(VIDEOS_KEY)   } catch (e) { /* ignore */ }
  try { localStorage.removeItem(RESULTS_KEY)  } catch (e) { /* ignore */ }
  try { localStorage.removeItem('adminTranscriptCache') } catch (e) { /* ignore */ }
  // Wipe the IDB stores (fire and forget).
  clearVideos()
  clearDiscovery()
  _idbRun('kv', 'readwrite', (os) => { os.delete('results') })
}

// Build a downloadable JSON backup of every video + transcript. Used so
// the user can keep a copy off-machine and skip the ScrapeCreators round
// trip if localStorage is ever wiped (or they move computers).
function downloadTranscriptsBackup(videos) {
  const today = new Date().toISOString().slice(0, 10)
  const payload = {
    version:    1,
    exportedAt: new Date().toISOString(),
    videoCount: Array.isArray(videos) ? videos.length : 0,
    videos:     Array.isArray(videos) ? videos : [],
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url
  a.download = `transcripts-backup-${today}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Read a previously-downloaded backup file and return its videos array.
// Throws on malformed JSON or a missing/empty videos field.
async function parseTranscriptsBackup(file) {
  const text = await file.text()
  let payload
  try { payload = JSON.parse(text) }
  catch (e) { throw new Error('That file isn\'t valid JSON.') }
  const videos = Array.isArray(payload?.videos) ? payload.videos
               : Array.isArray(payload)         ? payload     // tolerate raw array
               : null
  if (!videos)            throw new Error('No videos array found in the backup file.')
  if (videos.length === 0) throw new Error('Backup file contains zero videos.')
  // Normalise — fill in flags downstream code expects.
  return videos.map(v => ({
    ...v,
    hasTranscript: !!(v.transcript || v.transcriptJson),
    selected:      v.selected !== false,    // default true unless explicitly false
  }))
}

function fmtMinutes(totalSec) {
  if (totalSec <= 0) return 'less than 1 min'
  const m = Math.ceil(totalSec / 60)
  return m === 1 ? '1 min' : `${m} min`
}

function Step6Process({ videos, config, user, onBack, onDone, seedResults = [] }) {
  // Adapt the new video record shape to the existing row-shaped pipeline:
  // - video.id (youtube id) → row.youtubeId
  // - video.youtubeUrl                 → row.youtubeUrl
  // - rest of fields carry over directly
  const candidateRows = (videos || [])
    .filter(v => v.selected && (v.hasTranscript || v.transcript || v.transcriptJson))
    .map((v, i) => ({
      rowIndex:       i,
      title:          v.title || '',
      series:         v.series || '',
      playlists:      Array.isArray(v.playlists) ? v.playlists : [],
      date:           v.date || null,
      preacher:       v.preacher || '',
      durationS:      v.durationS || null,
      youtubeId:      v.id,
      youtubeUrl:     v.youtubeUrl || (v.id ? `https://www.youtube.com/watch?v=${v.id}` : ''),
      transcript:     v.transcript || '',
      transcriptJson: v.transcriptJson || null,
    }))
  const effectiveRows = config.testMode ? candidateRows.slice(0, 5) : candidateRows
  const [status, setStatus] = useState('idle')     // idle | running | paused | cancelled | done | blocked
  const [logs, setLogs] = useState([])
  const [results, setResults] = useState([])      // accumulated
  const [progress, setProgress] = useState({ done:0, total:effectiveRows.length })

  // ── Free-scan paywall ──
  const { profile: meProfile, churches: allChurches } = useAuth()
  const gateChurch = (allChurches || []).find(c => c.id === config.churchId) || null
  const [gate,        setGate]        = useState(null)   // { remaining, message } when blocked
  const [scanCap,     setScanCap]     = useState(null)   // cap the run to N videos
  const [showUpgrade, setShowUpgrade] = useState(false)

  const pausedRef = useRef(false)
  const cancelRef = useRef(false)

  function log(text, kind='info') {
    setLogs(l => [...l, { id: Date.now() + Math.random(), text, kind, at: new Date() }])
  }

  async function waitWhilePaused() {
    while (pausedRef.current && !cancelRef.current) await delay(120)
  }

  // Persist accumulated results so a tab close / refresh doesn't lose work.
  // `saveResults` slims each entry (drops transcriptSlice + row.transcript +
  // row.transcriptJson — those live in the videos store) so the results
  // blob stays small even after 200 videos. We also re-save the source
  // videos array as a defensive sync (its contents shouldn't have changed
  // since Step 2 but writing again is cheap and keeps the two stores in
  // step with each other).
  // `done` flag: scanning still in progress (step 4 → resume continues the
  // scan) vs finished (step 5 → resume lands on review). Mid-scan saves keep
  // step 4 so an interrupted scan auto-continues from where it stopped.
  function persistResults(acc, { done = false } = {}) {
    saveResults(acc)
    saveVideos(videos)
    saveProgress({
      step:            done ? 5 : 4,
      churchId:        config.churchId,
      defaultPreacher: config.defaultPreacher,
      testMode:        config.testMode,
    })
  }

  async function processOne(row, existingYtIds) {
    const out = {
      row,
      videoStatus: 'pending',  // pending | segmented | skipped | failed
      reason: null,
      sermons: [],
    }

    // Resume guard — skip videos already imported under this church.
    if (row.youtubeId && existingYtIds.has(row.youtubeId)) {
      out.videoStatus = 'skipped'; out.reason = 'Already imported'
      log(`↷ Skipped — already imported: ${row.title || row.youtubeId}`, 'debug')
      return out
    }
    if (!row.transcriptJson && !row.transcript) {
      out.videoStatus = 'skipped'; out.reason = 'No transcript supplied'
      return out
    }

    await waitWhilePaused()
    if (cancelRef.current) { out.videoStatus = 'failed'; out.reason = 'Cancelled'; return out }
    log(`▸ Processing: ${row.title || row.youtubeId || `row ${row.rowIndex+1}`}`, 'info')

    // ── Pre-call diagnostics — what are we actually sending? ──────────
    const transcriptText = row.transcript || ''
    const segCount       = Array.isArray(row.transcriptJson) ? row.transcriptJson.length : 0
    const sermonMeta     = { title: row.title || '', series: row.series || '', date: row.date || '' }
    log(`   ⌁ transcript: ${transcriptText.length.toLocaleString()} chars · ${segCount} timestamped segment${segCount === 1 ? '' : 's'}`, 'debug')
    if (transcriptText) {
      log(`   ⌁ first 100 chars: "${transcriptText.slice(0, 100).replace(/\n/g, ' ')}…"`, 'debug')
    } else if (segCount > 0) {
      const firstSeg = row.transcriptJson[0]
      log(`   ⌁ no plain text — first segment: "${String(firstSeg?.text || '').slice(0, 100)}…"`, 'debug')
    }
    log(`   ⌁ sermonMeta: ${JSON.stringify(sermonMeta)}`, 'debug')

    // Empty-input guard — both fields blank means we'd send Claude an
    // empty transcript, which the proxy usually rejects. Skip cleanly.
    if (!transcriptText && segCount === 0) {
      log(`   ⚠ Skipped — no captions on YouTube for this video`, 'warn')
      out.videoStatus = 'failed'
      out.reason = 'No captions on YouTube — can’t auto-scan'
      return out
    }

    // ── Manual start mode — single config.manualStartTs applied to
    // every video in the batch. We slice the transcript from that
    // point to the end of each video and scan only the slice. An empty
    // start falls through to the whole-video path so toggling the
    // mode without entering a time still works.
    const manualStart = String(config.manualStartTs || '').trim()
    if (config.scanMode === 'manual' && manualStart) {
      const startS  = tsToSeconds(manualStart)
      const totalMs = row.durationS ? row.durationS * 1000 : null
      const startMs = startS != null ? startS * 1000 : 0
      const endMs   = totalMs || (startMs + 60 * 60 * 1000)  // 1-hr cap when duration unknown
      if (totalMs && startMs >= totalMs) {
        log(`   ✕ Sermon start ${manualStart} is at or past the video end (${secondsToTs(totalMs/1000)})`, 'err')
        out.videoStatus = 'failed'
        out.reason      = 'Sermon start past video end'
        return out
      }
      log(`   ⌁ Mode: manual start ${secondsToTs(startMs/1000)}→end of video`, 'info')

      const sermonSegments = (row.transcriptJson || []).filter(seg => {
        const ms = seg?.startMs ?? seg?.start_ms ?? (typeof seg?.start === 'number' ? seg.start * 1000 : null)
        const n  = typeof ms === 'number' ? ms : parseInt(ms, 10)
        if (!Number.isFinite(n)) return false
        return n >= startMs && n <= endMs
      })
      const sermonTranscriptText = formatTimestampedTranscript(sermonSegments) || row.transcript || ''
      let refs = []
      try {
        refs = await scanTranscript(sermonTranscriptText, sermonMeta, sermonSegments, {
          onLog: (msg, kind) => log(`   ${msg}`, kind || 'info'),
        })
        if (!Array.isArray(refs)) refs = []
        if (refs.nonJson) {
          refs = []
        } else {
          log(`   ✓ ${refs.length} ref(s) from ${secondsToTs(startMs/1000)} onwards`, 'ok')
        }
        refs = refs.map(r2 => ({ ...r2, ...classifyRefForUpload(r2) }))
        { const summary = describeAutoClassification(refs); if (summary) log(`   ⌁ ${summary}`, 'info') }
      } catch (e) {
        log(`   ✕ Scan failed — ${e.message || e}`, 'err')
        out.videoStatus = 'failed'
        out.reason      = `Scan failed: ${e.message || e}`
        return out
      }
      const { title, source } = resolveTitle({ detected: null, suggestion: null, excel: row.title })
      out.sermons.push({
        title,
        titleSource:     source,
        titleEdited:     false,
        titleDetected:   null,
        titleSuggested:  null,
        mainPassage:     null,
        preacher:        row.preacher || config.defaultPreacher || '',
        preacherDetected: false,
        preacherSource:  row.preacher
                           ? (row.preacherSource || 'title')
                           : (config.defaultPreacher ? 'config' : 'none'),
        start_ms:        startMs,
        end_ms:          endMs,
        start_ts:        secondsToTs(startMs / 1000),
        end_ts:          secondsToTs(endMs / 1000),
        startS:          Math.floor(startMs / 1000),
        endS:            Math.floor(endMs / 1000),
        ai_confidence:   'medium',
        isFullService:   false,
        pass1Debug:      `Manual start ${secondsToTs(startMs/1000)} → end of video`,
        transcriptSlice: sermonTranscriptText,
        refs,
        picked:          true,
      })
      out.videoStatus = 'segmented'
      return out
    }

    // ── Whole-video mode (default) — one Claude scan over the full
    // transcript, no gap detection. Works for any church, doesn't
    // require the NLC service structure. The sermon record spans the
    // full duration and is flagged isFullService so review tooling
    // can opt into manual segmentation later.
    if (config.scanMode !== 'estimate') {
      log(`   ⌁ Mode: whole video (no gap detection)`, 'info')
      let refs = []
      try {
        refs = await scanTranscript(transcriptText, sermonMeta, row.transcriptJson || null, {
          onLog: (msg, kind) => log(`   ${msg}`, kind || 'info'),
        })
        if (!Array.isArray(refs)) refs = []
        if (refs.nonJson) {
          // scanTranscript already surfaced the warning via onLog and
          // exhausted its retry — sermon still uploads with 0 refs.
          refs = []
        } else {
          log(`   ✓ ${refs.length} ref(s) for "${row.title || row.youtubeId}"`, 'ok')
        }
        refs = refs.map(r2 => ({ ...r2, ...classifyRefForUpload(r2) }))
        { const summary = describeAutoClassification(refs); if (summary) log(`   ⌁ ${summary}`, 'info') }
      } catch (e) {
        const orange = /JSON parse failed/i.test(e.message || '')
        log(`   ${orange ? '⌁' : '✕'} Scan failed — ${e.message || e}`, orange ? 'orange' : 'err')
        // eslint-disable-next-line no-console
        console.error('[Step6Process] scanTranscript threw:', e)
        out.videoStatus = 'failed'
        out.reason      = `Scan failed: ${e.message || e}`
        return out
      }
      const { title, source } = resolveTitle({ detected: null, suggestion: null, excel: row.title })
      out.sermons.push({
        title,
        titleSource:     source,
        titleEdited:     false,
        titleDetected:   null,
        titleSuggested:  null,
        mainPassage:     null,
        preacher:        row.preacher || config.defaultPreacher || '',
        preacherDetected: false,
        preacherSource:  row.preacher
                           ? (row.preacherSource || 'title')
                           : (config.defaultPreacher ? 'config' : 'none'),
        start_ms:        0,
        end_ms:          row.durationS ? row.durationS * 1000 : null,
        start_ts:        null,
        end_ts:          row.durationS ? secondsToTs(row.durationS) : null,
        startS:          0,
        endS:            row.durationS || null,
        ai_confidence:   'medium',
        isFullService:   true,
        pass1Debug:      'Whole-video scan',
        transcriptSlice: row.transcript || '',
        refs,
        picked:          true,
      })
      out.videoStatus = 'segmented'
      return out
    }

    // ── PASS 1 — JS sermon-start detection. Skips broadcast pre-roll,
    // finds the children-dismissal moment, then locates the gap before
    // the sermon. Pure JS, no API call.
    const pass1 = detectSermonStartFromSegments(row.transcriptJson || [])
    log(`   ⌁ Pass 1: ${pass1.debug}`, 'info')

    const FULL_VIDEO_DURATION_MS = row.durationS ? row.durationS * 1000 : null

    // No sermon gap detected → mark as full-service, queue for review.
    if (pass1.sermon_start_ms == null) {
      log(`   ⚠ No sermon gap detected — added to review queue`, 'warn')
      const { title, source } = resolveTitle({ detected: null, suggestion: null, excel: row.title })
      out.sermons.push({
        title,
        titleSource:     source,
        titleEdited:     false,
        titleDetected:   null,
        titleSuggested:  null,
        mainPassage:     null,
        preacher:        row.preacher || config.defaultPreacher || '',
        preacherDetected: false,
        preacherSource:  row.preacher
                           ? (row.preacherSource || 'title')
                           : (config.defaultPreacher ? 'config' : 'none'),
        start_ms:        0,
        end_ms:          FULL_VIDEO_DURATION_MS,
        start_ts:        null,
        end_ts:          row.durationS ? secondsToTs(row.durationS) : null,
        startS:          0,
        endS:            row.durationS || null,
        ai_confidence:   'low',
        isFullService:   true,
        pass1Debug:      pass1.debug,
        transcriptSlice: row.transcript || '',
        refs:            [],
        picked:          true,
        needsReview:     true,
      })
      out.videoStatus = 'segmented'
      out.reason      = 'No sermon gap — full service'
      return out
    }

    // ── PASS 2 — Claude finds the sermon end + extracts metadata.
    let pass2
    try {
      pass2 = await detectSermonEndWithClaude(pass1.sermon_start_ms, row.transcriptJson || [])
      log(`   ⌁ Pass 2: end=${pass2.sermon_end_ms != null ? secondsToTs(pass2.sermon_end_ms / 1000) : 'null'} · conf=${pass2.confidence} · preacher="${pass2.preacher}"`, 'info')
    } catch (e) {
      log(`   ✕ Pass 2 failed — ${e.message || e}`, 'err')
      // eslint-disable-next-line no-console
      console.error('[Step6Process] Pass 2 threw:', e)
      out.videoStatus = 'failed'
      out.reason      = `Pass 2 failed: ${e.message || e}`
      return out
    }

    // 45-min fallback when Claude couldn't pin down the end.
    let sermonEndMs   = pass2.sermon_end_ms
    let endFromFallback = false
    if (sermonEndMs == null) {
      sermonEndMs     = pass1.sermon_start_ms + 45 * 60 * 1000
      endFromFallback = true
      log(`   ⚠ Sermon end not identified — using +45 min fallback (${secondsToTs(sermonEndMs / 1000)})`, 'warn')
    }
    // Clamp the end to the video duration (Claude shouldn't go past it).
    if (FULL_VIDEO_DURATION_MS && sermonEndMs > FULL_VIDEO_DURATION_MS) {
      sermonEndMs = FULL_VIDEO_DURATION_MS
    }

    // ── PASS 3 — scan the sermon segment for Bible refs.
    // Slice transcript_json to the sermon range, format as [MM:SS] text
    // (absolute timestamps), then run scanTranscript.
    const sermonSegments = (row.transcriptJson || []).filter(seg => {
      const ms = seg?.startMs ?? seg?.start_ms ?? (typeof seg?.start === 'number' ? seg.start * 1000 : null)
      const n  = typeof ms === 'number' ? ms : parseInt(ms, 10)
      if (!Number.isFinite(n)) return false
      return n >= pass1.sermon_start_ms && n <= sermonEndMs
    })
    const sermonTranscriptText = formatTimestampedTranscript(sermonSegments) || row.transcript || ''

    let refs = []
    try {
      refs = await scanTranscript(sermonTranscriptText, sermonMeta, sermonSegments, {
        onLog: (msg, kind) => log(`   ${msg}`, kind || 'info'),
      })
      if (!Array.isArray(refs)) refs = []
      if (refs.nonJson) {
        refs = []
      } else {
        log(`   ✓ Pass 3: ${refs.length} ref(s) in sermon segment`, 'ok')
      }
      refs = refs.map(r2 => ({ ...r2, ...classifyRefForUpload(r2) }))
      { const summary = describeAutoClassification(refs); if (summary) log(`   ⌁ ${summary}`, 'info') }
    } catch (e) {
      const orange = /JSON parse failed/i.test(e.message || '')
      log(`   ${orange ? '⌁' : '✕'} Pass 3 scan failed — ${e.message || e}`, orange ? 'orange' : 'err')
      // eslint-disable-next-line no-console
      console.error('[Step6Process] Pass 3 threw:', e)
      out.videoStatus = 'failed'
      out.reason      = `Pass 3 failed: ${e.message || e}`
      return out
    }

    // ── Sermon record — built from Pass 1+2 timings and Pass 2 metadata.
    // Title priority: Pass 2 suggestion → row title → fallback.
    const { title, source } = resolveTitle({
      detected:   null,
      suggestion: pass2.title_suggestion || null,
      excel:      row.title,
    })
    // Preacher priority: row.preacher (title-extracted / manual edit) →
    // Pass 2 detected → config default → ''.
    let preacher       = row.preacher || ''
    let preacherSource = row.preacher ? (row.preacherSource || 'title') : 'none'
    if (!preacher && pass2.preacher) {
      preacher       = pass2.preacher
      preacherSource = 'transcript'
    }
    if (!preacher && config.defaultPreacher) {
      preacher       = config.defaultPreacher
      preacherSource = 'config'
    }
    // Final confidence — Pass 2 confidence, downgraded when we had to use
    // the 45-min fallback for the end timestamp.
    const finalConfidence = endFromFallback
      ? 'low'
      : (pass2.confidence || 'medium')

    out.sermons.push({
      title,
      titleSource:     source,
      titleEdited:     false,
      titleDetected:   null,
      titleSuggested:  pass2.title_suggestion || null,
      mainPassage:     pass2.main_passage || null,
      preacher,
      preacherDetected: !!pass2.preacher,
      preacherSource,
      start_ms:        pass1.sermon_start_ms,
      end_ms:          sermonEndMs,
      start_ts:        secondsToTs(pass1.sermon_start_ms / 1000),
      end_ts:          secondsToTs(sermonEndMs / 1000),
      startS:          Math.floor(pass1.sermon_start_ms / 1000),
      endS:            Math.floor(sermonEndMs / 1000),
      ai_confidence:   finalConfidence,
      isFullService:   false,
      pass1Debug:      pass1.debug,
      pass1Dismissal:  pass1.dismissal_ms,
      pass1KidMarker:  pass1.kid_marker_ms,
      pass1GapAt:      pass1.gap_at_ms,
      pass1GapType:    pass1.gap_type,
      endFromFallback,
      transcriptSlice: sermonTranscriptText,
      refs,
      picked:          true,
    })

    out.videoStatus = 'segmented'
    return out
  }

  async function run(capOverride) {
    if (status === 'running') return
    const cap = capOverride != null ? capOverride : scanCap
    const rows = cap != null ? effectiveRows.slice(0, cap) : effectiveRows
    pausedRef.current = false
    cancelRef.current = false
    setGate(null)
    // Resume seed — videos already scanned in a prior (interrupted) session.
    // Skip them and carry their results forward so the scan continues from
    // where it stopped rather than re-scanning or losing work.
    const seeded = Array.isArray(seedResults) ? seedResults : []
    const doneIds = new Set(seeded.map(r => r?.row?.youtubeId).filter(Boolean))
    const acc = [...seeded]
    setStatus('running'); setLogs([]); setResults([...acc]); setProgress({ done: acc.length, total: rows.length })
    if (config.testMode) log(`⚠ Test mode — processing first ${rows.length} video(s) only`, 'warn')
    if (cap != null && cap < effectiveRows.length) log(`⚠ Free-scan limit — scanning the first ${cap} of ${effectiveRows.length} video(s).`, 'warn')
    if (doneIds.size) log(`Resuming — ${doneIds.size} video(s) already scanned, ${rows.length - doneIds.size} to go.`, 'info')
    log(`Estimated time: ${fmtMinutes(Math.max(0, rows.length - doneIds.size) * PER_VIDEO_SEC)}. You can pause and resume at any time.`, 'info')

    // Resume pre-check — fetch existing youtube_ids for this church so we
    // don't burn Claude calls on videos that are already in the library.
    const ytIds = rows.map(r => r.youtubeId).filter(Boolean)
    let existingYtIds = new Set()
    if (ytIds.length && config.churchId) {
      const { data, error: dupErr } = await supabase.from('sermons')
        .select('youtube_id').eq('church_id', config.churchId).in('youtube_id', ytIds)
      if (dupErr) log(`⚠ Could not check existing imports: ${dupErr.message}`, 'warn')
      existingYtIds = new Set((data || []).map(d => d.youtube_id))
      if (existingYtIds.size) log(`Will skip ${existingYtIds.size} video(s) already in this church.`, 'info')
    }

    let firstVideo = true
    for (let i = 0; i < rows.length; i++) {
      if (cancelRef.current) break
      await keepSessionAlive()   // keep the token fresh across a long scan
      // Already scanned in a prior session — carried in `acc`, skip silently.
      if (rows[i].youtubeId && doneIds.has(rows[i].youtubeId)) continue
      await waitWhilePaused()
      if (cancelRef.current) break
      if (!firstVideo) {
        log('   ⏸ Inter-video pause 5s…', 'info')
        await delay(DELAY_VIDEO_MS)
        if (cancelRef.current) break
      }
      firstVideo = false
      const r = await processOne(rows[i], existingYtIds)
      // ── Per-sermon AI summary — generated here (single finalization point,
      // after out.sermons is fully built, before results are slimmed/saved)
      // so the admin can read it on the Step-7 review cards before uploading.
      // Best-effort: the helper returns null on failure and never throws, so a
      // summary miss never aborts the run. aiSummary survives slimResults (only
      // transcriptSlice is stripped) so it persists to IndexedDB + the upload.
      for (const s of (r.sermons || [])) {
        if (!s.aiSummary) {
          log(`   ⌁ Summarising…`, 'debug')
          s.aiSummary = await generateSermonSummary(s.transcriptSlice || '', {
            title: s.title, preacher: s.preacher || '', date: r.row?.date || '', church_name: '',
          })
        }
        if (!s.displayTitle && s.aiSummary) {
          s.displayTitle = await generateDisplayTitle(s.aiSummary, { title: s.title })
        }
        if (!s.bigIdea && s.aiSummary) {
          s.bigIdea = await generateBigIdea(s.aiSummary, { title: s.title })
        }
      }
      acc.push(r)
      setResults([...acc])
      setProgress(p => ({ done: p.done+1, total: p.total }))
      persistResults(acc)
    }

    // Count this run's scans against the church's free allowance. Only NEW
    // videos actually scanned this session (not seeded carry-overs, not skipped
    // duplicates / failures) count. Super-admin imports are unlimited.
    const scanned = acc.filter(r => !doneIds.has(r?.row?.youtubeId) && r?.videoStatus === 'segmented').length
    if (scanned > 0 && config.churchId && meProfile?.role !== 'super_admin') {
      const { error: recErr } = await recordScans(config.churchId, scanned)
      if (recErr) console.warn('[Step6Process] recordScans failed:', recErr)
    }

    if (cancelRef.current) {
      setStatus('cancelled')
      log('Run cancelled.', 'warn')
    } else {
      setStatus('done')
      // Mark the scan complete so a reload now lands on review, not back here.
      persistResults(acc, { done: true })
      log(`Done. ${acc.length} video(s) processed.`, 'ok')
    }
  }

  // Before scanning, check the church's free-scan allowance. Paid churches and
  // those with enough remaining scans go straight to run(); otherwise we hold
  // and show the paywall banner so the admin can scan a subset or get in touch.
  async function gateAndRun() {
    if (!config.churchId) { run(); return }
    // Super admins upload unrestricted from the admin tool — no free-scan gate.
    if (meProfile?.role === 'super_admin') { run(); return }
    const total = effectiveRows.length
    const g = await canScan(config.churchId, total)
    if (g.allowed) { run(); return }
    setGate({ remaining: Math.max(0, g.remaining || 0), message: g.message })
    setStatus('blocked')
    log(`⚠ ${g.message}`, 'warn')
  }

  useEffect(() => { gateAndRun() /* once */ // eslint-disable-next-line
  }, [])

  const pct = progress.total ? Math.round(progress.done / progress.total * 100) : 0
  const etaSec = Math.max(0, (progress.total - progress.done)) * PER_VIDEO_SEC
  return (
    <div>
      <H2>5. Processing {progress.total} video{progress.total===1?'':'s'}</H2>
      <Sub>Scanning the full transcript of each video for Bible refs. 5s gap between videos.</Sub>

      {/* Free-scan paywall banner — shown when the import exceeds the church's
          remaining free scans. They can scan up to their limit or get in touch. */}
      {status === 'blocked' && gate && (
        <div style={{
          background:'rgba(232,200,122,0.08)', border:`1px solid ${C.accent}`,
          borderRadius:12, padding:'16px 18px', marginBottom:18,
        }}>
          <div style={{fontSize:14, fontWeight:700, color:C.text, marginBottom:6}}>
            ⚠ You have {gate.remaining} free scan{gate.remaining === 1 ? '' : 's'} remaining. This import contains {effectiveRows.length} video{effectiveRows.length === 1 ? '' : 's'}.
          </div>
          <div style={{fontSize:13, color:C.muted, marginBottom:14, lineHeight:1.5}}>
            Get in touch and we'll import your full sermon archive — same day.
          </div>
          <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
            {gate.remaining > 0 && (
              <button
                onClick={() => { setScanCap(gate.remaining); run(gate.remaining) }}
                style={{
                  background:'transparent', color:C.accent, border:`1px solid ${C.accent}`,
                  borderRadius:10, padding:'11px 16px', fontSize:13.5, fontWeight:800, cursor:'pointer',
                }}>
                Scan first {gate.remaining} only
              </button>
            )}
            <button
              onClick={() => setShowUpgrade(true)}
              style={{
                background:C.accent, color:'#1B2A4A', border:'none',
                borderRadius:10, padding:'11px 16px', fontSize:13.5, fontWeight:800, cursor:'pointer',
              }}>
              Get in touch to unlock →
            </button>
          </div>
        </div>
      )}

      {showUpgrade && (
        <UpgradeSheet
          church={{ id: config.churchId, name: gateChurch?.name }}
          profile={meProfile}
          scansUsed={FREE_SCAN_LIMIT - (gate?.remaining || 0)}
          title="Unlock your full sermon archive"
          onClose={() => setShowUpgrade(false)}
        />
      )}

      <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'18px 20px', marginBottom:18}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
          <div style={{fontSize:13, color:C.muted}}>
            <strong style={{color:C.text}}>{progress.done}</strong> / {progress.total} processed
            {(status === 'running' || status === 'paused') && (
              <span style={{marginLeft:10, color:C.faint}}>· ~{fmtMinutes(etaSec)} remaining</span>
            )}
          </div>
          <div style={{fontSize:12, fontWeight:700, color:status === 'done' ? C.green : status === 'paused' ? C.accent : status === 'cancelled' ? '#FF8888' : C.blue}}>
            {status.toUpperCase()}
          </div>
        </div>
        <div style={{height:6, background:C.surface2, borderRadius:3, overflow:'hidden'}}>
          <div style={{height:'100%', width:`${pct}%`, background:C.accent, transition:'width 200ms'}}/>
        </div>
      </div>

      <div style={{display:'flex', gap:8, marginBottom:18}}>
        {status === 'running' && (
          <PrimaryBtn kind="ghost" onClick={() => { pausedRef.current = true; setStatus('paused') }}>Pause</PrimaryBtn>
        )}
        {status === 'paused' && (
          <PrimaryBtn onClick={() => { pausedRef.current = false; setStatus('running') }}>Resume</PrimaryBtn>
        )}
        {(status === 'running' || status === 'paused') && (
          <PrimaryBtn kind="red" onClick={() => { cancelRef.current = true; pausedRef.current = false }}>Cancel</PrimaryBtn>
        )}
        {status === 'done' && (
          <>
            <PrimaryBtn kind="ghost" onClick={onBack}>← Back</PrimaryBtn>
            <PrimaryBtn onClick={() => onDone(results)}>Review results →</PrimaryBtn>
          </>
        )}
        {status === 'cancelled' && (
          <>
            <PrimaryBtn kind="ghost" onClick={onBack}>← Back</PrimaryBtn>
            <PrimaryBtn onClick={() => onDone(results)}>Review what processed →</PrimaryBtn>
          </>
        )}
      </div>

      <div style={{
        background:'#08080d', border:`1px solid ${C.border}`, borderRadius:10,
        padding:'14px 16px', maxHeight:360, overflowY:'auto',
        fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, lineHeight:1.7,
      }}>
        {logs.length === 0 && <div style={{color:C.faint}}>Waiting to start…</div>}
        {logs.map(l => (
          <div key={l.id} style={{
            color: l.kind === 'err' ? '#FF8888' : l.kind === 'orange' ? '#F59E0B' : l.kind === 'warn' ? C.accent : l.kind === 'ok' ? C.green : C.muted,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>{l.text}</div>
        ))}
      </div>
    </div>
  )
}

// Stable identifier for a single sermon/encouragement ref so we can match
// items between the local `processed` state and the import_queue table.
// (videoId + verseRef + timestamp + kind is unique enough in practice; the
// timestamp falls back to '' for refs that never carried one.)
function makeRefKey(ref, row) {
  const ts = ref?.timestamp_s ?? ref?.timestampS ?? ''
  const kind = ref?.kind === 'encouragement' ? 'encouragement' : 'sermon'
  return [row?.youtubeId || '', ref?.verseRef || '', String(ts), kind].join('|')
}

// Build the title / summary / text fields for an encouragement insert.
//
// `text` is the FULL transcript verbatim between the earliest ref's
// timestamp and the latest + 60s — captures the complete thought the
// speaker delivered, not a one-sentence Claude note. We compute start_ms
// and end_ms here too so the caller can persist the range alongside the
// text. Falls back to the joined Claude notes if transcript segments
// aren't available (e.g. the NeedsReview "upload now" path doesn't carry
// transcripts through).
//
// docs/FIX-ENCOURAGEMENT-UPLOAD.md — an encouragement is one moment, not a
// 20-30 minute transcript. `text`/`summary` are now the same short AI
// summary (never the full transcript); the full transcript excerpt, if it
// exists, goes in `transcript_excerpt` and is never shown in Community.
// Clip length defaults to 90s from the earliest ref's timestamp, capped at
// the hard 5-minute maximum — not "latest ref + 60s", which is what produced
// the 19-34 minute clips this fix doc exists to stop.
async function buildEncouragementContent(refs, transcriptSegments) {
  const CLIP_S = 90, MAX_CLIP_S = 300
  const refTimes = (refs || [])
    .map(ref => {
      const t = ref?.timestamp_s ?? ref?.timestampS
      return typeof t === 'number' ? t : null
    })
    .filter(t => t != null)
  const startS = refTimes.length ? Math.min(...refTimes) : null
  const endS   = startS != null ? startS + Math.min(CLIP_S, MAX_CLIP_S) : null
  const startMs = startS != null ? Math.floor(startS * 1000) : null
  const endMs   = endS   != null ? Math.floor(endS   * 1000) : null

  // Full word-for-word transcript text between start_ms and end_ms — kept
  // only as the source for the AI summary below, and optionally in
  // transcript_excerpt; never inserted as `text` directly.
  let transcriptExcerpt = ''
  const segs = Array.isArray(transcriptSegments) ? transcriptSegments : []
  if (segs.length && startMs != null && endMs != null) {
    transcriptExcerpt = segs
      .filter(s => {
        const ms = typeof s?.startMs === 'number' ? s.startMs : parseInt(s?.startMs, 10)
        return Number.isFinite(ms) && ms >= startMs && ms <= endMs
      })
      .map(s => String(s?.text || '').trim())
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
  const joinedNotes = (refs || [])
    .map(ref => (ref?.notes && ref.notes.trim()) || '')
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
  const sourceText = transcriptExcerpt || joinedNotes || (refs && refs[0]?.verseRef) || ''

  // AI-generated short title + summary + sermon-vs-encouragement check.
  // Falls back to the old heuristic if the Claude call fails for any
  // reason — never block the upload on it.
  const ai = sourceText ? await generateEncouragementSummary(sourceText) : null
  const title = ai?.title || (() => {
    const titleSource = joinedNotes || (refs && refs[0]?.verseRef) || ''
    return titleSource
      ? titleSource.split(/\s+/).slice(0, 5).join(' ').replace(/[.,;:!?…]+$/, '')
      : 'Encouragement'
  })()
  const summary = ai?.summary || (joinedNotes ? joinedNotes.slice(0, 280) : null)
  const isLikelySermon = ai?.isLikelySermon === true

  return {
    title, summary, text: summary || sourceText.slice(0, 400), startMs, endMs,
    transcriptExcerpt: transcriptExcerpt.length > (summary || '').length + 50 ? transcriptExcerpt : null,
    isLikelySermon,
  }
}

// ── STEP 7 — Review + upload + results ───────────────────────────────────
function Step7ReviewUpload({ processed, setProcessed, config, user, refreshBadge, results, setResults, onReset }) {
  const churchId = config.churchId

  function patchRow(rIdx, fn) {
    setProcessed(prev => prev.map((r,i) => i === rIdx ? fn(r) : r))
  }
  function setAllSermonsPicked(v) {
    setProcessed(prev => prev.map(p => ({
      ...p, sermons: p.sermons.map(s => ({...s, picked: v}))
    })))
  }
  const segmented      = processed.filter(p => p.videoStatus === 'segmented')
  const allSermons     = segmented.flatMap(p => p.sermons)
  const sermonsReady   = allSermons.filter(s => s.picked).length
  const sermonsTotal   = allSermons.length
  const skipped        = processed.filter(p => p.videoStatus !== 'segmented')

  // Count how many refs of each kind are eligible to be added to review
  // (not yet queued). Used to label the bulk buttons and disable them when
  // there's nothing left to add.
  const allRefsFlat = segmented.flatMap(p => (p.sermons || []).flatMap(s => (s.refs || [])))
  const addableSermonNotes   = allRefsFlat.filter(r => (r.kind || 'sermon') === 'sermon' && !r.queued).length
  const addableEncouragements = allRefsFlat.filter(r => r.kind === 'encouragement' && !r.queued).length
  const queuedRefs            = allRefsFlat.filter(r => r.queued).length

  // ── upload state ───────────────────────────────────────────
  const [sermonStatus, setSermonStatus] = useState('pending')  // pending | running | done
  const [uploadLog,    setUploadLog]    = useState([])
  const [counts, setCounts] = useState({
    sermons: 0, encouragements: 0, skipped: 0, needs_review: 0,
  })
  const [error, setError] = useState('')
  // Summary banner — { kind: 'uploaded' | 'queued', count } after each action.
  const [lastAction, setLastAction] = useState(null)

  function log(text, kind='info') {
    setUploadLog(l => [...l, { id: Date.now()+Math.random(), text, kind }])
  }

  async function uploadSermons() {
    if (sermonsReady === 0) return
    // Hard guard — a resume from a partial run can land us here without
    // a churchId on config (Step 5's "Start processing" button gates
    // for it, but the resume path bypasses Step 5). Without this check
    // every sermon went in with church_id=null and disappeared from
    // the Church tab's client-side filter.
    if (!churchId) {
      setError('No destination church selected. Go back to Step 4 (Configure scan), pick a church, then return here.')
      return
    }
    setSermonStatus('running'); setError('')
    try {
      // ── Dedup
      const ytIds = segmented.map(p => p.row.youtubeId).filter(Boolean)
      let existing = new Set()
      if (ytIds.length) {
        const { data } = await supabase.from('sermons')
          .select('youtube_id').eq('church_id', churchId).in('youtube_id', ytIds)
        existing = new Set((data || []).map(d => d.youtube_id))
      }

      let sermonsImported = 0
      let needsReviewCount = 0
      let skippedCount = 0

      // Queue services that never segmented.
      for (const p of processed) {
        if (p.videoStatus === 'segmented') continue
        const r = p.row
        await supabase.from('import_queue').insert({
          church_id:   churchId,
          type:        'service',
          video_id:    r.youtubeId || null,
          title:       r.title || null,
          youtube_url: r.youtubeUrl || (r.youtubeId ? `https://www.youtube.com/watch?v=${r.youtubeId}` : null),
          reason:      p.reason || p.videoStatus,
          data_json:   { rowIndex: r.rowIndex, date: r.date, series: r.series },
          created_by:  user?.id || null,
        })
        skippedCount++; needsReviewCount++
        log(`↷ Queued for review: ${r.title || r.youtubeId} — ${p.reason || p.videoStatus}`, 'warn')
      }

      for (const p of processed) {
        if (p.videoStatus !== 'segmented') continue
        const r = p.row
        if (r.youtubeId && existing.has(r.youtubeId)) {
          log(`↷ Dedup: ${r.title} already in this church — skipped`, 'warn')
          skippedCount++
          continue
        }
        for (const s of p.sermons) {
          if (!s.picked) continue
          try {
            const needsReview = !!s.isFullService || s.ai_confidence === 'low'
            // Best-effort AI summary — never blocks the import (returns null on
            // failure). Prefer the summary already generated during processing
            // (Step 6) or in review; only regenerate as a fallback when missing.
            const aiSummary = s.aiSummary
              || await generateSermonSummary(s.transcriptSlice || '', {
                   title: s.title, preacher: s.preacher || '', date: r.date || '', church_name: '',
                 })
            const displayTitle = s.displayTitle
              || (aiSummary ? await generateDisplayTitle(aiSummary, { title: s.title }) : null)
            const bigIdea = s.bigIdea
              || (aiSummary ? await generateBigIdea(aiSummary, { title: s.title }) : null)
            const sermonInsert = {
              uploaded_by:  user?.id || null,
              church_id:    churchId,
              title:        s.title,
              series:       r.series || null,
              preacher:     s.preacher || null,
              date:         r.date || null,
              youtube_id:   r.youtubeId || null,
              youtube_url:  r.youtubeUrl || (r.youtubeId ? `https://www.youtube.com/watch?v=${r.youtubeId}` : null),
              duration:     r.durationS || null,
              thumbnail:    r.thumbnail || (r.youtubeId ? `https://img.youtube.com/vi/${r.youtubeId}/mqdefault.jpg` : null),
              transcript:   s.transcriptSlice || '',
              needs_review: needsReview,
              is_full_service: !!s.isFullService,
              source:       'admin_tool',
              ai_summary:   aiSummary || null,
              display_title: displayTitle || null,
              big_idea:     bigIdea || null,
            }
            // Log the exact object going to Supabase — transcript is
            // truncated to keep the UI log readable, but full object is
            // still available via the console.log line right after.
            const previewInsert = { ...sermonInsert, transcript: `[${(sermonInsert.transcript || '').length} chars]` }
            log(`Inserting: ${JSON.stringify(previewInsert)}`, 'info')
            // eslint-disable-next-line no-console
            console.log('[uploadSermons] inserting sermon:', sermonInsert)

            let { data: sermonData, error: sErr } = await supabase
              .from('sermons')
              .insert(sermonInsert)
              .select()
            // Tolerate missing is_full_service / thumbnail / source / display_title /
            // big_idea columns from older deployments (run the migration to enable them).
            if (sErr && /column.*(is_full_service|thumbnail|source|ai_summary|display_title|big_idea).*does not exist/i.test(sErr.message || '')) {
              // eslint-disable-next-line no-unused-vars
              const { is_full_service, thumbnail, source, ai_summary, display_title, big_idea, ...fallbackInsert } = sermonInsert
              ;({ data: sermonData, error: sErr } = await supabase
                .from('sermons')
                .insert(fallbackInsert)
                .select())
              if (!sErr) log('⚠ is_full_service / thumbnail / ai_summary / display_title / big_idea column missing — saved without them. Migration: alter table public.sermons add column if not exists is_full_service boolean default false; alter table public.sermons add column if not exists thumbnail text; alter table public.sermons add column if not exists ai_summary text; alter table public.sermons add column if not exists display_title text; alter table public.sermons add column if not exists big_idea text;', 'warn')
            }

            if (sErr) {
              // eslint-disable-next-line no-console
              console.error('Sermon insert error:', sErr)
              log(`✗ Insert failed: ${sErr.message || 'unknown error'} | Code: ${sErr.code || 'n/a'}`, 'err')
              if (sErr.details) log(`   details: ${sErr.details}`, 'err')
              if (sErr.hint)    log(`   hint: ${sErr.hint}`, 'err')
              throw sErr
            }

            const sermonRow = Array.isArray(sermonData) ? sermonData[0] : sermonData
            if (!sermonRow?.id) {
              throw new Error(`Insert returned no row (data: ${JSON.stringify(sermonData)})`)
            }
            log(`✓ Inserted sermon ID: ${sermonRow.id}`, 'ok')

            // Skip refs the admin unchecked in the review screen. ref.picked
            // defaults to undefined which is treated as included; only an
            // explicit `false` excludes the ref from the insert. Then split
            // the survivors by kind — 'encouragement' refs become standalone
            // words of encouragement instead of sermon refs.
            const pickedRefs       = (s.refs || []).filter(ref => ref?.picked !== false)
            const sermonRefs       = pickedRefs.filter(ref => (ref.kind || 'sermon') === 'sermon')
            const encouragementRefs = pickedRefs.filter(ref => ref.kind === 'encouragement')
            if (sermonRefs.length) {
              const baseRows = dedupeRefRows(sermonRefs.map(ref => ({
                sermon_id:   sermonRow.id,
                verse_ref:   ref.verseRef,
                book:        ref.book,
                chapter:     ref.chapter,
                verse:       ref.verse,
                // Inclusive end verse for ranges like "Ephesians 6:8-13".
                // Single-verse refs and chapter-only refs default to the
                // start verse (or null when verse itself is unknown).
                end_verse:   ref.end_verse ?? ref.endVerse ?? ref.verse ?? null,
                // Tolerate both snake_case (current Claude output) and the
                // older camelCase shape.
                timestamp_s: ref.timestamp_s ?? ref.timestampS ?? null,
                timestamp:   ref.timestamp || null,
                notes:       ref.notes || null,
              })))
              let rErr
              ;({ error: rErr } = await supabase.from('sermon_refs').insert(
                baseRows.map(row => ({ ...row, church_id: churchId }))
              ))
              // Tolerate older deployments where `end_verse` and/or
              // `church_id` haven't been migrated yet. On a column-
              // missing error, retry with the missing columns stripped.
              if (rErr && /column.*does not exist/i.test(rErr.message || '')) {
                const msg = rErr.message || ''
                const keepChurch = !/church_id/i.test(msg)
                const keepEnd    = !/end_verse/i.test(msg)
                const retryRows = baseRows.map(row => {
                  // eslint-disable-next-line no-unused-vars
                  const { end_verse, ...rest } = row
                  const base = keepEnd ? row : rest
                  return keepChurch ? { ...base, church_id: churchId } : base
                })
                ;({ error: rErr } = await supabase.from('sermon_refs').insert(retryRows))
              }
              if (rErr) {
                // eslint-disable-next-line no-console
                console.error('Sermon refs insert error:', rErr)
                log(`✗ Refs insert failed: ${rErr.message} | Code: ${rErr.code || 'n/a'}`, 'err')
                throw rErr
              }
              log(`   + ${baseRows.length} ref${baseRows.length === 1 ? '' : 's'} linked`, 'ok')
            }
            if (encouragementRefs.length) {
              // One encouragement per sermon, carrying ALL of the
              // encouragement refs the admin marked. text/summary are the
              // short AI summary (docs/FIX-ENCOURAGEMENT-UPLOAD.md) — see
              // buildEncouragementContent.
              const earliestTs = encouragementRefs.reduce((acc, ref) => {
                const t = ref.timestamp_s ?? ref.timestampS
                if (typeof t !== 'number') return acc
                return acc == null || t < acc ? t : acc
              }, null)
              const url = r.youtubeId && earliestTs != null
                ? `https://www.youtube.com/watch?v=${r.youtubeId}&t=${earliestTs}s`
                : (r.youtubeUrl || null)
              const { title, summary, text, startMs, endMs, transcriptExcerpt, isLikelySermon } = await buildEncouragementContent(
                encouragementRefs,
                r.transcriptJson
              )
              if (isLikelySermon) {
                log(`⚠ Skipped an encouragement for "${s.title}" — looks like sermon teaching, not a congregation moment. Add it manually as a sermon moment if needed.`, 'warn')
              } else {
              const base = {
                church_id:  churchId,
                author_id:  user?.id || null,
                text,
                link_url:   url,
                link_label: url ? 'Watch the moment' : null,
              }
              const extras = {
                title,
                summary,
                start_ms:           startMs,
                end_ms:             endMs,
                transcript_excerpt: transcriptExcerpt,
                shared_by:  s.preacher || null,
                event_date: r.date     || null,
              }
              // Try the full insert; on missing-column errors, drop the
              // newer fields and retry. Existing deployments without
              // `summary`, `start_ms`, `end_ms`, `transcript_excerpt` still
              // get a working insert with title + text.
              async function tryInsert(payload) {
                return supabase.from('encouragements').insert(payload).select('id').single()
              }
              let res = await tryInsert({ ...base, ...extras })
              if (res.error && /column.*does not exist/i.test(res.error.message || '')) {
                // eslint-disable-next-line no-unused-vars
                const { summary: _s, start_ms: _sm, end_ms: _em, transcript_excerpt: _te, ...slim } = extras
                res = await tryInsert({ ...base, ...slim })
              }
              if (res.error) {
                res = await tryInsert(base)
              }
              if (res.error) {
                log(`✗ Encouragement insert failed: ${res.error.message}`, 'err')
              } else {
                const encId = res.data?.id
                if (encId) {
                  const refRows = encouragementRefs.map(ref => ({
                    encouragement_id: encId,
                    verse_ref:        ref.verseRef,
                    book:             ref.book,
                    chapter:          ref.chapter,
                    verse:            ref.verse,
                    end_verse:        ref.end_verse ?? ref.endVerse ?? ref.verse ?? null,
                    timestamp_s:      ref.timestamp_s ?? ref.timestampS ?? null,
                    timestamp:        ref.timestamp || null,
                    notes:            ref.notes || null,
                  }))
                  let { error: erErr } = await supabase.from('encouragement_refs').insert(refRows)
                  if (erErr && /column.*end_verse.*does not exist/i.test(erErr.message || '')) {
                    ;({ error: erErr } = await supabase.from('encouragement_refs').insert(
                      // eslint-disable-next-line no-unused-vars
                      refRows.map(({ end_verse, ...rest }) => rest)
                    ))
                  }
                  if (erErr) log(`⚠ encouragement_refs insert failed: ${erErr.message}`, 'warn')
                  log(`   + 1 word of encouragement created with ${refRows.length} ref${refRows.length === 1 ? '' : 's'}`, 'ok')
                }
              }
              }
            }
            sermonsImported++
            log(`✓ Sermon: "${s.title}" + ${sermonRefs.length} ref${sermonRefs.length === 1 ? '' : 's'}${encouragementRefs.length ? ` + ${encouragementRefs.length} encouragement${encouragementRefs.length === 1 ? '' : 's'}` : ''}`, 'ok')
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[uploadSermons] caught error for sermon:', s.title, e)
            log(`✕ Sermon "${s.title}" failed: ${e.message || e}`, 'err')
            await supabase.from('import_queue').insert({
              church_id:  churchId,
              type:       'service',
              video_id:   r.youtubeId || null,
              title:      s.title,
              youtube_url:r.youtubeUrl || null,
              reason:     `Insert failed: ${e.message || e}`,
              data_json:  { sermon: s, row: { date:r.date, series:r.series } },
              created_by: user?.id || null,
            })
            needsReviewCount++; skippedCount++
          }
        }
      }

      setCounts(c => ({
        ...c,
        sermons: c.sermons + sermonsImported,
        skipped: c.skipped + skippedCount,
        needs_review: c.needs_review + needsReviewCount,
      }))
      setLastAction({ kind: 'uploaded', count: sermonsImported })
      // Flip every just-uploaded sermon's picked flag off so a second click
      // can't accidentally double-insert (RLS dedup will block it anyway,
      // but the UX feedback is clearer this way).
      setProcessed(prev => prev.map(p => ({
        ...p,
        sermons: (p.sermons || []).map(s => ({ ...s, picked: false, uploaded: s.picked ? true : !!s.uploaded })),
      })))
      setSermonStatus('done')
      refreshBadge?.()
    } catch (e) {
      setError(e.message || String(e))
      setSermonStatus('pending')
    }
  }

  // ── In-review ref tracking ─────────────────────────────────────────
  // Individual refs (sermon notes or encouragements) can be parked in the
  // Needs Review queue indefinitely without being inserted into the app's
  // live sermon/encouragement tables. They sit there until an admin
  // dismisses them from the NeedsReview tab. The `queued` flag on each
  // ref drives the row's dimmed/badged appearance below.

  // On mount, reconcile the local `queued` flag with the actual contents
  // of import_queue so dismissals made elsewhere clear the badge here.
  useEffect(() => {
    if (!churchId) return
    let cancelled = false
    ;(async () => {
      const ytIds = Array.from(new Set(
        processed.map(p => p?.row?.youtubeId).filter(Boolean)
      ))
      if (!ytIds.length) return
      const { data } = await supabase.from('import_queue')
        .select('data_json, video_id')
        .eq('church_id', churchId)
        .in('video_id', ytIds)
        .is('dismissed_at', null)
      if (cancelled) return
      const queuedKeys = new Set()
      for (const row of (data || [])) {
        const r = row?.data_json?.ref
        if (!r) continue
        queuedKeys.add(makeRefKey(r, { youtubeId: row.video_id }))
      }
      // Only update if the local state would actually change, to avoid an
      // infinite loop with the persist-to-localStorage effect.
      let needsWrite = false
      processed.forEach(p => (p.sermons || []).forEach(s => (s.refs || []).forEach(rf => {
        const k = makeRefKey(rf, p.row)
        const should = queuedKeys.has(k)
        if (!!rf.queued !== should) needsWrite = true
      })))
      if (!needsWrite) return
      setProcessed(prev => prev.map(p => ({
        ...p,
        sermons: (p.sermons || []).map(s => ({
          ...s,
          refs: (s.refs || []).map(rf => ({
            ...rf,
            queued: queuedKeys.has(makeRefKey(rf, p.row)),
          })),
        })),
      })))
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId])

  // Queue an array of { pIdx, sIdx, rIdx } targets to import_queue and
  // mark them locally as queued (which dims them + flips off `picked`).
  async function addRefsToReview(targets) {
    if (!targets.length) return
    if (!churchId) {
      setError('No destination church selected. Go back to Step 4 (Configure scan), pick a church, then return here.')
      return
    }
    setError('')
    const queuedKeys = new Set()
    let okCount = 0
    for (const t of targets) {
      const p = processed[t.pIdx]
      const s = p?.sermons?.[t.sIdx]
      const ref = s?.refs?.[t.rIdx]
      if (!ref) continue
      if (ref.queued) continue
      const r = p.row
      const type = ref.kind === 'encouragement' ? 'encouragement' : 'service'
      const youtubeUrl = r.youtubeUrl || (r.youtubeId ? `https://www.youtube.com/watch?v=${r.youtubeId}` : null)
      // eslint-disable-next-line no-unused-vars
      const { queued, picked, _sIdx, _rIdx, _ts, ...refForDb } = ref
      const dataJson = {
        ref: refForDb,
        context: {
          sermon_title: s.title || r.title || null,
          youtubeId:    r.youtubeId || null,
          youtubeUrl,
          preacher:     s.preacher || null,
          date:         r.date || null,
          series:       r.series || null,
        },
      }
      const { error: qErr } = await supabase.from('import_queue').insert({
        church_id:   churchId,
        type,
        video_id:    r.youtubeId || null,
        title:       ref.verseRef || s.title || null,
        youtube_url: youtubeUrl,
        reason:      type === 'service'
          ? 'Sermon note kept for review'
          : 'Encouragement kept for review',
        data_json:   dataJson,
        created_by:  user?.id || null,
      })
      if (qErr) {
        log(`✗ Add-to-review failed: ${ref.verseRef} — ${qErr.message}`, 'err')
        continue
      }
      okCount++
      queuedKeys.add(makeRefKey(ref, r))
      log(`☆ Added to review: ${ref.verseRef}`, 'warn')
    }
    if (queuedKeys.size) {
      setProcessed(prev => prev.map(p => ({
        ...p,
        sermons: (p.sermons || []).map(s => ({
          ...s,
          refs: (s.refs || []).map(rf => queuedKeys.has(makeRefKey(rf, p.row))
            ? { ...rf, queued: true, picked: false }
            : rf),
        })),
      })))
    }
    setLastAction({ kind: 'in_review', count: okCount })
    refreshBadge?.()
  }

  // Pull every sermon-kind or encouragement-kind ref across all segmented
  // services and add the ones not already queued to review in one click.
  function addAllOfKindToReview(targetKind) {
    const targets = []
    processed.forEach((p, pIdx) => {
      if (p.videoStatus !== 'segmented') return
      ;(p.sermons || []).forEach((s, sIdx) => {
        (s.refs || []).forEach((ref, rIdx) => {
          const refKind = ref.kind === 'encouragement' ? 'encouragement' : 'sermon'
          const wantedType = targetKind === 'encouragement' ? 'encouragement' : 'sermon'
          if (refKind !== wantedType) return
          if (ref.queued) return
          targets.push({ pIdx, sIdx, rIdx })
        })
      })
    })
    if (targets.length) addRefsToReview(targets)
  }

  // Remove a single ref from review (dismisses the matching import_queue
  // row + clears the local `queued` flag).
  async function removeRefFromReview(target) {
    if (!churchId) return
    const p = processed[target.pIdx]
    const s = p?.sermons?.[target.sIdx]
    const ref = s?.refs?.[target.rIdx]
    if (!ref) return
    const key = makeRefKey(ref, p.row)
    // Look up the matching queue row by video_id + church_id + ref shape.
    const { data: rows } = await supabase.from('import_queue')
      .select('id, data_json, video_id')
      .eq('church_id', churchId)
      .eq('video_id', p.row?.youtubeId || null)
      .is('dismissed_at', null)
    const match = (rows || []).find(row => {
      const r = row?.data_json?.ref
      if (!r) return false
      return makeRefKey(r, { youtubeId: row.video_id }) === key
    })
    if (match?.id) {
      await supabase.from('import_queue').update({
        dismissed_at: new Date().toISOString(),
        dismissed_by: user?.id || null,
      }).eq('id', match.id)
    }
    setProcessed(prev => prev.map(p2 => ({
      ...p2,
      sermons: (p2.sermons || []).map(s2 => ({
        ...s2,
        refs: (s2.refs || []).map(rf => makeRefKey(rf, p2.row) === key
          ? { ...rf, queued: false }
          : rf),
      })),
    })))
    log(`✗ Removed from review: ${ref.verseRef}`, 'info')
    refreshBadge?.()
  }

  // ── Queue selected sermons for manual review later ─────────────────
  // Inserts into import_queue with reason "Needs review" and a structured
  // data_json that QueueList can promote to the sermons table via its
  // "Upload now" button. Does NOT touch the sermons table.
  async function queueSermonsForReview() {
    if (sermonsReady === 0) return
    if (!churchId) {
      setError('No destination church selected. Go back to Step 4 (Configure scan), pick a church, then return here.')
      return
    }
    setSermonStatus('running'); setError('')
    let queuedCount = 0
    try {
      for (const p of processed) {
        if (p.videoStatus !== 'segmented') continue
        const r = p.row
        for (const s of p.sermons) {
          if (!s.picked) continue
          const youtubeUrl = r.youtubeUrl || (r.youtubeId ? `https://www.youtube.com/watch?v=${r.youtubeId}` : null)
          // Slim payload — full sermon record (including refs) plus the
          // row metadata QueueList's "Upload now" needs to insert later.
          const dataJson = {
            sermon: s,
            row: {
              youtubeId:  r.youtubeId || null,
              youtubeUrl: youtubeUrl,
              durationS:  r.durationS || null,
              date:       r.date || null,
              series:     r.series || null,
              title:      r.title || null,
            },
            churchId,
          }
          const { error: qErr } = await supabase.from('import_queue').insert({
            church_id:   churchId,
            type:        'service',
            video_id:    r.youtubeId || null,
            title:       s.title || r.title || null,
            youtube_url: youtubeUrl,
            reason:      'Needs review',
            data_json:   dataJson,
            created_by:  user?.id || null,
          })
          if (qErr) {
            // eslint-disable-next-line no-console
            console.error('Queue insert error:', qErr)
            log(`✗ Queue failed: "${s.title}" — ${qErr.message}`, 'err')
            continue
          }
          queuedCount += 1
          log(`↷ Queued for review: "${s.title}"`, 'warn')
        }
      }
      setCounts(c => ({ ...c, needs_review: c.needs_review + queuedCount }))
      setLastAction({ kind: 'queued', count: queuedCount })
      // Clear picked flags on the queued sermons.
      setProcessed(prev => prev.map(p => ({
        ...p,
        sermons: (p.sermons || []).map(s => ({ ...s, picked: false, queued: s.picked ? true : !!s.queued })),
      })))
      setSermonStatus('done')
      refreshBadge?.()
    } catch (e) {
      setError(e.message || String(e))
      setSermonStatus('pending')
    }
  }

  async function handleFinish() {
    // Write the import_runs row only once we're calling it done.
    const { data: runRow } = await supabase.from('import_runs').insert({
      church_id:               churchId,
      admin_id:                user?.id || null,
      sermons_imported:        counts.sermons,
      encouragements_imported: counts.encouragements,
      skipped_count:           counts.skipped,
      needs_review_count:      counts.needs_review,
    }).select().single()
    log(`Wrote import run ${runRow?.id || ''}`, 'info')
    clearAllProgress()
    setResults(counts)
  }

  const canFinish = sermonStatus === 'done'

  // ── Done view ───────────────────────────────────────────────
  if (results) {
    const totalRefs = processed.reduce((n, p) => n + (p.sermons || []).reduce((m, s) => m + ((s.picked && s.refs?.length) || 0), 0), 0)
    return (
      <div style={{textAlign:'center', padding:'40px 20px'}}>
        <div style={{fontSize:54, marginBottom:14}}>🎉</div>
        <H1>Import complete</H1>
        <Sub>Run summary below. Anything that didn't make it has been queued in Needs Review.</Sub>
        <div style={{display:'flex', gap:14, justifyContent:'center', marginTop:24, marginBottom:32, flexWrap:'wrap'}}>
          <Stat label="Sermons uploaded" value={results.sermons || 0}     color={C.accent}/>
          <Stat label="Refs found"       value={totalRefs}                color={C.blue}/>
          <Stat label="Skipped"          value={results.skipped || 0}     color={C.faint}/>
          <Stat label="Needs review"     value={results.needs_review || 0} color="#FF8888"/>
        </div>
        <div style={{display:'flex', gap:10, justifyContent:'center'}}>
          <PrimaryBtn kind="ghost" onClick={() => window.location.assign('/app') }>View in app</PrimaryBtn>
          <PrimaryBtn onClick={onReset}>Start another import →</PrimaryBtn>
        </div>
      </div>
    )
  }

  return (
    <div>
      <H2>6. Review & upload</H2>
      <Sub>Cards are collapsed by default — click <em>Expand</em> on any row to edit, see refs, or jump to the YouTube timestamp.</Sub>

      <div style={{display:'flex', gap:14, marginBottom:18}}>
        <Stat label="Sermons ready" value={sermonsReady} color={C.accent}/>
        <Stat label="Skipped / failed" value={skipped.length} color="#FF8888"/>
      </div>

      {segmented.length === 0 && <Note kind="warn">Nothing to review — every video was skipped or failed.</Note>}

      {/* ── BULK SERMON CONTROLS ───────────────────────────── */}
      {segmented.length > 0 && (
        <SectionCard
          title={`Sermon notes (${sermonsReady} ready)`}
          subTitle={`${sermonsReady} of ${sermonsTotal} selected`}
          accent={C.accent}>
          <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:6, alignItems:'center'}}>
            <SmallBtn onClick={() => setAllSermonsPicked(true)}>☑ Select all</SmallBtn>
            <SmallBtn onClick={() => setAllSermonsPicked(false)}>Deselect all</SmallBtn>
            <SmallBtn
              disabled={!churchId || addableSermonNotes === 0}
              onClick={() => addAllOfKindToReview('sermon')}>
              ☆ Add all sermon notes to review{addableSermonNotes > 0 ? ` (${addableSermonNotes})` : ''}
            </SmallBtn>
            <SmallBtn
              disabled={!churchId || addableEncouragements === 0}
              onClick={() => addAllOfKindToReview('encouragement')}>
              ☆ Add all encouragements to review{addableEncouragements > 0 ? ` (${addableEncouragements})` : ''}
            </SmallBtn>
            {queuedRefs > 0 && (
              <span style={{fontSize:11, color:C.muted, fontFamily:FONT}}>
                {queuedRefs} ref{queuedRefs === 1 ? '' : 's'} already in review
              </span>
            )}
            <div style={{flex:1}}/>
            <PrimaryBtn
              kind="ghost"
              disabled={sermonsReady === 0 || sermonStatus === 'running' || !churchId}
              onClick={queueSermonsForReview}
              title={!churchId
                ? 'No destination church set — go back to Configure scan.'
                : 'Queue checked sermons for manual review later. Does NOT insert into the sermons table.'}>
              {sermonStatus === 'running' ? '…' : `Add to review queue (${sermonsReady})`}
            </PrimaryBtn>
            <PrimaryBtn
              kind="gold"
              disabled={sermonsReady === 0 || sermonStatus === 'running' || !churchId}
              onClick={uploadSermons}
              title={!churchId ? 'No destination church set — go back to Configure scan.' : 'Insert checked sermons into the sermons table immediately.'}>
              {sermonStatus === 'running'
                ? 'Uploading…'
                : !churchId
                  ? 'No church selected'
                  : `Upload selected sermons (${sermonsReady})`}
            </PrimaryBtn>
          </div>
        </SectionCard>
      )}

      {/* ── LAST-ACTION SUMMARY ─────────────────────────────── */}
      {lastAction && lastAction.count > 0 && (
        <Note kind={lastAction.kind === 'uploaded' ? 'ok' : 'warn'}>
          {lastAction.kind === 'uploaded'
            ? `${lastAction.count} sermon${lastAction.count === 1 ? '' : 's'} uploaded successfully.`
            : lastAction.kind === 'in_review'
              ? `${lastAction.count} ref${lastAction.count === 1 ? '' : 's'} added to review — find them under Needs Review.`
              : `${lastAction.count} sermon${lastAction.count === 1 ? '' : 's'} added to review queue.`}
        </Note>
      )}
      {lastAction && lastAction.count === 0 && (
        <Note kind="warn">
          {lastAction.kind === 'uploaded'
            ? 'No sermons were uploaded.'
            : lastAction.kind === 'in_review'
              ? 'Nothing new to add to review.'
              : 'No sermons were queued.'}
        </Note>
      )}

      {error && <Note kind="err">{error}</Note>}

      {/* ── PER-SERVICE COLLAPSED CARDS ─────────────────────── */}
      <H2>Services</H2>
      {processed.map((p, idx) => (
        <VideoReviewCard key={idx} p={p} idx={idx}
          patchRow={patchRow}
          onAddRefToReview={(sIdx, rIdx) => addRefsToReview([{ pIdx: idx, sIdx, rIdx }])}
          onRemoveRefFromReview={(sIdx, rIdx) => removeRefFromReview({ pIdx: idx, sIdx, rIdx })}/>
      ))}

      {/* ── UPLOAD LOG ──────────────────────────────────────── */}
      {uploadLog.length > 0 && (
        <>
          <H2>Upload log</H2>
          <div style={{
            background:'#08080d', border:`1px solid ${C.border}`, borderRadius:10,
            padding:'14px 16px', maxHeight:280, overflowY:'auto',
            fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12, lineHeight:1.7,
            marginBottom:18,
          }}>
            {uploadLog.map(l => (
              <div key={l.id} style={{
                color: l.kind === 'err' ? '#FF8888' : l.kind === 'orange' ? '#F59E0B' : l.kind === 'warn' ? C.accent : l.kind === 'ok' ? C.green : C.muted,
              }}>{l.text}</div>
            ))}
          </div>
        </>
      )}

      <div style={{display:'flex', gap:10, marginTop:24}}>
        <PrimaryBtn kind="ghost" onClick={onReset}>← Start over</PrimaryBtn>
        <PrimaryBtn disabled={!canFinish} onClick={handleFinish}>
          Finish import →
        </PrimaryBtn>
      </div>
    </div>
  )
}
function SectionCard({ title, subTitle, accent, children }) {
  return (
    <div style={{
      background:C.surface, border:`1px solid ${C.border}`, borderRadius:12,
      borderLeft:`3px solid ${accent}`, padding:'14px 18px', marginBottom:14,
    }}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10}}>
        <div style={{fontSize:14, fontWeight:800, color:C.text}}>{title}</div>
        <div style={{fontSize:11, color:C.faint}}>{subTitle}</div>
      </div>
      {children}
    </div>
  )
}
function SmallBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        background:C.surface2, color:C.text, border:`1px solid ${C.borderHi}`,
        padding:'8px 12px', borderRadius:7, fontSize:12, fontWeight:600,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
        fontFamily:FONT,
      }}>{children}</button>
  )
}
function Stat({ label, value, color }) {
  return (
    <div style={{
      background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
      padding:'14px 18px', minWidth:130,
    }}>
      <div style={{fontSize:11, color:C.faint, letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700}}>{label}</div>
      <div style={{fontSize:24, fontWeight:800, color, marginTop:4}}>{value}</div>
    </div>
  )
}

function VideoReviewCard({ p, idx, patchRow, onAddRefToReview, onRemoveRefFromReview }) {
  const r = p.row
  const [expanded, setExpanded] = useState(false)

  const total = r.durationS || Math.max(
    ...p.sermons.map(s => s.endS || 0),
    0
  ) || 1
  const youtubeWatchBase = r.youtubeId ? `https://www.youtube.com/watch?v=${r.youtubeId}` : (r.youtubeUrl || '')

  // Aggregates for the one-line summary
  const firstSermon  = p.sermons[0]
  const pickedAnySermon = p.sermons.some(s => s.picked)
  const summaryTitle = firstSermon?.title || r.title || '(untitled)'
  const summarySource = firstSermon?.titleSource || 'excel'
  const summaryPreacher = firstSermon?.preacher || ''
  const summaryRefCount = p.sermons.reduce((n,s) => n + (s.refs?.length || 0), 0)
  const summaryPlayUrl = youtubeWatchBase
    ? `${youtubeWatchBase}${firstSermon?.startS ? `&t=${firstSermon.startS}s` : ''}`
    : ''
  const dateLabel = r.date
    ? new Date(r.date).toLocaleDateString(undefined, { weekday:'short', day:'numeric', month:'short', year:'numeric' })
    : null

  // The service-level checkbox toggles every sermon in the service at once
  function toggleServicePicked(v) {
    patchRow(idx, p2 => ({
      ...p2,
      sermons: p2.sermons.map(s => ({...s, picked: v})),
    }))
  }

  const segmented = p.videoStatus === 'segmented'

  return (
    <div style={{
      background:C.surface, border:`1px solid ${C.border}`, borderRadius:12,
      marginBottom:10, overflow:'hidden',
    }}>
      {/* ─── COLLAPSED ONE-LINER ─── */}
      <div style={{display:'flex', alignItems:'center', gap:10, padding:'12px 16px'}}>
        {segmented ? (
          <input type="checkbox" checked={pickedAnySermon}
            onChange={e => toggleServicePicked(e.target.checked)}
            style={{accentColor:C.accent, width:16, height:16, cursor:'pointer'}}/>
        ) : (
          <span style={{fontSize:16, color:C.accent, width:16, textAlign:'center'}}>⚠</span>
        )}
        {summaryPlayUrl && (
          <a href={summaryPlayUrl} target="_blank" rel="noreferrer"
            title="Open on YouTube at sermon start"
            style={{
              width:22, height:22, borderRadius:'50%',
              background:'rgba(220,38,38,0.18)', color:'#fff',
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              fontSize:10, textDecoration:'none', flexShrink:0,
            }}>▶</a>
        )}
        <div style={{flex:1, minWidth:0, fontSize:13, color:C.text, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
          {segmented ? (
            <>
              {dateLabel && <span style={{color:C.muted}}>{dateLabel}</span>}
              <Dot/>
              <strong style={{color:C.text, fontWeight:700}}>{cleanSermonTitle(summaryTitle)}</strong>
              <TitleBadge source={firstSermon?.titleEdited ? 'edited' : summarySource}/>
              {/* Confidence badge — spec mapping:
                  • FULL SERVICE (amber) — no gap found / isFullService
                  • LOW (red) — fallback used (Pass 2 returned null end) or low conf
                  • MED (amber) — gap + medium conf
                  • HIGH (green) — gap + high conf */}
              {firstSermon?.isFullService ? (
                <Badge color={C.accent}>FULL SERVICE</Badge>
              ) : firstSermon?.endFromFallback || firstSermon?.ai_confidence === 'low' ? (
                <Badge color="#FF8888">LOW</Badge>
              ) : firstSermon?.ai_confidence === 'medium' ? (
                <Badge color={C.accent}>MED</Badge>
              ) : firstSermon?.ai_confidence === 'high' ? (
                <Badge color={C.green}>HIGH</Badge>
              ) : null}
              {summaryPreacher && <><Dot/><span style={{color:C.muted}}>{summaryPreacher}</span></>}
              <Dot/><span style={{color:C.muted}}>{summaryRefCount} refs</span>
              {firstSermon?.start_ts && firstSermon?.end_ts && (
                <><Dot/><span style={{color:C.muted}}>{firstSermon.start_ts}→{firstSermon.end_ts}</span></>
              )}
            </>
          ) : (
            <>
              {dateLabel && <span style={{color:C.muted}}>{dateLabel}</span>}
              <Dot/>
              <strong style={{color:C.text, fontWeight:700}}>{cleanSermonTitle(r.title) || '(untitled)'}</strong>
              <Dot/>
              <span style={{color:C.accent}}>Needs review — {p.reason || p.videoStatus}</span>
            </>
          )}
        </div>
        <button onClick={() => setExpanded(v => !v)}
          style={{
            background:'transparent', border:`1px solid ${C.borderHi}`,
            borderRadius:6, padding:'5px 10px', fontSize:11, fontWeight:700,
            color:C.muted, cursor:'pointer', fontFamily:FONT, whiteSpace:'nowrap',
          }}>{expanded ? 'Collapse ▴' : 'Expand ▾'}</button>
      </div>

      {/* Always-visible truncated AI summary preview (full text in the
          expanded card's per-sermon AI SUMMARY sections). */}
      {!expanded && firstSermon?.aiSummary && (
        <div style={{
          padding:'0 16px 12px 52px', fontSize:12.5, color:C.muted, lineHeight:1.5,
          display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
        }}>
          {firstSermon.aiSummary}
        </div>
      )}

      {/* ─── EXPANDED DETAIL ─── */}
      {expanded && (
        <div style={{borderTop:`1px solid ${C.border}`, padding:'14px 18px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14, marginBottom:14}}>
            <div style={{minWidth:0, flex:1}}>
              <div style={{fontSize:11, color:C.faint, letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, marginBottom:4}}>
                Service {idx+1}{r.date ? ` · ${r.date}` : ''}{r.durationS ? ` · ${secondsToTs(r.durationS)}` : ''}
              </div>
              <div style={{fontSize:15, fontWeight:700, color:C.text, marginBottom:4}}>{cleanSermonTitle(r.title) || '(untitled)'}</div>
              {r.youtubeId && <div style={{fontSize:12, color:C.muted}}>{youtubeWatchBase}</div>}
            </div>
            <StatusPill status={p.videoStatus} reason={p.reason}/>
          </div>

          {segmented && (() => {
            // Flatten + sort every ref across this service's sermons.
            // Tolerate both timestamp_s (current snake_case) and timestampS
            // (older camelCase) — old data should still display correctly.
            // _sIdx + _rIdx let the row checkbox round-trip back to the
            // original ref in the nested processed state for toggling.
            const allRefs = p.sermons
              .flatMap((s, sIdx) => (Array.isArray(s.refs) ? s.refs : []).map((ref, rIdx) => ({
                ...ref,
                _sIdx: sIdx,
                _rIdx: rIdx,
                _ts: typeof ref.timestamp_s === 'number' ? ref.timestamp_s
                   : typeof ref.timestampS  === 'number' ? ref.timestampS
                   : null,
              })))
              .sort((a, b) => (a._ts ?? Number.MAX_SAFE_INTEGER) - (b._ts ?? Number.MAX_SAFE_INTEGER))
            const refCount = allRefs.length
            const pickedRefCount = allRefs.filter(r => r.picked !== false).length
            return (
              <>
                {/* Pass 1 / 2 timing summary — one canonical line per the
                    spec when we have a real sermon range; falls back to
                    the raw debug string when the service couldn't be
                    segmented. Verify ↗ jumps to YouTube at sermon_start_ms. */}
                {(() => {
                  const s = firstSermon
                  if (!s) return null
                  const dismissalTs = s.pass1Dismissal != null ? secondsToTs(s.pass1Dismissal / 1000) : null
                  const gapTs       = s.pass1GapAt     != null ? secondsToTs(s.pass1GapAt     / 1000) : null
                  const haveTimes   = !s.isFullService && s.start_ts && s.end_ts
                  const verifyUrl   = (r.youtubeId && s.startS != null)
                    ? `https://www.youtube.com/watch?v=${r.youtubeId}&t=${s.startS}s`
                    : null
                  return (
                    <div style={{
                      fontSize:11, color:C.muted, marginBottom:10,
                      fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace',
                      display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
                    }}>
                      <span>
                        {haveTimes ? (
                          <>
                            {dismissalTs && <>Children dismissed at {dismissalTs} · </>}
                            {gapTs && <>Gap at {gapTs} · </>}
                            Sermon {s.start_ts}→{s.end_ts}
                            {s.endFromFallback && <span style={{color:'#FF8888'}}> (end via 45 min fallback)</span>}
                          </>
                        ) : (
                          <>⌁ Pass 1: {s.pass1Debug || '(no segmentation)'}</>
                        )}
                      </span>
                      {verifyUrl && (
                        <a href={verifyUrl} target="_blank" rel="noreferrer"
                          style={{
                            color:C.accent, textDecoration:'none', fontWeight:700,
                            fontSize:11, padding:'2px 8px', borderRadius:5,
                            border:`1px solid ${C.accent}`,
                          }}>Verify ↗</a>
                      )}
                    </div>
                  )
                })()}

                {/* Editable details — title / date / series / preacher.
                    Changes flow into the row (title/date/series) or the
                    first sermon (preacher), so the upload step writes the
                    edited values to the sermons row. */}
                <div style={{
                  background:C.surface2, border:`1px solid ${C.border}`,
                  borderRadius:10, padding:'12px 14px', marginBottom:14,
                }}>
                  <div style={{fontSize:11, fontWeight:700, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8}}>
                    Edit details
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'2fr 130px 1fr 1fr', gap:8}}>
                    <label style={{display:'flex', flexDirection:'column', gap:4}}>
                      <span style={{fontSize:10, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase'}}>Title</span>
                      <input style={{...inputStyle, padding:'7px 9px', fontSize:12}}
                        value={r.title || ''}
                        onChange={e => patchRow(idx, p2 => ({
                          ...p2,
                          row:     { ...p2.row, title: e.target.value },
                          sermons: (p2.sermons || []).map((s, i) => i === 0 ? { ...s, title: e.target.value, titleEdited: true } : s),
                        }))}/>
                    </label>
                    <label style={{display:'flex', flexDirection:'column', gap:4}}>
                      <span style={{fontSize:10, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase'}}>Date</span>
                      <input type="date" style={{...inputStyle, padding:'7px 9px', fontSize:12}}
                        value={r.date || ''}
                        onChange={e => patchRow(idx, p2 => ({
                          ...p2,
                          row: { ...p2.row, date: e.target.value, dateSource: 'manual' },
                        }))}/>
                      {sourceLabel(r.dateSource) && (
                        <span style={{fontSize:10, color:C.faint}}>{sourceLabel(r.dateSource)}</span>
                      )}
                    </label>
                    <label style={{display:'flex', flexDirection:'column', gap:4}}>
                      <span style={{fontSize:10, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase'}}>Series</span>
                      <input style={{...inputStyle, padding:'7px 9px', fontSize:12}}
                        value={r.series || ''}
                        onChange={e => patchRow(idx, p2 => ({
                          ...p2,
                          row: { ...p2.row, series: e.target.value },
                        }))}/>
                    </label>
                    <label style={{display:'flex', flexDirection:'column', gap:4}}>
                      <span style={{fontSize:10, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase'}}>Preacher</span>
                      <input style={{...inputStyle, padding:'7px 9px', fontSize:12}}
                        placeholder="(unset)"
                        value={firstSermon?.preacher || ''}
                        onChange={e => patchRow(idx, p2 => ({
                          ...p2,
                          sermons: (p2.sermons || []).map((s, i) => i === 0 ? { ...s, preacher: e.target.value, preacherDetected: false, preacherSource: 'manual' } : s),
                        }))}/>
                      {sourceLabel(firstSermon?.preacherSource || r.preacherSource) && (
                        <span style={{fontSize:10, color:C.faint}}>{sourceLabel(firstSermon?.preacherSource || r.preacherSource)}</span>
                      )}
                    </label>
                  </div>
                </div>

                {/* AI summary — generated during processing (Step 6); collapsible
                    so the admin can read it before uploading. One block per
                    sermon in the service. */}
                {p.sermons.map((s, sIdx) => (
                  <SermonSummaryBlock key={sIdx} p={p} idx={idx} sIdx={sIdx} sermon={s} patchRow={patchRow}/>
                ))}

                {/* Prominent ref count at the top of the expanded card */}
                <div style={{
                  fontSize:15, fontWeight:800, color: refCount > 0 ? C.accent : '#FF8888',
                  marginBottom:12,
                }}>
                  {refCount > 0
                    ? (() => {
                        const sermonRefCount = allRefs.filter(r => r.picked !== false && (r.kind || 'sermon') === 'sermon').length
                        const encRefCount    = allRefs.filter(r => r.picked !== false && r.kind === 'encouragement').length
                        const parts = []
                        if (sermonRefCount) parts.push(`${sermonRefCount} sermon ref${sermonRefCount === 1 ? '' : 's'}`)
                        if (encRefCount)    parts.push(`${encRefCount} word${encRefCount === 1 ? '' : 's'} of encouragement`)
                        if (parts.length === 0) parts.push('Nothing selected')
                        return `${parts.join(' · ')} · ${pickedRefCount}/${refCount} selected for upload`
                      })()
                    : 'No Bible refs found — consider manual review'}
                </div>

                <Timeline total={total} sermons={p.sermons}/>

                {refCount > 0 && (
                  <div style={{
                    marginTop:14,
                    background:C.surface2, border:`1px solid ${C.border}`,
                    borderRadius:10, overflow:'hidden',
                  }}>
                    {/* Header row */}
                    <div style={{
                      display:'grid', gridTemplateColumns:'32px 130px 70px 130px 1fr 110px 36px',
                      gap:10, padding:'8px 12px', fontSize:11, fontWeight:700,
                      letterSpacing:'0.08em', textTransform:'uppercase', color:C.faint,
                      borderBottom:`1px solid ${C.border}`, background:'rgba(0,0,0,0.18)',
                      alignItems:'center',
                    }}>
                      <div style={{textAlign:'center'}}>
                        <input type="checkbox"
                          checked={pickedRefCount === refCount && refCount > 0}
                          ref={el => { if (el) el.indeterminate = pickedRefCount > 0 && pickedRefCount < refCount }}
                          title={pickedRefCount === refCount ? 'Deselect all refs' : 'Select all refs'}
                          onChange={e => {
                            const picked = e.target.checked
                            patchRow(idx, p2 => ({
                              ...p2,
                              sermons: (p2.sermons || []).map(s => ({
                                ...s,
                                refs: (s.refs || []).map(r2 => ({ ...r2, picked })),
                              })),
                            }))
                          }}
                          style={{accentColor:C.accent, width:14, height:14, cursor:'pointer'}}/>
                      </div>
                      <div>Verse ref</div>
                      <div>Timestamp</div>
                      <div>Save as</div>
                      <div>Notes</div>
                      <div style={{textAlign:'center'}}>Review</div>
                      <div style={{textAlign:'center'}}>▶</div>
                    </div>
                    {/* Ref rows */}
                    {allRefs.map((ref, ri) => {
                      const ts   = ref.timestamp
                                || (ref._ts != null ? secondsToTs(ref._ts) : '—')
                      const url  = r.youtubeId && ref._ts != null
                                ? `https://www.youtube.com/watch?v=${r.youtubeId}&t=${ref._ts}`
                                : null
                      const picked = ref.picked !== false
                      const queued = !!ref.queued
                      const kind   = ref.kind === 'encouragement' ? 'encouragement' : 'sermon'
                      const setKind = (next) => patchRow(idx, p2 => ({
                        ...p2,
                        sermons: (p2.sermons || []).map((s, si) => si !== ref._sIdx ? s : ({
                          ...s,
                          refs: (s.refs || []).map((r2, rIdx) => rIdx !== ref._rIdx ? r2 : ({ ...r2, kind: next })),
                        })),
                      }))
                      return (
                        <div key={ri} style={{
                          display:'grid', gridTemplateColumns:'32px 130px 70px 130px 1fr 110px 36px',
                          gap:10, padding:'10px 12px', alignItems:'center', fontSize:13,
                          borderBottom: ri < allRefs.length - 1 ? `1px solid ${C.border}` : 'none',
                          opacity: queued ? 0.5 : (picked ? 1 : 0.45),
                          background: queued ? 'rgba(232,200,122,0.05)' : 'transparent',
                        }}>
                          <div style={{textAlign:'center'}}>
                            <input type="checkbox" checked={picked}
                              disabled={queued}
                              title={queued
                                ? 'In review — remove from review to upload'
                                : picked ? 'Skip this ref on upload' : 'Include this ref on upload'}
                              onChange={() => patchRow(idx, p2 => ({
                                ...p2,
                                sermons: (p2.sermons || []).map((s, si) => si !== ref._sIdx ? s : ({
                                  ...s,
                                  refs: (s.refs || []).map((r2, rIdx) => rIdx !== ref._rIdx ? r2 : ({ ...r2, picked: !picked })),
                                })),
                              }))}
                              style={{accentColor:C.accent, width:14, height:14, cursor: queued ? 'not-allowed' : 'pointer'}}/>
                          </div>
                          <div style={{
                            fontWeight:700, color:C.text,
                            fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace',
                            textDecoration: picked && !queued ? 'none' : 'line-through',
                            display:'flex', alignItems:'center', gap:6, flexWrap:'wrap',
                          }}>
                            <span>{ref.verseRef}</span>
                            {queued && (
                              <span style={{
                                background:'rgba(232,200,122,0.18)', color:C.accent,
                                fontSize:9, fontWeight:800, letterSpacing:'0.06em',
                                padding:'2px 6px', borderRadius:8, textTransform:'uppercase',
                                border:`1px solid rgba(232,200,122,0.4)`,
                              }}>In review</span>
                            )}
                          </div>
                          <div style={{color:C.muted, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12}}>{ts}</div>
                          <div>
                            <select
                              disabled={!picked || queued}
                              value={kind}
                              onChange={e => setKind(e.target.value)}
                              title={kind === 'sermon'
                                ? 'Insert as a sermon ref under this sermon record'
                                : 'Insert as a standalone word of encouragement'}
                              style={{
                                ...inputStyle, padding:'5px 6px', fontSize:11,
                                width:'100%',
                                color: kind === 'encouragement' ? '#FF6B9D' : C.text,
                                fontWeight: 600,
                              }}>
                              <option value="sermon">Sermon notes</option>
                              <option value="encouragement">Word of encouragement</option>
                            </select>
                          </div>
                          <div style={{color:C.muted, lineHeight:1.45}}>{ref.notes || '—'}</div>
                          <div style={{textAlign:'center'}}>
                            {queued ? (
                              <button
                                type="button"
                                onClick={() => onRemoveRefFromReview?.(ref._sIdx, ref._rIdx)}
                                title="Dismiss the review-queue entry and re-enable upload"
                                style={{
                                  background:'transparent', color:C.muted,
                                  border:`1px solid ${C.borderHi}`,
                                  padding:'4px 8px', borderRadius:6, fontSize:10,
                                  fontWeight:700, cursor:'pointer', fontFamily:FONT,
                                  letterSpacing:'0.04em',
                                }}>Remove</button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onAddRefToReview?.(ref._sIdx, ref._rIdx)}
                                title="Park this ref in Needs Review without uploading it"
                                style={{
                                  background:'transparent', color:C.accent,
                                  border:`1px solid rgba(232,200,122,0.4)`,
                                  padding:'4px 8px', borderRadius:6, fontSize:10,
                                  fontWeight:700, cursor:'pointer', fontFamily:FONT,
                                  letterSpacing:'0.04em',
                                }}>☆ Add</button>
                            )}
                          </div>
                          <div style={{textAlign:'center'}}>
                            {url ? (
                              <a href={url} target="_blank" rel="noreferrer"
                                title={`Open YouTube at ${ts}`}
                                style={{
                                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                                  width:28, height:28, borderRadius:'50%',
                                  background:'rgba(220,38,38,0.18)', color:'#fff',
                                  fontSize:11, textDecoration:'none',
                                }}>▶</a>
                            ) : (
                              <span style={{color:C.faint, fontSize:11}}>—</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}
// Collapsible AI-summary block for one sermon on a review card. The summary is
// normally generated during Step 6 processing; if it's missing the admin can
// generate it on demand (when the transcript slice is still attached) and the
// result is written back into state via patchRow so it persists to upload.
function SermonSummaryBlock({ p, idx, sIdx, sermon, patchRow }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const summary = (sermon.aiSummary || '').trim()
  // transcriptSlice may be stripped by slimming; it's re-attached on rehydrate,
  // but if it's genuinely absent we can't regenerate here (upload will do it).
  const hasSlice = !!((sermon.transcriptSlice || '').trim())

  async function generate() {
    if (busy || !hasSlice) return
    setBusy(true)
    try {
      const text = await generateSermonSummary(sermon.transcriptSlice || '', {
        title: sermon.title, preacher: sermon.preacher || '', date: p.row?.date || '',
      })
      const displayTitle = text ? await generateDisplayTitle(text, { title: sermon.title }) : null
      const bigIdea = text ? await generateBigIdea(text, { title: sermon.title }) : null
      patchRow(idx, p2 => ({
        ...p2,
        sermons: (p2.sermons || []).map((s, i) => i === sIdx ? { ...s, aiSummary: text || '', displayTitle: displayTitle || s.displayTitle || '', bigIdea: bigIdea || s.bigIdea || '' } : s),
      }))
      setOpen(true)
    } catch (_e) {
      // best-effort — a failed summary never blocks review or upload
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      background:C.surface2, border:`1px solid ${C.border}`,
      borderRadius:10, padding:'10px 14px', marginBottom:14,
    }}>
      <div style={{display:'flex', alignItems:'center', gap:10}}>
        <button type="button" onClick={() => setOpen(v => !v)}
          style={{
            background:'transparent', border:'none', padding:0, cursor:'pointer',
            fontFamily:FONT, display:'flex', alignItems:'center', gap:8, flex:1,
            textAlign:'left', minWidth:0,
          }}>
          <span style={{fontSize:11, fontWeight:700, color:C.faint, letterSpacing:'0.08em', textTransform:'uppercase'}}>
            AI summary{(p.sermons?.length || 0) > 1 ? ` — ${cleanSermonTitle(sermon.title) || `sermon ${sIdx+1}`}` : ''}
          </span>
          <span style={{fontSize:11, color:C.muted}}>{open ? '▴' : '▾'}</span>
        </button>
        {!summary && (
          hasSlice ? (
            <button type="button" onClick={generate} disabled={busy}
              style={{
                background:'transparent', color:C.accent, border:`1px solid rgba(232,200,122,0.4)`,
                padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
                cursor: busy ? 'default' : 'pointer', fontFamily:FONT, whiteSpace:'nowrap',
                opacity: busy ? 0.6 : 1,
              }}>{busy ? 'Generating…' : 'Generate'}</button>
          ) : (
            <span style={{fontSize:10, color:C.faint, whiteSpace:'nowrap'}}>Summary generated at upload</span>
          )
        )}
      </div>
      {open && (
        <div style={{marginTop:8, fontSize:13, color:C.text, lineHeight:1.55, whiteSpace:'pre-wrap'}}>
          {summary || (
            <span style={{color:C.muted}}>{busy ? 'Generating…' : 'No summary generated.'}</span>
          )}
        </div>
      )}
    </div>
  )
}
function Dot() {
  return <span style={{color:C.faint, fontSize:10}}>·</span>
}
function TitleBadge({ source }) {
  if (!source || source === 'excel' || source === 'edited') return null
  if (source === 'detected') {
    return (
      <span style={{
        background:'rgba(232,200,122,0.15)', color:C.accent, fontSize:9, fontWeight:800,
        letterSpacing:'0.06em', padding:'2px 6px', borderRadius:8, textTransform:'uppercase',
        border:`1px solid rgba(232,200,122,0.4)`,
      }}>Detected</span>
    )
  }
  return (
    <span style={{
      background:'rgba(232,200,122,0.10)', color:C.accent, fontSize:9, fontWeight:800,
      letterSpacing:'0.06em', padding:'2px 6px', borderRadius:8, textTransform:'uppercase',
      border:`1px dashed rgba(232,200,122,0.5)`,
    }}>AI suggested</span>
  )
}
function StatusPill({ status, reason }) {
  const map = {
    segmented: { bg:'rgba(80,200,150,0.10)', fg:C.green, label:'Ready' },
    skipped:   { bg:'rgba(232,200,122,0.10)', fg:C.accent, label:'Skipped' },
    failed:    { bg:'rgba(220,38,38,0.15)', fg:'#FF8888', label:'Failed' },
    pending:   { bg:C.surface2, fg:C.faint, label:'Pending' },
  }
  const s = map[status] || map.pending
  return (
    <div style={{
      background:s.bg, color:s.fg, padding:'5px 10px', borderRadius:14,
      fontSize:11, fontWeight:700, whiteSpace:'nowrap',
    }} title={reason || ''}>{s.label}{reason ? ` · ${reason}` : ''}</div>
  )
}
function Timeline({ total, sermons }) {
  return (
    <div style={{position:'relative', height:18, background:C.surface2, borderRadius:9, overflow:'hidden'}}>
      {sermons.map((s, i) => {
        const lo = Math.max(0, (s.startS ?? 0) / total) * 100
        const hi = Math.min(1, (s.endS ?? total) / total) * 100
        return (
          <div key={`s${i}`} style={{
            position:'absolute', left:`${lo}%`, width:`${Math.max(1.5, hi-lo)}%`,
            top:2, bottom:2, background:`linear-gradient(90deg, ${C.accent}, #C9A85A)`,
            borderRadius:7, opacity: s.picked ? 1 : 0.25,
          }} title={`${s.title} · ${s.start_ts || '?'}–${s.end_ts || '?'}`}/>
        )
      })}
    </div>
  )
}
// SermonRow + SectionLabel + EncRow removed — the expanded VideoReviewCard
// now renders a flat refs table directly. Editable per-sermon fields can
// be reintroduced if the review workflow needs them again.
function Badge({ children, color }) {
  return (
    <span style={{
      background:`${color}1f`, color, fontSize:10, fontWeight:800, letterSpacing:'0.06em',
      padding:'3px 8px', borderRadius:10, textTransform:'uppercase', border:`1px solid ${color}40`,
    }}>{children}</span>
  )
}
function ConfidenceBadge({ level }) {
  const c = level === 'high' ? C.green : level === 'low' ? '#FF8888' : C.accent
  return <Badge color={c}>{level || 'medium'} conf.</Badge>
}

// (Step5Done has moved inline into Step7ReviewUpload's done view.)

// ─────────────────────────────────────────────────────────────────────────
//                       NEEDS REVIEW
// ─────────────────────────────────────────────────────────────────────────
function NeedsReview({ refreshBadge }) {
  const [tab, setTab] = useState('services')   // services | encouragements | history
  return (
    <div style={{maxWidth:1100}}>
      <H1>Needs Review</H1>
      <Sub>Anything the importer couldn't auto-handle ends up here. Dismiss what you've fixed elsewhere.</Sub>
      <div style={{display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${C.border}`}}>
        {[
          {id:'services',       label:'Skipped Services'},
          {id:'encouragements', label:'Skipped Encouragements'},
          {id:'history',        label:'Import History'},
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding:'12px 16px', background:'transparent',
              border:'none',
              borderBottom: tab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
              color: tab === t.id ? C.accent : C.muted,
              fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:FONT,
            }}>{t.label}</button>
        ))}
      </div>
      {tab === 'services'       && <QueueList type="service"       refreshBadge={refreshBadge}/>}
      {tab === 'encouragements' && <QueueList type="encouragement" refreshBadge={refreshBadge}/>}
      {tab === 'history'        && <ImportHistory/>}
    </div>
  )
}

function QueueList({ type, refreshBadge }) {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyIds, setBusyIds] = useState(() => new Set())
  const [rowError, setRowError] = useState({})  // { [queueRowId]: errorMessage }

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('import_queue')
      .select('*').eq('type', type).is('dismissed_at', null).order('created_at', { ascending:false })
    setItems(data || []); setLoading(false)
  }
  useEffect(() => { load() // eslint-disable-next-line
  }, [type])

  async function dismiss(id) {
    await supabase.from('import_queue').update({
      dismissed_at: new Date().toISOString(),
      dismissed_by: user?.id || null,
    }).eq('id', id)
    load(); refreshBadge?.()
  }

  // Promote a queued sermon to the sermons table. Mirrors Step7ReviewUpload's
  // uploadSermons single-sermon path. After a successful insert the queue
  // row is dismissed so it disappears from this list.
  async function uploadFromQueue(it) {
    const sermon = it?.data_json?.sermon
    const row    = it?.data_json?.row || {}
    const churchId = it?.data_json?.churchId || it?.church_id
    if (!sermon || !churchId) {
      setRowError(e => ({ ...e, [it.id]: 'Queue row has no sermon payload — cannot upload from here.' }))
      return
    }
    setBusyIds(s => { const n = new Set(s); n.add(it.id); return n })
    setRowError(e => ({ ...e, [it.id]: '' }))
    try {
      const needsReview = !!sermon.isFullService || sermon.ai_confidence === 'low'
      // Every playlist this video is in (deduped). `series` stays the primary
      // one; `playlists` carries the full set so the sermon shows under each.
      const playlistList = Array.isArray(row.playlists) && row.playlists.length
        ? [...new Set(row.playlists.filter(Boolean))]
        : (row.series ? [row.series] : [])
      const sermonInsert = {
        uploaded_by:  user?.id || null,
        church_id:    churchId,
        title:        sermon.title,
        series:       row.series || null,
        playlists:    playlistList.length ? playlistList : null,
        preacher:     sermon.preacher || null,
        date:         row.date || null,
        youtube_id:   row.youtubeId || null,
        youtube_url:  row.youtubeUrl || null,
        duration:     row.durationS || null,
        thumbnail:    row.thumbnail || (row.youtubeId ? `https://img.youtube.com/vi/${row.youtubeId}/mqdefault.jpg` : null),
        transcript:   sermon.transcriptSlice || '',
        needs_review: needsReview,
        is_full_service: !!sermon.isFullService,
        source:       'admin_tool',
      }
      let { data: insData, error: sErr } = await supabase.from('sermons').insert(sermonInsert).select()
      // Drop optional columns that may not exist yet (pre-migration) and retry.
      if (sErr && /column.*(is_full_service|thumbnail|source|playlists).*does not exist/i.test(sErr.message || '')) {
        // eslint-disable-next-line no-unused-vars
        const { is_full_service, thumbnail, source, playlists, ...fallback } = sermonInsert
        ;({ data: insData, error: sErr } = await supabase.from('sermons').insert(fallback).select())
      }
      if (sErr) throw sErr
      const sermonRow = Array.isArray(insData) ? insData[0] : insData
      if (!sermonRow?.id) throw new Error('Insert returned no row')

      // Skip refs the admin unchecked in review, then split by kind:
      // 'sermon' refs land in sermon_refs as usual; 'encouragement' refs
      // become standalone encouragements (one per ref).
      const pickedRefs        = (sermon.refs || []).filter(ref => ref?.picked !== false)
      const sermonRefs        = pickedRefs.filter(ref => (ref.kind || 'sermon') === 'sermon')
      const encouragementRefs = pickedRefs.filter(ref => ref.kind === 'encouragement')
      if (sermonRefs.length) {
        const baseRows = dedupeRefRows(sermonRefs.map(ref => ({
          sermon_id:   sermonRow.id,
          verse_ref:   ref.verseRef,
          book:        ref.book,
          chapter:     ref.chapter,
          verse:       ref.verse,
          end_verse:   ref.end_verse ?? ref.endVerse ?? ref.verse ?? null,
          timestamp_s: ref.timestamp_s ?? ref.timestampS ?? null,
          timestamp:   ref.timestamp || null,
          notes:       ref.notes || null,
        })))
        let rErr
        ;({ error: rErr } = await supabase.from('sermon_refs').insert(
          baseRows.map(r => ({ ...r, church_id: churchId }))
        ))
        // Tolerate end_verse / church_id columns missing on older deployments.
        if (rErr && /column.*does not exist/i.test(rErr.message || '')) {
          const msg = rErr.message || ''
          const keepChurch = !/church_id/i.test(msg)
          const keepEnd    = !/end_verse/i.test(msg)
          const retryRows = baseRows.map(r => {
            // eslint-disable-next-line no-unused-vars
            const { end_verse, ...rest } = r
            const base = keepEnd ? r : rest
            return keepChurch ? { ...base, church_id: churchId } : base
          })
          ;({ error: rErr } = await supabase.from('sermon_refs').insert(retryRows))
        }
        if (rErr) throw rErr
      }
      if (encouragementRefs.length) {
        // Single encouragement carrying ALL the picked refs — mirrors the
        // bulk upload path. NeedsReview rows don't carry transcript
        // segments through, so buildEncouragementContent falls back to
        // the joined Claude notes for `text` (rather than the full
        // verbatim transcript). Name and summary are still set.
        const ytId = row.youtubeId || sermon.youtubeId || null
        const earliestTs = encouragementRefs.reduce((acc, ref) => {
          const t = ref.timestamp_s ?? ref.timestampS
          if (typeof t !== 'number') return acc
          return acc == null || t < acc ? t : acc
        }, null)
        const url = ytId && earliestTs != null
          ? `https://www.youtube.com/watch?v=${ytId}&t=${earliestTs}s`
          : (row.youtubeUrl || null)
        const { title, summary, text, startMs, endMs, transcriptExcerpt, isLikelySermon } = await buildEncouragementContent(
          encouragementRefs,
          row.transcriptJson  // usually undefined here; helper falls back gracefully
        )
        if (isLikelySermon) {
          // eslint-disable-next-line no-console
          console.warn('Skipped encouragement — looks like sermon teaching:', sermon.title)
        } else {
        const base = {
          church_id:  churchId,
          author_id:  user?.id || null,
          text,
          link_url:   url,
          link_label: url ? 'Watch the moment' : null,
        }
        const extras = {
          title,
          summary,
          start_ms:           startMs,
          end_ms:             endMs,
          transcript_excerpt: transcriptExcerpt,
          shared_by:  sermon.preacher || null,
          event_date: row.date || null,
        }
        async function tryInsert(payload) {
          return supabase.from('encouragements').insert(payload).select('id').single()
        }
        let res = await tryInsert({ ...base, ...extras })
        if (res.error && /column.*does not exist/i.test(res.error.message || '')) {
          // eslint-disable-next-line no-unused-vars
          const { summary: _s, start_ms: _sm, end_ms: _em, transcript_excerpt: _te, ...slim } = extras
          res = await tryInsert({ ...base, ...slim })
        }
        if (res.error) {
          res = await tryInsert(base)
        }
        if (!res.error) {
          const encId = res.data?.id
          if (encId) {
            const refRows = encouragementRefs.map(ref => ({
              encouragement_id: encId,
              verse_ref:        ref.verseRef,
              book:             ref.book,
              chapter:          ref.chapter,
              verse:            ref.verse,
              end_verse:        ref.end_verse ?? ref.endVerse ?? ref.verse ?? null,
              timestamp_s:      ref.timestamp_s ?? ref.timestampS ?? null,
              timestamp:        ref.timestamp || null,
              notes:            ref.notes || null,
            }))
            let { error: erErr } = await supabase.from('encouragement_refs').insert(refRows)
            if (erErr && /column.*end_verse.*does not exist/i.test(erErr.message || '')) {
              await supabase.from('encouragement_refs').insert(
                // eslint-disable-next-line no-unused-vars
                refRows.map(({ end_verse, ...rest }) => rest)
              )
            }
          }
        }
        }
      }
      // Success — dismiss the queue row so it's not still showing here.
      await supabase.from('import_queue').update({
        dismissed_at: new Date().toISOString(),
        dismissed_by: user?.id || null,
      }).eq('id', it.id)
      load(); refreshBadge?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Upload-from-queue failed:', e)
      setRowError(er => ({ ...er, [it.id]: e.message || String(e) }))
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(it.id); return n })
    }
  }

  if (loading) return <div style={{color:C.faint, padding:14}}>Loading…</div>
  if (!items.length) return <Note kind="ok">Nothing in the queue — you're all caught up.</Note>

  return (
    <div>
      {items.map(it => {
        // Promotable = queue row carries a full sermon payload that we can
        // insert into the sermons table. Older queue rows (e.g. failed
        // segmentations) don't have this so they get Dismiss-only.
        const promotable = !!it?.data_json?.sermon
        // Ref-only entries (parked sermon notes / encouragements) carry a
        // single ref + the sermon context it came from. Rendered with a
        // small "In review" tag and the parent sermon title underneath.
        const refOnly  = !promotable && !!it?.data_json?.ref
        const refCtx   = it?.data_json?.context || {}
        const busy = busyIds.has(it.id)
        return (
          <div key={it.id} style={{
            background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
            padding:'14px 18px', marginBottom:10,
            display:'flex', alignItems:'center', gap:14, flexWrap:'wrap',
          }}>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:14, fontWeight:700, color:C.text, marginBottom:3, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                <span>{it.title || '(no title)'}</span>
                {refOnly && (
                  <span style={{
                    background:'rgba(232,200,122,0.18)', color:C.accent,
                    fontSize:9, fontWeight:800, letterSpacing:'0.06em',
                    padding:'2px 6px', borderRadius:8, textTransform:'uppercase',
                    border:`1px solid rgba(232,200,122,0.4)`,
                  }}>In review</span>
                )}
              </div>
              <div style={{fontSize:12, color:C.muted, marginBottom:3}}>
                {new Date(it.created_at).toLocaleString()} · {it.reason || 'no reason given'}
              </div>
              {refOnly && (refCtx.sermon_title || refCtx.preacher || refCtx.date) && (
                <div style={{fontSize:11, color:C.faint, marginBottom:3}}>
                  {refCtx.sermon_title || ''}
                  {refCtx.preacher ? ` · ${refCtx.preacher}` : ''}
                  {refCtx.date ? ` · ${refCtx.date}` : ''}
                </div>
              )}
              {it.youtube_url && (
                <a href={it.youtube_url} target="_blank" rel="noreferrer"
                  style={{fontSize:11, color:C.blue, textDecoration:'none'}}>↗ {it.youtube_url}</a>
              )}
              {rowError[it.id] && (
                <div style={{marginTop:6, fontSize:12, color:'#FF8888'}}>{rowError[it.id]}</div>
              )}
            </div>
            {promotable && (
              <PrimaryBtn kind="gold" onClick={() => uploadFromQueue(it)} disabled={busy}>
                {busy ? 'Uploading…' : 'Upload now'}
              </PrimaryBtn>
            )}
            <PrimaryBtn kind="ghost" onClick={() => dismiss(it.id)} disabled={busy}>
              {refOnly ? 'Remove' : 'Dismiss'}
            </PrimaryBtn>
          </div>
        )
      })}
    </div>
  )
}

// ── MISSING FIELDS SCREEN ────────────────────────────────────────────────
// Lists sermons in the active church missing a preacher or a date. Two
// sub-tabs (one per field). Cards group by series so the admin can batch-
// apply a value across an entire series. Per-row Save also still works.
function MissingFieldsScreen({ refreshBadge }) {
  const { profile } = useAuth()
  const [tab, setTab] = useState('preacher')
  const [preacherCount, setPreacherCount] = useState(0)
  const [dateCount,     setDateCount]     = useState(0)
  const [summaryCount,  setSummaryCount]  = useState(null)
  const [mainPassageCount, setMainPassageCount] = useState(null)
  // Loaded once per tab open + after each save — cheap query, scoped by RLS.
  return (
    <div style={{maxWidth:1100}}>
      <H1>Missing Fields</H1>
      <Sub>Sermons missing a preacher, a date, an AI summary, or a classified main passage — fill them in here.</Sub>
      <div style={{display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${C.border}`, flexWrap:'wrap'}}>
        {[
          { id:'preacher', label:`Missing Preacher / Shared by (${preacherCount})` },
          { id:'date',     label:`Missing Date (${dateCount})` },
          { id:'summary',  label:`Missing AI Summary${summaryCount == null ? '' : ` (${summaryCount})`}` },
          { id:'mainpassage', label:`Missing Main Passage${mainPassageCount == null ? '' : ` (${mainPassageCount})`}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding:'12px 16px', background:'transparent',
              border:'none',
              borderBottom: tab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
              color: tab === t.id ? C.accent : C.muted,
              fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:FONT,
            }}>{t.label}</button>
        ))}
      </div>
      {tab === 'preacher' && (
        <MissingFieldList
          churchId={profile?.church_id || null}
          field="preacher"
          setCount={setPreacherCount}
          refreshBadge={refreshBadge}/>
      )}
      {tab === 'date' && (
        <MissingFieldList
          churchId={profile?.church_id || null}
          field="date"
          setCount={setDateCount}
          refreshBadge={refreshBadge}/>
      )}
      {tab === 'summary' && (
        <MissingSummariesPanel setCount={setSummaryCount}/>
      )}
      {tab === 'mainpassage' && (
        <MainPassagePanel setCount={setMainPassageCount}/>
      )}
    </div>
  )
}

// Fetch EVERY sermon missing an ai_summary OR a display_title (paging past
// PostgREST's ~1000-row cap via an id cursor), optionally scoped to one
// church. A sermon with a summary but no title (predates display_title
// generation existing) only needs the cheaper title-only call in the run
// loop below — ai_summary is selected here so callers can tell which case
// they're in. Throws on error so the caller can surface a "run the
// migration" message. Only small columns are pulled so even thousands of
// rows stay cheap.
async function fetchSermonsMissingSummary(churchId = null) {
  const out = []
  let cursor = null
  // Tolerate big_idea not existing yet (supabase-schema-sermon-big-idea.sql
  // not run) — same column-not-there fallback pattern used elsewhere in this
  // file, so the existing summary/display-title backfill keeps working
  // either way.
  let hasBigIdeaColumn = true
  for (let guard = 0; guard < 500; guard++) {   // safety cap: 500k rows
    const cols = hasBigIdeaColumn
      ? 'id, title, preacher, date, church_id, ai_summary, display_title, big_idea'
      : 'id, title, preacher, date, church_id, ai_summary, display_title'
    const orFilter = hasBigIdeaColumn
      ? 'ai_summary.is.null,display_title.is.null,big_idea.is.null'
      : 'ai_summary.is.null,display_title.is.null'
    let q = supabase.from('sermons').select(cols).or(orFilter).order('id', { ascending: true }).limit(1000)
    if (churchId) q = q.eq('church_id', churchId)
    if (cursor) q = q.gt('id', cursor)
    let { data, error } = await q
    if (error && hasBigIdeaColumn && /big_idea/i.test(error.message || '')) {
      hasBigIdeaColumn = false
      q = supabase.from('sermons').select('id, title, preacher, date, church_id, ai_summary, display_title')
        .or('ai_summary.is.null,display_title.is.null').order('id', { ascending: true }).limit(1000)
      if (churchId) q = q.eq('church_id', churchId)
      if (cursor) q = q.gt('id', cursor)
      ;({ data, error } = await q)
    }
    if (error) throw error
    if (!data || data.length === 0) break
    out.push(...data)
    if (data.length < 1000) break
    cursor = data[data.length - 1].id
  }
  return { rows: out, hasBigIdeaColumn }
}

// Platform-wide AI-summary + display-title backfill (RLS scopes it: super
// admins see every church, other admins just their own). Finds sermons
// missing either an ai_summary or a display_title — the latter covers older
// sermons that already have a summary but predate display_title generation
// existing — and generates whichever's missing, one at a time.
function MissingSummariesPanel({ setCount }) {
  const [status, setStatus] = useState('idle')   // idle | scanning | ready | running | done
  const [rows, setRows]     = useState([])
  const [names, setNames]   = useState({})        // church_id -> name
  const [msg, setMsg]       = useState('')
  const [hasBigIdeaColumn, setHasBigIdeaColumn] = useState(true)

  const scan = useCallback(async () => {
    setStatus('scanning'); setMsg('Finding sermons missing a summary, display title or big idea…'); setRows([])
    let list, bigIdeaOk
    try {
      ;({ rows: list, hasBigIdeaColumn: bigIdeaOk } = await fetchSermonsMissingSummary(null))   // paged — true total, not capped at 1000
    } catch (e) {
      setStatus('idle')
      setMsg(/ai_summary|display_title/i.test(e.message || '')
        ? 'Run the migration first: alter table public.sermons add column if not exists ai_summary text; alter table public.sermons add column if not exists display_title text;'
        : `Could not scan: ${e.message}`)
      return
    }
    setHasBigIdeaColumn(bigIdeaOk)
    setRows(list); setCount?.(list.length)
    const ids = [...new Set(list.map(r => r.church_id).filter(Boolean))]
    if (ids.length) {
      const m = {}
      // church-name lookup, also paged in case there are many distinct churches
      for (let i = 0; i < ids.length; i += 500) {
        const { data: chs } = await supabase.from('churches').select('id, name').in('id', ids.slice(i, i + 500))
        for (const c of (chs || [])) m[c.id] = c.name
      }
      setNames(m)
    }
    setStatus('ready')
    setMsg(`${list.length} sermon(s) across ${ids.length} church(es) are missing a summary, display title${bigIdeaOk ? ' or big idea' : ''}. Sermons with no summary yet also need their stored transcript (older imports without one are skipped).${bigIdeaOk ? '' : ' (Run supabase-schema-sermon-big-idea.sql to also backfill big ideas here.)'}`)
  }, [setCount])

  useEffect(() => { scan() }, [scan])

  async function runAll() {
    const list = rows.slice()
    if (!list.length) return
    // Confirm before a potentially long / API-costly run.
    if (!window.confirm(`This will generate AI text for ${list.length} ${list.length === 1 ? 'sermon' : 'sermons'} — one Claude call each (two for sermons with no summary yet), saved to the database as it goes (so a failure never loses completed ones). It can take a while and uses API credits. Proceed?`)) return
    setStatus('running')
    let ok = 0, skip = 0, fail = 0
    for (let i = 0; i < list.length; i++) {
      const r = list[i]
      setMsg(`Generating… ${i + 1}/${list.length} · ${ok} done${skip ? `, ${skip} skipped` : ''}${fail ? `, ${fail} failed` : ''}`)
      // Isolate each sermon so one failure can't abort the whole batch — every
      // successful update is saved immediately in the loop.
      try {
        // Already has a summary — only need whatever derived field(s) are
        // still missing (title, big idea, or both), no transcript required.
        // big_idea is only ever touched when the column exists (scan()
        // already detected this) — otherwise every row would fail the update.
        if (r.ai_summary) {
          const patch = {}
          if (!r.display_title) patch.display_title = await generateDisplayTitle(r.ai_summary, { title: r.title }) || null
          if (hasBigIdeaColumn && !r.big_idea) patch.big_idea = await generateBigIdea(r.ai_summary, { title: r.title })
          if (Object.keys(patch).length === 0) { skip++; continue }
          const { error: uErr } = await supabase.from('sermons').update(patch).eq('id', r.id)
          if (uErr) fail++; else ok++
          continue
        }
        const { data: srow } = await supabase.from('sermons').select('transcript').eq('id', r.id).single()
        const t = srow?.transcript || ''
        if (!t.trim()) { skip++; continue }
        const summary = await generateSermonSummary(t, { title: r.title, preacher: r.preacher, date: r.date, church_name: names[r.church_id] || '' })
        if (!summary) { fail++; continue }
        const displayTitle = await generateDisplayTitle(summary, { title: r.title })
        const patch = { ai_summary: summary, display_title: displayTitle || null }
        if (hasBigIdeaColumn) patch.big_idea = await generateBigIdea(summary, { title: r.title }) || null
        const { error: uErr } = await supabase.from('sermons').update(patch).eq('id', r.id)
        if (uErr) fail++; else ok++
      } catch { fail++ }
    }
    setStatus('done')
    setCount?.(Math.max(0, list.length - ok))
    setMsg(`Done — ${ok} updated${skip ? `, ${skip} skipped (no transcript)` : ''}${fail ? `, ${fail} failed` : ''}. Already-saved work is kept — Rescan to retry the rest.`)
  }

  return (
    <div>
      <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12}}>
        <PrimaryBtn kind="gold" onClick={scan} disabled={status === 'scanning' || status === 'running'}>
          {status === 'scanning' ? 'Scanning…' : 'Rescan'}
        </PrimaryBtn>
        {rows.length > 0 && status !== 'running' && (
          <PrimaryBtn kind="gold" onClick={runAll}>
            {`Generate all ${rows.length}`}
          </PrimaryBtn>
        )}
        {status === 'running' && <PrimaryBtn kind="ghost" disabled>Generating…</PrimaryBtn>}
      </div>
      {msg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{msg}</div>}
      {rows.length > 0 && status !== 'done' && (
        <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:460, overflowY:'auto'}}>
          {rows.slice(0, 500).map((r, i) => (
            <div key={r.id} style={{display:'flex', gap:10, alignItems:'center', padding:'9px 14px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`, fontSize:13, color:C.text}}>
              <span style={{flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.title || '(untitled)'}</span>
              <span style={{color:C.faint, fontSize:12, flexShrink:0}}>{names[r.church_id] || '—'}</span>
            </div>
          ))}
          {rows.length > 500 && <div style={{padding:'9px 14px', fontSize:12, color:C.faint}}>…and {rows.length - 500} more (all will be processed).</div>}
        </div>
      )}
    </div>
  )
}

// Pre-existing sermons that predate sermon_refs.kind being classified during
// scan (the scan prompt — SCAN_SYSTEM_PROMPT in useSermons.js — already asks
// Claude for kind on every NEW sermon, so this is purely a one-off backfill
// for older ones). docs/FIX-SERMON-CLIPS.md §4.1: a cheap notes-text check
// first ("main text"/"main passage"/"sermon text"), then an AI call (ref
// list + timestamps + notes only, no transcript) for the rest. Writes
// NOTHING to the database — only a review CSV, same Blob+anchor download
// pattern as downloadTranscriptsBackup above. The actual `update sermon_refs
// set kind='main_passage'` SQL is written separately, after Leanne reviews
// the CSV.
function looksLikeMainPassageNote(note) {
  return /\bmain\s*(text|passage)\b|\bsermon\s*text\b/i.test(note || '')
}

async function fetchSermonsMissingMainPassage() {
  const out = []
  let cursor = null
  for (let guard = 0; guard < 500; guard++) {   // safety cap: 500k rows
    let q = supabase.from('sermons')
      .select('id, title, display_title, date, church_id, sermon_refs(id, book, chapter, verse, end_verse, timestamp_s, notes, kind)')
      .order('id', { ascending: true })
      .limit(500)
    if (cursor) q = q.gt('id', cursor)
    const { data, error } = await q
    if (error) throw error
    if (!data || data.length === 0) break
    for (const s of data) {
      const refs = s.sermon_refs || []
      if (refs.length > 0 && !refs.some(r => r.kind === 'main_passage')) out.push(s)
    }
    if (data.length < 500) break
    cursor = data[data.length - 1].id
  }
  return out
}

function MainPassagePanel({ setCount }) {
  const [status, setStatus] = useState('idle')   // idle | scanning | ready | classifying | done
  const [sermons, setSermons] = useState([])
  const [names, setNames] = useState({})
  const [results, setResults] = useState([])      // [{ sermonId, sermonTitle, church, date, refId, chosenLabel, method }]
  const [msg, setMsg] = useState('')

  const scan = useCallback(async () => {
    setStatus('scanning'); setMsg('Finding sermons without a classified main passage…'); setResults([])
    let list
    try {
      list = await fetchSermonsMissingMainPassage()
    } catch (e) {
      setStatus('idle')
      setMsg(/kind/i.test(e.message || '') ? 'Run supabase-schema-sermon-passages.sql first (adds sermon_refs.kind).' : `Could not scan: ${e.message}`)
      return
    }
    setSermons(list); setCount?.(list.length)
    const ids = [...new Set(list.map(s => s.church_id).filter(Boolean))]
    const m = {}
    for (let i = 0; i < ids.length; i += 500) {
      const { data: chs } = await supabase.from('churches').select('id, name').in('id', ids.slice(i, i + 500))
      for (const c of (chs || [])) m[c.id] = c.name
    }
    setNames(m)
    setStatus('ready')
    setMsg(`${list.length} sermon(s) across ${ids.length} church(es) have no classified main passage. Classify, then download the review CSV — nothing is saved to the database here.`)
  }, [setCount])

  useEffect(() => { scan() }, [scan])

  async function runClassify() {
    const list = sermons.slice()
    if (!list.length) return
    if (!window.confirm(`This will classify ${list.length} ${list.length === 1 ? 'sermon' : 'sermons'} — a quick notes check first, then one Claude call each for the rest (no transcript, cheap). Produces a CSV for review; nothing is written to the database. Proceed?`)) return
    setStatus('classifying')
    const out = []
    let byNotes = 0, byAi = 0, unresolved = 0
    for (let i = 0; i < list.length; i++) {
      const s = list[i]
      setMsg(`Classifying… ${i + 1}/${list.length} · ${byNotes + byAi} found, ${unresolved} unresolved`)
      const refs = s.sermon_refs || []
      const noteMatch = refs.find(r => looksLikeMainPassageNote(r.notes))
      let chosen = null, method = null
      if (noteMatch) { chosen = noteMatch; method = 'notes'; byNotes++ }
      else {
        try {
          chosen = await classifyMainPassage(s.display_title || s.title, refs)
          if (chosen) { method = 'ai'; byAi++ } else unresolved++
        } catch { unresolved++ }
      }
      if (chosen) {
        out.push({
          sermonId: s.id,
          sermonTitle: s.display_title || s.title || '(untitled)',
          church: names[s.church_id] || '',
          date: s.date || '',
          refId: chosen.id,
          chosenLabel: `${chosen.book} ${chosen.chapter}:${chosen.verse}${chosen.end_verse != null && chosen.end_verse !== chosen.verse ? `-${chosen.end_verse}` : ''}`,
          method,
        })
      }
    }
    setResults(out)
    setStatus('done')
    setMsg(`Done — ${out.length} of ${list.length} classified (${byNotes} from notes, ${byAi} by AI, ${unresolved} unresolved). Download the CSV below to review before any database write.`)
  }

  function downloadCsv() {
    const header = ['sermon_id', 'sermon_title', 'church', 'date', 'chosen_ref_id', 'chosen_passage', 'method']
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`
    const lines = [header.join(',')].concat(
      results.map(r => [r.sermonId, r.sermonTitle, r.church, r.date, r.refId, r.chosenLabel, r.method].map(esc).join(','))
    )
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `main-passage-classification-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div>
      <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:12}}>
        <PrimaryBtn kind="gold" onClick={scan} disabled={status === 'scanning' || status === 'classifying'}>
          {status === 'scanning' ? 'Scanning…' : 'Rescan'}
        </PrimaryBtn>
        {sermons.length > 0 && status !== 'classifying' && status !== 'done' && (
          <PrimaryBtn kind="gold" onClick={runClassify}>{`Classify all ${sermons.length}`}</PrimaryBtn>
        )}
        {status === 'classifying' && <PrimaryBtn kind="ghost" disabled>Classifying…</PrimaryBtn>}
        {results.length > 0 && (
          <PrimaryBtn kind="ghost" onClick={downloadCsv}>Download review CSV ({results.length})</PrimaryBtn>
        )}
      </div>
      {msg && <div style={{fontSize:12.5, color:C.muted, marginBottom:12}}>{msg}</div>}
      {sermons.length > 0 && status !== 'done' && (
        <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:460, overflowY:'auto'}}>
          {sermons.slice(0, 500).map((s, i) => (
            <div key={s.id} style={{display:'flex', gap:10, alignItems:'center', padding:'9px 14px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`, fontSize:13, color:C.text}}>
              <span style={{flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{s.display_title || s.title || '(untitled)'}</span>
              <span style={{color:C.faint, fontSize:12, flexShrink:0}}>{names[s.church_id] || '—'}</span>
            </div>
          ))}
          {sermons.length > 500 && <div style={{padding:'9px 14px', fontSize:12, color:C.faint}}>…and {sermons.length - 500} more (all will be processed).</div>}
        </div>
      )}
      {results.length > 0 && status === 'done' && (
        <div style={{background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', maxHeight:460, overflowY:'auto'}}>
          {results.slice(0, 500).map((r, i) => (
            <div key={r.sermonId} style={{display:'flex', gap:10, alignItems:'center', padding:'9px 14px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`, fontSize:13, color:C.text}}>
              <span style={{flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r.sermonTitle}</span>
              <span style={{color:C.accent, fontSize:12, fontWeight:700, flexShrink:0}}>{r.chosenLabel}</span>
              <span style={{color:C.faint, fontSize:11, flexShrink:0}}>{r.method}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MissingFieldList({ churchId, field, setCount, refreshBadge }) {
  const [sermons, setSermons] = useState([])
  const [loading, setLoading] = useState(true)
  // Per-row local edit buffer keyed by sermon id. Nothing is saved until
  // the user clicks "Save all" at the bottom.
  const [edits,   setEdits]   = useState({})
  // Per-series bulk-input buffer.
  const [bulk,    setBulk]    = useState({})
  const [saving,  setSaving]  = useState(false)
  const [saveErr, setSaveErr] = useState('')

  const load = useCallback(async () => {
    if (!churchId) { setSermons([]); setCount(0); setLoading(false); return }
    setLoading(true)
    // Sermons missing the column (preacher / date).
    let qS = supabase.from('sermons').select('*').eq('church_id', churchId)
    if (field === 'preacher') qS = qS.or('preacher.is.null,preacher.eq.')
    else                       qS = qS.is('date', null)
    // Encouragements missing the equivalent column. "preacher" for
    // sermons corresponds to "shared_by" for encouragements; the date
    // column is "event_date".
    let qE = supabase.from('encouragements').select('*').eq('church_id', churchId).eq('removed', false)
    if (field === 'preacher') qE = qE.or('shared_by.is.null,shared_by.eq.')
    else                       qE = qE.is('event_date', null)
    const [sRes, eRes] = await Promise.all([qS, qE])
    if (sRes.error) console.error('[MissingFieldList] sermons load:', sRes.error)
    if (eRes.error) console.error('[MissingFieldList] encouragements load:', eRes.error)
    const sermons       = (sRes.data || []).map(s => ({ ...s, _kind: 'sermon' }))
    const encouragements = (eRes.data || []).map(e => ({ ...e, _kind: 'encouragement' }))
    // Merge — sermons grouped by series first (existing behaviour),
    // then encouragements as a single "Encouragements" group at the
    // bottom so they don't disrupt sermon-series review flow.
    const merged = [...sermons, ...encouragements]
    setSermons(merged)
    setCount(merged.length)
    setEdits({})
    setBulk({})
    setSaveErr('')
    setLoading(false)
  }, [churchId, field, setCount])

  useEffect(() => { load() }, [load])

  // "Apply to all" no longer saves immediately — it just populates the
  // per-row edit buffer for every sermon in the series. The user reviews,
  // edits any rows they want different, and then hits "Save all" once.
  function applySeriesToEdits(seriesKey, items) {
    const raw = bulk[seriesKey]
    const value = (raw == null ? '' : String(raw)).trim()
    if (!value || !Array.isArray(items) || items.length === 0) return
    setEdits(prev => {
      const next = { ...prev }
      for (const s of items) next[s.id] = value
      return next
    })
  }

  // Batch save every row whose edit buffer is non-empty. Runs the updates
  // in parallel; rows that fail keep their typed value so the user can
  // retry. Successful rows disappear when the list reloads (they no
  // longer match the "missing" filter).
  async function saveAll() {
    const entries = Object.entries(edits)
      .map(([id, raw]) => [id, (raw == null ? '' : String(raw)).trim()])
      .filter(([, v]) => v.length > 0)
    if (entries.length === 0) return
    setSaving(true); setSaveErr('')
    // Route each write to the correct table based on the row's _kind.
    // Sermons use {preacher, date}; encouragements use {shared_by, event_date}.
    const byId = new Map(sermons.map(s => [s.id, s]))
    const results = await Promise.all(entries.map(async ([id, value]) => {
      const row = byId.get(id)
      const isEnc = row?._kind === 'encouragement'
      const table = isEnc ? 'encouragements' : 'sermons'
      const col   = field === 'preacher'
        ? (isEnc ? 'shared_by'  : 'preacher')
        : (isEnc ? 'event_date' : 'date')
      const { error } = await supabase.from(table).update({ [col]: value }).eq('id', id)
      return { id, error }
    }))
    setSaving(false)
    const failed = results.filter(r => r.error)
    if (failed.length > 0) {
      console.error('[MissingFieldList] saveAll partial failure:', failed)
      setSaveErr(`Saved ${entries.length - failed.length}/${entries.length}. ${failed.length} failed — try again.`)
    }
    await load()
    refreshBadge?.()
  }

  if (loading) return <div style={{color:C.faint, padding:'40px 0', textAlign:'center'}}>Loading…</div>
  if (!churchId) return <Note kind="warn">No church selected — set your primary church to use this screen.</Note>
  if (sermons.length === 0) return (
    <div style={{padding:'48px 0', textAlign:'center', color:C.muted}}>
      🎉 No sermons missing a {field}.
    </div>
  )

  // Group by series — preserve first-seen order from the query (sorted by
  // series asc, date desc within series).
  const groups = []
  const seen = new Map()
  for (const s of sermons) {
    // Encouragements have no series — bucket them together so they
    // sit in one clear group at the bottom of the list.
    const key = s._kind === 'encouragement'
      ? 'Encouragements'
      : (s.series || '(no series)')
    if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ key, items: [] }) }
    groups[seen.get(key)].items.push(s)
  }

  const pendingCount = Object.values(edits).filter(v => v != null && String(v).trim()).length

  return (
    <div>
      {groups.map(g => (
        <div key={g.key} style={{marginBottom:28}}>
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'10px 14px', background:C.surface2, borderRadius:8,
            borderLeft:`3px solid ${C.accent}`, marginBottom:10,
          }}>
            <div style={{fontSize:13, fontWeight:800, color:C.text}}>
              {g.key} · {g.items.length} sermon{g.items.length === 1 ? '' : 's'}
            </div>
            <div style={{display:'flex', gap:6, alignItems:'center'}}>
              <input
                type={field === 'date' ? 'date' : 'text'}
                placeholder={field === 'date' ? '' : 'Preacher for series'}
                value={bulk[g.key] || ''}
                onChange={e => setBulk(b => ({ ...b, [g.key]: e.target.value }))}
                style={{...inputStyle, padding:'6px 9px', fontSize:12, width: field === 'date' ? 140 : 200}}/>
              <PrimaryBtn kind="ghost"
                disabled={!(bulk[g.key] || '').trim()}
                onClick={() => applySeriesToEdits(g.key, g.items)}>
                Apply to all {g.items.length}
              </PrimaryBtn>
            </div>
          </div>
          {g.items.map(s => (
            <MissingFieldCard key={s.id} sermon={s}
              field={field}
              edit={edits[s.id]}
              setEdit={v => setEdits(e => ({ ...e, [s.id]: v }))}/>
          ))}
        </div>
      ))}

      {/* Floating Save All button — pinned top-right of the viewport so
          it's always reachable while scrolling a long list. Rows still
          missing a value remain on the page after save. */}
      <div style={{
        position:'fixed', top:24, right:24, zIndex:50,
        display:'flex', alignItems:'flex-end', flexDirection:'column', gap:6,
      }}>
        <button
          type="button"
          onClick={saveAll}
          disabled={pendingCount === 0 || saving}
          style={{
            background: pendingCount === 0 ? C.surface2 : C.accent,
            color:      pendingCount === 0 ? C.faint    : C.navy,
            border: `1px solid ${pendingCount === 0 ? C.border : C.accent}`,
            borderRadius: 999,
            padding: '10px 18px',
            fontSize: 13, fontWeight: 800, letterSpacing: '0.02em',
            cursor: (pendingCount === 0 || saving) ? 'default' : 'pointer',
            fontFamily: FONT,
            boxShadow: pendingCount === 0 ? 'none' : '0 6px 18px rgba(0,0,0,0.35)',
            opacity: saving ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}>
          {saving ? 'Saving…' : `Save all${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
        </button>
        {saveErr && (
          <div style={{
            background:'rgba(249,112,102,0.12)', color:'#F97066',
            border:'1px solid rgba(249,112,102,0.3)', borderRadius:8,
            padding:'6px 10px', fontSize:11, maxWidth:280, textAlign:'right',
          }}>{saveErr}</div>
        )}
      </div>
    </div>
  )
}

// Module-level memo: maps youtube_id → fetched description (or null if the
// fetch returned nothing). Lives across re-renders so re-mounting a card
// from a list reorder / tab switch doesn't burn another ScrapeCreators
// credit. `pending` holds in-flight Promises so two cards for the same
// video don't both fire.
const _descriptionCache = new Map()
const _descriptionPending = new Map()
async function fetchSermonDescription(yt) {
  if (!yt) return null
  if (_descriptionCache.has(yt)) return _descriptionCache.get(yt)
  if (_descriptionPending.has(yt)) return _descriptionPending.get(yt)
  const p = (async () => {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: { action: 'fetch_video_info', youtube_url: `https://www.youtube.com/watch?v=${yt}` },
    })
    let desc = null
    if (!error && data) {
      desc = (data.description
           || data.videoDetails?.shortDescription
           || data.videoDetails?.description
           || data.snippet?.description
           || ''
      ).trim() || null
    }
    _descriptionCache.set(yt, desc)
    _descriptionPending.delete(yt)
    return desc
  })()
  _descriptionPending.set(yt, p)
  return p
}

function MissingFieldCard({ sermon, field, edit, setEdit }) {
  // Normalise across sermon / encouragement so the rest of the JSX is
  // type-agnostic. Sermons use {youtube_id, title, series, date, preacher,
  // start_ts/end_ts}; encouragements use {link_url, title || text, shared_by,
  // event_date}. The "field" being edited maps differently per type:
  //   field === 'preacher' →  sermons.preacher / encouragements.shared_by
  //   field === 'date'     →  sermons.date     / encouragements.event_date
  const isEnc = sermon._kind === 'encouragement'
  let yt = sermon.youtube_id || null
  if (!yt && isEnc && sermon.link_url) {
    const m = String(sermon.link_url).match(/[?&]v=([A-Za-z0-9_-]{6,})|youtu\.be\/([A-Za-z0-9_-]{6,})|\/shorts\/([A-Za-z0-9_-]{6,})|\/embed\/([A-Za-z0-9_-]{6,})/)
    yt = m?.[1] || m?.[2] || m?.[3] || m?.[4] || null
  }
  const url = sermon.youtube_url || sermon.link_url || (yt ? `https://www.youtube.com/watch?v=${yt}` : null)
  const displayDateRaw = isEnc ? sermon.event_date : sermon.date
  const dateLabel = displayDateRaw
    ? new Date(displayDateRaw).toLocaleDateString(undefined, { weekday:'short', day:'numeric', month:'short', year:'numeric' })
    : '—'
  const displayTitle = isEnc
    ? (sermon.title || (sermon.text ? sermon.text.slice(0, 80) + (sermon.text.length > 80 ? '…' : '') : '(untitled)'))
    : (cleanSermonTitle(sermon.title) || '(untitled)')
  const displaySharer = isEnc ? sermon.shared_by : sermon.preacher
  const groupLine = isEnc
    ? `Word of encouragement · ${field === 'date' && !sermon.event_date ? 'no date' : dateLabel}`
    : `${sermon.series || '(no series)'} · ${field === 'date' && !sermon.date ? 'no date' : dateLabel}`
  // Description state: starts cached if we've already fetched, otherwise
  // null + loading until fetch_video_info resolves. Auto-fetched on mount
  // so the admin doesn't have to click a button per card.
  const [desc, setDesc] = useState(yt ? _descriptionCache.get(yt) ?? null : null)
  const [descLoading, setDescLoading] = useState(yt ? !_descriptionCache.has(yt) : false)
  useEffect(() => {
    if (!yt || _descriptionCache.has(yt)) return
    let cancelled = false
    setDescLoading(true)
    fetchSermonDescription(yt).then(d => {
      if (cancelled) return
      setDesc(d)
      setDescLoading(false)
    })
    return () => { cancelled = true }
  }, [yt])

  return (
    <div style={{
      background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
      padding:14, marginBottom:10, display:'flex', gap:14, alignItems:'flex-start',
    }}>
      {yt && (
        // YouTube's mid-video frame at the highest reliable resolution.
        // `hq2.jpg` is the ~50% frame at 480×360. `maxres2.jpg` exists for
        // many videos at 1280×720 — try it first via onError fallback.
        <img src={`https://img.youtube.com/vi/${yt}/maxres2.jpg`}
          alt="Mid-video frame"
          onError={e => {
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = '1'
              e.currentTarget.src = `https://img.youtube.com/vi/${yt}/hq2.jpg`
            }
          }}
          style={{
            flexShrink:0, width:300, aspectRatio:'16/9', objectFit:'cover',
            borderRadius:8, background:C.surface2,
          }}/>
      )}
      <div style={{flex:1, minWidth:0}}>
        {/* Type badge — Sermon (gold) vs Encouragement (pink). */}
        <div style={{
          display:'inline-block',
          fontSize:9, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase',
          padding:'2px 8px', borderRadius:999,
          background: isEnc ? 'rgba(255,107,157,0.15)' : 'rgba(232,200,122,0.15)',
          color: isEnc ? '#FF6B9D' : C.accent,
          border: `1px solid ${isEnc ? 'rgba(255,107,157,0.4)' : 'rgba(232,200,122,0.4)'}`,
          marginBottom:6,
        }}>{isEnc ? 'Encouragement' : 'Sermon'}</div>
        <div style={{fontSize:14, fontWeight:700, color:C.text, marginBottom:3}}>
          {displayTitle}
        </div>
        <div style={{fontSize:12, color:C.muted, marginBottom:10}}>
          {groupLine}
          {displaySharer ? ` · ${displaySharer}` : ''}
          {!isEnc && sermon.start_ts && sermon.end_ts && (
            <> · <span style={{fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace'}}>{sermon.start_ts}→{sermon.end_ts}</span></>
          )}
        </div>
        {yt && (descLoading || desc) && (
          <div style={{
            fontSize:12, color:C.muted, marginBottom:10, lineHeight:1.5,
            padding:'8px 10px', background:C.surface2,
            border:`1px solid ${C.border}`, borderRadius:6,
            maxHeight:160, overflowY:'auto',
            whiteSpace:'pre-wrap', wordBreak:'break-word',
          }}>
            <div style={{
              fontSize:10, fontWeight:700, color:C.faint,
              letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:4,
            }}>Video description</div>
            {descLoading
              ? <div style={{color:C.faint}}>Loading…</div>
              : desc
                ? desc
                : <div style={{color:C.faint, fontStyle:'italic'}}>No description.</div>}
          </div>
        )}
        <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
          <input
            type={field === 'date' ? 'date' : 'text'}
            placeholder={field === 'date' ? '' : (isEnc ? 'Shared by (who brought the word)' : 'Preacher name')}
            value={edit ?? ''}
            onChange={e => setEdit(e.target.value)}
            style={{...inputStyle, padding:'7px 10px', fontSize:13, width: field === 'date' ? 160 : 240}}/>
          {url && (
            <a href={url} target="_blank" rel="noreferrer"
              style={{
                fontSize:12, fontWeight:600, color:C.muted, textDecoration:'none',
                padding:'7px 10px', border:`1px solid ${C.border}`, borderRadius:6,
              }}>{isEnc ? 'Watch the moment ↗' : 'Watch on YouTube ↗'}</a>
          )}
        </div>
      </div>
    </div>
  )
}

function ImportHistory() {
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('import_runs')
      .select('*, churches(name), profiles(full_name,email)')
      .order('run_at', { ascending:false }).limit(50)
      .then(({ data }) => { setRuns(data || []); setLoading(false) })
  }, [])

  if (loading) return <div style={{color:C.faint, padding:14}}>Loading…</div>
  if (!runs.length) return <Note kind="info">No import runs yet.</Note>
  return (
    <div>
      {runs.map(r => (
        <div key={r.id} style={{
          background:C.surface, border:`1px solid ${C.border}`, borderRadius:10,
          padding:'14px 18px', marginBottom:10,
        }}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
            <div style={{fontSize:13, fontWeight:700}}>
              {r.churches?.name || '—'} · {new Date(r.run_at).toLocaleString()}
            </div>
            <div style={{fontSize:11, color:C.faint}}>
              by {r.profiles?.full_name || r.profiles?.email || 'unknown'}
            </div>
          </div>
          <div style={{display:'flex', gap:14, fontSize:12, color:C.muted}}>
            <span>📜 <strong style={{color:C.accent}}>{r.sermons_imported}</strong> sermons</span>
            <span>💜 <strong style={{color:C.purple}}>{r.encouragements_imported}</strong> encouragements</span>
            <span>↷ <strong style={{color:C.faint}}>{r.skipped_count}</strong> skipped</span>
            <span>⚠ <strong style={{color:'#FF8888'}}>{r.needs_review_count}</strong> needs review</span>
          </div>
        </div>
      ))}
    </div>
  )
}
